/**
 * i18n runtime — LNG-01..04, OP-004/005.
 * Sender UI is English; the recipient journey and service messages ship EN+ES.
 * A missing `es` key falls back to English (never a raw key); unknown keys are
 * programming errors and throw.
 */

export const SUPPORTED_LOCALES = ['en', 'es'] as const
export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]

export const EN_MESSAGES = {
  'nav.home': 'Home',
  'nav.agreements': 'Agreements',
  'nav.templates': 'Templates',
  'nav.insights': 'Insights',
  'nav.settings': 'Settings',
  'nav.more': 'More',
  'nav.main_label': 'Main',
  'nav.skip_to_content': 'Skip to content',
  'nav.signed_in_as': 'Signed in as',
  'nav.more_settings_label': 'From Settings',
  'nav.more_my_info': 'My Info and signatures',
  'nav.more_plan': 'Plan and credits',
  'nav.more_backup': 'Backup and restore',
  'nav.more_help': 'Help',
  'shell.offline':
    "You're offline. Your documents, signing and templates work as usual; sending and account changes wait until you're back online.",
  'shell.not_found_title': "This page doesn't exist",
  'shell.not_found_body': 'The link may be old or mistyped. Nothing on this device has changed.',
  'shell.go_home': 'Go to Home',
  'shell.go_agreements': 'Open Agreements',
  'shell.page_failed': "This page couldn't be loaded.",
  'shell.page_failed_body':
    'Reload to try again. Your documents and drafts are saved on this device.',
  'shell.page_update': 'A new version is available.',
  'shell.page_update_body': 'Reload to continue. Your drafts are saved on this device.',
  'shell.page_offline': "This page isn't available offline yet.",
  'shell.page_offline_body':
    'Connect to the internet and reload. Your documents are saved on this device.',
  'shell.page_failed_body_recipient': 'Reload to try again.',
  'shell.page_update_body_recipient': 'Reload to continue.',
  'shell.page_offline_body_recipient': 'Connect to the internet and reload.',
  'shell.storage_title': "This browser isn't letting {product} save documents on this device.",
  'shell.storage_body':
    'This happens in a private window, when site data is blocked for {product}, or when the device is out of space. Open {product} in a normal window, allow site data for it or free up space, then reload.',
  'shell.contact_support': 'Email support',
  'shell.new_device_title': 'Your documents stay on the device where you made them',
  'shell.new_device_body':
    'This device starts empty. To bring your documents and templates here, back them up on your other device and restore the backup here.',
  'shell.new_device_action': 'Bring them over',
  'shell.dismiss': 'Dismiss',
  'common.view_all': 'View all',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.reload': 'Reload',
  'common.new_version_available':
    'A new version is ready. Reload when you are done with the current draft.',
  'status.draft': 'Draft',
  'status.sending': 'Sending',
  'status.send_failed': "Couldn't send",
  'status.waiting': 'Waiting for others',
  'status.waiting_for': 'Waiting for {name}',
  'status.ready_to_finish': 'Ready to finish',
  'status.your_turn': 'Your turn to sign',
  'status.completed': 'Completed',
  'status.finish_failed': "Couldn't finish",
  'status.declined': 'Declined',
  'status.voided': 'Voided',
  'status.expired': 'Expired',
  'status.participant_not_yet': 'Not their turn yet',
  'status.participant_waiting': 'Waiting to sign',
  'status.participant_signed': 'Signed',
  'status.participant_declined': 'Declined',
  'status.participant_not_signed': 'Not signed',
  'status.participant_cc': 'Gets a copy',
  'home.title': 'Home',
  'home.welcome': 'Welcome to {product}',
  'home.welcome_back': 'Welcome back',
  'home.handoff_document_waiting': '{host} sent you a document. Check it before it is used.',
  'home.handoff_waiting': '{host} is waiting for you to choose a document.',
  'home.handoff_host_unknown': 'Your AI assistant',
  'home.handoff_open': 'Open',
  'home.first_run':
    'New here? Send a PDF for someone else to sign, sign one yourself, or start from a ready-made agreement.',
  'home.sign_document': 'Sign a document',
  'home.sign_document_description': 'Import a PDF or image and sign it on this device.',
  'home.send_for_signature': 'Send for signature',
  'home.send_for_signature_description':
    'Prepare a request and share it yourself or let us deliver it.',
  'home.use_template': 'Use a template',
  'home.use_template_description': 'Reuse a saved layout.',
  'home.use_template_starter': 'Start from a ready-made agreement.',
  'home.templates_title': 'Use a template',
  'home.templates_empty': 'No templates yet. Save one while signing a document.',
  'home.templates_manage': 'Manage templates',
  'home.use_again': 'Use again',
  'home.continue': 'Continue',
  'home.waiting_title': 'Waiting for others',
  'home.waiting_empty': 'Requests you send for signature show here.',
  'home.waiting_unavailable': "Your requests couldn't be refreshed.",
  'home.requests_offline': "Your requests couldn't be refreshed. You're offline.",
  'home.requests_as_of': 'Showing them as of {time}.',
  'home.requests_as_of_day': 'Showing them as of {time}.',
  'home.signed_out': 'Sign in to see the requests you sent.',
  'home.completed_title': 'Completed',
  'home.completed_empty': 'Finished agreements show here, ready to use again.',
  'home.new_agreement_title': 'New agreement',
  'home.use_again_layout_repair': 'This layout needs repair before it can be reused.',
  'home.use_again_failed': 'Could not create a new draft from this source.',
  'home.drafts_check_failed': "Some drafts couldn't be checked right now.",
  'home.document_open_failed': "This document couldn't be opened on this device.",

  'home.description': 'Sign locally or prepare a request when you are ready.',
  'home.attention_title': 'Needs attention',
  'home.attention_description': 'Requests and drafts that need you.',
  'home.attention.pending_finalization': 'Ready to finish',
  'home.attention.delivery_failure': "Email didn't go through",
  'home.attention.signing_expiring': 'Expires soon',
  'home.attention.sender_signature': 'Your turn to sign',
  'home.attention.next_link_ready': 'Share the next link',
  'home.attention.request_declined': 'Declined: see why',
  'home.attention.finishing': 'Finishing…',
  'home.recent_document': 'Document',
  'home.recent_draft': 'Draft',
  'home.recent_completed': 'Completed',
  'home.attention_signed_out':
    'Sign in to see the requests that need you. Your drafts are still here.',
  'home.attention_unavailable': "Requests that need you couldn't be refreshed.",
  'requests.attention_title': 'Requests needing attention',
  'requests.attention_description': 'Requests and drafts that need you.',
  'requests.attention_count': '{count} need your attention',
  'requests.attention_count_one': '1 needs your attention',
  'requests.attention_empty': 'Nothing needs you right now',
  'requests.attention_empty_hint': 'Requests waiting on you and unfinished drafts show up here.',
  'requests.detail_title': 'Request detail',
  'requests.detail_description': 'The current service-observed status of this request.',
  'requests.detail_unavailable':
    'Could not load this request. Check your connection and try again.',
  'requests.detail_not_found':
    "This request isn't available. It may have been deleted, or it belongs to another account.",
  'requests.detail_retry': 'Try again',
  'requests.back_to_agreements': 'Back to agreements',
  'requests.detail_responses': 'Required signer responses',
  'requests.detail_progress': '{submitted} of {total} accepted',
  'requests.detail_delivery': 'Delivery mode',
  'requests.detail_signing_deadline': 'Signing deadline',
  'requests.detail_recovery_deadline': 'Finalization recovery deadline',
  'requests.detail_signers': 'Participants',
  'requests.detail_step': 'Response: {state} · Delivery: {delivery}',
  'requests.detail_viewed': 'Viewed {time}',
  'requests.person_signed_at': 'Signed {time}',
  'requests.person_declined_at': 'Declined {time}',
  'requests.detail_not_viewed': 'Not viewed yet',
  'requests.timeline_title': 'Timeline',
  'requests.timeline_description':
    'What the service recorded for this request, oldest first. Times are when the service recorded each event.',
  'requests.timeline_participant': 'a participant',
  'requests.timeline_previous_recipient': 'the previous recipient',
  'requests.timeline.created': 'Prepared',
  'requests.timeline.activated': 'Sent',
  'requests.timeline.activated_manual_one': 'Signing link created',
  'requests.timeline.activated_manual': 'Signing links created',
  'requests.timeline.delivered': 'Email delivered to {name}',
  'requests.timeline.reminder_sent': 'Reminder sent to {name}',
  'requests.timeline.viewed': 'Viewed by {name}',
  'requests.timeline.corrected': 'Details corrected for {name}',
  'requests.timeline.corrected_earlier': 'Details corrected',
  'requests.timeline.signed': 'Signed by {name}',
  'requests.timeline.declined': 'Declined by {name}',
  'requests.timeline.completed': 'Completed',
  'requests.timeline.voided': 'Voided',
  'requests.timeline.expired': 'Expired',
  'requests.timeline.upcoming_label': 'Still to happen',
  'requests.timeline.upcoming_sign': '{name} signs',
  'requests.timeline.upcoming_you': 'You sign',
  'requests.timeline.upcoming_you_later': 'After the others sign',
  'requests.timeline.upcoming_after': 'When everyone has signed',
  'requests.timeline.upcoming_finish': 'When the signed copy is made',
  'requests.detail_finalization_pending':
    'All required responses are sealed. Finalization is pending; refresh for the latest recovery state.',
  'requests.finalization_title': 'Complete this request',
  'requests.finalization_action': 'Finalize on this device',
  'requests.finalization_committed':
    'The completed PDF and evidence were committed. Verify the PDF before saving a copy.',
  'requests.finalization_conflict':
    'Another finalizer or response-set change moved this request. Refresh before trying again.',
  'requests.finalization_failed':
    'Could not complete this request. The accepted responses remain recorded.',
  'requests.download_title': 'Completed PDF',
  'requests.download_description':
    'This device verifies the encrypted download and exact PDF hash before saving a copy.',
  'requests.download_verify': 'Verify PDF',
  'requests.download_verified': 'PDF verified on this device. Choose Save PDF to keep a copy.',
  'requests.download_save': 'Save PDF',
  'requests.download_saved': 'PDF saved.',
  'requests.download_started': 'PDF download started. Check your browser downloads for the file.',
  'requests.activity_save_cancelled': 'Save cancelled. The request was not changed.',
  'requests.download_cancelled': 'Save cancelled. The completed request remains available.',
  'requests.download_unavailable':
    'Saving is unavailable here. Try this request on another supported browser.',
  'requests.download_failed': "The signed PDF couldn't be saved.",
  'requests.download_check_failed':
    "The signed PDF couldn't be checked yet. Download signed PDF tries again.",
  'requests.download_needs_local_key':
    'This device needs its local envelope key to open the encrypted PDF.',
  'requests.detail_delivery_failed':
    'Delivery needs attention for {names}. The recorded delivery state does not mean the recipient has viewed the document.',
  'requests.detail_recovery_actions': 'Recovery actions',
  'requests.share_again': 'Share again',
  'requests.detail_free_correction':
    'Free recipient details cannot be corrected after creation. Keep the existing evidence, then void this request and prepare a new one if details must change.',
  'requests.detail_correction_resend':
    'Send the corrected invitation now using this request; no new delivery credit.',
  'requests.detail_void_title': 'Void this request?',
  'requests.detail_void_action': 'Void request',
  'requests.detail_void_warning':
    'This permanently closes this request and its current links. Accepted evidence is preserved; bytes already downloaded cannot be recalled.',
  'requests.detail_prepare_new': 'Prepare a new request',
  'requests.detail_cancel': 'Cancel',
  'requests.detail_extend_title': 'Extend signing deadline',
  'requests.detail_extend_note':
    'Choose a total signing window from the original activation. The service checks the limit before changing the deadline.',
  'requests.detail_extend_after_response':
    'A response has already been accepted, so this deadline cannot be extended.',
  'requests.state.unknown': 'State unavailable',
  'requests.state.local_draft': 'Local draft',
  'requests.state.preparing': 'Preparing',
  'requests.state.preparation_failed': 'Preparation failed',
  'requests.state.active': 'Active',
  'requests.state.awaiting_finalization': 'Awaiting finalization',
  'requests.state.completed': 'Completed',
  'requests.state.finalization_failed': 'Finalization needs recovery',
  'requests.state.declined': 'Declined',
  'requests.state.voided': 'Voided',
  'requests.state.expired': 'Expired',
  'requests.recipient.blocked': 'Waiting for turn',
  'requests.recipient.active': 'Awaiting response',
  'requests.recipient.submitted': 'Accepted',
  'requests.recipient.declined': 'Declined',
  'requests.delivery_mode.manual': 'Share the link yourself',
  'requests.delivery_mode.managed': 'Managed invitation',
  'requests.delivery.not_managed': 'Sender shares link',
  'requests.delivery.queued': 'Queued',
  'requests.delivery.dispatching': 'Sending',
  'requests.delivery.accepted': 'Accepted by provider',
  'requests.delivery.delivered': 'Delivered',
  'requests.delivery.bounced': 'Bounced',
  'requests.delivery.complained': 'Complaint',
  'requests.delivery.suppressed': 'Suppressed',
  'requests.delivery.unknown': 'Outcome unknown',
  'requests.sent_on': 'Sent {date}',
  'requests.completed_on': 'Completed {date}',
  'requests.expires_on': 'Expires {date}',
  'requests.expires_left': 'Expires {date} · {count} days left',
  'requests.expires_left_one': 'Expires {date} · 1 day left',
  'requests.expired_on': 'Expired {date}',
  'requests.progress_signed': '{signed} of {total} signed',
  'requests.expiring_banner': 'The signing link stops working on {date}.',
  'requests.next.share_title': 'Share the signing link',
  'requests.next.share_body':
    "We don't email anyone for this request. Send each person their link yourself. Send it again any time as a reminder; it's the same link.",
  'requests.next.next_link': '{name} is next. Share their link.',
  'requests.next.waiting_title': 'Waiting for {names}',
  'requests.next.managed_body':
    "We emailed the signing link. If they haven't signed yet, send a reminder.",
  'requests.next.bounced': "The email to {name} couldn't be delivered.",
  'requests.next.your_turn_title': 'Your turn to sign',
  'requests.next.your_turn_body': '{names} signed. Add your signature to finish "{title}".',
  'requests.next.your_turn_first': 'Sign your part of "{title}" first. The others sign after you.',
  'requests.adopted_next': 'You signed. Now share the link with {name}.',
  'requests.adopted_next_managed': "You signed. We're emailing {name} their link.",
  'requests.adopted_done': "You signed. Everyone has signed, so we're finishing the agreement.",
  'requests.next.review_sign': 'Review and sign',
  'requests.sign.title': 'Review and sign',
  'requests.sign.lead':
    'Read "{title}", agree to sign electronically, then fill in and sign your fields.',
  'requests.sign.others_note':
    'Only your fields are shown here. What {names} filled in appears on the signed PDF.',
  'requests.sign.finish': 'Sign and finish',
  'requests.sign.todo': '{count} fields left to fill in.',
  'requests.sign.todo_one': '1 field left to fill in.',
  'requests.sign.loading': 'Opening the document…',
  'requests.sign.no_source':
    'Open {product} on the device you sent this from to sign. That device holds the document.',
  'requests.sign.save_failed': "Your signature couldn't be saved on this device.",
  'requests.sign.no_fields':
    'This document has no fields for you. Sign and finish records that you agree to it as sent.',
  'requests.share_link': 'Share…',
  'requests.link_until': 'Link works until {date}',
  'requests.link_copied': 'Link copied.',
  'requests.link_shared': 'Link shared.',
  'requests.copy_by_hand': "The link couldn't be copied automatically. Select it and copy it:",
  'requests.link_failed': "The link couldn't be prepared.",
  'requests.link_too_soon':
    "{name}'s link was prepared recently, so it can be fetched here again after {time}. A link you already shared still works.",
  'requests.link_limit':
    "{name}'s link can't be fetched again here. Use the one you already shared, or void this request and send a new one.",
  'requests.remind': 'Send reminder',
  'requests.reminded': 'Reminder on its way to {name}. {count} more can be sent.',
  'requests.reminded_one': 'Reminder on its way to {name}. 1 more can be sent.',
  'requests.remind_too_soon': 'We emailed {name} recently. You can send a reminder after {time}.',
  'requests.remind_after': 'You can send a reminder after {time}.',
  'requests.remind_failed': "The reminder couldn't be sent.",
  'requests.remind_limit': "You've sent every reminder this request allows for {name}.",
  'requests.last_reminded': 'Last reminder {date}',
  'requests.correct_failed': "The details couldn't be corrected.",
  'requests.correct_scope':
    "Correcting gives {name} a new link, and the one you sent stops working. Signatures already given don't change.",
  'requests.corrected': "Details corrected. {name}'s old link no longer works.",
  'requests.finish.title': 'Everyone signed',
  'requests.finish.working': 'Finishing the signed copy on this device…',
  'requests.finish.body':
    'This device makes the final signed PDF. Keep {product} open for a moment.',
  'requests.finish.retry': 'Finish now',
  'requests.finish.failed': "The agreement couldn't be finished.",
  'requests.finish.sender_marks_missing':
    "Your own signature for this agreement isn't on this device any more, so the signed copy can't be made. Void the request and send it again.",
  'requests.finish.conflict':
    'Another device is finishing this agreement. Check again in a moment.',
  'requests.finish.elsewhere':
    'Open {product} on the device you sent this from to finish it by {date}. Only that device can make the signed copy.',
  'requests.finish.done': 'Finished. The signed copy is ready.',
  'requests.finish.elsewhere_nodate':
    'Open {product} on the device you sent this from to finish it. Only that device can make the signed copy.',
  'requests.done.title': 'Signed by everyone',
  'requests.done.next_hint':
    'Send again starts a new request from the unsigned original, with the same people. Use again starts a new agreement from the same document.',
  'requests.download_pdf': 'Download signed PDF',
  'requests.download_checking': 'Checking the signed copy…',
  'requests.download_ready': 'Verified signed copy',
  'requests.file_signed': 'Signed PDF',
  'requests.file_signed_hint': 'The agreement with everyone’s signatures.',
  'requests.document_view': 'View document',
  'requests.document_hide': 'Hide document',
  'requests.document_as_sent': 'As you sent it',
  'requests.document_signed_copy': 'Signed copy',
  'requests.document_pages_one': '{count} page',
  'requests.document_pages': '{count} pages',
  'requests.document_unavailable': 'This document isn’t on this device, so it can’t be shown here.',
  'requests.kept_on_device': 'A copy is kept on this device.',
  'requests.server_copy_until':
    'The server keeps a copy until {date} ({count} days left). Save it now; after that only the copy you saved remains.',
  'requests.server_copy_until_one':
    'The server keeps a copy until {date} (1 day left). Save it now; after that only the copy you saved remains.',
  'requests.server_copy_gone': 'The server copy was deleted on {date}. Use the copy you saved.',
  'requests.download_elsewhere':
    'The signed PDF opens only on the device you sent this from. Open {product} there, or move your library to this device.',
  'requests.move_library': 'Move my library',
  'requests.cc.title': 'People who get a copy',
  'requests.cc.waiting': 'Gets the final copy when everyone signs',
  'requests.cc.emailed': 'We email them the final copy',
  'requests.cc.share': 'Share final copy',
  'requests.cc.failed': "The final copy link couldn't be prepared.",
  'requests.closed.declined': '{name} declined on {date}.',
  'requests.closed.declined_someone': 'Declined on {date}.',
  'requests.closed.declined_nodate': '{name} declined.',
  'requests.closed.declined_plain': 'Declined.',
  'requests.closed.reason': 'Their reason: "{reason}"',
  'requests.closed.no_reason': 'No reason given.',
  'requests.closed.void_reason': 'Your reason: "{reason}"',
  'requests.closed.expired': "Expired on {date}. {names} didn't sign in time.",
  'requests.closed.expired_plain': 'Expired on {date}.',
  'requests.closed.voided': 'You voided this request on {date}.',
  'requests.closed.voided_plain': 'You voided this request.',
  'requests.closed.finish_failed':
    "This agreement couldn't be finished in time, so it isn't signed. Send a new request to collect signatures again.",
  'requests.closed.prepare_failed':
    "This request never went out: it couldn't be prepared in time, so nobody was sent anything.",
  'requests.preparing.body':
    "This request hasn't gone out yet. Check it from Review and send on the device that started it.",
  'requests.closed.next':
    'Send it again with the same people. It gets a new link and a new deadline.',
  'requests.send_new': 'Send a new request',
  'requests.send_again': 'Send again',
  'requests.save_template': 'Save as template',
  'requests.template_saved': '“{title}” is saved to your templates.',
  'requests.template_failed': "The template couldn't be saved.",
  'requests.template_open': 'Open templates',
  'requests.no_source': 'Open {product} on the device you sent this from to send it again.',
  'requests.restart_failed': "The new request couldn't be prepared.",
  'requests.activity.save': 'Save activity record',
  'requests.activity.hint':
    'A PDF of who was asked, what happened and when. It is not a signed agreement. Available until {date}.',
  'requests.activity.failed': "The activity record couldn't be saved.",
  'requests.activity.heading': 'Activity record',
  'requests.activity.notice':
    'This is not a signed agreement. The request ended before everyone signed, so no signed document exists.',
  'requests.activity.source':
    "Built on the sender's device from what the {product} service recorded about this request.",
  'requests.activity.footer': '{product} activity record · not a signed agreement',
  'requests.activity.section_request': 'Request',
  'requests.activity.section_privacy': 'What this record does not include',
  'requests.activity.title': 'Title',
  'requests.activity.outcome': 'Outcome',
  'requests.activity.sent': 'Sent',
  'requests.activity.deadline': 'Signing deadline',
  'requests.activity.ended': 'Ended',
  'requests.activity.decline_reason': 'Reason given for declining',
  'requests.activity.void_reason': 'Reason given for voiding',
  'requests.activity.request_id': 'Request ID',
  'requests.activity.generated': 'Record created',
  'requests.activity.viewed': 'viewed {time}',
  'requests.activity.signed': 'signed {time}',
  'requests.more_title': 'More actions',
  'requests.void.consequence_manual':
    "{names}'s link stops working right away. We don't message anyone, so let them know yourself.",
  'requests.void.consequence_managed':
    "{names}'s link stops working right away and reminders stop. We don't email them about it.",
  'requests.void.slot':
    'It stops counting toward your active requests. Your document stays on this device.',
  'requests.void.reason_label': 'Why are you voiding it? (optional)',
  'requests.void.reason_mistake': 'Sent by mistake',
  'requests.void.reason_changes': 'The document needs changes',
  'requests.void.reason_wrong_person': 'Wrong person or email',
  'requests.void.reason_other': 'Other',
  'requests.void.reason_other_label': 'Reason',
  'requests.void.correct_instead': 'Wrong email or name? Correct it instead',
  'requests.void_failed': "The request couldn't be voided.",
  'requests.extend.current': 'Current deadline: {date}',
  'requests.extend.week': '1 more week',
  'requests.extend.two_weeks': '2 more weeks',
  'requests.extend.pick': 'Pick a date',
  'requests.extend.date_label': 'New deadline',
  'requests.extend.preview': 'New deadline: {date}',
  'requests.extend.max': 'You can extend up to {date}.',
  'requests.extend_failed': "The deadline couldn't be changed.",
  'requests.people_title': 'People',
  'requests.person_you': '{name} (you)',
  'requests.you': 'You',
  'requests.email.sending': 'Email on its way',
  'requests.email.delivered': 'Email delivered',
  'requests.email.bounced': 'Email bounced',
  'requests.email.complained': 'Marked as spam',
  'requests.email.suppressed': 'Email blocked',
  'requests.attachments': 'Attachments: {collected} of {requested} received',
  'requests.attachments_note': "Received files can't be opened here yet.",
  'requests.message.invite':
    'Hi {name}, please review and sign "{title}". The link works until {date}.',
  'requests.message.invite_unnamed':
    'Please review and sign "{title}". The link works until {date}.',
  'requests.message.reminder':
    'Hi {name}, a quick reminder to review and sign "{title}". The link works until {date}.',
  'requests.message.reminder_unnamed':
    'A quick reminder to review and sign "{title}". The link works until {date}.',
  'requests.message.final_copy':
    'Hi {name}, here is the signed copy of "{title}". The link works until {date}.',
  'requests.message.final_copy_unnamed':
    'Here is the signed copy of "{title}". The link works until {date}.',
  'home.actions_label': 'Start',
  'home.recent_description': 'The three most recent documents on this device.',
  'home.templates_label': 'Recent templates',
  'import.choose_files': 'Choose files',
  'import.drop_title': 'Drop files here or choose them',
  'import.prepare_send_title': 'Prepare a request',
  'import.prepare_send_description': 'Choose a PDF or image, then add a signer before sending.',
  'import.drop_hint': 'Files are read on this device. Importing uploads nothing.',
  'import.send_drop_hint':
    'Files are read on this device. Only creating the request, after your review, uploads an encrypted copy.',
  'import.accept_hint':
    'PDF, Word (DOCX), JPEG, PNG or HEIC. Save Excel and PowerPoint as PDF first.',
  'import.mode_title': 'How should these files be combined?',
  'import.mode_combined': 'One document (files combined in order)',
  'import.mode_separate': 'Separate documents',
  'import.mode_combined_note':
    'A combined package uses one active request and one delivery credit.',
  'import.mode_combined_note_sign': 'The files become one document to sign, in this order.',
  'import.mode_separate_note_sign':
    'Each file is saved as its own document in Agreements, ready to open and sign.',
  'import.import_button': 'Continue',
  'import.combine_button': 'Combine and continue',
  'import.importing': 'Importing…',
  'import.state.importing': 'Reading…',
  'import.state.validating': 'Checking…',
  'import.state.converting': 'Converting…',
  'import.state.choice': 'Word file',
  'import.state.ready': 'Ready',
  'import.state.failed': 'Needs attention',
  'import.state.cancelled': 'Cancelled',
  'import.pages': '{count} pages',
  'import.page_one': '1 page',
  'import.retry': 'Try again',
  'import.remove': 'Remove',
  'import.replace': 'Replace',
  'import.replace_drops_title': 'Some fields would have no page',
  'import.replace_drops_one':
    'The new document has {pages}, so 1 field on a later page will be removed. Who signs and your message stay.',
  'import.replace_drops':
    'The new document has {pages}, so {count} fields on later pages will be removed. Who signs and your message stay.',
  'import.replace_drops_accept': 'Replace and remove those fields',
  'import.replace_other': 'Choose another file',
  'import.move_up': 'Move up',
  'import.move_down': 'Move down',
  'import.duplicate_warning':
    'The same source was added more than once. Keep both only if that is deliberate.',
  'import.guidance.export_to_pdf_locally':
    'Convert this file to PDF on this device first (Print → Save as PDF), then add the PDF here.',
  'import.batch_failed': 'Nothing was imported. Fix or remove the failing file and try again.',
  'import.error_prefix': 'Could not import this file.',
  'import.issue.encrypted': 'This PDF is password-protected.',
  'import.issue.malformed': "This file couldn't be opened.",
  'import.issue.zero_pages': 'This PDF has no pages.',
  'import.issue.active_content':
    "This PDF has scripts or live form parts that can't be signed safely here.",
  'import.issue.existing_signature':
    'This PDF already has a digital signature. Signing it here would break that signature.',
  'import.issue.too_large': 'This file is too large to sign here.',
  'import.issue.too_many_pages': 'This PDF has too many pages to sign at once.',
  'import.issue.pixel_limit': 'This image is too large to open.',
  'import.issue.image_unsupported': "This image type can't be opened here.",
  'import.issue.heic_unsupported': "This browser can't open HEIC photos.",
  'import.issue.unknown_type': '{product} opens PDF, Word (DOCX), JPEG, PNG and HEIC files.',
  'import.issue.docx_failed': "We couldn't convert this Word file.",
  'import.issue.docx_too_large': 'This Word file is too large to convert here.',
  'import.issue.docx_too_complex': 'This Word file is too complex to convert here.',
  'import.issue.docx_too_many_pages': 'This Word file has more than {count} pages.',
  'import.issue.docx_timeout': 'This Word file took too long to convert here.',
  'import.issue.doc_old_format':
    "This is an older Word file (.doc), which can't be converted here.",
  'import.issue.docx_protected':
    'This Word file is password-protected or in the older .doc format.',
  'import.next.print_to_pdf': 'Print it to PDF first, then add that copy.',
  'import.next.existing_signature':
    'Ask the sender for an unsigned copy, or print it to PDF to sign a flattened copy.',
  'import.next.choose_supported': 'Choose a PDF or a photo instead.',
  'import.next.docx_export':
    'Save it as a PDF in Word (File → Save As → PDF), then choose the PDF.',
  'import.next.docx_split':
    'Save it in parts of up to {count} pages as PDFs in Word (File → Save As → PDF), then choose them.',
  'import.next.heic': 'Choose a JPEG, or export the photo as JPEG and add it again.',
  'import.next.pixel_limit': 'Add a smaller copy instead, such as a screenshot of the photo.',
  'import.issue.storage_write': "This device couldn't save the file.",
  'import.next.storage_write':
    'In a private window, open {product} in a normal window instead. Otherwise free some space on this device and try again.',
  'import.rotate': 'Rotate',
  'import.drop_title_touch': 'Choose a document or a photo',
  'import.camera': 'Take a photo',
  'import.camera_more': 'Add another page',
  'import.partial': '{imported} imported · {failed} need attention. Fix or remove them below.',
  'import.open_agreements': 'Open Agreements',
  'import.ready_count': '{count} ready',
  'import.empty': 'Add at least one file to continue.',
  'import.thumbnail_alt': 'Preview of {name}',
  'import.template_title': 'Add a document for your template',
  'import.template_description':
    'Choose the document, then add who signs and place their fields. It becomes a template when you save it.',
  'import.sources_title': 'Or add from',
  'import.sources_system_note':
    'Google Drive, OneDrive and other storage apps on this device open from Choose files.',
  'import.source.scan': 'Scan',
  'import.source.scan_hint': 'Photograph each page',
  'import.source.library': '{product} library',
  'import.source.library_hint': 'Templates and saved documents',
  'import.source.library_hint_documents': 'Your saved documents',
  'import.source.google_drive': 'Google Drive',
  'import.source.dropbox': 'Dropbox',
  'import.source.cloud_hint': 'Pick a file to bring in',
  'import.source.cloud_hint_system': "Opens your device's files",
  'import.library_title': 'From your {product} library',
  'import.library_search': 'Search by name',
  'import.library_failed': "Your library couldn't be opened. Try again.",
  'import.library_templates': 'Templates',
  'import.library_gallery': 'Ready-made agreements',
  'import.library_documents': 'Saved documents',
  'import.library_kind_template': 'Template',
  'import.library_kind_document': 'Saved document',
  'import.library_documents_note':
    'Starts a new copy with the same pages and fields. The original stays as it is.',
  'import.library_no_documents': 'No saved documents on this device yet.',
  'import.library_no_match': 'Nothing matches that name.',
  'import.cloud_help_title': 'Add from {provider}',
  'import.cloud_help_body':
    "{provider} isn't connected to {product} in this browser yet, but you can still add a file from it.",
  'import.cloud_help_touch':
    "Tap Choose files, then pick {provider} in the list of places. If it isn't there, install the {provider} app first.",
  'import.cloud_help_desktop':
    'Choose files and open your {provider} folder, or download the file from {provider} and choose it from Downloads.',
  'import.cloud_failed': "{provider} couldn't be opened.",
  'import.cloud_popup_blocked':
    'Your browser blocked the {provider} window. Allow pop-ups for this site, then try again.',
  'import.cloud_too_large': 'That file is too large to sign here.',
  'import.crop': 'Crop',
  'import.crop_title': 'Crop the page',
  'import.crop_hint': 'Drag the corners to the edges of the paper.',
  'import.crop_failed': "This photo couldn't be shown for cropping.",
  'import.crop_handle_nw': 'Top-left corner',
  'import.crop_handle_ne': 'Top-right corner',
  'import.crop_handle_sw': 'Bottom-left corner',
  'import.crop_handle_se': 'Bottom-right corner',
  'import.crop_apply': 'Done',
  'import.crop_reset': 'Use the whole photo',
  'import.converted_badge': 'Converted from Word',
  'import.converted_title': 'Converted from Word — check the layout',
  'import.converted_note':
    '{name} was converted on this device; nothing was uploaded. Fonts and spacing can differ a little from Word. If something looks off, save it as a PDF in Word and add that instead.',
  'import.converted_page': 'Page {number}',
  'import.converted_previous': 'Previous',
  'import.converted_next': 'Next',
  'import.converted_continue': 'Looks right, continue',
  'import.docx_choice_title': 'This is a Word file',
  'import.docx_choice_body':
    '{product} can turn it into a PDF on this device. If you already have a PDF of it, you can use that instead.',
  'import.docx_choice_convert': 'Convert it for me',
  'import.docx_choice_pdf': 'Choose a PDF instead',
  'import.choose_pdf': 'Choose a PDF',
  'import.preview': 'Preview',
  'editor.title': 'Prepare document',

  'editor.description': 'Place fields, add signers, then send.',
  'editor.reused_review_source':
    "A fresh copy with the same fields and nobody's details. Review the document for anything from last time, then add the people to send it to.",
  'editor.reused_sign': 'A fresh copy with the same fields. Fill it in and sign.',
  'editor.continue_to_send': 'Review and send',
  'editor.add_signer_first': 'Add an external signer to continue to Send.',
  'editor.note_more': 'More',
  'editor.note_less': 'Less',
  'editor.note_dismiss': 'Hide this note',
  'editor.go_to_recipients': 'Go to recipients',
  'editor.send_save_failed': 'The draft could not be saved. Try again before sending.',
  'editor.document_title': 'Document title',
  'editor.tools_label': 'Document tools',
  'editor.pending': 'Opening the document…',
  'editor.field_palette': 'Fields',
  'editor.field.signature': 'Signature',
  'editor.field.initials': 'Initials',
  'editor.field.date_signed': 'Date signed',
  'editor.field.full_name': 'Full name',
  'editor.field.email': 'Email',
  'editor.field.text': 'Text',
  'editor.field.checkbox': 'Checkbox',
  'editor.field.company': 'Company',
  'editor.field.title': 'Title',
  'editor.attachments_title': 'Attachments',
  'editor.attachments_hint':
    'Ask a signer to attach a file with their response. Attachments are evidence of their own: they are never added to the signed document, and each request is offered to the person who owns it.',
  'editor.attachments_lifetime_only': 'Attachments are part of Lifetime. Your draft is unchanged.',
  'editor.attachment_add': 'Add an attachment request',
  'editor.attachment_owner': 'Who provides the file?',
  'editor.attachment_me': 'Me (optional)',
  'editor.attachment_sender_optional': 'Files you provide are optional and never block a signer.',
  'editor.attachment_label': 'What are you asking for?',
  'editor.attachment_instructions': 'Instructions (optional)',
  'editor.attachment_required_label': 'Required before the signer can finish',
  'editor.attachment_required': 'required',
  'editor.attachment_optional': 'optional',
  'editor.attachment_limits': '{types} · up to {files} · up to {megabytes} MB each',
  'editor.attachment_scope':
    'Visible to the uploader and you; finalizer access follows the reviewed key-access contract.',
  'editor.attachment_save': 'Add request',
  'editor.attachment_remove': 'Remove',
  'editor.attachment_cancel': 'Cancel',
  'editor.placement_overlay':
    'Document field placement area. Choose a field type, then use the arrow keys and press Enter to place it.',
  'editor.place_hint': 'Pick a field, then click the page to place it.',
  'editor.place_hint_touch': 'Pick a field, then tap the page to place it.',
  'editor.selected': 'Selected field',
  'editor.owner': 'Owner',
  'editor.owner_none': 'Unassigned',
  'editor.required': 'Required',
  'editor.locked': 'Locked',
  'editor.max_length': 'Maximum length',
  'editor.duplicate': 'Duplicate',
  'editor.delete': 'Delete',
  'editor.undo': 'Undo',
  'editor.redo': 'Redo',
  'editor.zoom_in': 'Zoom in',
  'editor.zoom_out': 'Zoom out',
  'editor.fit_width': 'Fit width',
  'editor.fit_page': 'Fit page',
  'editor.field_settings': 'Settings',
  'editor.finish_blocked_none': 'Add a field first',
  'editor.saving': 'Saving…',
  'editor.saved': 'Saved · revision {revision}',
  'editor.unsaved': 'Unsaved changes',
  'editor.first_use':
    'Place a field, then drag it into position. Use the arrow keys to nudge and Tab to move between fields. Preview shows the recipient view without sending anything.',
  'editor.first_use_sign':
    'Choose Signature, then click where you sign: your saved signature fills in. Click a name or text box to type in it.',
  'editor.first_use_sign_touch':
    'Tap Signature, then tap where you sign: your saved signature fills in. Tap a name or text box to type in it.',
  'editor.first_use_dismiss': 'Got it',
  'editor.rotate': 'Rotate view',
  'editor.save_as_template': 'Save as template',
  'editor.template_title': 'Template name',
  'editor.template_save': 'Save template',
  'editor.template_saved': 'Template saved.',
  'editor.template_save_failed': 'Template not saved ({code}).',
  'editor.template_clean_source_required':
    'This document has no clean unsigned source to save as a template.',
  'editor.value': 'Value',
  'editor.formula': 'Formula',
  'editor.formula_expression': 'Expression',
  'editor.formula_result': 'Computed value',
  'editor.formula_unresolved': 'Waiting for inputs',
  'editor.formula_cycle': 'This formula depends on itself.',
  'editor.formula_error': 'Formula problem: {code}',
  'editor.formula_dependencies': 'Uses: {fields}',
  'editor.formula_readonly': 'Calculated values are read-only.',
  'editor.anchor_title': 'Place by text',
  'editor.anchor_text': 'Anchor text',
  'editor.anchor_scope': 'Scope',
  'editor.anchor_scope_page': 'This page',
  'editor.anchor_scope_document': 'Whole document',
  'editor.anchor_case': 'Match case',
  'editor.anchor_whole_word': 'Whole word only',
  'editor.anchor_occurrence': 'Occurrence',
  'editor.anchor_occurrence_first': 'First match',
  'editor.anchor_occurrence_all': 'Every match',
  'editor.anchor_find': 'Find matches',
  'editor.anchor_none': 'No match found. Place the field manually.',
  'editor.anchor_unavailable': 'This source has no selectable text. Place the field manually.',
  'editor.anchor_matches': '{count} matches found',
  'editor.anchor_matches_one': '1 match found',
  'editor.anchor_accept': 'Accept placement',
  'editor.anchor_close': 'Close',
  'editor.in_person': 'Sign in person',
  'editor.in_person_exit': 'Exit in-person signing',
  'editor.in_person_title': 'In-person signing',
  'editor.in_person_progress': 'Signer {index} of {total}',
  'editor.in_person_pass': 'Pass the device to {name}',
  'editor.in_person_pass_hint': '{name}: read the document, then tick the statement below to sign.',
  'editor.in_person_turn': '{name} is signing',
  'editor.in_person_resumed': 'Picked up where signing stopped. Nothing entered so far was lost.',
  'editor.in_person_empty': 'Add each person who will sign on this device with you.',
  'editor.in_person_add': 'Add a person',
  'editor.in_person_remove': 'Remove',
  'editor.in_person_name_placeholder': 'Name of the person signing',
  'editor.in_person_default_name': 'Signer {number}',
  'editor.in_person_start': 'Start signing in person',
  'editor.in_person_needs_signer':
    'Add the people signing with you first. Each one then gets their own boxes on the page.',
  'editor.in_person_needs_name': 'Give each person signing in person their name.',
  'editor.in_person_needs_field':
    '{name} has no box to fill yet. Under "Placing for", choose {name}, then tap the page.',
  'editor.in_person_consent_needed': 'Tick the statement above to continue.',
  'editor.in_person_owner_first':
    'Finish your own boxes first ({field}), then start signing in person.',
  'editor.in_person_last': 'Done — finish signing',
  'editor.in_person_consent':
    'I am this participant, I have reviewed the document, and I agree to sign it electronically.',
  'editor.in_person_not_adopted': 'Not adopted yet',
  'editor.in_person_adopted': 'Signature adopted',
  'editor.in_person_adopt': 'Adopt this signature',
  'editor.in_person_clear': 'Clear',
  'editor.in_person_next': 'Done — hand to the next person',
  'editor.in_person_done': 'Everyone has signed. Finish to make the signed copy.',
  'editor.value_checked': 'Checked',
  'editor.adopt_signature': 'Adopted signature',
  'editor.adopt_none': 'Not adopted yet',
  'editor.sign_here': 'Sign here',
  'editor.change_signature': 'Change signature',
  'editor.change_mark': 'Change',
  'editor.sign': 'Sign',
  'editor.adopt_failed': "Your signature couldn't be saved on this device. Try again.",
  'editor.adopt_saved_note':
    'Saved on this device for your next document. You can change it in Settings.',
  'editor.save_failed': "Your latest change couldn't be saved on this device.",
  'editor.owner_sign': 'Who fills this',
  'editor.field_signs_in_person': '{name} signs this when you sign in person.',
  'editor.date_signed_note': "Filled with the date you finish, from this device's calendar.",
  'editor.suggest_fields': 'Suggest fields',
  'editor.suggest_found_one': 'Found 1 place to fill, on this device.',
  'editor.suggest_found': 'Found {count} places to fill, on this device.',
  'editor.suggest_add_all': 'Add all',
  'editor.suggest_dismiss_all': 'Dismiss',
  'editor.suggest_add_one': 'Add {field}',
  'editor.suggest_dismiss_one': 'Dismiss this suggestion',
  'editor.suggest_none': 'No places to fill were found. Place fields from the list.',
  'editor.suggest_failed':
    "Fields couldn't be suggested for this document. Place them from the list.",
  'editor.discard_draft': 'Delete',
  'editor.finish': 'Finish',
  'lifecycle.importing': 'Importing',
  'lifecycle.import_failed': 'Import failed',
  'lifecycle.draft': 'Draft',
  'lifecycle.completed': 'Completed',
  'lifecycle.sent': 'Sent',
  'completed.title': 'Signed on this device',
  'completed.title_by': 'Signed on this device by {names}',
  'completed.signer_you': 'you',
  'completed.subtitle': 'Completed {date}',
  'completed.pages_one': '1 page',
  'completed.pages': '{count} pages',
  'completed.save_copy': 'Download signed PDF',
  'completed.save_share_print': 'Save, share or print',
  'completed.share': 'Share',
  'completed.verify': "Check this device's copy",
  'completed.view': 'View signed document',
  'completed.view_close': 'Hide signed document',
  'completed.print': 'Print',
  'completed.done': 'Done',
  'completed.check_copy': 'Check a saved copy',
  'completed.check_hint':
    'Choose a PDF to see whether it is exactly the copy signed here. Nothing is uploaded.',
  'completed.device_time': '{time} (this device’s clock)',
  'completed.check_match': 'This file is exactly the copy signed on this device.',
  'completed.check_mismatch':
    'This file isn’t the copy signed here. It may be another document, or it was changed after signing.',
  'completed.verify_match': 'This copy matches the fingerprint recorded when it was completed.',
  'completed.verify_mismatch':
    'This copy doesn’t match its recorded fingerprint. Don’t rely on it; use Use again to sign a fresh copy.',
  'completed.saved': 'Saved. The signed copy also stays on this device.',
  'completed.shared': 'Done. The signed copy also stays on this device.',
  'completed.export_failed':
    'That didn’t work. The signed copy is still on this device; try again.',
  'completed.details': 'Details',
  'completed.fingerprint': 'Fingerprint (SHA-256)',
  'completed.time_label': 'Time recorded by this device',
  'trash.delete': 'Delete',
  'trash.delete_named': 'Delete {title}',
  'trash.discarded_toast': '“{title}” was deleted.',
  'trash.deleted_toast': '“{title}” moved to Recently Deleted. It stays there for 30 days.',
  'trash.undo': 'Undo',
  'trash.open': 'Open Recently Deleted',
  'trash.dismiss': 'Close',
  'trash.cancel': 'Cancel',
  'trash.link': 'Recently Deleted ({count})',
  'trash.sent_blocked':
    'This agreement was sent for signing, so it can’t be deleted here. Open its request instead.',
  'trash.delete_failed': 'This agreement couldn’t be deleted.',
  'trash.restore_failed': 'This agreement couldn’t be restored.',
  'trash.purge_failed': 'This agreement couldn’t be deleted for good.',
  'trash.load_failed': 'Recently Deleted couldn’t be opened.',
  'trash.title': 'Recently Deleted',
  'trash.description':
    'Signed agreements and templates you delete on this device stay here for 30 days, then are removed for good.',
  'trash.back': 'Agreements',
  'trash.empty': 'Nothing deleted in the last 30 days',
  'trash.empty_description': 'Signed agreements and templates you delete wait here for 30 days.',
  'trash.gone_on': 'Removed for good on {date}',
  'trash.deleted_on': 'Deleted {date}',
  'trash.restore': 'Restore',
  'trash.restored': '“{title}” is back in Agreements.',
  'trash.purge': 'Delete permanently',
  'trash.purge_confirm': 'This can’t be undone. Copies you saved elsewhere aren’t touched.',
  'trash.purged': '“{title}” was deleted for good.',
  'certificate.download': 'Download summary',
  'certificate.download_all': 'Download all (.zip)',
  'certificate.hint_bundle':
    'Download all saves the signed PDF and the summary together in one .zip.',
  'trash.section_agreements': 'Agreements',
  'trash.section_templates': 'Templates',
  'trash.none_agreements': 'No deleted agreements.',
  'trash.none_templates': 'No deleted templates.',
  'trash.template_restored': '“{title}” is back in Templates.',
  'trash.template_restore_failed': 'This template couldn’t be restored.',
  'certificate.hint_remote':
    'The summary is a separate PDF, a certificate of completion: who signed, when, and the fingerprints of the signed PDF.',
  'certificate.hint_local':
    'The summary is a separate PDF, a certificate of completion: when the agreement was signed on this device and the fingerprints of the signed PDF.',
  'certificate.failed': 'The summary couldn’t be prepared. Try again.',
  'certificate.bundle_failed': 'The files couldn’t be prepared. Try again.',
  'certificate.bundle_saved': 'Files saved in one .zip.',
  'certificate.bundle_started':
    'Download started. Check your browser downloads for the .zip with both files.',
  'certificate.heading': 'Certificate of completion',
  'certificate.footer': '{product} certificate of completion · {reference}',
  'certificate.footer_local': '{product} certificate of completion · recorded on this device',
  'certificate.page': 'Page {page} of {total}',
  'certificate.overview_reference': 'Reference: {reference}',
  'certificate.overview_subject': 'Subject: {title}',
  'certificate.overview_status': 'Status: Completed',
  'certificate.overview_pages': 'Document pages: {count}',
  'certificate.overview_signatures': 'Signatures: {count}',
  'certificate.overview_initials': 'Initials: {count}',
  'certificate.overview_routing': 'Signing order: {order}',
  'certificate.overview_sender': 'Sent by',
  'certificate.overview_recorded': 'Recorded by',
  'certificate.overview_recorded_device': 'This device only',
  'certificate.time_zone':
    'Times are shown in {zone} ({offset}). The audit trail on the later pages uses UTC.',
  'certificate.time_zone_local': 'Times are shown in {zone} ({offset}), from this device’s clock.',
  'certificate.made_on_request': 'Made on request on {time} from the records {product} holds.',
  'certificate.made_on_device': 'Made on this device on {time}.',
  'certificate.signer_copy_note':
    'Signer’s copy: email addresses appear only on the sender’s copy.',
  'certificate.section_signers': 'Signer events',
  'certificate.section_in_person': 'In-person signer events',
  'certificate.section_copies': 'Copies',
  'certificate.section_summary': 'Summary events',
  'certificate.section_disclosure': 'Electronic record and signature disclosure',
  'certificate.column_signature': 'Signature',
  'certificate.column_timestamps': 'Timestamps',
  'certificate.column_status': 'Status',
  'certificate.role_sender': 'Sender',
  'certificate.mark_in_pdf': 'Shown in the signed PDF',
  'certificate.mark_none': 'Not signed',
  'certificate.mark_adoption': 'Signature adoption: {method}',
  'certificate.mark_from_sender_device': 'The sender’s saved signature, from this device',
  'certificate.mark_from_device': 'The saved signature, from this device',
  'certificate.time_sent': 'Sent: {time}',
  'certificate.time_queued': 'Email queued: {time}',
  'certificate.time_link_shared': 'Link shared: {time}',
  'certificate.time_viewed': 'Viewed: {time}',
  'certificate.time_signed': 'Signed: {time}',
  'certificate.time_copy_shared': 'Signed copy shared: {time}',
  'certificate.copy_status': 'Gets a copy',
  'certificate.disclosure': 'Electronic record and signature disclosure:',
  'certificate.disclosure_accepted': 'Accepted: {time}, version {version}, {language}',
  'certificate.disclosure_accepted_untimed': 'Accepted, version {version}, {language}',
  'certificate.disclosure_accepted_time': 'Accepted: {time}',
  'certificate.disclosure_none': 'Not recorded',
  'certificate.disclosure_intro':
    'The statement each signer accepted before signing, exactly as it was shown to them.',
  'certificate.disclosure_group': 'Version {version}, {language}. Accepted by: {names}',
  'certificate.disclosure_text_missing':
    'This version’s text is not part of this app, so it is not repeated here. Its SHA-256 is {hash}.',
  'certificate.summary_sent': 'Request sent',
  'certificate.summary_sent_status': 'Encrypted on the sender’s device',
  'certificate.summary_viewed': 'First viewed',
  'certificate.summary_viewed_status': 'Document shown',
  'certificate.summary_signed': 'Signing complete',
  'certificate.summary_signed_status': 'Everyone signed',
  'certificate.summary_completed': 'Completed',
  'certificate.summary_completed_status': 'Signed PDF fingerprint recorded',
  'certificate.summary_completed_local': 'Signed on this device',
  'certificate.notice_retention':
    'You can download the signed PDF and this certificate from {product} until {date}. After that {product} deletes the PDF and keeps only the completion and audit records its retention rules require. Save both now if you need them later.',
  'certificate.notice_unsigned':
    'Not signed by {product}. No approved signing key was configured when this agreement was completed, so the completion record carries no service signature. The fingerprints below are the records {product} holds.',
  'certificate.notice_signed':
    '{product} signed the completion record with key {key} ({algorithm}). The signature covers that record only, identified by the completion record SHA-256 below. It does not cover when this certificate was generated or audit events after completion, and it does not show how the PDF looks.',
  'certificate.notice_local':
    'Device-recorded. This agreement was completed on this device and was never sent to {product}. Times come from this device’s clock and names are what was entered on it. {product} did not see, sign or verify this record.',
  'certificate.section_agreement': 'Agreement',
  'certificate.section_participants': 'Participants',
  'certificate.section_integrity': 'Fingerprints',
  'certificate.section_trail': 'Audit trail',
  'certificate.section_privacy': 'What this certificate does not include',
  'certificate.title': 'Title',
  'certificate.reference': 'Request reference',
  'certificate.sent_by': 'Sent by',
  'certificate.delivery': 'Delivery',
  'certificate.delivery_managed': 'Invitations emailed by {product}',
  'certificate.delivery_manual': 'Signing links shared by the sender',
  'certificate.routing': 'Signing order',
  'certificate.routing_parallel': 'Any order',
  'certificate.routing_sequential': 'One after another',
  'certificate.created': 'Created',
  'certificate.activated': 'Sent',
  'certificate.completed': 'Completed',
  'certificate.generated': 'Certificate generated',
  'certificate.pages': 'Pages',
  'certificate.completed_device': 'Completed (this device’s clock)',
  'certificate.device_time_label': 'Time shown on this device',
  'certificate.identity_note':
    'Names and email addresses are as the sender entered them. Opening a signing link shows possession of the link, not who someone is.',
  'certificate.local_identity_note':
    'Names are as they were typed on this device; they were not verified.',
  'certificate.local_signer_note':
    'Signed on this device. No signer name was recorded with this completion.',
  'certificate.participant_sender': 'Sender (signing step)',
  'certificate.participant_signer': 'Signer {count}',
  'certificate.participant_owner': 'Signed by',
  'certificate.owner_signed_at': 'Signed (this device’s clock)',
  'certificate.owner_unnamed': 'The owner of this device (no name recorded)',
  'certificate.participant_cc': 'Copy recipient {count}',
  'certificate.cc_role': 'Receives a copy; does not sign',
  'certificate.presented': 'Document shown',
  'certificate.accepted': 'Signature accepted',
  'certificate.consent': 'Consent accepted',
  'certificate.consent_value': 'Version {version}, language {locale}',
  'certificate.consent_local': 'Agreed to sign electronically ({language})',
  'certificate.consent_hash': 'Consent text SHA-256',
  'certificate.response_hash': 'Response SHA-256',
  'certificate.adoption_method': 'Signature method',
  'certificate.method_draw': 'Drawn',
  'certificate.method_type': 'Typed',
  'certificate.method_upload': 'Uploaded image',
  'certificate.adopted_at': 'Adopted (this device’s clock)',
  'certificate.integrity_note':
    'A SHA-256 fingerprint identifies an exact file: any change gives a different fingerprint. It shows whether a file changed, not who signed it or when.',
  'certificate.integrity_attested':
    'The device that completed the agreement computed the final PDF’s fingerprint; {product} checked the encrypted copy it stores.',
  'certificate.final_pdf': 'Final PDF SHA-256',
  'certificate.final_pdf_size': 'Final PDF size',
  'certificate.bytes': '{count} bytes',
  'certificate.source': 'Original document SHA-256',
  'certificate.field_layout': 'Field layout SHA-256',
  'certificate.responses': 'Responses SHA-256 ({count})',
  'certificate.manifest': 'Completion record SHA-256',
  'certificate.audit_tip': 'Audit trail SHA-256 at completion',
  'certificate.field_values': 'Filled-in values SHA-256',
  'certificate.profile': 'Evidence profile',
  'certificate.composer': 'PDF composer',
  'certificate.signature': 'Service signature',
  'certificate.signature_signed': 'Signed with key {key} ({algorithm})',
  'certificate.signature_unsigned': 'Not signed',
  'certificate.signature_local': 'None: recorded on this device',
  'certificate.trail_note': 'Each event as {product}’s servers recorded it, in UTC.',
  'certificate.actor_sender': 'Sender',
  'certificate.actor_sender_for': 'Sender, for {participant}',
  'certificate.actor_system': '{product}',
  'certificate.actor_system_for': '{product}, for {participant}',
  'certificate.not_collected':
    'IP addresses and device or browser details are not collected, so none appear here.',
  'certificate.limits':
    'This certificate records what {product} observed. It is not notarization, identity verification or legal advice.',
  'certificate.local_limits':
    'This certificate records what this device observed. It is not notarization, identity verification or legal advice.',
  'certificate.event.created': 'Request created',
  'certificate.event.activated': 'Request sent',
  'certificate.event.manual_share_intent': 'Sender shared a signing link',
  'certificate.event.invitation_queued': 'Invitation queued',
  'certificate.event.invitation_dispatched': 'Invitation sent',
  'certificate.event.invitation_provider_result': 'Email delivery result recorded',
  'certificate.event.bootstrap_observed': 'Signing link requested',
  'certificate.event.document_presented': 'Document shown',
  'certificate.event.consent_accepted': 'Consent accepted',
  'certificate.event.response_accepted': 'Signature accepted',
  'certificate.event.recipient_corrected': 'Recipient details corrected',
  'certificate.event.deadline_extended': 'Signing deadline extended',
  'certificate.event.attachment_accepted': 'Attachment received',
  'certificate.event.attachment_replaced': 'Attachment replaced',
  'certificate.event.attachment_access_authorized': 'Attachment access granted',
  'certificate.event.attachment_notice_queued': 'Attachment notice queued',
  'certificate.event.reminder_queued': 'Reminder queued',
  'certificate.event.reminder_dispatched': 'Reminder sent',
  'certificate.event.reminder_skipped': 'Reminder skipped',
  'certificate.event.declined': 'Declined',
  'certificate.event.voided': 'Request voided',
  'certificate.event.preparation_discarded': 'Unsent request discarded',
  'certificate.event.expired': 'Request expired',
  'certificate.event.finalization_started': 'Finalization started',
  'certificate.event.finalization_failed': 'Finalization failed',
  'certificate.event.completed': 'Agreement completed',
  'certificate.event.final_download_authorized': 'Access to the final copy granted',
  'certificate.event.artifact_deleted': 'Server copy deleted',
  'editor.finish_no_fields': 'Add a signature or another field to the document before you finish.',
  'editor.finish_no_signature_title': 'This document has no signature',
  'editor.finish_no_signature_body':
    'You can finish it as a filled form, or add a signature field first.',
  'editor.finish_without_signature': 'Finish without signing',
  'editor.finish_add_signature': 'Add a signature',
  'editor.finish_issues_title': 'Before you finish',
  'editor.issue_go_to': 'Go to field',
  'editor.issue_page': 'Page {page}',
  'editor.unavailable':
    'This document is unavailable. It may have been deleted, or its saved copy could not be read.',
  'editor.sent_title': 'Sent for signature',
  'editor.sent_body': 'This agreement is out for signature.',
  'editor.sent_locked':
    "A sent agreement can't be changed. Its status, links and signed copy are on the request. To send it again with changes, use it again as a new draft.",
  'editor.sent_view_request': 'View request',
  'editor.not_found_title': 'Document not found',
  'editor.owner_me': 'Me',
  'editor.switch_to_sign': 'Sign it myself instead',
  'editor.switch_to_send': 'Send for signature instead',
  'editor.edit_recipients_message': 'Edit recipients and message',
  'editor.to_finish_title': 'Before you finish',
  'editor.ready_to_finish': 'Ready to finish.',
  'editor.ready_in_person': 'Ready to sign in person.',
  'editor.page_of': 'Page {page} of {count}',
  'editor.close_settings': 'Close',
  'editor.settings_done': 'Done',
  'editor.back': 'Back to agreements',
  'editor.save_all': 'All changes saved',
  'editor.save_retry': 'Retry',
  'editor.more': 'More',
  'editor.finish_next': 'Next',
  'editor.finish_blocked_one': '{count} left',
  'editor.finish_blocked': '{count} left',
  'editor.steps_label': 'Steps',
  'editor.step_setup': 'Recipients and message',
  'editor.step_fields': 'Fields',
  'editor.step_setup_short': 'Recipients',
  'editor.step_next_fields': 'Next: add fields',
  'editor.back_to_fields': 'Back to fields',
  'editor.invalid_owners_one': '{count} field needs an owner before this can be sent.',
  'editor.invalid_owners': '{count} fields need an owner before this can be sent.',
  'editor.signers_title': 'People signing on this device',
  'editor.signers_hint':
    'Add each person who will sign here in person. The fields you place can then belong to them.',
  'editor.setup_signers': 'Signers',
  'editor.local_note':
    'Kept on this device, encrypted. Nothing is sent until you review and share.',
  'editor.local_note_sign': 'Kept on this device, encrypted.',
  'editor.armed_hint': 'Click or tap the page to place {field}.',
  'editor.armed_hint_touch': 'Tap the page to place {field}.',
  'editor.armed_cancel': 'Cancel',
  'editor.view_label': 'Page view',
  'editor.previous_page': 'Previous page',
  'editor.next_page': 'Next page',
  'editor.placing_for': 'Fields for',
  'editor.palette_search': 'Search fields',
  'editor.palette_no_match': 'No field matches.',
  'editor.palette_more': 'More fields',
  'editor.palette_group_signing': 'Signing',
  'editor.palette_group_info': 'Contact details',
  'editor.palette_group_input': 'Input',
  'editor.recipient_remove_title': 'Remove {name}?',
  'editor.recipient_remove_fields_one':
    '{name} has 1 field. Removing them also removes their field.',
  'editor.recipient_remove_fields':
    '{name} has {count} fields. Removing them also removes their fields.',
  'editor.recipient_remove_confirm': 'Remove with their fields',
  'editor.recipient_remove_move_to': 'Or give their fields to',
  'editor.recipient_remove_move_none': 'Nobody: remove the fields',
  'editor.recipient_remove_move': 'Move fields and remove',
  'editor.recipient_remove_cancel': 'Cancel',
  'editor.recipient_removed': 'Removed {name}.',
  'editor.recipient_removed_fields_one': 'Removed {name} and their field.',
  'editor.recipient_removed_fields': 'Removed {name} and their {count} fields.',
  'editor.recipient_removed_moved': 'Removed {name}. Their fields now go to {to}.',
  'editor.issue.signature_missing': 'Add your signature',
  'editor.issue.initials_missing': 'Add your initials',
  'editor.issue.required_missing': 'Fill in {field}',
  'editor.issue.tick_missing': 'Tick {field}',
  'editor.issue.text_too_long': '{field}: the text is too long.',
  'editor.issue.preset_email': '{field}: enter an email address, like name@example.com.',
  'editor.issue.preset_number': '{field}: enter a number.',
  'editor.issue.preset_letters': '{field}: use letters only.',
  'editor.issue.preset_date': '{field}: enter a valid date.',
  'editor.issue.preset_zip': '{field}: enter a 5-digit ZIP code.',
  'editor.issue.preset_zip4': '{field}: enter a ZIP+4 code, like 12345-6789.',
  'editor.issue.preset_ssn': '{field}: enter the number as 123-45-6789.',
  'editor.issue.preset_custom': "{field}: the value doesn't match the required format.",
  'editor.issue.formula': "{field}: the formula can't be calculated. Check its expression.",
  'editor.issue.formula_divide_by_zero': '{field}: the formula divides by zero.',
  'editor.issue.formula_unresolved': '{field}: fill the fields this formula uses first.',
  'editor.issue.unknown': '{field}: this field needs attention.',
  'editor.issue.signs_in_person': '{name} signs in person',
  'editor.issue.signer_no_fields': '{name} has nothing to sign yet',
  'editor.issue.signer_no_name': 'Name the person signing in person',
  'editor.issue.compose_failed':
    "The signed copy couldn't be created. Your draft is unchanged; try again.",
  'editor.completed': 'Completed · {hash}',
  'editor.completed_replay': 'Already completed · {hash}',
  'editor.export': 'Export',
  'editor.export_saved': 'Exported ({via}).',
  'editor.export_cancelled': 'Export cancelled — the completed copy is still in your vault.',
  'editor.export_unavailable':
    'Export is unavailable here — the completed copy is still in your vault.',
  'editor.export_failed': 'Export failed ({code}) — the completed copy is still in your vault.',
  'editor.copy': 'Copy',
  'editor.paste': 'Paste',
  'editor.pages_open': 'Open page list',
  'editor.pages_close': 'Close page list',
  'editor.thumbnails': 'Pages',
  'editor.preview': 'Preview',
  'editor.preview_close': 'Back to editing',
  'editor.preview_participant': 'Preview as',
  'editor.preview_viewport': 'Viewport',
  'editor.preview_start': 'Start',
  'editor.preview_next': 'Next',
  'editor.preview_done': 'All required fields visited',
  'editor.preview_note':
    'Preview — not signed. Nothing is sent, no credit is used and no event is recorded.',
  'editor.preview_cc_note': 'CC recipients receive the final copy only and never sign.',
  'editor.no_fields': 'No fields on this page yet.',
  'editor.recipients': 'Recipients',
  'editor.recipient_add': 'Add recipient',
  'editor.recipient_name': 'Name',
  'editor.recipient_email': 'Email (optional)',
  'editor.recipient_kind': 'Role',
  'editor.recipient_kind_signer': 'Signer',
  'editor.recipient_kind_cc': 'Gets a copy',
  'editor.recipient_kind_cc_lifetime': 'Gets a copy (Lifetime)',
  'editor.recipient_kind_cc_unavailable': 'Gets a copy (not on Free)',
  'editor.recipient_more': 'Language',
  'editor.recipient_language': 'Invitation language',
  'editor.recipient_language_default': 'English (default)',
  'editor.recipient_remove': 'Remove recipient',
  'editor.sender_signs': 'I also need to sign',
  'editor.sender_row': 'You',
  'editor.sender_order': 'When do you sign?',
  'editor.sender_order_before': 'First, before the others',
  'editor.sender_order_after': 'Last, after the others',
  'editor.recipient_duplicate':
    'Two recipients share the same name or email. Keep both only if that is deliberate.',
  'editor.recipients_empty': 'No recipients yet. Add one to assign fields.',
  'editor.recipient_field_count': '{count} fields',
  'editor.recipient_field_count_one': '1 field',
  'editor.recipient_email_field': 'Email',
  'editor.recipient_email_hint': 'Needed if {product} emails it for you.',
  'editor.recipient_email_invalid': 'Check this email address.',
  'editor.recipient_field_count_none': 'no fields yet',
  'editor.placing_for_count': '{name} · {fields}',
  'editor.setup_issue_no_signer': 'Add the name of the person who needs to sign.',
  'editor.setup_issue_name': 'Add their name.',
  'editor.only_you_question': 'Only you need to sign?',
  'editor.only_you_action': 'Sign it yourself',
  'editor.change_document': 'Change document',
  'editor.field_assign': 'Assign',
  'editor.recipient_name_placeholder': 'Full name',
  'editor.routing': 'Signing order',
  'editor.routing_parallel': 'Everyone at the same time',
  'editor.routing_sequential': 'One after another',
  'editor.move_up': 'Move {name} earlier',
  'editor.move_down': 'Move {name} later',
  'editor.recipient_target': 'Fill this recipient',
  'editor.recent_recipients': 'Recent recipients',
  'editor.recent_empty': 'Recipients you name are kept here for faster reuse.',
  'editor.recent_use': 'Add',
  'editor.recent_forget': 'Remove',
  'editor.recent_forget_all': 'Clear all',
  'editor.pick_contact': 'Pick from contacts',
  'editor.contact_unavailable':
    'This device has no contact picker. Fields can still be typed by hand.',
  'editor.contact_empty': 'No contact was selected.',
  'editor.contact_filled': 'Recipient filled from contacts.',
  'editor.autofill': 'Autofill from My Info',
  'editor.autofill_title': 'Preview — nothing is filled until you confirm',
  'editor.autofill_apply': 'Fill {count} fields',
  'editor.autofill_apply_one': 'Fill 1 field',
  'editor.autofill_cancel': 'Cancel',
  'editor.autofill_none': 'No empty sender-owned field matches My Info.',
  'editor.autofill_no_profile': 'Add your name and details in Settings, under My Info, first.',
  'editor.autofill_applied': 'Filled {count} fields from My Info.',
  'editor.autofill_applied_one': 'Filled 1 field from My Info.',
  'editor.label': 'Label',
  'editor.tooltip': 'Tooltip',
  'editor.validation': 'Validation',
  'editor.validation_custom': 'Custom pattern',
  'editor.validation_ssn_note': 'Format only — never identity verification.',
  'editor.formatting': 'Formatting',
  'editor.format.none': 'None',
  'editor.format.plain_text': 'Plain text',
  'editor.format.number': 'Number',
  'editor.format.currency': 'Currency',
  'editor.format.percent': 'Percent',
  'editor.format.date': 'Date',
  'editor.editable': 'Recipient can edit',
  'editor.message': 'Message to recipients',
  'editor.details': 'Details',
  'editor.details_more': 'Filing (optional)',
  'editor.subject': 'Subject',
  'editor.message_body': 'Message',
  'editor.category': 'Category',
  'editor.category_uncategorized': 'Uncategorized',
  'editor.reminders': 'Reminders',
  'editor.reminders_off': 'Off',
  'reminders.first_after_option': 'First after {days}',
  'reminders.after_window':
    'This request closes after {days}, before the first reminder is due, so none will be sent. Choose an earlier one.',
  'reminders.repeat_every': 'Then every',
  'editor.reminders_managed_only':
    'Automatic reminders are sent only with a managed invitation. Choose it on the Send screen; links you share yourself get no automatic reminders.',
  'editor.reminders_hint':
    'Reminders are emailed to signers who have not signed yet and stop once they sign or the request ends.',
  'editor.counter': '{count}/{max}',
  'editor.review_message':
    'Added to the text you share with the link. When {product} emails it for you, we send a standard invitation instead.',
  'editor.overflow_warning':
    'This field may overflow: about {lines} lines of text for {available} available.',
  'count.days': '{count} days',
  'count.days_one': '1 day',
  'count.years': '{count} years',
  'count.years_one': '1 year',
  'count.files': '{count} files',
  'count.files_one': '1 file',
  'count.pages': '{count} pages',
  'count.pages_one': '1 page',
  'count.participants': '{count} participants',
  'count.participants_one': '1 participant',
  'account_action.title': 'Confirm a request from your AI assistant',
  'account_action.description': 'An assistant can prepare this, but only you can finish it.',
  'account_action.your_assistant': 'Your AI assistant',
  'account_action.ask_disconnect':
    '{host} asked to be disconnected. It will lose access to your agreements.',
  'account_action.ask_manage':
    '{host} asked to change what it can see to {count} selected agreements.',
  'account_action.ask_manage_one':
    '{host} asked to change what it can see to 1 selected agreement.',
  'account_action.ask_export':
    '{host} asked to prepare an export of your account data. Local files and your own backups are not included.',
  'account_action.ask_delete':
    'Deleting your account happens on its own page, where you confirm it with a code.',
  'account_action.expires': 'This request expires {when}.',
  'account_action.reauth_note': 'You will be asked to sign in again before it goes ahead.',
  'account_action.confirm': 'Confirm',
  'account_action.not_now': 'Not now',
  'account_action.to_deletion': 'Go to account deletion',
  'account_action.to_settings': 'Back to Settings',
  'account_action.reauth_needed': 'Sign in again to confirm this request.',
  'account_action.sign_in_needed': 'Sign in to the account this request belongs to.',
  'account_action.not_found': 'This request was not found on this account.',
  'account_action.expired': 'This request has expired. Ask your assistant to prepare it again.',
  'account_action.already_done': 'This request has already been completed.',
  'account_action.failed': 'This request could not be completed. Try again in a moment.',
  'account_action.done_disconnect': '{host} is disconnected.',
  'account_action.done_manage': 'What {host} can see has been updated.',
  'account_action.done_export': 'Your account export is ready.',
  'ops.health.title': 'Service health',
  'ops.health.description':
    'Operator view of queues, mail, finalization and alerts. No document content is shown.',
  'ops.health.costs': 'Cost review',
  'ops.health.unconfigured':
    'No operator accounts are declared for this deployment, so health stays closed.',
  'ops.health.forbidden': 'This account is not an operator account.',
  'ops.health.signed_out': 'Sign in with an operator account to see service health.',
  'ops.health.failed': 'Service health could not load.',
  'ops.health.meta': '{environment} · API {version} · mail {provider}',
  'ops.health.kill_switch_on':
    'The managed-send kill switch is on: managed invitations are skipped.',
  'ops.health.queue_lag': 'Queue lag',
  'ops.health.dead_letters': 'Dead letters',
  'ops.health.oldest_pending': 'Oldest pending mail',
  'ops.health.mail': 'Delivered / bounced / complained',
  'ops.health.leases': 'Finalization leases (expired)',
  'ops.health.overdue': 'Overdue purges',
  'ops.health.errors': 'Errors / requests',
  'ops.health.cost': 'Estimated cost this period',
  'ops.health.alerts_title': 'Alerts',
  'ops.health.alerts_firing': 'Firing now: {count}',
  'ops.health.alerts_unset': 'No alert policy is declared, so every alert reads UNSET.',
  'ops.health.owner': 'Owner: {owner}',
  'ops.health.top_errors': 'Top error codes',
  'ops.health.isolate_scope': 'Request counts cover one API isolate, not the whole fleet.',
  'agreements.title': 'Agreements',
  'agreements.empty': 'No agreements yet',
  'agreements.count': '{count} agreements',
  'agreements.count_one': '1 agreement',
  'agreements.search_label': 'Search agreements',
  'agreements.search_placeholder': 'Search by title or name',
  'agreements.searching': 'Searching…',
  'agreements.search_count': '{count} of {total} agreements',
  'agreements.search_count_one': '{count} of 1 agreement',
  'agreements.search_count_partial': '{count}+ of {total} agreements so far',
  'agreements.search_count_partial_one': '{count}+ of 1 agreement so far',
  'agreements.search_empty': 'No agreements match this search.',
  'agreements.search_empty_description':
    'Your library is unchanged. Clear the search to see every agreement again.',

  'agreements.description': 'Documents you sign or send show up here.',
  'agreements.new': 'New agreement',
  'agreements.search_hint': 'Matches a title, a role or a label.',
  'agreements.views_label': 'Views',
  'agreements.view_all': 'All',
  'agreements.view_drafts': 'Drafts',
  'agreements.view_waiting': 'Waiting for others',
  'agreements.view_completed': 'Completed',
  'agreements.view_closed': 'Declined, voided or expired',
  'agreements.view_attention': 'Needs attention',
  'agreements.view_empty_drafts': 'No drafts on this device.',
  'agreements.empty_drafts_hint': "Documents you start and haven't finished show here.",
  'agreements.empty_waiting': 'Nothing is waiting for signatures',
  'agreements.empty_waiting_hint': 'Requests you send show here until everyone signs.',
  'agreements.empty_completed': 'Nothing completed yet',
  'agreements.empty_completed_hint': 'Agreements everyone has signed show here.',
  'agreements.empty_closed': 'Nothing declined, voided or expired',
  'agreements.empty_closed_hint': 'Requests that end without every signature show here.',
  'agreements.section_device': 'On this device',
  'agreements.section_requests': 'Requests',
  'agreements.status_filter': 'Status',
  'agreements.requests_signed_out': 'Sign in to see the requests you have sent.',
  'agreements.requests_failed': "Your requests couldn't be loaded.",
  'agreements.requests_more_failed': "More requests couldn't be loaded.",
  'agreements.requests_no_match': 'No requests match this search.',
  'agreements.local_failed': "Your agreements couldn't be loaded from this device.",
  'agreements.retry': 'Try again',
  'agreements.load_more': 'Load more',
  'agreements.section_elsewhere': 'Requests not on this device',
  'agreements.requests_empty': 'No requests here',
  'agreements.requests_empty_hint': 'Requests you send appear here with their status.',
  'agreements.request_progress': '{submitted} of {total} signed',
  'agreements.waiting_for_more': 'Waiting for {name} and {count} more',
  'agreements.at_sent': 'Sent {date}',
  'agreements.at_completed': 'Completed {date}',
  'agreements.at_created': 'Created {date}',
  'agreements.at_edited': 'Edited {date}',
  'agreements.expires': 'Expires {date}',
  'agreements.expires_today': 'Expires today at {time}',
  'agreements.expired_on': 'Expired {date}',
  'agreements.delivery_manual': 'You share the link',
  'agreements.delivery_managed': 'Sent by email',
  'agreements.action_remind': 'Remind',
  'agreements.action_share_again': 'Share again',
  'agreements.action_sign': 'Sign',
  'agreements.action_finish': 'Finish',
  'agreements.action_review': 'Review',
  'agreements.action_send_again': 'Send again',
  'agreements.view_request': 'View request',
  'document.pages': '{count} pages',
  'document.page_one': '1 page',
  'document.status.ready': 'Ready',
  'document.status.processing': 'Processing',
  'document.status.error': 'Needs attention',
  'templates.title': 'Templates',

  'templates.description': 'Saved layouts you can reuse for a new document.',
  'templates.toolbar_label': 'Library controls',
  'templates.search_empty': 'No templates match this search.',
  'templates.search_empty_description':
    'Your library is unchanged. Clear the search or change the filter to see every template again.',
  'templates.empty': 'Create your first reusable template',
  'templates.empty_description':
    'Start from a ready-made agreement below, or import your own PDF. Templates stay on this device.',
  'templates.empty_import': 'Add your own document',
  'templates.new': 'New template',
  'templates.restore_menu': 'Restore or import',
  'templates.restore_backup': 'Restore from a backup file',
  'templates.views': 'Show',
  'templates.filter.all': 'All',
  'templates.search_label': 'Search templates',
  'templates.search_placeholder': 'Search by title, role or label',
  'templates.sort_label': 'Sort',
  'templates.sort_and_filter': 'Sort and filter',
  'templates.sort.recently_used': 'Recently used',
  'templates.sort.alphabetical': 'Alphabetical',
  'templates.sort.last_modified': 'Last modified',
  'templates.filter.favorites': 'Favorites',
  'templates.filter.recently_deleted': 'Recently Deleted',
  'templates.filter.role_count': 'Role count',
  'templates.filter.role_count_any': 'Any',
  'templates.filter.role_count_value': '{count}',
  'templates.count': '{count} templates',
  'templates.count_one': '1 template',
  'templates.use': 'Use',
  'templates.actions': 'More actions',
  'templates.action.preview': 'Preview',
  'templates.action.edit': 'Edit',
  'templates.action.duplicate': 'Duplicate',
  'templates.action.favorite': 'Favorite',
  'templates.action.unfavorite': 'Remove favorite',
  'templates.action.export': 'Export source PDF',
  'templates.action.delete': 'Delete',
  'templates.action.purge': 'Delete permanently',
  'templates.favorite_badge': 'Favorite',
  'templates.roles_summary': 'Roles: {roles}',
  'templates.roles_none': 'No roles yet',
  'templates.page_count': '{count} pages',
  'templates.page_count_one': '1 page',
  'templates.field_count': '{count} fields',
  'templates.field_count_one': '1 field',
  'templates.field_page': 'Page {page}',
  'templates.used_at': 'Used {date}',
  'templates.updated_at': 'Modified {date}',
  'templates.source_unavailable':
    'The PDF behind this template is missing. Replace the PDF to use it again.',
  'templates.replace_pdf': 'Replace PDF',
  'templates.deleted_badge': 'Deleted',
  'templates.purge_at': 'Removed for good on {date}',
  'templates.purge_expired': 'Past the 30-day window — removed the next time the app opens.',
  'templates.restore': 'Restore',
  'templates.restore_failed':
    'That template is past its 30-day window and can no longer be restored.',
  'templates.deleted_toast': 'Template deleted.',
  'templates.deleted_toast_many': '{count} templates deleted.',
  'templates.undo': 'Undo',
  'templates.undo_all': 'Undo all',
  'templates.open_recently_deleted': 'Open Recently Deleted',
  'templates.open_recently_deleted_count': 'Recently Deleted ({count})',
  'templates.recently_deleted_local_note':
    'Deleted templates stay here for 30 days on this device. A backup you exported earlier keeps its copy.',
  'templates.agreements_bin_link':
    'See deleted agreements and templates together in Recently Deleted.',
  'templates.deleted_empty': 'Nothing deleted in the last 30 days',
  'templates.deleted_empty_description': 'Templates you delete stay here for 30 days.',
  'templates.purge_all': 'Empty Recently Deleted',
  'templates.purge_title': 'Delete “{title}” permanently?',
  'templates.purge_all_title': 'Empty Recently Deleted?',
  'templates.purge_body':
    "This can't be undone. Backups you exported earlier are not changed, and agreements made from these templates stay.",
  'templates.purge_confirm': 'Delete permanently',
  'templates.purged': '“{title}” was deleted permanently.',
  'templates.purged_all': 'Recently Deleted is empty.',
  'templates.purge_failed': "This template couldn't be deleted permanently.",
  'templates.new_title': 'New template',
  'templates.new_from_draft': 'From a prepared draft',
  'templates.new_from_file': 'From a PDF on this device',
  'templates.new_draft_label': 'Prepared draft',
  'templates.new_source': 'Start from',
  'templates.new_file_choose': 'Choose a PDF',
  'templates.new_file_change': 'Choose another PDF',
  'templates.new_file_more': 'Or add from Word, Google Drive, Dropbox or Scan',
  'templates.new_title_label': 'Template name',
  'templates.new_title_missing': 'Give the template a name.',
  'templates.new_roles': 'Name the roles',
  'templates.new_roles_hint':
    'Roles stand in for the people you send to, like Client or Tenant. You fill in the real people each time you use it.',
  'templates.new_role_label': 'Role {number}',
  'templates.default_role.signer': 'Signer {number}',
  'templates.default_role.cc': 'CC {number}',
  'templates.created': '“{title}” saved to your templates.',
  'templates.saved': 'Changes to “{title}” saved.',
  'templates.use_now': 'Use it now',
  'templates.saved_view': 'View in Templates',
  'templates.save_sender_note':
    'Your own signing step is saved with your fields. Place your signature field first if you sign too.',
  'templates.new_create': 'Create template',
  'templates.new_place_fields': 'Place fields',
  'templates.new_file_next':
    'Next, add the people who sign and place their fields. Nothing is sent.',
  'templates.make_title': 'Make a template',
  'templates.make_body':
    'Add the people who sign as roles, like Client, and place their fields. Then save it as a template. Nothing is sent from here.',
  'templates.editing_title': 'Edit the template',
  'templates.editing_body':
    'Place, move or remove each role’s fields, then save. The template changes only when you save. Nothing is sent from here.',
  'templates.leave_title': 'Leave without saving?',
  'templates.leave_body': 'Your changes to this template aren’t saved yet.',
  'templates.leave_discard': 'Discard changes',
  'templates.edit_place_fields': 'Place fields on the page',
  'templates.edit_place_save_first': 'Save or discard the changes above before placing fields.',
  'templates.edit_open_failed': 'This template couldn’t be opened for editing.',
  'templates.new_creating': 'Creating…',
  'templates.new_failed': "This template couldn't be created.",
  'templates.new_clean_source':
    'That draft has no clean source to reuse. Import the original PDF and try again.',
  'templates.cancel': 'Cancel',
  'templates.import_note':
    'Bring back a template you deleted in the last 30 days, or restore everything from a backup file in Settings.',
  'templates.import_pick_file': 'Add a document as a template',
  'templates.preview_title': 'Template preview',
  'templates.preview_note': 'Preview shows the saved layout. Nothing is created, sent or signed.',
  'templates.preview_missing': 'This template is no longer on this device.',
  'templates.preview_page': 'Page {page}',
  'templates.preview_more_pages': '{count} more pages are not shown here.',
  'templates.role_me': 'You',
  'templates.use_sheet_title': 'Use this template',
  'templates.use_no_fields': 'No fields yet: you place them after this step.',
  'templates.save_no_fields':
    'This layout has no fields yet. Place them first, or save it now and place them each time it is used.',
  'templates.use_intro':
    'Who is this for? Names go on the agreement; an email lets it be sent to them.',
  'templates.use_sender_signs': 'You sign this too: your fields came with the template.',
  'templates.use_role_cc': 'gets a copy',
  'templates.use_this_is_me': 'This is me',
  'templates.use_name': 'Name',
  'templates.use_name_missing': 'Add a name for {role}.',
  'templates.use_email': 'Email (optional)',
  'templates.use_email_invalid':
    'Enter a full email address, like name@example.com, or leave it empty.',
  'templates.use_preview_all': 'See all pages',
  'templates.use_extra_signer': 'Another signer',
  'templates.use_extra_add': 'Add another signer',
  'templates.use_extra_remove': 'Remove signer',
  'templates.use_extra_note': 'You place their fields on the next step.',
  'templates.use_extra_name_missing': 'Add a name, or remove this signer.',
  'templates.use_extra_limit': 'Up to {count} people can sign one agreement.',
  'templates.use_agreement_title': 'Agreement name',
  'templates.use_title_for': '{title} – {name}',
  'templates.use_continue_send': 'Continue',
  'templates.use_continue_sign': 'Continue to sign',
  'templates.use_sign_instead': 'Sign it myself instead',
  'templates.edit_title': 'Edit template',
  'templates.edit_title_label': 'Title',
  'templates.edit_roles': 'Roles',
  'templates.edit_role_label': 'Role name',
  'templates.edit_role_missing': 'Give this role a name.',
  'templates.edit_role_kind': 'Does',
  'templates.role_kind.signer': 'Signs',
  'templates.role_kind.cc': 'Gets a copy',
  'templates.edit_remove_role_fields':
    'Its {count} fields will have no owner until you assign them.',
  'templates.edit_remove_role_fields_one': 'Its 1 field will have no owner until you assign it.',
  'templates.edit_keep_role': 'Keep role',
  'templates.edit_move_up': 'Move up',
  'templates.edit_move_down': 'Move down',
  'templates.edit_remove_role': 'Remove role',
  'templates.edit_add_role': 'Add role',
  'templates.edit_fields': 'Fields',
  'templates.edit_field_owner': 'Owner',
  'templates.edit_field_unassigned': 'Unassigned',
  'templates.edit_remove_field': 'Remove field',
  'templates.edit_no_fields': 'This template has no fields yet.',
  'templates.edit_save': 'Save changes',
  'templates.edit_failed': "This template couldn't be saved.",
  'templates.edit_refresh_thumbnail': 'Refresh thumbnail',
  'templates.edit_discard': 'Discard your changes to this template?',
  'templates.edit_discard_confirm': 'Discard',
  'templates.edit_keep_editing': 'Keep editing',
  'templates.edit_source': 'PDF',
  'templates.thumbnail_refreshed': 'Thumbnail refreshed.',
  'templates.thumbnail_failed': "The thumbnail couldn't be refreshed.",
  'templates.replace_save_first': 'Save or discard your changes before replacing the PDF.',
  'templates.replace_fits': 'Every field fits the new PDF.',
  'templates.replace_decide':
    'The new PDF has fewer pages. Choose what happens to {count} fields on the missing pages.',
  'templates.replace_decide_one':
    'The new PDF has fewer pages. Choose what happens to the field on a missing page.',
  'templates.replace_keep_field': 'Move to page 1',
  'templates.replace_remove_field': 'Remove',
  'templates.replace_apply': 'Use this PDF',
  'templates.replaced': 'PDF replaced.',
  'templates.replace_failed': "The PDF couldn't be replaced.",
  'templates.exported': 'Source PDF exported.',
  'templates.export_unavailable': 'That template has no source to export.',
  'templates.export_failed': "The PDF couldn't be exported.",
  'templates.use_failed': "This template couldn't be used.",
  'templates.reload_failed': "Templates couldn't be reloaded.",
  'templates.favorite_failed': "This template couldn't be updated.",
  'templates.duplicate_failed': "This template couldn't be duplicated.",
  'templates.delete_failed': "This template couldn't be deleted.",
  'starter.title': 'Starter agreements',
  'starter.description':
    'Ready-made agreements to fill in and send. They are starting points, not legal advice.',
  'starter.categories': 'Categories',
  'starter.category.all': 'All',
  'starter.category.confidentiality': 'Confidentiality',
  'starter.category.housing': 'Housing',
  'starter.category.work': 'Work',
  'starter.category.sales': 'Sales',
  'starter.category.personal': 'Personal',
  'starter.category.finance': 'Finance',
  'starter.browse': 'Starter agreements',
  'starter.use': 'Use this agreement',
  'starter.read_text': 'Read the full text',
  'starter.choices': 'Choose the terms',
  'starter.choice_label': 'Choice {number}',
  'starter.free_one_signer':
    'On Free, an agreement goes to {limit} other person at a time. Tick This is me for your own side, or sign it yourself instead.',
  'starter.optional_hint': 'Optional sentence: untick to leave it out.',
  'starter.details': 'Fill in the details',
  'starter.details_hint':
    'These go into the agreement before it is sent. The people signing fill in their own parts.',
  'starter.detail_missing': 'Fill this in to continue.',
  'starter.detail_unprintable': "These characters can't be printed yet: {characters}",
  'starter.who_signs': 'Who signs',
  'starter.use_failed': "This agreement couldn't be created.",
  'insights.title': 'Insights',

  'insights.description':
    'What you have signed and sent, counted on this device and for your account.',
  'insights.pending': 'Reading local counts…',
  'insights.empty': 'Insights appear once you sign or send a document.',
  'insights.empty_description':
    'Counts come from what you do on this device: documents you import, prepare and finish. Nothing is uploaded.',
  'insights.empty_sign': 'Sign a document',
  'insights.empty_send': 'Send for signature',
  'insights.scope_device': 'This device',
  'insights.in_period': 'In the selected period',
  'insights.local_note': 'Device counts are worked out on this device and never sent anywhere.',
  'insights.backup_now': 'Back up now',
  'insights.connected.title': 'Requests you sent',
  'insights.connected.refresh': 'Refresh',
  'insights.connected.retry': 'Try again',
  'insights.connected.as_of': 'Updated {time}',
  'insights.connected.signed_out': 'Sign in to see the requests you sent.',
  'insights.connected.sign_in': 'Sign in',
  'insights.connected.empty': 'No open requests and nothing sent in this period.',
  'insights.connected.unavailable': "Request counts couldn't be loaded. Device counts still work.",
  'insights.connected.stale': "Request counts couldn't refresh; these are from the last update.",
  'insights.connected.stale_period':
    "Request counts for {period} couldn't be loaded. These are for {shown}, from the last update.",
  'insights.connected.loading': 'Reading request counts…',
  'insights.connected.definition': 'What this counts',
  'insights.connected.open': 'See them in Agreements',
  'insights.connected.more': 'More details',
  'insights.connected.sample': 'Based on {count}',
  'insights.connected.unknown': 'Outcome not known yet: {count}',
  'insights.connected.complete': 'Complete',
  'insights.connected.partial': 'Some data is missing.',
  'insights.connected.insufficient': 'Shown once there are at least {count}.',
  'insights.connected.not_applicable': 'Nothing to measure yet.',
  'insights.connected.provisional': 'Can still change while requests are open.',
  'insights.connected.too_few': 'Too few yet',
  'insights.connected.minutes': '{value} min',
  'insights.connected.hours': '{value} h',
  'insights.connected.days': '{value} days',
  'insights.connected.R01': 'Open requests',
  'insights.connected.R02': 'People still to sign',
  'insights.connected.R03': 'Can sign now',
  'insights.connected.R04': 'Invitations delivered',
  'insights.connected.R05': 'Opened by signers',
  'insights.connected.R06': 'Signers finished',
  'insights.connected.R07': 'Declined',
  'insights.connected.R08': 'Expired',
  'insights.connected.R09': 'Fully signed',
  'insights.connected.R10': 'Signed by everyone',
  'insights.connected.R11': 'Typical time to open',
  'insights.connected.R12': 'Typical time to sign',
  'insights.connected.R13': 'Reminders sent',
  'insights.connected.def.R01':
    'Requests you sent that are still open, waiting for someone to sign.',
  'insights.connected.def.R02': 'People who still need to sign one of your open requests.',
  'insights.connected.def.R03': 'People whose turn it is to sign right now.',
  'insights.connected.def.R04':
    'Email invitations {product} delivered for requests you sent in this period.',
  'insights.connected.def.R05':
    'Signers who opened the agreement, for requests sent in this period. Opening is seen on the signing page; it does not prove who opened it.',
  'insights.connected.def.R06':
    'Signers who finished their part, for requests sent in this period.',
  'insights.connected.def.R07': 'Requests sent in this period that someone declined.',
  'insights.connected.def.R08':
    'Requests sent in this period that ran out of time before everyone signed.',
  'insights.connected.def.R09': 'Requests sent in this period that everyone signed.',
  'insights.connected.def.R10': 'The share of requests sent in this period that everyone signed.',
  'insights.connected.def.R11': 'The typical time from sending until a signer first opens it.',
  'insights.connected.def.R12': 'The typical time from sending until a signer finishes.',
  'insights.connected.def.R13': 'Reminder emails the email service accepted for delivery.',
  'insights.scope_account': 'Your account',
  'insights.kpi.sent': 'Sent',
  'insights.kpi.sent_hint': 'Requests sent in this period',
  'insights.kpi.rate': '{rate} of those sent',
  'insights.kpi.so_far': 'so far',
  'insights.kpi.pending': '{count} people still to sign',
  'insights.kpi.pending_one': '1 person still to sign',
  'insights.timing.needs': 'Shown from {min} signatures ({count} so far)',
  'insights.timing.too_few': 'Too few yet (needs {count})',
  'insights.timing.based_on': 'Middle of {count} signatures',
  'insights.timing.based_on_one': 'From 1 signature',
  'insights.chart.requests_title': 'Sent and fully signed',
  'insights.chart.requests_description':
    'Requests by the day they were sent. Fully signed counts the ones everyone has signed since.',
  'insights.chart.sent': 'Sent',
  'insights.chart.day': 'Day',
  'insights.chart.week': 'Week',
  'insights.chart.table': 'Show as a table',
  'insights.chart.device_title': 'Created and finished',
  'insights.chart.device_description': 'Documents on this device, by the day it happened.',
  'insights.chart.created': 'Created',
  'insights.chart.finished': 'Finished',
  'insights.outcome.title': 'Where they stand',
  'insights.outcome.description': 'The {count} requests sent in this period.',
  'insights.outcome.description_one': 'The 1 request sent in this period.',
  'insights.outcome.completed': 'Fully signed',
  'insights.outcome.in_progress': 'Waiting for signatures',
  'insights.outcome.declined': 'Declined',
  'insights.outcome.expired': 'Expired',
  'insights.outcome.voided': 'Cancelled',
  'insights.activity.title': 'Recent activity',
  'insights.activity.all': 'See requests',
  'insights.activity.empty': 'Nothing has happened on your requests yet.',
  'insights.activity.sent': 'Sent for signature',
  'insights.activity.opened': 'Opened by a signer',
  'insights.activity.signed': 'Signed by a signer',
  'insights.activity.completed': 'Fully signed',
  'insights.activity.declined': 'Declined',
  'insights.activity.expired': 'Expired',
  'insights.activity.voided': 'Cancelled',
  'insights.signers.title': 'Signers',
  'insights.signers.description':
    'People asked to sign the requests sent in this period. One person can count in more than one row.',
  'insights.signers.delivery_note':
    'Email delivery and reminders are tracked for requests {product} sends for you.',
  'insights.range_label': 'Period',
  'insights.range.7d': 'Last 7 days',
  'insights.range.30d': 'Last 30 days',
  'insights.range.90d': 'Last 90 days',
  'insights.refresh': 'Count again',
  'insights.window_label': '{period} · {dates}',
  'insights.section_inventory': 'On this device',
  'insights.section_trends': 'In this period',
  'insights.trends_empty': 'Nothing was created or completed in this period.',
  'insights.section_templates': 'Top templates',
  'insights.stale': 'Counts were refreshed. The numbers below are current.',
  'insights.error': "Insights couldn't be read on this device. The rest of the app keeps working.",
  'insights.value_unavailable': 'Not available',
  'insights.value_no_confirmed_backup': 'No backup yet',
  'insights.value_about': 'about {value}',
  'insights.estimate_label': 'Estimate, not a measured saving.',
  'insights.drilldown_summary': 'See which ({count})',
  'insights.drilldown_summary_one': 'See which (1)',
  'insights.drilldown_all': 'See all {count} in Agreements',
  'insights.drilldown_empty': 'Nothing counted.',
  'insights.templates_empty': 'No template has been used in this period.',
  'insights.template_usage': '{count} uses · last {date}',
  'insights.template_usage_one': '1 use · last {date}',
  'insights.template_gone': 'A deleted template',
  'insights.metric.L01': 'Documents',
  'insights.metric.L02': 'Completed',
  'insights.metric.L03': 'Drafts in progress',
  'insights.metric.L04': 'Templates saved',
  'insights.metric.L05': 'Top templates',
  'insights.metric.L06': 'Recently active',
  'insights.metric.L07': 'Created',
  'insights.metric.L08': 'Completed',
  'insights.metric.L09': 'Pages completed',
  'insights.metric.L10': 'Pages handled digitally',
  'insights.metric.L11': 'Space used',
  'insights.metric.L12': 'Last backup',
  'insights.unit.documents': 'documents',
  'insights.unit.document_one': 'document',
  'insights.unit.templates': 'templates',
  'insights.unit.template_one': 'template',
  'insights.unit.uses': 'uses',
  'insights.unit.use_one': 'use',
  'insights.unit.pages': 'pages',
  'insights.unit.page_one': 'page',
  'insights.unit.manual': 'manual',
  'insights.unit.automatic': 'automatic',
  'insights.row.untitled': 'Untitled document',
  'insights.row.storage_origin': 'Space this app uses',
  'insights.row.storage_vault': 'Your encrypted documents',
  'insights.row.backup_manifest': 'Backup record on this device',
  'settings.title': 'Settings',

  'settings.description': 'Your account, your info, and what this device keeps.',
  'settings.sections_label': 'Settings sections',
  'settings.back_to_draft': 'Back to your draft',
  'settings.help_title': 'Help and legal',
  'settings.help_description': 'Questions, problems, and the terms you use the app under.',
  'settings.help_email': 'Email support',
  'settings.help_email_subject': '{product} support',
  'settings.help_email_body': '\n\n---\nApp version: {version}',
  'settings.privacy_policy': 'Privacy policy',
  'settings.terms': 'Terms of use',
  'settings.version': '{product} version {version}',
  'settings.recently_deleted_title': 'Recently Deleted',
  'settings.recently_deleted_description':
    'Signed agreements and templates you delete on this device stay here for {days} days, then are removed for good.',
  'settings.recently_deleted_open': 'Open Recently Deleted',
  'workspace.title': 'Workspace',
  'workspace.description':
    'Copies you choose to publish so a connected AI assistant can search them. Your documents stay on this device.',
  'workspace.show_archived': 'Show archived',
  'workspace.signed_out': 'Sign in to see and publish workspace copies.',
  'workspace.unavailable':
    'The workspace is not open on this service yet. Nothing has been published.',
  'workspace.failed': 'The workspace could not be updated. Nothing has changed — try again.',
  'workspace.read_failed': 'Your workspace could not be read. Try again in a moment.',
  'workspace.empty_title': 'No published copies',
  'workspace.empty_description':
    'Publish a copy of a document below when you want an AI assistant to be able to find it.',
  'workspace.state_private': 'Private',
  'workspace.state_published': 'Published',
  'workspace.state_unpublished': 'Unpublished',
  'workspace.state_archived': 'Archived',
  'workspace.state_deleted': 'Deleted',
  'workspace.action_publish': 'Publish',
  'workspace.action_unpublish': 'Unpublish',
  'workspace.action_archive': 'Archive',
  'workspace.action_delete': 'Delete',
  'workspace.cancel': 'Cancel',
  'workspace.item_meta': 'Version {version} · updated {when}',
  'workspace.delete_confirm':
    'Delete this copy and its searchable text from the service? Your local document is not affected.',
  'workspace.publish_title': 'Publish a copy',
  'workspace.publish_document': 'Document',
  'workspace.publish_choose': 'Choose a document',
  'workspace.disclosure':
    'I understand {product} can read this copy so it can be searched. The copy stays until I unpublish or delete it.',
  'workspace.publish_button': 'Publish copy',
  'workspace.nothing_to_publish': 'Every document on this device already has a published copy.',
  'workspace.no_documents': 'No documents on this device yet.',
  'workspace.published': 'Published a copy of “{title}”.',
  'workspace.done_publish': '“{title}” is published again.',
  'workspace.done_unpublish': '“{title}” is unpublished. AI assistants can no longer see it.',
  'workspace.done_archive': '“{title}” is archived.',
  'workspace.deleted':
    'Deleted “{title}”. Its stored bytes and text are removed within {hours} hours.',
  'settings.account_delete_before': 'Before you delete',
  'settings.account_delete_local_title': 'Documents on this device',
  'settings.account_delete_local':
    'This device holds {items}. Deleting your account does not remove them from this device.',
  'settings.account_delete_local_gated':
    "This device holds {items}. After your account is deleted they can't be opened in {product} on this device again, unless you restore them from a backup.",
  'settings.account_delete_backup_first': 'Save a backup first',
  'settings.account_delete_backed_up': "I have a backup, or I don't need these documents",
  'settings.group_general': 'My Info',
  'settings.group_prefs': 'Preferences',
  'settings.group_account': 'Account',
  'settings.group_ai': 'AI connections',
  'settings.group_help': 'Help and legal',
  'settings.group_danger': 'Export or delete',
  'settings.group_data': 'Your data',
  'settings.hub_account': 'Sign-in, plan and credits',
  'settings.hub_general': 'Your name, email and saved signatures',
  'settings.hub_prefs': 'Appearance and the language of invitations',
  'settings.hub_data': 'Backup, restore and Recently Deleted',
  'settings.hub_ai': 'AI assistants and the copies they can read',
  'settings.hub_help': 'Support, privacy policy and terms',
  'settings.hub_danger': 'Download your account data or delete the account',
  'settings.appearance_title': 'Appearance',
  'settings.appearance_label': 'Theme',
  'settings.appearance_description': 'How this app looks on this device.',
  'settings.appearance_system': 'System',
  'settings.appearance_light': 'Light',
  'settings.appearance_dark': 'Dark',
  'settings.appearance_system_hint': 'Follows this device, and keeps following it when it changes.',
  'settings.appearance_saved': 'Saved on this device.',
  'settings.invitations_title': 'Invitations',
  'settings.invitations_description':
    "The language of the invitation email and the signing page for people you add. The document itself isn't translated.",
  'settings.invitation_locale': 'Default language for invitations',
  'settings.invitation_locale_saved':
    'Saved. People you add from now on start in this language, and you can still change it for each person.',
  'settings.diagnostics_title': 'Diagnostics',
  'settings.diagnostics_description':
    'Local diagnostics help support solve problems. They never include document content, field values, signatures or tokens.',
  'settings.diagnostics_size': 'Stored diagnostics: {size}',
  'settings.diagnostics_retention': 'Retention: 14 days or 5 MB, whichever comes first.',
  'settings.diagnostics_clear': 'Clear diagnostics',
  'settings.diagnostics_export': 'Export diagnostics',
  'settings.diagnostics_export_consent': "I'll send this file to support myself.",
  'settings.diagnostics_cleared': 'Diagnostics cleared.',
  'settings.diagnostics_exported': 'Diagnostics export ready.',
  'settings.diagnostics_export_failed': "The diagnostics file couldn't be created.",
  'transfer.title': 'Move to another device',
  'transfer.description':
    'Your documents stay on this device until you move them. It takes an encrypted backup and two minutes.',
  'transfer.step_backup_title': '1. Save a backup here',
  'transfer.step_backup_body': 'Choose a passphrase and save the encrypted backup file.',
  'transfer.step_backup_action': 'Go to backup',
  'transfer.step_carry_title': '2. Get the file to the new device',
  'transfer.step_carry_body':
    'Use iCloud Drive, Google Drive, AirDrop, a USB cable or email it to yourself. The file is encrypted.',
  'transfer.step_restore_title': '3. Restore on the new device',
  'transfer.step_restore_body':
    'Open {product} there, go to Settings → Backup and restore, choose the file and enter the passphrase.',
  'transfer.step_restore_action': 'Restore on this device',
  'transfer.account_note':
    'Signing in on the new device brings back your plan and credits. Your documents come back only from the backup.',
  'signin.title': 'Sign in',
  'signin.gate_intro':
    'Sign in once on this device. Your documents stay on this device. Only a document you send for signature is uploaded, encrypted, so the other person can sign it.',
  'signin.guest_title': 'Add your earlier work to this account?',
  'signin.guest_body': 'These were made on this device before you signed in:',
  'signin.guest_documents_one': '1 document',
  'signin.guest_documents_other': '{count} documents',
  'signin.guest_templates_one': '1 template',
  'signin.guest_templates_other': '{count} templates',
  'signin.guest_other_one': '1 other item (a signature, draft or My Info)',
  'signin.guest_other_other': '{count} other items (signatures, drafts and My Info)',
  'signin.guest_attach': 'Add to my account',
  'signin.guest_decline': "They're not mine",
  'signin.guest_decline_confirm':
    "They'll stay on this device but won't appear in any account, and you won't be asked again. This can't be undone.",
  'signin.guest_decline_yes': 'Keep them out of my account',
  'signin.guest_decline_back': 'Back',
  'signin.gate_switched':
    'You left the previous account on this device. Its documents stay here, hidden, until it signs in again. Sign in with the account you want to use.',
  'signin.gate_signed_out':
    "You're signed out. Your documents stay on this device and come back when you sign in again.",
  'settings.marketing_failed': "Couldn't save your choice. Try again.",
  'settings.marketing_load_failed': "Couldn't load your choice.",
  'signin.no_intent':
    'Signing in is only needed for sending and account features. Local work stays available without it.',
  'signin.resumes_send':
    "After you sign in, you'll go back to sending your request. Your draft is kept.",
  'signin.resumes_purchase': "After you sign in, you'll go back to your purchase.",
  'signin.resumes_account': "After you sign in, you'll go back to your account settings.",
  'signin.email_label': 'Email address',
  'signin.send_code': 'Email me a sign-in link',
  'signin.code_sent': 'We sent a {digits}-digit code to {email}. It expires in {minutes} minutes.',
  'signin.link_sent':
    'We sent a sign-in link and a {digits}-digit code to {email}. Open the link on this device, or enter the code here. Both expire in {minutes} minutes.',
  'signin.check_email_title': 'Check your email',
  'signin.link_title': 'Add {provider} to your account',
  'signin.link_body':
    'You already have a {product} account with {email}. To keep it one account, confirm it: open the link we just sent there on this device, or enter the code here, within {minutes} minutes. {provider} is then added, and next time it signs you straight in.',
  'signin.link_other_way': 'Sign in another way',
  'signin.provider_apple': 'Apple',
  'signin.provider_google': 'Google',
  'signin.continue_apple': 'Continue with Apple',
  'signin.continue_google': 'Continue with Google',
  'signin.continue_passkey': 'Sign in with a passkey',
  'signin.or': 'or',
  'signin.signing_in': 'Signing you in…',
  'signin.elsewhere_title': 'Finish where you asked',
  'signin.elsewhere_body':
    'This sign-in link works once, in the browser where you asked for it, for a few minutes. Enter this code there instead:',
  'signin.elsewhere_body_app':
    'You asked to sign in from the {product} app. Open the app to finish, or enter this code there:',
  'signin.elsewhere_open_app': 'Open the {product} app',
  'signin.elsewhere_sign_in_here': 'Sign in on this device instead',
  'signin.change_email': 'Change email',
  'signin.code_label': 'One-time code',
  'signin.verify': 'Verify',
  'signin.resend': 'Send a new code',
  'signin.resend_wait': 'Send a new code in {time}',
  'signin.check_waiting': 'Running a quick security check…',
  'signin.check_failed':
    "The security check couldn't load. Check your connection or turn off content blockers for this site, then try again.",
  'signin.check_retry': 'Try again',
  'signin.cancel': 'Not now',
  'signin.error_gate_unset':
    'Sign-in is not configured on this deployment yet. Nothing was created or charged.',
  'signin.error_invalid_code': "That code didn't work. Check it and try again, or send a new code.",
  'signin.error_rate_limited': 'Too many attempts. Wait a moment and try again.',
  'signin.error_rate_limited_until':
    "A code couldn't be sent because too many were asked for. Try again after {time}, or use a code you already have.",
  'signin.error_rate_limited_in': 'Too many attempts. Try again in {time}.',
  'signin.use_different_email': 'Use a different email',
  'signin.legal': 'By continuing, you agree to the {terms} and the {privacy}.',
  'signin.error_verification_failed':
    'We could not verify this sign-in request. Wait for the security check to finish, then try again. If it keeps failing, reload the page.',
  'signin.error_unavailable':
    "We couldn't reach the sign-in service. Check your connection and try again.",
  'signin.error_offline':
    "You're offline. Signing in needs a connection once; after that {product} works offline.",
  'signin.error_storage':
    "This browser isn't letting {product} save documents on this device, so sign-in can't finish. Open {product} in a normal window or allow site data for it, then try again.",
  'signin.error_provider_cancelled': 'Sign-in was cancelled. Choose a way to sign in.',
  'signin.error_provider_failed':
    "That sign-in couldn't be finished. Try again, or continue with your email.",
  'signin.error_passkey_cancelled': 'No passkey was used. Try again, or continue with your email.',
  'signin.error_passkey_failed':
    "Your passkey couldn't be used to sign in. If you haven't added one yet, sign in with your email, then add one in Settings.",
  'settings.account_title': 'Account',
  'settings.account_card_title': 'Sign-in',
  'settings.account_why_sign_in':
    'Sign in to send documents for signature by email and to keep your plan and purchases with you. Everything else works without an account.',
  'settings.account_signed_in': 'Signed in.',
  'settings.account_signed_in_as': 'Signed in as {email}',
  'settings.passkeys_title': 'Passkeys',
  'settings.passkeys_description':
    'Sign in with your face, fingerprint or screen lock instead of an emailed code. Your password manager keeps the passkey (iCloud Keychain, Google Password Manager and others).',
  'settings.passkeys_add': 'Add a passkey',
  'settings.passkeys_none': 'No passkeys yet.',
  'settings.passkeys_added_on': 'Added {date}',
  'settings.passkeys_synced': 'Synced across your devices',
  'settings.passkeys_default_name': 'Passkey',
  'settings.passkeys_remove': 'Remove',
  'settings.passkeys_remove_label': 'Remove {name}',
  'settings.passkeys_added': 'Passkey added. Next time, choose "Sign in with a passkey".',
  'settings.passkeys_cancelled': 'No passkey was added.',
  'settings.passkeys_add_failed': "The passkey couldn't be added. Try again.",
  'settings.passkeys_remove_failed': "The passkey couldn't be removed. Try again.",
  'settings.passkeys_load_failed': "Your passkeys couldn't be loaded.",
  'settings.account_session_expired':
    'Your sign-in has expired. Sign in again to send for signature or manage purchases.',
  'settings.account_sign_in_again': 'Sign in again',
  'settings.account_new_device_one':
    'This device has no documents yet. Your account has {count} request on the service; documents themselves come back only from a backup.',
  'settings.account_new_device':
    'This device has no documents yet. Your account has {count} requests on the service; documents themselves come back only from a backup.',
  'settings.account_new_device_empty':
    'This device has no documents yet. Documents come back only from a backup, never from signing in.',
  'settings.confirm_identity_title': "Confirm it's you",
  'settings.confirm_identity_body': "For your security, we'll email a 6-digit code to {email}.",
  'settings.confirm_identity_send': 'Email me a code',
  'settings.confirm_identity_sent': 'We sent a code to {email}. Enter it to continue.',
  'settings.confirm_identity_confirm': 'Confirm and continue',
  'settings.confirm_identity_resend': 'Send a new code',
  'settings.confirm_identity_sign_in':
    "For your security, sign in again to continue. You'll come back here.",
  'settings.account_export_scope':
    "Download a record of what your account holds on our service: profile, plan, purchases, requests you sent and workspace items. It's a file to keep, not to import: signing in brings your plan and credits back on any device. Your documents live on this device; to keep or move them, save a backup.",
  'settings.account_export_backup': 'Back up documents',
  'settings.account_deletion_scope':
    'Your account, its purchases and credits, and anything you published to your online workspace are deleted.',
  'settings.account_retention_financial':
    'Payment and credit records are kept as long as needed for tax, refunds and dispute evidence.',
  'settings.account_retention_audit':
    'A minimal security and audit log is kept as long as needed for tax, refunds and dispute evidence.',
  'settings.account_retention_sealed':
    'Agreements other people already signed stay available to them for the usual time.',
  'settings.account_retention_days': 'Kept for {days}.',
  'settings.account_retention_lifecycle': 'Kept for as long as the signed record lasts.',
  'settings.account_delete_requests_checking': "Checking your account's requests…",
  'settings.account_delete_requests_unknown':
    "We couldn't check your requests right now. Any still waiting for signatures will be voided.",
  'settings.account_delete_requests_active_one':
    '1 request still waiting for signatures will be voided, and its signers will see that.',
  'settings.account_delete_requests_active':
    '{count} requests still waiting for signatures will be voided, and their signers will see that.',
  'settings.account_delete_requests_sealed_one':
    '1 finished request stays available to its signers for the usual time.',
  'settings.account_delete_requests_sealed':
    '{count} finished requests stay available to their signers for the usual time.',
  'settings.account_delete_requests_none': 'Your account has no requests to void.',
  'settings.account_reconcile': 'Problem with a purchase?',
  'settings.account_reconcile_description':
    "Paid but don't see it? Enter the store receipt or checkout reference from your receipt email and we'll check which account it belongs to.",
  'settings.account_reconcile_kind': 'Reference type',
  'settings.account_reconcile_kind_receipt': 'Store receipt',
  'settings.account_reconcile_kind_session': 'Checkout reference',
  'settings.account_reconcile_reference': 'Receipt or checkout reference',
  'settings.account_reconcile_submit': 'Check purchase',
  'settings.account_reconcile_need_reference':
    'Enter the store receipt or checkout reference first.',
  'settings.account_reconcile_on_this_account':
    'This purchase is already on this account. Nothing else needs to happen.',
  'settings.account_reconcile_other_account':
    "This purchase belongs to a different account. To use it, sign in with that account's email.",
  'settings.account_reconcile_not_recorded':
    "We can't find this purchase. Check the reference in your receipt email.",
  'settings.account_reconcile_not_confirmed':
    "The payment provider hasn't confirmed this purchase yet. Try again in a few minutes.",
  'settings.account_reconcile_failed': "The purchase couldn't be checked.",
  'settings.account_switch_clear_local':
    'This device clears the account-scoped local state the service names.',
  'settings.account_switch_keep_vault': 'This device keeps its local vault.',
  'settings.account_switch_attach_explicit':
    'Attaching an existing vault to another account is a separate, explicit action.',
  'settings.account_export_done': 'Your account data is ready ({at}).',
  'settings.account_export_summary':
    'It covers {requests} requests, {purchases} purchases and {items} workspace items.',
  'settings.account_export_download': 'Download account data',
  'settings.account_export_truncated_requests':
    'The file includes your newest {count} requests. Contact support for older ones.',
  'settings.account_export_truncated_workspace':
    'The file includes your newest {count} workspace items. Contact support for older ones.',
  'settings.account_export_failed': "Your account data couldn't be exported.",
  'settings.account_delete_reauth':
    "To protect your account, we may email a code to {email} to confirm it's you.",
  'settings.account_delete_reauth_no_email':
    "To protect your account, you may be asked to sign in again to confirm it's you.",
  'settings.account_delete_confirm_label': 'Type DELETE to confirm',
  'settings.account_delete_confirm': 'Delete my account',
  'settings.account_delete_signed_out':
    'Sign in to delete your account here, or use the web page below without signing in.',
  'settings.account_delete_receipt':
    'Your account was deleted. Receipt {receipt}: quote it if you contact support.',
  'settings.account_delete_receipt_at': 'Deleted {at}.',
  'settings.account_delete_replay':
    'This account was already deleted. This is the same deletion, not a new one.',
  'settings.account_delete_recheck': 'Check again',
  'settings.account_delete_recheck_signed_out':
    "This device is signed out, so the deletion can't be checked again here. The receipt above is your record of it.",
  'settings.account_delete_disposition':
    'Requests voided: {requests}. Sessions and AI connections were ended.',
  'settings.account_delete_receipt_download': 'Download receipt',
  'settings.account_delete_restore_hint':
    'Saved a backup? Signing in again starts a new, empty account. Then open Settings, Your data, and restore the backup file to get your documents back.',
  'settings.account_delete_outside_erasure':
    "Files on your devices and backups you saved aren't affected; delete those yourself if you want them gone.",
  'settings.account_delete_failed': "Your account couldn't be deleted.",
  'settings.account_delete_web_hint':
    'No app at hand? You can also delete your account in a browser.',
  'settings.account_cache_age_today': 'Cached entitlement from today.',
  'settings.account_cache_age': 'Cached entitlement from {days} days ago.',
  'settings.account_cache_age_one': 'Cached entitlement from 1 day ago.',
  'settings.account_vault_documents': 'This device holds {count} documents.',
  'settings.account_vault_documents_one': 'This device holds 1 document.',
  'settings.account_vault_empty': "This device's vault is empty — nothing has been saved here yet.",
  'settings.account_vault_empty_connected':
    "This device's vault is empty. Remote data is not downloaded here, so this is not a statement about what your account holds.",
  'settings.account_vault_empty_remote':
    "This device's vault is empty, but your account holds remote records ({records}) — an empty local vault is not the same as having no documents.",
  'packs.option_title': '${price} for {credits} delivery credits',
  'packs.option_title_store': '{price} for {credits} delivery credits',
  'packs.option_detail': 'One-time purchase. The credits never expire and cover managed delivery.',
  'packs.credits_healthy': 'Your available credits can cover a managed request.',
  'packs.credits_low':
    'This request needs more credits than you have left. You can share the signing links yourself instead, or add a one-time pack.',
  'packs.credits_zero':
    'No delivery credits left. You can still share signing links yourself. Lifetime is unaffected.',
  'packs.none_on_sale': 'Credit packs are not on sale yet.',
  'packs.credits_low_free':
    'This request needs more credits than you have left. You can share the signing links yourself instead.',
  'packs.title': 'Delivery credits',
  'packs.buy_credits': 'Buy delivery credits',
  'packs.lifetime_disclosure':
    "Delivery credits don't expire. Extra credits are one-time purchases; nothing is ever charged automatically.",
  'packs.sheet_title': 'Buy delivery credits',
  'packs.sheet_intro':
    'One credit sends one request by email, with reminders, whatever the number of signers.',
  'packs.sheet_balance':
    'You have {count} delivery credits left. One credit sends one request by email.',
  'packs.sheet_balance_one':
    'You have 1 delivery credit left. One credit sends one request by email.',
  'packs.bought': 'Credits added. You now have {count} delivery credits.',
  'packs.bought_one': 'Credits added. You now have 1 delivery credit.',
  'ops.cost.title': 'Operations costs',
  'ops.cost.unavailable':
    'Operations costs are unavailable. Sign in with an authorized operator account.',
  'ops.cost.scenario_notice':
    'Changes on this page are a local scenario only. They do not change billing, prices or production configuration.',
  'ops.cost.rate_card': 'Rate card and sources',
  'ops.cost.r2_rate': 'R2 Standard storage, USD per GB-month',
  'ops.cost.workers_minimum': 'Workers Paid account minimum, USD per month',
  'ops.cost.email_allowance': 'Included outgoing emails per account-month',
  'ops.cost.email_overage': 'Outgoing email overage, USD per 1,000',
  'ops.cost.official_source': 'Official pricing page',
  'ops.cost.reset_rates': 'Reset to repo rates',
  'ops.cost.measured': 'Measured inputs and partial estimate',
  'ops.cost.observed':
    '{envelopes} envelopes, {emails} emails and {bytes} stored bytes observed this period.',
  'ops.cost.baseline': 'API estimate for measured components',
  'ops.cost.repriced': 'Scenario estimate for those components',
  'ops.cost.workers_separate':
    'Workers account minimum ({amount}) is shown separately; this partial estimate does not allocate it.',
  'ops.cost.unmeasured': 'Components still unmeasured',
  'ops.cost.pack_review': 'Credit-pack margin review',
  'ops.cost.pack_pending':
    'Enter measured delivery cost and commercial inputs to calculate a scenario. Sale is enabled by product direction; margin approval remains pending.',
  'ops.cost.delivery_unit': 'Delivery cost per managed envelope, USD',
  'ops.cost.channel_fee_percent': 'Payment channel fee, percent of price',
  'ops.cost.channel_fee_fixed': 'Payment channel fixed fee, USD per purchase',
  'ops.cost.refund_allowance': 'Refund and chargeback allowance, percent of price',
  'ops.cost.target_margin': 'Target margin, percent',
  'ops.cost.margin_pending': 'Margin inputs pending',
  'ops.cost.meets_target': 'Meets scenario target',
  'ops.cost.below_target': 'Below scenario target',
  'workflows.title': 'Saved send settings',
  'workflows.review_always':
    'Send a template again with the same order, delivery, message and reminders. You always review it before anything is sent.',
  'workflows.save_from_draft': 'Save the send settings of a draft',
  'workflows.no_drafts':
    'Use a template and set up how it is sent. Then you can save those settings here.',
  'workflows.save': 'Save settings',
  'workflows.saved_notice': 'Saved the settings of “{title}”.',
  'workflows.empty': 'No saved send settings yet.',
  'workflows.item_template': 'Template: {title}',
  'workflows.item_settings': '{routing} · {delivery} · {days} to respond',
  'workflows.routing.parallel': 'Everyone at once',
  'workflows.routing.sequential': 'One after another',
  'workflows.delivery.manual': 'You share the link',
  'workflows.delivery.managed': '{product} emails it',
  'workflows.send_again': 'Send again',
  'workflows.send_again_title': 'Send again: {title}',
  'workflows.remove': 'Remove',
  'workflows.removed': 'Removed “{title}”.',
  'workflows.save_failed': "These send settings couldn't be saved.",
  'workflows.remove_failed': "These send settings couldn't be removed.",
  'workflows.send_again_failed': "These send settings couldn't be opened.",
  'workflows.repair.template_missing':
    'The template behind these settings is gone. Remove them and save new ones from another template.',
  'workflows.repair.template_deleted':
    'The template behind these settings is in Recently Deleted. Restore it to send again.',
  'workflows.repair.role_unassigned':
    'The template’s roles changed since these settings were saved. Remove them and save new ones.',
  'workflows.repair_no_side_effects': 'Nothing was created, reserved or charged.',
  'workflows.note.template_changed': 'The template changed since you saved these settings.',
  'workflows.note.manual_no_credits':
    'You have no delivery credits left, so you’ll share the link yourself this time.',
  'workflows.note.limit':
    'Some settings go past what your plan allows. The review shows what to change.',
  'settings.account_current': 'Connected account: {account}',
  'connections.title': 'AI assistants',
  'connections.intro':
    'AI assistants connected to this account. Each connection is separate: revoking one never changes another.',
  'connections.refresh': 'Refresh',
  'connections.unavailable': 'We could not read your connections. Nothing has changed — try again.',
  'connections.signed_out':
    'Sign in to see and manage the AI assistants connected to this account.',
  'connections.empty_title': 'No AI assistant is connected',
  'connections.empty_description':
    'Nothing can read this account through an assistant until you approve a connection. The steps below connect one.',
  'connections.connected_only_note':
    'Only connected assistants are listed. A revoked assistant has to ask for your approval again before it can connect.',
  'connections.host_granted': 'Connected {date}',
  'connections.host_last_used': 'Last used {date}',
  'connections.host_never_used': 'Not used yet',
  'connections.state.active': 'Connected',
  'connections.state.pending_consent': 'Waiting for your approval',
  'connections.state.revoked': 'Revoked',
  'connections.state.expired': 'Expired',
  'connections.distribution.openai': 'ChatGPT',
  'connections.distribution.claude': 'Claude',
  'connections.distribution.other': 'Other AI assistant',
  'connections.permissions_title': 'What this assistant can do',
  'connections.permissions_note':
    'Adding a permission later needs your approval again: this page never widens a connection on its own.',
  'connections.item_scope_all': 'It can see everything you publish to this assistant.',
  'connections.item_scope_selected': 'It can see {count} items you selected.',
  'connections.item_scope_selected_one': 'It can see 1 item you selected.',
  'connections.item_scope_selected_count_unknown': 'It can see only the items you selected.',
  'connections.item_scope_not_reported':
    'The service did not report which items this assistant can see.',
  'connections.activity_title': 'Recent tool activity',
  'connections.activity_none': 'No tool calls recorded for this assistant.',
  'connections.activity_calls': '{count} tool calls recorded.',
  'connections.activity_calls_one': '1 tool call recorded.',
  'connections.activity_tool': '{name} · {count} calls',
  'connections.activity_tool_one': '{name} · 1 call',
  'connections.revoke': 'Revoke access',
  'connections.revoke_title': 'Revoke {host}?',
  'connections.revoke_stops':
    'Its access stops immediately: every token this assistant was given stops working, so it can no longer read anything here and a call it already started fails.',
  'connections.revoke_keeps':
    'Nothing else changes: your other connections keep working, and your documents, templates and agreements are untouched.',
  'connections.revoke_only_this': 'This revokes one assistant. It deletes nothing.',
  'connections.revoke_confirm': 'Revoke this assistant',
  'connections.revoke_cancel': 'Keep it connected',
  'connections.revoke_done': '{host} was revoked. Its tokens no longer work.',
  'connections.revoke_gone': 'That connection is already gone. Nothing was changed.',
  'connections.revoke_failed': 'We could not revoke this connection. Nothing changed.',
  'connections.revoke_signed_out': 'Sign in again to revoke this connection.',
  'connections.add_title': 'Connect an assistant',
  'connections.add_intro':
    'Pick the assistant you use and follow its steps. You approve the connection on a {product} page, and you can disconnect it here at any time.',
  'connections.add_hosts': 'Assistants',
  'connections.host.claude': 'Claude',
  'connections.host.claude_code': 'Claude Code',
  'connections.host.chatgpt': 'ChatGPT',
  'connections.host.codex': 'Codex',
  'connections.host.cursor': 'Cursor',
  'connections.copy': 'Copy',
  'connections.copied': 'Copied',
  'connections.copy_failed': "Couldn't copy. Select the text and copy it yourself.",
  'connections.add_to_cursor': 'Add to Cursor',
  'connections.step.claude_open':
    'In Claude (claude.ai or the Claude desktop app), open Settings, then Connectors, and choose Add custom connector.',
  'connections.step.claude_paste': 'Name it {product} and paste this server address:',
  'connections.step.claude_connect':
    'Choose Add, then Connect. A {product} page opens: sign in if it asks, check what Claude may do and choose Allow. The connector is on your Claude account, so claude.ai and the desktop app both have it.',
  'connections.step.claude_code_add': 'In a terminal, run:',
  'connections.step.claude_code_auth':
    'In Claude Code, type /mcp, choose signbento, then Authenticate. Your browser opens {product}: choose Allow.',
  'connections.step.chatgpt_developer':
    'In ChatGPT, open Settings, then Apps & Connectors, then Advanced settings, and turn on Developer mode.',
  'connections.step.chatgpt_create':
    'Back in Apps & Connectors, choose Create. Name it {product}, choose OAuth for authentication and paste this server address:',
  'connections.step.chatgpt_connect':
    'Choose Create. A {product} page opens: sign in if it asks and choose Allow. In a chat, pick {product} from the tools menu.',
  'connections.step.codex_add': 'In a terminal, run:',
  'connections.step.codex_login': 'Then sign in. Your browser opens {product}: choose Allow.',
  'connections.step.cursor_add':
    'Choose Add to Cursor. Or open Cursor Settings, then MCP, choose Add a custom MCP server and paste:',
  'connections.step.cursor_connect':
    '{product} shows as needing a login: choose Connect. A {product} page opens: choose Allow.',
  'connections.add_menus_move':
    "Menus move between app versions. If a step looks different, look for Connectors or MCP in the assistant's settings.",
  'connections.add_approvals':
    'Anything an assistant prepares to send, sign or change waits for your approval in {product}.',
  'connections.add_unavailable':
    "Connecting assistants isn't available on this version of {product} yet.",
  'connect.title': 'Connect an AI assistant',
  'connect.checking': 'Reading the connection request…',
  'connect.signed_out':
    'An AI assistant wants to connect to your {product} account. Sign in to see what it asks for.',
  'connect.sign_in': 'Sign in to continue',
  'connect.heading': 'Connect {host} to {product}',
  'connect.asks': '{host} is asking to:',
  'connect.elevation':
    '{host} is already connected and is asking for more. Allowing gives it all of this:',
  'connect.unverified':
    "{product} hasn't verified this app's name. Allow it only if you started this connection yourself just now.",
  'connect.return_to': 'After you allow it, you go back to {destination}.',
  'connect.manage': 'You can disconnect it at any time in Settings, AI connections.',
  'connect.allow': 'Allow',
  'connect.deny': 'Cancel',
  'connect.returning': 'Approved. Taking you back to the assistant…',
  'connect.already': 'This assistant is already approved. Taking you back to it…',
  'connect.denied': 'Cancelled. Nothing was connected; you can close this page.',
  'connect.invalid': "This connection request can't be used. Start again from your AI assistant.",
  'connect.missing':
    'There is no connection request here. Connections start in your AI assistant: Settings shows how.',
  'connect.open_settings': 'How to connect an assistant',
  'connect.failed': "The connection couldn't be approved. Nothing was connected.",
  'connect.unavailable': "Connecting assistants isn't available right now. Nothing was connected.",
  'connections.scope.profile_read': 'Read your name and account details',
  'connections.scope.agreements_read': 'Read your agreements and their status',
  'connections.scope.agreements_write': 'Create and change agreements',
  'connections.scope.workspace_read': 'Read the items you published to this assistant',
  'connections.scope.workspace_write': 'Add or change items in the workspace',
  'connections.scope.templates_read': 'Read your templates',
  'connections.scope.templates_write': 'Create and change templates',
  'connections.scope.content_read': 'Read the content of items you published',
  'connections.scope.contacts_read': 'Read your contacts',
  'connections.scope.contacts_write': 'Create and change contacts',
  'connections.scope.analytics_read': 'Read your usage summaries',
  'connections.scope.delivery_write': 'Send and deliver requests',
  'connections.scope.evidence_read': 'Read evidence records',
  'connections.scope.billing_read': 'Read billing and credit details',
  'connections.scope.purchase_start': 'Start a purchase',
  'connections.scope.account_manage': 'Change account settings',
  // The trusted review surface — SM3 (B7.3, B9, B11.2, C5). A model prepares; a person approves
  // here. Every refusal and every state has its own sentence, and no message names a capability,
  // a token or a link.
  'reviews.title': 'Reviews',
  'reviews.intro':
    'What an AI assistant prepared for you to approve. Nothing is sent, signed or charged until you record your decision here.',
  'reviews.refresh': 'Refresh',
  'reviews.unavailable':
    'We could not read your reviews ({code}). Nothing has changed — try again.',
  'reviews.signed_out': 'Sign in to see the actions an AI assistant prepared for this account.',
  'reviews.empty_title': 'Nothing is waiting for your approval',
  'reviews.empty_description':
    'When an AI assistant prepares something for you, it waits here until you approve or deny it. No assistant can approve on your behalf.',
  'reviews.open': 'Review and approve',
  'reviews.row_deadline': 'Answer within {days} days',
  'reviews.row_deadline_one': 'Answer within 1 day',
  'reviews.row_document': '{title} · revision {revision}',
  'reviews.row_operation': '{kind} · {target}',
  'reviews.back': 'Back to reviews',
  'reviews.not_found': 'That review is not available on this account.',
  'reviews.not_found_note':
    'It may belong to another account, or it may never have existed. Nothing was changed.',
  'reviews.state.awaiting_approval': 'Waiting for your approval',
  'reviews.state.approved': 'Approved',
  'reviews.state.rejected': 'Denied',
  'reviews.state.committed': 'Committed',
  'reviews.state.expired': 'Expired',
  'reviews.state.invalidated': 'No longer valid',
  'reviews.invalidated.source_revision_changed':
    'The source document changed after this was prepared.',
  'reviews.invalidated.participants_changed':
    'The participant list changed after this was prepared.',
  'reviews.invalidated.fields_changed': 'The field assignments changed after this was prepared.',
  'reviews.invalidated.action_changed': 'The prepared action changed after this was prepared.',
  'reviews.invalidated.grant_revoked': 'The access given to the AI host was revoked.',
  'reviews.invalidated.grant_revision_changed': 'The permissions of the AI host changed.',
  'reviews.invalidated.account_inactive': 'This account is not active.',
  'reviews.invalidated.review_superseded': 'A newer preparation replaced this one.',
  'reviews.document_title': 'Document',
  'reviews.document_identity': '{title} · revision {revision} · {pages} · {size}',
  'reviews.document_digest': 'Content fingerprint {digest}',
  'reviews.document_no_content':
    'The document stays in your workspace. The preview below is read from there for you alone; this screen never hands it to the assistant.',
  'reviews.preview_title': 'Preview from your workspace',
  'reviews.preview_current': 'This is the current version in your workspace.',
  'reviews.preview_moved':
    'Your workspace now has revision {current}; this review froze revision {frozen}. It has to be prepared again.',
  'reviews.preview_unavailable':
    'The workspace copy could not be read, so this cannot be approved right now.',
  // An operational review (SM5) approves an *edit*, not a draft. It froze an agreement the person
  // already activated, so there is no workspace copy behind it: the surface names the family, the
  // step it acts on, what changes and the effect, and no preview sentence applies to one.
  'reviews.agreement_title': 'Agreement',
  'reviews.agreement_identity': '{title} · revision {revision}',
  'reviews.agreement_no_content':
    'The agreement itself stays in your workspace. This screen shows its identity and the edit, never its content.',
  'reviews.operation_title': 'What this review would do',
  'reviews.operation.kind.correction': "Correct a participant's details",
  'reviews.operation.kind.resend': 'Send another invitation',
  'reviews.operation.kind.void': 'Void the agreement',
  'reviews.operation.kind.expiry_extension': 'Extend the deadline',
  'reviews.operation_target': '{agreement} · {recipient}',
  'reviews.operation_target_envelope': '{agreement} · the agreement as a whole',
  'reviews.operation_revision':
    'Prepared against revision {revision}; it is re-read when the edit is committed.',
  'reviews.operation.effect.replace_capability_and_edit_identity':
    "Replaces that participant's signing link and edits their identity details.",
  'reviews.operation.effect.queue_another_invitation':
    'Queues another invitation for that participant.',
  'reviews.operation.effect.revoke_every_capability_and_close':
    'Revokes every signing link and closes the agreement.',
  'reviews.operation.effect.lengthen_deadline': 'Lengthens the deadline for the agreement.',
  'reviews.operation_changes_title': 'What changes',
  'reviews.operation_change': '{field}: {to}',
  'reviews.operation.field.name': 'Name',
  'reviews.operation.field.email': 'Email',
  'reviews.operation.field.role_id': 'Role',
  'reviews.operation.field.locale_code': 'Language',
  'reviews.operation_resend': 'This also hands over a new invitation.',
  'reviews.operation_requested_days': 'It asks for {days} more days on the deadline.',
  'reviews.operation_requested_days_one': 'It asks for 1 more day on the deadline.',
  'reviews.operation_reason': 'Reason: {reason}',
  'reviews.operation_credit': 'Credit effect: {credit}',
  'reviews.operation_missing':
    'This review acts on an agreement, but the edit it would perform was not described.',
  'reviews.operation_missing_body':
    'There is nothing here to approve. Ask the model to prepare the edit again.',
  'reviews.participants_title': 'Participants, in order',
  'reviews.participant_signer': 'Signer',
  'reviews.participant_cc': 'Copy',
  'reviews.participant_sender': 'Sender',
  'reviews.participant_position': '{index}. {name}',
  'reviews.participant_no_email': 'No email on file',
  'reviews.copy_loading': 'Opening the document…',
  'reviews.copy_unreadable':
    'The document could not be opened here, so its fields cannot be shown. Nothing has changed.',
  'reviews.placed_field': '{type} · {name}',
  'reviews.placement_page': 'Page {page}',
  'reviews.placement_default': 'End of page {page}, added because the assistant placed none',
  'reviews.placement_anchor_found': 'Next to “{text}”',
  'reviews.placement_anchor_skipped':
    '“{text}” is not in this document, so this field is left out.',
  'reviews.placement_anchor_missing': '“{text}” is not in this document.',
  'reviews.placement_anchor_matching': 'Anchor matching: {case}, {words}.',
  'reviews.placement_anchor_match_case_insensitive': 'any case',
  'reviews.placement_anchor_match_case_sensitive': 'exact case',
  'reviews.placement_anchor_match_whole_word': 'whole words only',
  'reviews.placement_anchor_match_anywhere': 'anywhere in the text',
  'reviews.placement_blocking':
    'A field can’t be placed because its text isn’t in this document. Ask the assistant to fix the placement and prepare it again.',
  'reviews.placement_label': 'Label: {label}',
  'reviews.placement_tooltip': 'Hint: {tooltip}',
  'reviews.placement_value': 'Filled in for you: “{value}” (the signer can change it)',
  'reviews.placement_value_locked': 'Filled in for you and locked: “{value}”',
  'reviews.placement_max_length': 'Up to {count} characters',
  'reviews.placement_validation': 'Must be: {preset}',
  'reviews.placement_formatting': 'Formatting: {format}',
  'reviews.placement_formula': 'Calculated: {expression}',
  'reviews.fields_title': 'Fields',
  'reviews.fields_none': 'No fields are assigned.',
  'reviews.field_required': 'Required',
  'reviews.field_optional': 'Optional',
  'reviews.field_locked': 'Locked',
  'reviews.field_owner': 'for {role}',
  'reviews.deadline_title': 'Deadline',
  'reviews.deadline_value': '{days} to respond (up to {max})',
  'reviews.delivery_title': 'Delivery',
  'reviews.delivery_mode.manual': 'You share the signing links yourself',
  'reviews.delivery_mode.managed': 'Managed delivery',
  'reviews.routing_mode.parallel': 'All signers can act at once',
  'reviews.routing_mode.sequential': 'Signers act in order',
  'reviews.sender_order.before': 'You sign before the recipients',
  'reviews.sender_order.after': 'You sign after the recipients',
  'reviews.credit_title': 'Credit effect',
  'reviews.credit_none': 'No delivery credit is used.',
  'reviews.credit_trial': 'One trial credit is used.',
  'reviews.credit_amount': 'Delivery credits used: {debited}; {available} will remain.',
  'reviews.credit_at_activation': 'Charged when the action is performed, never before.',
  'reviews.approve': 'Approve',
  'reviews.deny': 'Deny',
  'reviews.approve_title': 'Approve this prepared action?',
  'reviews.approve_binding':
    'Approving records your decision against this exact document revision, participant set and fields. It expires five minutes after you record it.',
  'reviews.approve_confirm': 'Record my approval',
  'reviews.approve_cancel': 'Not now',
  'reviews.approved_recorded': 'Approval recorded.',
  'reviews.approved_expires':
    'This approval expires at {expires} and is bound to this exact revision.',
  'reviews.approved_token_note':
    'The approval token goes to the AI host over its own authenticated connection, never to this page.',
  'reviews.rejected_recorded': 'You denied this. Nothing was sent or charged.',
  'reviews.committed_note':
    'This action was committed: the approval was used and nothing else is needed here.',
  'reviews.committed_send_note':
    'Your assistant has finished preparing it. Nothing is sent until you open it, check the fields and press Send.',
  'reviews.send_now': 'Open it to send',
  'reviews.send_unavailable':
    'This agreement can’t be opened here yet. Reload the page and try again.',
  'reviews.send_failed': 'We couldn’t open it on this device. Nothing was sent.',
  'reviews.decision_expired':
    'This review expired before your decision. Ask the model to prepare it again.',
  'reviews.decision_terminal': 'This review is already finished, so it cannot be decided again.',
  'reviews.decision_forbidden':
    'The access given to the AI host is no longer active, so this cannot be approved.',
  'reviews.decision_failed': 'We could not record your decision. Nothing changed.',
  'reviews.decision_signed_out': 'Sign in again to record your decision.',
  'reviews.decision_gone': 'That review is no longer available.',
  'reviews.reask_title': 'This has to be prepared again',
  'reviews.reask_body':
    'Something material changed after this was prepared, so an approval now would no longer match the action. Ask the model to prepare it again.',
  'reviews.reask_invalidated': 'This review is no longer valid: {reason}',
  'reviews.reask_source_moved':
    'The source document changed, so the frozen revision is no longer current.',
  'reviews.continuation_title': 'Finish in this browser',
  'reviews.continuation_intro':
    'Your own browser completes the prepared action. The AI host never holds send or sign authority.',
  'reviews.continuation_open': 'Open the browser continuation',
  'reviews.continuation_authority_seal':
    'This continuation can seal, upload and activate the prepared draft.',
  'reviews.continuation_authority_edit':
    'This continuation can edit fields and metadata only; it cannot send or sign.',
  'reviews.continuation_no_host_send':
    'The AI host cannot send or sign this. Only you can, in your browser.',
  'reviews.continuation_draft': '{title} · {participants} · {days} to respond',
  'reviews.continuation_expired':
    'This continuation has expired. Ask the model to prepare the action again.',
  'reviews.continuation_gone': 'There is no continuation for this review yet.',
  'reviews.continuation_failed': 'We could not open the continuation.',
  'reviews.continuation_signed_out': 'Sign in again to open the continuation.',
  'reviews.handoff.title': 'Handoff',
  'reviews.handoff.intro':
    'A model asked you to act on a document on your own device. Nothing runs in the background while the app is closed.',
  'reviews.handoff.state.waiting_for_user': 'Waiting for you',
  'reviews.handoff.state.waiting_for_device': 'Waiting for the device that holds the source',
  'reviews.handoff.state.running': 'Running',
  'reviews.handoff.state.succeeded': 'Succeeded',
  'reviews.handoff.state.failed': 'Failed',
  'reviews.handoff.state.cancelled': 'Cancelled',
  'reviews.handoff.state.expired': 'Expired',
  'reviews.handoff.operation': 'Operation {id}',
  'reviews.handoff.action_ref': 'Prepared action: {action}',
  'reviews.handoff.expires': 'Expires {expires}',
  'reviews.handoff.actions_title': 'What you can do',
  'reviews.handoff.resume': 'Resume here',
  'reviews.handoff.resume_done': 'Resumed on this device.',
  'reviews.handoff.wrong_device_title': 'This is the wrong device',
  'reviews.handoff.wrong_device':
    'This handoff was opened on another device. It stays open there, and nothing changed here.',
  'reviews.handoff.no_actions': 'There is nothing left to do on this handoff.',
  'reviews.handoff.no_background':
    'Nothing continues in the background. This operation only moves when you act on it.',
  'reviews.handoff.restart': 'Ask the model to open a new handoff',
  'reviews.handoff.restart_note':
    'This one cannot be reopened. A new operation starts a fresh handoff.',
  'reviews.handoff.complete': 'Report completion',
  'reviews.handoff.complete_outcome': 'Outcome',
  'reviews.handoff.complete_succeeded': 'Succeeded',
  'reviews.handoff.complete_failed': 'Failed',
  'reviews.handoff.commit_ref': 'Commit reference',
  'reviews.handoff.commit_ref_note':
    'Completion is reported only after a commit. Without a reference it cannot be reported as succeeded.',
  'reviews.handoff.failure_reason': 'Reason',
  'reviews.handoff.submit': 'Send',
  'reviews.handoff.document_title': 'Your AI assistant sent you a document',
  'reviews.handoff.document_intro':
    'Check it is the right one. Using it keeps a copy in your {product} workspace that this assistant can read, so it can prepare the agreement. Nothing is sent until you approve it.',
  'reviews.handoff.document_name': 'Document name',
  'reviews.handoff.document_default_name': 'Document for signature',
  'reviews.handoff.document_pages': '{count} pages · {size}',
  'reviews.handoff.document_pages_one': '1 page · {size}',
  'reviews.handoff.document_loading': 'Opening the document…',
  'reviews.handoff.document_unreadable':
    'This file could not be opened as a PDF, so it cannot be used. Ask the assistant to send it again.',
  'reviews.handoff.use_document': 'Use this document',
  'reviews.handoff.not_this_document': "It's not the right document",
  'reviews.handoff.document_used':
    'Done. The assistant can now prepare the agreement, and you will review it before anything is sent.',
  'reviews.handoff.signed_out': 'Sign in again to open this handoff.',
  'reviews.handoff.gone': 'That handoff is not available on this account.',
  'reviews.handoff.unavailable': 'We could not read this handoff. Nothing has changed.',
  'reviews.handoff.failed': 'We could not update this handoff. Nothing changed.',
  'reviews.handoff.terminal': 'This handoff is already finished, so it cannot be updated again.',
  'reviews.handoff.returned_title': 'Content shared back',
  'reviews.handoff.returned_body':
    'A reference and a fingerprint were shared with the host, never the document.',
  'reviews.handoff.not_returned': 'Nothing has been shared back with the host.',
  'reviews.handoff.failure.wrong_device': 'Opened on the wrong device',
  'reviews.handoff.failure.source_unavailable': 'The source is no longer available',
  'reviews.handoff.failure.no_commit': 'No commit was recorded',
  'reviews.handoff.failure.user_cancelled': 'You cancelled it',
  'reviews.handoff.failure.upload_expired': 'The upload window expired',
  'reviews.handoff.failure.upload_rejected': 'The upload was refused',
  'reviews.handoff.failure.grant_revoked': 'The access given to the AI host was revoked',
  'reviews.handoff.explanation.wrong_device':
    'The source is on another device. Open the handoff there; it stays open.',
  'reviews.handoff.explanation.source_unavailable':
    'The source is no longer available on the device, so this cannot be completed.',
  'reviews.handoff.explanation.no_commit':
    'Completion was reported without a commit, so it is not recorded as a success.',
  'reviews.handoff.explanation.user_cancelled': 'You cancelled this handoff.',
  'reviews.handoff.explanation.upload_expired':
    'The upload window expired before the file arrived.',
  'reviews.handoff.explanation.upload_rejected': 'The uploaded file was refused.',
  'reviews.handoff.explanation.grant_revoked':
    'The access given to the AI host was revoked, so this handoff was cancelled.',
  'reviews.handoff.explanation.workspace_full_free':
    'Your {product} workspace is full. Free accounts keep up to {free_items} items, {free_versions} versions and {free_size}. Remove items in {product} to free space, then tap "Use this document" again. Lifetime keeps up to {lifetime_items} items and {lifetime_size}.',
  'reviews.handoff.explanation.workspace_full_lifetime':
    'Your {product} workspace is full. Lifetime keeps up to {lifetime_items} items, {lifetime_versions} versions and {lifetime_size}. Remove items in {product} to free space, then tap "Use this document" again.',
  'reviews.settings_title': 'Reviews',
  'reviews.settings_description':
    'When an AI host prepares something for you, it waits here for your approval. Nothing is sent, signed or charged without it.',
  'reviews.settings_open': 'Open reviews',
  'settings.account_sign_in': 'Sign in',
  'settings.account_sign_out': 'Sign out',
  'settings.account_sign_out_of': 'Sign out of {email}',
  'settings.sign_out_note': 'Your documents, templates and backups stay on this device.',
  'settings.sign_out_confirm_title': 'Sign out?',
  'settings.sign_out_confirm_body':
    "Your documents stay on this device. To get back in, you'll need a code we email to {email}.",
  'settings.sign_out_confirm_body_no_email':
    "Your documents stay on this device. To get back in, you'll need a code we email to you.",
  'settings.sign_out_confirm_cancel': 'Stay signed in',
  'settings.sign_out_note_gated':
    'Your documents stay on this device and come back when you sign in again. Another account that signs in here gets its own separate space.',
  'settings.account_export': 'Export account data',
  'settings.account_delete': 'Delete account',
  'settings.account_delete_web': 'Open the deletion page',
  'settings.backup_title': 'Backup and restore',
  'settings.backup_description':
    'Your documents are kept on this device. A backup is an encrypted file you keep somewhere safe, like iCloud Drive or Google Drive, so you can restore it here or on another device.',
  'settings.backup_status_never': 'No backup yet.',
  'settings.backup_status_last': 'Last backup: {at}',
  'settings.backup_status_changes':
    "You've made changes since then. Back up again to include them.",
  'settings.backup_status_current': 'Nothing has changed on this device since then.',
  'settings.backup_status_failed':
    "The backup on {at} didn't finish. Your documents here are unchanged.",
  'backup.auto.no_destination': 'Choose a destination to enable automatic backup.',
  'backup.auto.adapter_not_durable':
    'This destination cannot keep a durable grant, so automatic backup stays off and manual backup remains available.',
  'backup.failure.permission_revoked':
    'Access to the destination was revoked. Your local vault is unchanged; reconnect or choose another location.',
  'backup.failure.destination_missing':
    'The destination folder is missing. Choose another location or back up now.',
  'backup.failure.destination_full':
    'The destination is full. Choose another location or free space and back up now.',
  'backup.failure.generic': 'The last attempt failed. Your local vault is unchanged.',
  'settings.backup_passphrase': 'Backup passphrase',
  'settings.backup_passphrase_hint':
    'At least {count} characters. You need it to restore, and it cannot be recovered if you forget it.',
  'settings.backup_passphrase_repeat': 'Repeat the passphrase',
  'settings.backup_passphrase_mismatch': "The passphrases don't match.",
  'settings.backup_now': 'Save encrypted backup',
  'settings.backup_saved': 'Backup saved: {file}. Keep it with its passphrase.',
  'settings.backup_downloaded':
    'Backup file downloaded: {file}. Move it somewhere safe, like a cloud drive, and keep its passphrase.',
  'settings.backup_shared':
    'Backup file ready: {file}. Make sure you saved it somewhere safe, like Files or a cloud drive.',
  'settings.backup_cancelled': 'Backup cancelled. Nothing was saved.',
  'settings.backup_unavailable': "This browser can't save files. Try another browser.",
  'settings.backup_failed': "The backup couldn't be saved. Your documents here are unchanged.",
  'settings.backup_count_document_one': '{count} document',
  'settings.backup_count_document': '{count} documents',
  'settings.backup_count_completion_one': '{count} signed copy',
  'settings.backup_count_completion': '{count} signed copies',
  'settings.backup_count_draft_one': '{count} draft',
  'settings.backup_count_draft': '{count} drafts',
  'settings.backup_count_template_one': '{count} template',
  'settings.backup_count_template': '{count} templates',
  'settings.backup_count_signature_one': '{count} saved signature',
  'settings.backup_count_signature': '{count} saved signatures',
  'settings.backup_restore_title': 'Restore from a backup',
  'settings.backup_restore_description':
    'Choose a backup file you saved earlier. You will see what it holds before anything changes.',
  'settings.backup_restore_pick': 'Choose a backup file',
  'settings.backup_restore_pick_another': 'Choose a different file',
  'settings.backup_restore_not_backup': "That file isn't a {product} backup, or it is damaged.",
  'settings.backup_restore_account_export':
    "That's your account data file. It's a record to keep, not a backup: it holds no documents. Choose the .agbackup file you saved under Backup.",
  'settings.backup_restore_made': 'Backup made {at}.',
  'settings.backup_restore_passphrase': 'Passphrase for this backup',
  'settings.backup_restore_unlock': 'Open backup',
  'settings.backup_restore_checking': 'Opening…',
  'settings.backup_restore_wrong_passphrase': "That passphrase doesn't open this backup.",
  'settings.backup_restore_newer_version':
    'This backup was made by a newer version of the app. Update the app, then try again.',
  'settings.backup_restore_damaged': "This backup couldn't be read. It may be damaged.",
  'settings.backup_restore_failed':
    "The backup couldn't be restored. Nothing on this device was changed.",
  'settings.backup_restore_contents': 'It holds {items}.',
  'settings.backup_restore_contents_empty': 'It holds no documents or templates.',
  'settings.backup_restore_mode': 'How should it be restored?',
  'settings.backup_restore_merge': 'Add to this device',
  'settings.backup_restore_merge_hint':
    "Adds what's missing. Everything already on this device stays as it is.",
  'settings.backup_restore_replace': 'Make this device match the backup',
  'settings.backup_restore_replace_hint':
    "Anything on this device that isn't in the backup is removed.",
  'settings.backup_restore_replace_removes':
    "This removes {items} from this device, because they aren't in the backup.",
  'settings.backup_restore_replace_removes_nothing': 'Nothing on this device would be removed.',
  'settings.backup_restore_replace_overwrites_one':
    'It also replaces {count} item you changed on this device with the backup copy.',
  'settings.backup_restore_replace_overwrites':
    'It also replaces {count} items you changed on this device with the backup copies.',
  'settings.backup_restore_replace_confirm': 'I understand this changes what is on this device',
  'settings.backup_restore_run': 'Restore',
  'settings.backup_restore_run_replace': 'Replace and restore',
  'settings.backup_restore_done':
    'Restored. {applied} items added or updated, {skipped} already here.',
  'settings.backup_restore_done_items': 'Restored {items}.',
  'settings.backup_restore_done_empty': 'Restored. The backup held no documents or templates.',
  'settings.backup_restore_done_nothing_new': 'Nothing new to restore.',
  'settings.backup_restore_already_here_one': '1 item was already on this device.',
  'settings.backup_restore_already_here': '{count} items were already on this device.',
  'settings.backup_restore_open_agreements': 'Open Agreements',
  'settings.backup_restore_conflicts_one':
    '{count} item differs from the copy on this device. The copy here was kept.',
  'settings.backup_restore_conflicts':
    '{count} items differ from the copies on this device. The copies here were kept.',
  'settings.backup_prompt_title': 'Protect your work',
  'settings.backup_prompt_body':
    'Your documents and templates live only on this device. Save an encrypted backup so you can get them back if this device is lost.',
  'settings.backup_prompt_dismiss': 'Not now',
  'settings.telemetry_title': 'Usage data',
  'settings.telemetry_label': 'Share anonymous usage data',
  'settings.telemetry_description':
    'Anonymous counts of which features get used help us improve the app. Everything keeps working when this is off.',
  'settings.my_info_title': 'My Info',
  'settings.my_info_description':
    'Fills in your own name, initials and details on documents, so you type them once.',
  'settings.my_info_suggested':
    'Filled in from your adopted signature and the email you signed in with. Save to keep it.',
  'settings.my_info_suggested_email': 'Your sign-in email is filled in. Save to keep it.',
  'settings.my_info_local_note': 'Kept on this device only. Signing in never uploads it.',
  'settings.my_info_use_account_email': 'Use my sign-in email',
  'settings.display_name': 'Full name',
  'settings.initials': 'Initials',
  'settings.initials_note': 'Used where a document asks for your initials.',
  'settings.email': 'Email (optional)',
  'settings.email_invalid': 'Enter a full email address, like name@example.com.',
  'settings.phone': 'Phone (optional)',
  'settings.company': 'Company (optional)',
  'settings.title_field': 'Title (optional)',
  'settings.address': 'Address (optional)',
  'settings.date_format': 'Date format',
  'settings.date_format_device': '{example} (this device)',
  'settings.date_format_option': '{example} ({format})',
  'settings.save': 'Save',
  'settings.saved': 'Saved.',
  'settings.save_failed': 'Could not save. Nothing was changed.',
  'settings.vault_unavailable':
    'The local vault is not available, so My Info and signatures cannot be opened here.',
  'settings.signatures_title': 'Signatures',
  'settings.signatures_description':
    "Draw, type or upload your signature and initials. They're saved encrypted on this device.",
  'settings.signatures_empty': 'Nothing saved yet. Draw, type or upload a signature below.',
  'settings.signature_add': 'Add a signature or initials',
  'settings.signature_add_cancel': 'Cancel',
  'settings.signature_for': 'Save as',
  'settings.signature_type_signature': 'Signature',
  'settings.signature_type_initials': 'Initials',
  'settings.signature_tab_draw': 'Draw',
  'settings.signature_tab_type': 'Type',
  'settings.signature_tab_upload': 'Upload',
  'settings.signature_canvas_label': 'Signature drawing area',
  'settings.signature_draw_hint':
    'Draw with a mouse, pen or finger. With a keyboard, select the area and draw with the arrow keys, or switch to Type.',
  'settings.signature_clear': 'Clear',
  'settings.signature_typed_label': 'Type your name',
  'settings.signature_typed_placeholder': 'e.g. Sam Sender',
  'settings.signature_typed_note': 'Saved as a picture of your typed name.',
  'settings.signature_upload_label': 'Upload an image',
  'settings.signature_upload_hint':
    'PNG or JPEG only, up to {megabytes} MB and {megapixels} megapixels. SVG, XML and other script-bearing files are refused.',
  'settings.signature_save': 'Save signature',
  'settings.signature_saved': 'Signature saved.',
  'settings.signature_deleted': 'Signature deleted.',
  'settings.signature_undo': 'Undo',
  'settings.signature_delete_failed': "The signature couldn't be deleted.",
  'settings.signature_delete': 'Delete',
  'settings.signature_created': 'Saved {date}',
  'settings.signature_default': 'In use by default',
  'settings.signature_blank': 'Nothing to save yet — draw, type or upload a mark first.',
  'settings.signature_blank_raster': 'That image has no visible ink. Choose another image.',
  'settings.signature_not_inspected':
    'No transparency check was possible here, so this image is judged by its declared size only.',
  'settings.signature_upload_unsupported':
    'That file type cannot be a signature. Upload a PNG or JPEG image.',
  'settings.signature_upload_pixel_limit':
    'That image is too large to decode safely (over {megapixels} megapixels).',
  'settings.signature_upload_too_large': 'That file is larger than the {megabytes} MB limit.',
  'settings.signature_upload_malformed': 'That file could not be read as a PNG or JPEG image.',
  'settings.signature_upload_failed': 'The upload could not be used.',
  // The product name is never hard-coded: it comes from BRAND at the call site (BRD-01).
  'recipient.powered_by': 'Powered by {product}',
  'recipient.trust_plain': 'Secure signing link',
  'recipient.cta_title': 'Need something signed yourself?',
  'recipient.cta_lead':
    'Try {product} free. Sign on your own phone or computer, then send anyone a link to sign.',
  'recipient.cta_point_local': 'Your documents stay on your device',
  'recipient.cta_point_share': 'Send the link by WhatsApp, email or text',
  'recipient.cta_point_once': 'Need more? Pay once. No subscription.',
  'recipient.cta_try': 'Try {product} free',
  'recipient.cta_no_account': 'No account needed to start.',
  'recipient.cta_quick_sign_in': 'One quick sign-in with your email. No password.',
  'recipient.upgrade_title': 'Get {product} Lifetime',
  'recipient.upgrade_lead':
    'You use {product} Free in this browser. With Lifetime, send to more people and let {product} email and remind them.',
  'recipient.upgrade_action': 'Upgrade to Lifetime',
  'recipient.open_product': 'Open {product}',
  'recipient.lifetime_note': 'You use {product} Lifetime in this browser.',
  'common.loading': 'Loading…',
  'error.notice.what': 'Something went wrong.',
  // The support code sits on its own muted line under the sentence (sweep rank 6): a person reads
  // the sentence, and support gets the code without the person describing the failure.
  'error.code_line': 'Code: {code}',
  'error.recovery.retry_later': 'Try again in a moment.',
  'error.recovery.fix_input': 'Check the highlighted values and try again.',
  'error.recovery.reload': 'Reload the page to continue.',
  'error.recovery.update_app': 'Update the app, then try again.',
  'error.recovery.free_a_slot': 'Void or wait for an active request, or share a link yourself.',
  'error.recovery.remove_signer': 'Remove the extra signer, or upgrade for more signers.',
  'error.recovery.share_manually': 'Share the signing link yourself instead.',
  // Where purchases are not offered (the installed app, or no checkout configured), a limit names
  // only the free ways forward.
  'error.recovery.remove_signer_free': 'Remove the extra signer.',
  'error.recovery.free_limits': 'Continue within the free limits.',
  'error.recovery.upgrade': 'Review the plans, or continue with the free limits.',
  'error.recovery.sign_in_again': 'Sign in again to continue.',
  'error.recovery.link_account': 'Link an account to continue.',
  'error.recovery.check_connection': 'Check your connection and try again.',
  'error.recovery.contact_support': 'Contact support if this keeps happening.',
  'error.recovery.not_authorized':
    'You are not authorized for that. Ask the sender to grant access.',
  'error.recovery.retry_same_request': 'Try the same request again.',
  'error.notice.help': 'Email support about this error',
  'purchases.title': 'Plan and credits',
  'purchases.guest':
    "You're using the free version without an account. Sign in to see your plan or restore purchases.",
  'purchases.lifetime_active': 'Lifetime is active on this account.',
  'purchases.lifetime_revoked': 'The Lifetime purchase on this account was refunded or revoked.',
  'purchases.free_with_credits': 'You have delivery credits available.',
  'purchases.free_without_credits':
    'No delivery credits left. You can still share signing links yourself.',
  'purchases.unknown': 'We could not read your purchase state right now.',
  'purchases.plan_free': "You're on Free",
  'purchases.plan_free_revoked': 'Your Lifetime purchase was refunded, so you are on Free',
  'purchases.plan_lifetime': 'You have Lifetime',
  'purchases.plan_signers_one': 'Up to {count} signer per request',
  'purchases.plan_signers': 'Up to {count} signers per request',
  'purchases.plan_requests': '{used} of {limit} active requests in use',
  'purchases.plan_window_one': 'Signing links last {count} day',
  'purchases.plan_window': 'Signing links last {count} days',
  'purchases.plan_window_lifetime': 'Signing links last {days} days, or up to {max} if you choose',
  'purchases.plan_credits_one': '{count} delivery credit left',
  'purchases.plan_credits': '{count} delivery credits left',
  'purchases.plan_reserved_one': '({count} held for a request in progress)',
  'purchases.plan_reserved': '({count} held for requests in progress)',
  'purchases.trial_available':
    'Free email delivery: your first request can be emailed to signers for you.',
  'purchases.trial_reserved': 'Free email delivery: being used by a request in progress.',
  'purchases.trial_consumed':
    'Free email delivery: used. You can still share signing links yourself.',
  'purchases.restore_scope':
    'Bought Lifetime or credits on another device? Restore brings them to this account. It does not bring back documents; use a backup for those.',
  'purchases.restore': 'Restore purchases',
  'purchases.restored': 'Your purchases are restored.',
  'purchases.restored_lifetime': 'Lifetime is restored on this account.',
  'purchases.nothing_to_restore': 'Nothing new to restore. Your plan is up to date.',
  'purchases.restore_failed': "Purchases couldn't be restored.",
  'purchases.sign_in_again': 'Sign in again to see your plan and restore purchases.',
  'purchases.sign_in_to_restore': 'Sign in to restore purchases',
  'purchases.offline': "Your plan couldn't be loaded right now. Your documents aren't affected.",
  'purchases.offline_cached': "You're offline. Showing your plan as of {at}.",
  'purchases.lifetime_offer':
    'Lifetime: ${price} once, no subscription. Up to {signers} signers per request, {requests} active requests, longer signing links and {credits} email deliveries included.',
  'purchases.lifetime_offer_store':
    'Lifetime: {price} once, no subscription. Up to {signers} signers per request, {requests} active requests, longer signing links and {credits} email deliveries included.',
  'purchases.upgrade': 'Get Lifetime for ${price}',
  'purchases.upgrade_store': 'Get Lifetime for {price}',
  'purchases.checkout_not_on_sale': 'That purchase is not available yet. Nothing was charged.',
  'purchases.checkout_failed': "Checkout couldn't be opened. Nothing was charged.",
  'purchases.store_pending':
    'Your purchase is waiting for approval. Nothing is unlocked until the store confirms it, and it shows here when it does.',
  'purchases.store_cancelled': 'Purchase cancelled. Nothing was charged.',
  'purchases.store_failed':
    "The purchase couldn't be finished. If the store charged you, Restore purchases adds it to this account.",
  'purchases.store_refused':
    "This purchase couldn't be added to this account. If it was bought on another account, sign in to that one and restore.",
  'purchases.store_other_account':
    "A purchase on this device belongs to another account, so it wasn't added here.",
  'purchases.unavailable':
    'Purchases are not available here yet. Free features keep working, including signing on this device and sharing signing links yourself.',
  'purchases.return_title': 'Purchase status',
  'purchases.verify_pending': 'Checking this purchase with the payment provider…',
  'purchases.return_purchased': 'Purchase confirmed. Lifetime is active on this account.',
  'purchases.return_pack_purchased':
    'Purchase confirmed. Delivery credits were added to this account.',
  'purchases.return_pending':
    'The payment provider has not confirmed this purchase yet. Nothing is unlocked until it does. You can check again from Purchases and credits.',
  'purchases.return_revoked':
    'This purchase was refunded or revoked. Lifetime is no longer active.',
  'purchases.return_nothing_to_verify':
    'There is no purchase to confirm for this account. Nothing was charged.',
  'purchases.return_canceled':
    'Checkout was cancelled. Nothing was charged and your free plan is exactly as it was.',
  'purchases.return_plan': 'Your plan: {plan}',
  'purchases.plan_name_free': 'Free',
  'purchases.plan_name_lifetime': 'Lifetime',
  'purchases.return_unlocked':
    'You can now send to up to {signers} signers per request, keep {requests} requests active, and you have {credits} delivery credits.',
  'purchases.return_check_again': 'Check again',
  'purchases.return_done': 'Done',
  'purchases.return_draft_kept': 'Your prepared work is still here. Nothing was lost.',
  'purchases.return_review':
    'Review your draft again before sending: what you prepared was beyond the free plan limits.',
  'purchases.return_continue': 'Continue where I left off',
  'purchases.return_settings': 'Back to plan and credits',
  'purchases.return_unlocked_title': 'Lifetime unlocked',
  'purchases.return_continue_draft': 'Continue with your draft',
  'lifetime_offer.title_signers': 'Need {count} signers?',
  'lifetime_offer.title_delivery': 'Want {product} to send it for you?',
  'lifetime_offer.title_active': 'Need more requests at once?',
  'lifetime_offer.title_copy': 'Need to send someone a copy?',
  'lifetime_offer.title_order': 'Need people to sign in order?',
  'lifetime_offer.title_window': 'Need a longer signing link?',
  'lifetime_offer.title_saved': 'Want to save how you send?',
  'lifetime_offer.title_request': 'Need to fix or extend this request?',
  'lifetime_offer.title_plan': 'Need more signers?',
  'lifetime_offer.title_attachments': 'Need files from your signers?',
  'lifetime_offer.reason_signers': 'Your Free plan sends to {free} signer at a time.',
  'lifetime_offer.reason_plan':
    'Your Free plan sends to {free} signer at a time, and you share the link yourself.',
  'lifetime_offer.reason_attachments':
    'Asking signers to upload files, like an ID or a receipt, is part of Lifetime.',
  'lifetime_offer.reason_copy':
    'Sending someone a copy of the signed document is part of Lifetime.',
  'lifetime_offer.reason_delivery':
    "You've used your free emailed send. With Lifetime, {product} emails the signing link and reminds people for you.",
  'lifetime_offer.reason_active':
    'Free keeps up to {free} requests waiting for signatures at once. Lifetime allows {requests}.',
  'lifetime_offer.reason_order': 'Choosing who signs first is part of Lifetime.',
  'lifetime_offer.reason_window':
    'Free signing links last {free} days. With Lifetime they last {days} days by default, up to {max}.',
  'lifetime_offer.reason_saved':
    'Lifetime remembers who signs, the order, the message, reminders and link length for each template.',
  'lifetime_offer.reason_correct':
    "With Lifetime you can fix a signer's name or email after sending, without starting over.",
  'lifetime_offer.reason_extend':
    'With Lifetime you can give signers more time, up to {max} days from when you sent it.',
  'lifetime_offer.compare_label': 'Free and Lifetime compared',
  'lifetime_offer.compare_free': 'Free',
  'lifetime_offer.compare_free_signers': '{free} signer at a time',
  'lifetime_offer.compare_free_sharing': 'You share the link',
  'lifetime_offer.compare_free_window': 'Links last {days} days',
  'lifetime_offer.compare_lifetime_signers': 'Up to {signers} signers, in order, with copies',
  'lifetime_offer.compare_lifetime_delivery': '{product} emails and reminds them',
  'lifetime_offer.compare_lifetime_window': 'Links up to {max} days',
  'lifetime_offer.compare_lifetime_active': 'Up to {requests} requests at once',
  'lifetime_offer.upgrade': 'Upgrade to Lifetime',
  'lifetime_offer.benefits_title': 'Upgrade to {product} Lifetime',
  'lifetime_offer.benefits_intro': 'Get the flexibility you need. One payment, no subscription.',
  'lifetime_offer.benefit_signers_title': 'Add more than {free} signer',
  'lifetime_offer.benefit_signers':
    'Send one request to up to {signers} people, together or in turn, with copies.',
  'lifetime_offer.benefit_delivery_title': 'Let {product} send and remind',
  'lifetime_offer.benefit_delivery':
    'We email each signer their link and remind them. {credits} emailed sends included.',
  'lifetime_offer.benefit_more_title': 'Longer links, more at once',
  'lifetime_offer.benefit_more':
    'Links last up to {max} days, with up to {requests} requests waiting at once.',
  'lifetime_offer.benefit_once_title': 'One-time purchase',
  'lifetime_offer.benefit_once': '{price} once. No subscription, nothing charged automatically.',
  'lifetime_offer.continue': 'Continue',
  'lifetime_offer.maybe_later': 'Maybe later',
  'lifetime_offer.free_stays':
    'Signing, sharing links yourself and seeing who has viewed and signed stay free.',
  'lifetime_offer.price': '{price} once. No subscription.',
  'lifetime_offer.keep_one_signer': 'Keep 1 signer',
  'lifetime_offer.share_myself': 'Share it myself',
  'lifetime_offer.not_now': 'Not now',
  'lifetime_offer.save_failed':
    "Your draft couldn't be saved, so the purchase didn't start. Nothing was charged.",
  'lifetime_offer.resumed': "You're signed in. Nothing has been charged yet.",
  'lifetime_offer.tag': 'Lifetime',
  'lifetime_offer.quiet_signers': 'More than one signer is part of Lifetime.',
  'lifetime_offer.quiet_copy': 'Copies are part of Lifetime.',
  'lifetime_offer.quiet_delivery': 'Emailed delivery is part of Lifetime.',
  'lifetime_offer.quiet_active': 'More requests at once is part of Lifetime.',
  'lifetime_offer.quiet_order': 'Signing order is part of Lifetime.',
  'lifetime_offer.quiet_window': 'Longer links are part of Lifetime.',
  'lifetime_offer.quiet_saved': 'Saved send settings are part of Lifetime.',
  'lifetime_offer.quiet_correct': 'Correcting a signer is part of Lifetime.',
  'lifetime_offer.quiet_extend': 'Giving more time is part of Lifetime.',
  'lifetime_offer.quiet_plan': 'More signers and emailed delivery are part of Lifetime.',
  'lifetime_offer.quiet_attachments': 'Asking for files is part of Lifetime.',
  'lifetime_offer.locked_order': 'Signing order',
  'lifetime_offer.locked_window': 'Longer signing link',
  'lifetime_offer.locked_saved': 'Saved send settings',
  'lifetime_offer.locked_attachments': 'Ask signers for files',
  'request.correction_requires_lifetime':
    'Corrections are part of Lifetime. On the free plan, void this request and prepare a new one — your prepared work stays.',
  'request.correction_after_submission':
    'That signer has already submitted. Accepted signatures are never edited; prepare a new request instead.',
  'request.correction_declined': 'That signer declined, so there is nothing to correct.',
  'request.correction_terminal':
    'This request has ended, so its recipients can no longer be corrected.',
  'request.correction_expired':
    'The signing window has closed. An expired request is never reopened — prepare a new one.',
  'request.resend_limit':
    'This signer has had all the invitations the plan allows. Share the link you already have.',
  'request.resend_too_soon': 'Too soon after the last invitation. Try again shortly.',
  'request.extension_requires_lifetime': 'Changing the deadline is part of Lifetime.',
  'request.extension_terminal': 'Only an active request has a deadline that can be extended.',
  'request.extension_not_active':
    'This request has not started yet, so there is no deadline to extend.',
  'request.extension_expired':
    'The signing window has already closed. An expired request is never reopened — prepare a new one.',
  'request.extension_after_acceptance':
    'Someone has already accepted, so the deadline can no longer change.',
  'request.extension_must_lengthen':
    'An extension can only move the deadline later, never earlier.',
  'request.cc_read_after_commit':
    'Read access for a copied-in participant opens once the request is completed.',
  'error.recovery.upgrade_or_void_and_recreate':
    'Upgrade, or void this request and prepare a new one.',
  'error.recovery.share_the_existing_link': 'Share the link you already have.',
  'error.recovery.wait_then_resend': 'Wait a few minutes, then try again.',
  'error.recovery.upgrade_or_new_request': 'Upgrade, or prepare a new request.',
  'error.recovery.choose_later_deadline': 'Choose a later date than the current deadline.',
  'error.recovery.wait_for_completion': 'Wait until the request is completed.',
  // S6.5 error-catalogue pass: one specific, actionable line per reachable registry action.
  'error.recovery.wait_void_upgrade':
    'Void or wait for an active request, or upgrade to start more.',
  'error.recovery.reduce_signers_or_upgrade': 'Remove a signer, or upgrade for more signers.',
  'error.recovery.share_it_myself_or_upgrade':
    'Share the signing link yourself, or upgrade to have it sent for you.',
  'error.recovery.share_it_myself_or_buy_pack':
    'Share the signing link yourself, or buy a delivery pack.',
  'error.recovery.create_new_request': 'Prepare a new request to send a fresh link.',
  'error.recovery.view_current_state': 'Open the request to see its current state.',
  'error.recovery.refresh_state': 'Refresh to see the latest state of this request.',
  'error.recovery.retry_support_code': 'Try again; if it repeats, give support this error code.',
  'error.recovery.refresh_balance': 'Refresh to see the purchase you already made.',
  'error.recovery.refresh_or_reconcile':
    'Refresh in a moment; if the purchase is still missing, contact support.',
  'error.recovery.reconnect_destination': 'Reconnect your backup location, then try again.',
  'error.recovery.retry_or_change_location': 'Try again, or choose a different backup location.',
  'error.recovery.remove_password_or_export_plain':
    'Remove the password from the PDF, or export an unprotected copy and add that.',
  'error.recovery.choose_another_file': 'Choose a different file that opens normally.',
  'error.recovery.flattened_export_guidance': 'Export a flattened copy of the PDF, then add that.',
  'error.recovery.invalidate_or_reject':
    'Remove the existing signature from the PDF, or reject it and upload a clean copy.',
  'error.recovery.split_or_reduce_file': 'Split the PDF or reduce its size, then add it again.',
  'error.recovery.split_document': 'Split the document into smaller parts and add them again.',
  'error.recovery.downscale_image': 'Downscale the image, then add it again.',
  'error.recovery.export_to_pdf_locally':
    'Convert the image to PDF on this device, then add the PDF.',
  'error.recovery.retry_compose':
    'Try again; if it keeps failing, remove the file and add it again.',
  'error.recovery.retry_or_manual_fields': 'Try again, or place the fields by hand.',
  'error.recovery.retry_backoff': 'Try again in a few minutes; sending is temporarily unavailable.',
  'error.recovery.manual_share':
    'Share the signing link yourself; email to that address is suppressed.',
  'error.recovery.wait_or_manual': 'Wait and try again later, or share the link yourself.',
  'error.recovery.none_required': 'Nothing to do — this is retried automatically.',
  'error.recovery.retry_or_contact_support':
    'Try the purchase again; if it repeats, contact support.',
  'error.recovery.restore_or_contact_support':
    'Restore your purchases; if Lifetime is still missing, contact support.',
  'error.recovery.request_new_code': 'Request a new code and enter it.',
  'error.recovery.free_space_or_change_location':
    'Free up space, or choose a different backup location.',
  'error.recovery.retry_backup': 'Try the backup again.',
  'error.recovery.choose_another_backup': 'Choose another backup file to restore.',
  'error.recovery.check_backup_passphrase': 'Check the backup passphrase, then try again.',
  'error.recovery.update_app_for_backup': 'Update the app, then restore the backup.',
  'error.recovery.free_local_space': 'Free up space, then try again.',
  'error.recovery.restore_from_backup': 'Restore this file from a backup, then try again.',
  'error.recovery.recover_previous_revision': 'Recover the previous revision, then try again.',
  'error.recovery.reload_and_merge':
    'Reload to get the latest revision, then reapply your changes.',
  'error.recovery.reprepare_finalize': 'Prepare the final document again.',
  'error.recovery.refresh_and_retry': 'Refresh to get the latest state, then try again.',
  'error.recovery.incomplete_evidence_export':
    'The deadline has passed. Export the incomplete-evidence record.',
  'error.recovery.wait_for_signers': 'Wait until every signer has responded.',
  'error.recovery.fix_field_errors': 'Fix the highlighted fields and try again.',
  'error.recovery.sign_in': 'Sign in, then try again.',
  'error.recovery.view_available_items': 'Open your list to see what is still available.',
  'error.recovery.reduce_payload': 'Reduce the size of what you are sending and try again.',
  'error.recovery.backoff_retry':
    'Too many requests in a short time. Wait a moment, then try again.',
  'send.resend': 'Resend',
  'send.resend_queued': 'Invitation queued. {remaining} left for this signer.',
  'send.resend_revealed': 'Link ready. {remaining} left for this signer.',
  'send.resend_link_ready': 'The link for {name}',
  'send.correct': 'Correct details',
  'send.correct_unavailable': 'No correction available for this step',
  'send.correct_scope':
    'Correcting rotates the link: the one you sent stops working. Accepted signatures are never edited, and a file already downloaded cannot be recalled.',
  'send.correct_name': 'Name',
  'send.correct_email': 'Email',
  'send.correct_submit': 'Apply correction',
  'send.corrected':
    'Corrected. The new link is version {version}; the previous one no longer works.',
  'send.extend_label': 'Days to respond (from the original start)',
  'send.extend_submit': 'Extend deadline',
  'send.extend_limit':
    'An extension can reach at most {allowed} days from the original start; you asked for {days}.',
  'send.extended': 'The deadline is now {date}, and the document was not changed.',
  'send.cc_reveal': 'Reveal final copy link',
  'send.cc_reveal_loading': 'Preparing the link',
  'send.cc_revealed': 'Final copy link for {name} is ready below. It expires on its own.',
  'send.cc_waiting': 'Read access opens when the request completes',
  'send.step_conflict': 'Someone else changed this step. Reload the status and try again.',
  'send.step_failed': 'That did not work.',
  'send.request_status_title': 'Request status',
  'send.request_status_unavailable':
    'Status is unavailable right now. Nothing about the request changed.',
  'send.request_state': '{state} · {submitted} of {total} responded',
  'send.request_step': '{order}. {name} — {state}',
  'send.request_step_sender': 'your step',
  'send.next_link_ready':
    '{name} holds the active link now. No email was sent: share their link when you are ready.',
  'send.adopt_sender_step': 'Sign my part',
  'send.adopt_sender_note':
    'Your own step is recorded as an adoption of what you approved; it never uses a link, and every earlier signer has finished.',
  'send.sender_display_name': 'The sender',
  'send.sender_name_label': 'Your name',
  'send.sender_name_hint': 'Recipients see this as who the request is from.',
  'send.adopted': 'Your step is recorded. The request can be completed now.',
  'send.adoption_not_due': 'Not your turn yet: an earlier signer still has to finish.',
  'send.adoption_failed': 'We could not record your step.',
  'send.refresh_status': 'Refresh status',
  'send.title': 'Send a request',

  'send.description': 'Choose a prepared document and send it.',
  'send.pending': 'Loading your drafts…',
  'send.list_failed': 'Could not read prepared drafts on this device. Reload to try again.',
  'send.ready_title': 'Ready to share',
  'send.link_not_ready': "The link isn't ready yet. Open the request to share it.",
  'send.prepare_document': 'Prepare a document',
  'send.list_label': 'Choose what to send',
  'send.load_more': 'Load more drafts',
  'send.review_title': 'Check the details',
  'send.review_fields_one': '1 field',
  'send.review_fields': '{count} fields',
  'send.sent_draft_title': 'Request already created',
  'send.pending_recovery_title': 'Check this request before sending again',
  'send.pending_recovery_description':
    'A request was prepared for this draft. Its activation may already have succeeded. Check the service state to continue this exact request.',
  'send.create_recovery_description':
    'Creating this request was interrupted. Check for the same draft on the service before continuing; {product} will reuse its original request ID.',
  'send.pending_recovery_code': 'Latest check: {code}',
  'send.pending_recovery_checked':
    "Checked at {time}: this request hasn't gone out yet. Try again in a moment.",
  'send.spent_title': "This request can't be sent",
  'send.spent_description': "The service turned this attempt down for good, so it can't finish.",
  'send.spent_next':
    'Send again first makes sure this attempt can never go out, then starts a new request with the same document, people and fields.',
  'send.spent_send_again': 'Send again',
  'send.spent_restarted_elsewhere':
    'This draft was already sent again, or removed, in another tab.',
  'send.spent_open_agreements': 'Open Agreements',
  'send.replaced_title': 'Already sent again',
  'send.replaced_description':
    "This draft was already sent again, so this copy won't be sent. Find its request in Agreements.",
  'send.spent_fresh_failed':
    "Couldn't start a new request from this draft yet. Check its status in Requests, then try again.",
  'send.pending_recovery_corrupt':
    'This device cannot read the saved send attempt. This draft is paused to prevent a duplicate request. Check your Requests list or use a fresh draft.',
  'send.check_request': 'Check and continue',
  'send.use_again_editor': 'Open editor to use again',
  'send.already_sent':
    'This draft already created a request. Use Again in the editor to start a new one.',
  'send.view_request': 'View request',
  'send.local_link_not_saved':
    'The request exists, but this device could not save its link to the draft. Keep this status page open or find the request in your account.',
  'send.share_unavailable_copy': "The share sheet didn't open. Copy the link instead.",
  'send.edit_document': 'Edit document',
  'send.review_document': 'Document',
  'send.review_signers': 'External signers',
  'send.review_signature_fields': 'Required signature fields',
  'send.review_delivery': 'Delivery',
  'send.review_credit_effect': 'Credit effect',
  'send.review_zero_credits': 'No delivery credit',
  'send.review_one_credit': '1 credit on activation',
  'send.review_trial_effect': 'One Free trial use if still eligible',
  'send.review_trial_unavailable': 'Trial unavailable; managed creation may be refused',
  'send.review_unknown_effect': 'Trial or credit checked before creation',
  'send.review_manual': 'Share the link yourself',
  'send.review_managed': 'Managed invitation',
  'send.review_expiry': 'Signing window',
  'send.review_days': '{days}',
  'send.review_window_free':
    'This draft asked for a longer window. Free requests can be signed for {days}.',
  'send.delivery_manual_choice': 'Share the link yourself — no delivery credit',
  'send.delivery_managed_choice': 'Managed invitation — use an eligible trial or credit',
  'send.delivery_managed_note':
    'The service checks trial or credit eligibility before creating the request. You can return to manual sharing here.',
  'send.reminders': 'Automatic reminders',
  'send.managed_no_credits':
    'No delivery credit is available in the latest account snapshot. The service will check again; manual sharing remains available.',
  'send.managed_trial_unavailable':
    'The latest account snapshot does not show an available managed trial. The service will check again; manual sharing remains available.',
  'send.no_required_signature_recommendation':
    'No required signature field is assigned to an external signer. Review the fields before creating this request.',
  'send.free_signer_limit':
    'This draft has {count} external signers. Free allows {limit}; keep the draft and remove a signer, or verify a Lifetime account before creating the request.',
  'send.free_signer_limit_confirmed':
    'This draft has {count} external signers. Your Free account allows {limit}; the draft is saved. Remove a signer or upgrade before creating the request.',
  'send.review_upgrade': 'Review Lifetime',
  'send.managed_switch_manual': 'Send it yourself (manual)',
  'send.remove_extra_signer': 'Remove extra signer',
  'send.active_limit_one':
    'You have {count} active request, the most your plan allows at once. Wait for it to finish or void it if you no longer need it, then send this. Your draft is saved.',
  'send.active_limit':
    'You have {count} active requests, the most your plan allows at once. Wait for one to finish or void one you no longer need, then send this. Your draft is saved.',
  'send.active_limit_upgrade_one': 'Get Lifetime: up to {count} active request',
  'send.active_limit_upgrade': 'Get Lifetime: up to {count} active requests',
  'send.active_limit_waiting': 'See waiting requests',
  'send.free_signer_limit_free':
    'This draft has {count} external signers. Your Free account allows {limit}; the draft is saved. Remove a signer before creating the request.',
  'send.fix_before_sending_title': 'Fix before sending',
  'send.issue.no_external_signer': 'Add someone to sign this document.',
  'send.issue.unassigned_required_one': '1 required field has no signer assigned.',
  'send.issue.unassigned_required_other': '{count} required fields have no signer assigned.',
  'send.issue.invalid_owner_one': '1 field belongs to a recipient who was removed.',
  'send.issue.invalid_owner_other': '{count} fields belong to a recipient who was removed.',
  'send.issue.unknown': 'Something in this draft needs attention before it can be sent.',
  'send.issue.action_add_recipient': 'Add recipient',
  'send.issue.action_assign_fields': 'Assign fields',
  'send.issue.action_open_draft': 'Open draft',
  'send.failed': 'We could not send this request.',
  'send.start': 'Send for signature',
  'send.checking_plan': 'Checking your plan…',
  'send.review_problems': 'Review what needs fixing',
  'send.start_manual': 'Create link',
  'send.start_managed': 'Send with {product}',
  'send.start_manual_sign': 'Sign and create link',
  'send.start_managed_sign': 'Sign and send',
  'send.start_title': 'Send for signature',
  'send.start_description':
    'Choose the document you want signed. Next you add who signs and where.',
  'send.replace_title': 'Change document',
  'send.replace_description':
    'Choose the new file. Who signs, your message and the fields stay with your draft.',
  'send.replace_back': 'Back to your draft',
  'send.continue_title': 'Continue a draft',
  'send.continue': 'Continue',
  'send.continue_to': 'To {names}',
  'send.active_limit_banner_one':
    'You have {count} request waiting, the most your plan allows at once. You can prepare this now and send it when that one finishes.',
  'send.active_limit_banner':
    'You have {count} requests waiting, the most your plan allows at once. You can prepare this now and send it when one finishes.',
  'send.step_review': 'Review and send',
  'send.step_review_short': 'Review',
  'send.steps_progress': 'Step {current} of {total}',
  'send.review_page_title': 'Review and send',
  'send.review_people': 'People',
  'send.edit': 'Edit',
  'send.edit_people': 'Edit who signs',
  'send.edit_message': 'Edit the message',
  'send.person_signs': 'Signs',
  'send.person_you_first': 'You · sign first',
  'send.person_you_last': 'You · sign last',
  'send.person_signs_first': 'Signs first',
  'send.person_signs_last': 'Signs last',
  'send.person_no_email': 'No email',
  'send.person_no_name': 'No name yet',
  'send.draft_missing':
    "This draft isn't on this device. Drafts stay on the device where they were made, so open it there.",
  'send.back_to_start': 'Choose something to send',
  'send.order_in_turn': 'One after another: {names}.',
  'send.order_together': 'Everyone gets their own link at the same time.',
  'send.review_link_until': 'Works until {date} ({days})',
  'send.review_message': 'Your message',
  'send.review_note_manual': 'Added to the message you share with the link.',
  'send.review_note_managed':
    "Emails we send use a standard invitation, so your words aren't in them. They're added when you share a link yourself.",
  'send.your_part': 'Your part',
  'send.your_part_page': 'Page {page}',
  'send.your_part_none': 'You have no fields here. Signing first approves the document as it is.',
  'send.your_part_check': 'Check your fields',
  'send.sender_name_needed': 'Add your name above. It is recorded with your signature.',
  'send.sender_name_needed_from': 'Add your name above so recipients know who the request is from.',
  'send.sender_first_note':
    'You sign first. “{action}” asks you to adopt your signature, then the others get their links.',
  'send.sender_last_note':
    'You sign last. When everyone else has signed, open the request to add your signature.',
  'send.sending_managed': 'Sending… {product} emails your signers as soon as this is done.',
  'send.sending_manual': 'Creating your signing link… This can take a few seconds.',
  'send.progress_preparing': 'Preparing the request',
  'send.progress_securing': 'Encrypting the document on this device',
  'send.progress_uploading': 'Uploading the encrypted copy',
  'send.progress_activating': 'Starting the request',
  'send.progress_activating_manual': 'Starting the request and making the links',
  'send.delivery_heading': 'Choose how to send it',
  'send.delivery_question': 'How would you like to send this for signature?',
  'send.delivery_managed_only':
    "With Lifetime, {product} emails every request. If an email can't be delivered, the request's page lets you share the link yourself.",
  'send.delivery_managed_title': 'Let {product} handle it',
  'send.delivery_managed_trial': 'We email each signer, remind them and track progress.',
  'send.delivery_managed_unknown': '{product} emails each signer their link and sends reminders.',
  'send.delivery_managed_credit_one':
    'We email each signer, remind them and track progress. Uses 1 of your {count} delivery.',
  'send.delivery_managed_credit':
    'We email each signer, remind them and track progress. Uses 1 of your {count} deliveries.',
  'send.delivery_managed_locked':
    'We email each signer, remind them and track progress. Part of Lifetime.',
  'send.delivery_manual_title': 'Share it myself',
  'send.delivery_manual_hint':
    'Get a link for each signer and send it your way: email, WhatsApp, text and more.',
  'send.delivery_badge_free': 'Free',
  'send.delivery_badge_trial': 'First send free',
  'send.delivery_trial_title': 'Your first send is on us',
  'send.delivery_trial_body':
    '{product} emails and reminds everyone, and you can add up to {signers} signers, copies and a signing order. After this, Free sends are shared by you.',
  'send.delivery_trial_kept': 'Sharing it yourself keeps your free first send for another time.',
  'send.compare_title': 'Quick comparison',
  'send.compare_manual': 'Share it myself',
  'send.compare_managed': '{product} handles it',
  'send.compare_who': 'Who sends it',
  'send.compare_who_manual': 'You, any way you like',
  'send.compare_who_managed': '{product}, by email',
  'send.compare_reminders': 'Reminders',
  'send.compare_reminders_manual': 'You follow up',
  'send.compare_reminders_managed': 'Automatic',
  'send.compare_signers': 'Signers',
  'send.compare_signers_manual': '{count} at a time',
  'send.compare_signers_managed': 'Up to {count}',
  'send.compare_link': 'Link lasts',
  'send.compare_link_manual': '{days} days',
  'send.compare_link_managed': 'Up to {days} days',
  'send.compare_cost': 'Cost',
  'send.compare_cost_manual': 'Free',
  'send.compare_cost_trial': 'Free this time',
  'send.compare_cost_lifetime': 'Lifetime',
  'send.compare_cost_unknown': 'Trial or credit',
  'send.delivery_no_credits':
    "You've used all your deliveries, so share the link yourself this time.",
  'send.delivery_buy_pack': 'Buy more deliveries',
  'send.limits_title': 'Before you can send',
  'send.limit_signers':
    'Free sends to {limit} signer at a time, and this draft has {count}. Your draft is saved.',
  'send.limit_cc_one':
    "Sending a copy to {names} is part of Lifetime. You can share the signed PDF yourself when it's done.",
  'send.limit_cc':
    "Sending copies to {names} is part of Lifetime. You can share the signed PDF yourself when it's done.",
  'send.remove_cc': 'Remove {names}',
  'send.limits_upgrade': 'See what Lifetime adds',
  'send.limits_use_trial': 'Let {product} send it, free this time',
  'send.plan_banner_free': 'Free plan: {free} external signer at a time.',
  'send.plan_banner_upgrade_hint': 'Upgrade to add more signers and let {product} handle delivery.',
  'send.plan_banner_share_hint': 'You share each signing link yourself.',
  'send.plan_banner_upgrade': 'Upgrade',
  'send.plan_banner_lifetime': 'Lifetime: {count} delivery credits left.',
  'send.plan_banner_lifetime_one': 'Lifetime: 1 delivery credit left.',
  'send.plan_banner_lifetime_hint':
    '{product} emails the request and reminds each signer; each request uses one credit.',
  'send.issue.signer_without_signature_one': '{names} has nowhere to sign.',
  'send.issue.signer_without_signature_other': '{names} have nowhere to sign.',
  'send.issue.sender_without_signature':
    'You also sign, but there is nowhere for you to sign. Add a signature box for yourself.',
  'send.issue.action_add_signature': 'Add a signature box',
  'send.issue.recipient_name_missing_one': 'One person has no name yet.',
  'send.issue.recipient_name_missing_other': '{count} people have no name yet.',
  'send.issue.action_add_name': 'Add names',
  'send.issue.managed_email_missing_one': 'Add an email for {names} so we can send it.',
  'send.issue.managed_email_missing_other': 'Add emails for {names} so we can send it.',
  'send.issue.action_add_email': 'Add email',
  'send.issue.share_myself_instead': 'Share it myself instead',
  'send.issue.cc_needs_lifetime_one': 'A copy for {names} is part of Lifetime.',
  'send.issue.cc_needs_lifetime_other': 'Copies for {names} are part of Lifetime.',
  'send.sent_managed_title': 'Sent',
  'send.sent_managed_description':
    "We're emailing the invitation now. The request page shows when each person opens and signs it.",
  'send.sent_managed_signed_first':
    "You signed. We're emailing {names} their link now. The request page shows when they open and sign it.",
  'send.sent_managed_to': '{name} · {email}',
  'send.sent_reminders': 'Reminders: after {first}, then every {repeat}.',
  'send.sent_reminders_off': 'No automatic reminders.',
  'send.sent_trial_used':
    'That was your free first send. From now on, Free requests are shared by you.',
  'send.sent_trial_used_lifetime':
    'That was your free first send. From now on, Free requests are shared by you, or Lifetime sends them ({credits} emailed sends included).',
  'send.sent_credits_left_one': '{count} delivery left.',
  'send.sent_credits_left': '{count} deliveries left.',
  'send.sent_link_until': 'The link works until {date}.',
  'send.share_title_one': 'Share the link with {name}',
  'send.share_title_many': "Share each person's link",
  'send.created_title': 'Request created',
  'send.share_description':
    "Send it any way you like. Nothing is emailed for you. The request page shows when it's opened and signed.",
  'send.share_signed_first': 'You signed. Now share the link with {names}.',
  'send.share': 'Share…',
  'send.copy_message': 'Copy message',
  'send.copied': 'Copied',
  'send.link_label': 'Signing link for {name}',
  'send.share_message_label': 'Message for {name}',
  'send.each_own_link':
    "Each person has their own link. Don't forward one person's link to someone else.",
  'send.qr_caption': 'Scan to open the signing page. Works until {date}.',
  'send.next_after':
    '{next} gets their link after {current} signs. Share it from the request page then.',
  'send.done': 'Done',
  'send.copy_link': 'Copy link',
  'share.message': 'Hi {name}, please review and sign "{title}". The link works until {date}.',
  'share.message_no_expiry': 'Hi {name}, please review and sign "{title}".',
  'share.message_from':
    'Hi {name}, {sender} asked you to review and sign "{title}". The link works until {date}.',
  'share.message_from_no_expiry': 'Hi {name}, {sender} asked you to review and sign "{title}".',
  'share.message_ask_from': '{sender} asked you to review and sign "{title}".',
  'share.message_ask': 'Please review and sign "{title}".',
  'share.message_until': 'The link works until {date}.',
  'send.show_qr': 'Show QR code',
  'share.via': 'Share via',
  'share.more': 'More',
  'share.qr_short': 'QR code',
  'share.sheet_title': 'Share the link',
  'share.sheet_title_named': "Share {name}'s link",
  'share.channel_email': 'Email',
  'share.channel_sms': 'SMS',
  'share.email_title': 'Send by email',
  'share.email_office365': 'Outlook (work or school)',
  'share.email_app': 'Mail app on this device',
  'share.all_apps': 'All apps',
  'share.other_apps': 'Other apps on this device',
  'share.paste_note': 'Message and link copied. Paste them into {app}.',
  'share.paste_manual':
    "The message couldn't be copied. Copy it from here, then paste it into {app}.",
  'send.qr_label': 'QR code for {name}’s signing link',
  'send.observed': 'What we observed',
  'send.link_copied': 'Link copied on this device',
  'send.qr_shown': 'QR code shown on this device',
  'send.no_delivery_claim':
    'Sharing is yours to do: we record that you shared, never that anyone received or opened it. Delivery appears only when the signing page is opened.',
  'recipient.signing_title': 'Review and sign',
  'recipient.loading': 'Loading…',
  'recipient.offline': 'We could not reach the signing service. Your work is safe; try again.',
  'recipient.submitted': 'You already signed this document.',
  'recipient.receipt_title': 'Signature received',
  'recipient.receipt_body': 'Your signature is received.',
  'recipient.final_title': 'Your signed copy',
  'recipient.final_hint': 'The signed copy of an agreement was shared with you.',
  'recipient.final_failed': 'Your signed copy couldn’t be downloaded.',
  'recipient.receipt_signed_on': 'You signed this on {date}.',
  'recipient.receipt_complete': 'This agreement is complete.',
  'recipient.receipt_reference': 'Receipt reference: {code}',
  'recipient.next_title': 'What happens next',
  'recipient.next_waiting':
    'Others still need to sign. When everyone has, {sender}’s device finishes the agreement and makes the signed copy.',
  'recipient.next_waiting_one':
    '{name} still needs to sign. After that, {sender}’s device finishes the agreement and makes the signed copy.',
  'recipient.next_waiting_sender':
    '{sender} still needs to sign. After that, their device finishes the agreement and makes the signed copy.',
  'recipient.next_finishing':
    'Everyone has signed. The agreement is finished on {sender}’s device the next time they open {product}.',
  'recipient.next_email': 'We’ll email you a link to your signed copy as soon as it’s ready.',
  'recipient.next_come_back':
    'Keep this link. Come back to it to download your signed copy: it stays here for {days} after the agreement is finished.',
  'recipient.next_come_back_plain':
    'Keep this link and come back to it later to download your signed copy.',
  'recipient.next_ready':
    'Download your signed copy and keep it somewhere safe. It’s available here until {date}.',
  'recipient.next_closed':
    'The signed copy was available here until {date}. Ask {sender} for a copy.',
  'recipient.next_ended':
    'This request ended before everyone signed, so there is no signed copy. Contact {sender} if you have questions.',
  'recipient.sender_fallback': 'the sender',
  'recipient.final_download': 'Download signed PDF',
  'recipient.final_downloading': 'Getting your copy…',
  'recipient.final_not_ready': 'The signed copy isn’t ready yet. Come back to this link later.',
  'recipient.final_closed':
    'This copy is no longer available here. Ask the person who sent it for the file.',
  'recipient.final_no_key':
    'This link is incomplete. Open the full link from your email or message.',
  'recipient.cc_intro': '{sender} shared the signed copy of “{title}” with you.',
  'recipient.cc_available_until': 'Available until {date}. Download it to keep a copy.',
  'recipient.cc_title': 'Signed copy',
  'recipient.cc_invalid': 'This link doesn’t work.',
  'recipient.cc_invalid_next':
    'Check that you opened the whole link from your email or message. If it still doesn’t work, ask the person who shared it for a new one.',
  'recipient.from_sender': 'From {sender}',
  'recipient.no_key':
    'This link is incomplete, so the document can’t be opened. Open the full link from your email or message, or ask the sender to send it again.',
  'recipient.open_failed': 'We could not open this document. Ask the sender to resend it.',
  'recipient.page_of': 'Page {page} of {total}',

  'recipient.progress': '{filled} of {required} required fields completed',
  'recipient.required_fields': 'Required fields',
  'recipient.previous_page': 'Previous page',
  'recipient.next_page': 'Next page',
  'recipient.field_required': '{label} is required.',
  'recipient.signature_apply': 'Use this signature',
  'recipient.signature_applied': 'Signature added',
  'recipient.signature_label': 'Draw your signature',
  'recipient.signature_clear': 'Clear',
  'recipient.submit_failed':
    'Your signature couldn’t be submitted. Check your connection and try again.',
  'recipient.consent_intro': 'By continuing you agree to sign this document electronically.',
  'recipient.language_label': 'Language',
  'recipient.attachments_title': 'Attachments',
  'recipient.attachments_progress': '{collected} of {required} required attachments collected',
  'recipient.attachment_required': 'required',
  'recipient.attachment_limits': '{types} · up to {files} · up to {megabytes} MB each',
  'recipient.attachment_choose': 'Choose a file',
  'recipient.attachment_remove': 'Remove',
  'recipient.attachment_stored': 'added',
  'recipient.attachment_pending': 'not stored yet',
  'recipient.attachment_collected': 'Attachment stored.',
  'recipient.attachment_kept':
    'Files you added are kept with your signature and can’t be removed here. If you added the wrong file, tell {sender}.',
  'recipient.attachment_rejected': 'That file was refused. Choose a different file.',
  'recipient.attachment_retry':
    'The upload did not finish. Try again — the same file will not be duplicated.',
  'recipient.attachment_blocks_finish': 'This attachment is required before you can finish.',
  'recipient.not_yet': 'This request is still with an earlier signer.',
  'recipient.not_yet_hint':
    'Your link is valid and nothing has changed: this document is signed in order, so it becomes yours once the previous step is complete. Reload to check again.',
  'recipient.consent_locale_note':
    'This statement is shown in {locale}, the closest language it is available in.',
  'recipient.submit': 'Submit signature',
  'recipient.submitting': 'Sending your signature securely…',
  'recipient.submit_busy': 'Submitting…',
  'recipient.submit_retry': 'Try again',
  'recipient.submit_link_changed':
    'This link was replaced or the request changed, so this signature can’t be sent. Use the newest link {sender} sent you.',
  'recipient.submit_expired': 'This link expired before you finished. Ask {sender} for a new link.',
  'recipient.declined': 'This request was declined.',
  'recipient.from_sender_expires': 'From {sender} · Link expires {date}',
  'recipient.consent_title': 'Agree to sign electronically',
  'recipient.consent_lead':
    '{sender} sent you “{title}” to review and sign. Before you start, read and accept this statement.',
  'recipient.consent_continue': 'Continue',
  'recipient.disclosure_summary': 'About signing electronically',
  'recipient.disclosure_record':
    'Your acceptance is kept with your signature, together with the date, the time and the version of this statement.',
  'recipient.disclosure_decline':
    'You don’t have to sign electronically. To say no, open Other options and choose Decline to sign. {sender} will see that you declined.',
  'recipient.disclosure_copy':
    'When everyone has signed, you can get the signed copy. For a paper copy or questions about the document, contact {sender}.',
  'recipient.disclosure_privacy': 'Privacy policy',
  'recipient.disclosure_terms': 'Terms of use',
  'recipient.consent_unavailable':
    'This page can’t show the consent statement it should. Reload the page; if it keeps happening, ask the sender for a new link.',
  'recipient.next': 'Next',
  'recipient.finish_ready': 'Ready to finish.',
  'recipient.remaining_one': '1 required field left',
  'recipient.remaining_other': '{count} required fields left',
  'recipient.field_error_required': 'This field is required.',
  'recipient.field_error_sign': 'Tap here to sign.',
  'recipient.field_error_check': 'Check this box to continue.',
  'recipient.field_error_too_long': 'This is too long.',
  'recipient.field_error_email': 'Enter an email address, like name@example.com.',
  'recipient.field_error_number': 'Enter a number.',
  'recipient.field_error_letters': 'Use letters only.',
  'recipient.field_error_date': 'Enter a date.',
  'recipient.field_error_zip': 'Enter a 5-digit ZIP code.',
  'recipient.field_error_zip4': 'Enter a ZIP+4 code, like 12345-6789.',
  'recipient.field_error_ssn': 'Enter it as 123-45-6789.',
  'recipient.field_error_format': 'This doesn’t match the format the sender asked for.',
  'recipient.field_error_formula': 'Fill in the fields this total uses.',
  'recipient.date_signed_hint': 'Filled in with today’s date when you finish',
  'recipient.locked_hint': 'Set by the sender',
  'recipient.formula_hint': 'Calculated from your entries',
  'recipient.previous_field': 'Previous field',
  'recipient.optional_left_one': '1 optional field empty',
  'recipient.optional_left_other': '{count} optional fields empty',
  'recipient.optional_review': 'Review',
  'recipient.todo_title': 'Still to do',
  'recipient.todo_item': 'Page {page} · {label}',
  'recipient.draft_restored': 'We kept what you filled in on this device.',
  'recipient.render_failed':
    'This page could not be displayed. Update Android System WebView or your browser. On iPhone, update iOS, then reopen this document.',
  'recipient.document_label': 'Document',
  'recipient.page_text': 'Text of page {page}',
  'recipient.sign_here': 'Sign here',
  'recipient.initial_here': 'Initial here',
  'recipient.sign_short': 'Sign',
  'recipient.initial_short': 'Initial',
  'recipient.adopt_title': 'Adopt your signature',
  'recipient.adopt_title_both': 'Adopt your signature and initials',
  'recipient.adopt_title_initials': 'Adopt your initials',
  'recipient.adopt_draw': 'Draw',
  'recipient.adopt_type': 'Type',
  'recipient.adopt_upload': 'Upload',
  'recipient.adopt_upload_choose': 'Choose an image',
  'recipient.adopt_upload_hint': 'A PNG or JPEG of your mark on a plain background.',
  'recipient.adopt_upload_for_signature': 'This image becomes your signature.',
  'recipient.adopt_upload_for_initials': 'This image becomes your initials.',
  'recipient.adopt_upload_for_both':
    'This image becomes your signature. Your initials are typed below.',
  'recipient.adopt_upload_failed':
    "That image couldn't be used. Choose a PNG or JPEG with a visible mark.",
  'recipient.adopt_name_label': 'Your name',
  'recipient.adopt_initials_label': 'Initials',
  'recipient.adopt_preview_signature': 'Your signature appears here',
  'recipient.adopt_preview_initials': 'Your initials',
  'recipient.initials_draw_label': 'Draw your initials',
  'recipient.adopt_statement':
    'By selecting Adopt and sign, I agree that this mark is my electronic signature and initials on this document.',
  'recipient.adopt_statement_signature':
    'By selecting Adopt and sign, I agree that this mark is my electronic signature on this document.',
  'recipient.adopt_statement_initials':
    'By selecting Adopt and sign, I agree that these are my electronic initials on this document.',
  'recipient.adopt_and_sign': 'Adopt and sign',
  'recipient.adopt_cancel': 'Cancel',
  'recipient.zoom_in': 'Zoom in',
  'recipient.zoom_out': 'Zoom out',
  'recipient.zoom_fit': 'Fit to width',
  'recipient.options': 'Other options',
  'recipient.decline_action': 'Decline to sign',
  'recipient.session_info': 'Session information',
  'recipient.finish_later': 'Finish later',
  'recipient.finish_later_title': 'Finish later?',
  'recipient.finish_later_body':
    'What you typed stays on this device until {date}. Open the same link on this device to carry on. When you come back, you’ll accept the statement and add your signature again.',
  'recipient.finish_later_blocked':
    'This browser isn’t keeping data for this page, so what you typed can’t be kept. If you leave now, you’ll fill it in again when you come back.',
  'recipient.finish_later_confirm': 'Save and leave',
  'recipient.finish_later_saved_title': 'Saved for later',
  'recipient.finish_later_saved':
    'What you typed is kept on this device until {date}. You can close this page. To carry on, open the same link again on this device.',
  'recipient.finish_later_resume': 'Continue signing',
  'recipient.decline_title': 'Decline to sign?',
  'recipient.decline_consequence':
    'Declining ends this request for everyone, and {sender} will see that you declined. You can’t undo this.',
  'recipient.decline_continue': 'Continue to decline',
  'recipient.decline_keep_signing': 'Keep signing',
  'recipient.decline_reason_label': 'Reason (optional, {sender} will see it)',
  'recipient.decline_reason_count': '{count} of {max} characters',
  'recipient.decline_reason_too_long': 'The reason is too long. Shorten it to continue.',
  'recipient.decline_submit': 'Decline',
  'recipient.decline_failed':
    'We couldn’t record that you declined. Check your connection and try again.',
  'recipient.declined_title': 'You declined to sign',
  'recipient.declined_on': 'You declined “{title}” on {date}.',
  'recipient.declined_told':
    '{sender} can see that you declined, and the request has ended for everyone.',
  'recipient.declined_reason': 'Your reason',
  'recipient.declined_next_steps':
    'Nothing else is needed from you. If you change your mind, ask {sender} to send a new request.',
  'recipient.back': 'Back',
  'recipient.session_signer': 'Signing as',
  'recipient.session_sender': 'Sent by',
  'recipient.session_expires': 'Link expires',
  'recipient.session_reference': 'Request reference',
  'recipient.session_copy': 'Copy reference',
  'recipient.session_copied': 'Reference copied',
  'recipient.not_me': 'This isn’t me',
  'recipient.not_me_title': 'Not {name}?',
  'recipient.not_me_body':
    'This request is for {name}. Please don’t sign it. Telling {sender} lets them send it to the right person; it ends this request.',
  'recipient.not_me_continue': 'Tell the sender',
  'recipient.not_me_reason': 'Sent to the wrong person.',
  'recipient.help_title': 'Need help?',
  'recipient.help_render_q': 'The document doesn’t show or looks wrong',
  'recipient.help_render_a':
    'Reload the page. If it still doesn’t show, open the link in another browser, or ask the sender to send it again.',
  'recipient.help_sign_q': 'I can’t add my signature',
  'recipient.help_sign_a':
    'Tap a “Sign here” box, type your name or draw with your finger or mouse, then choose Adopt and sign.',
  'recipient.help_link_q': 'My link expired or doesn’t work',
  'recipient.help_link_a':
    'Links stop working when they expire or are replaced by a newer one. Ask the person who sent it for a new link.',
  'recipient.help_wrong_q': 'This reached the wrong person',
  'recipient.help_wrong_a':
    'Don’t sign it. Open Other options and choose “This isn’t me” to let the sender know.',
  'recipient.help_contact':
    'For questions about the document itself, contact the person who sent it. We can’t see your document.',
  'recipient.help_email': 'Email support',
  'recipient.expired_next': 'Ask the person who sent it for a new link.',
  'recipient.unavailable_next':
    'It may have been replaced by a newer link, or the request was cancelled or declined. If you still need to sign, use the newest message from the sender or ask them for a new link.',
  'recipient.declined_next':
    'Nobody can sign it any more. If that’s unexpected, contact the person who sent it.',
  'recipient.invalid': 'This signing link doesn’t work.',
  'recipient.invalid_next':
    'Check that you opened the whole link from your email or message. If it still doesn’t work, ask the sender for a new one.',
  'recipient.submitted_next': 'Come back to this link later to download your signed copy.',
  'recipient.expired': 'This signing link has expired.',
  'recipient.unavailable': 'This signing link is no longer available.',
} as const

export type MessageKey = keyof typeof EN_MESSAGES

export const ES_MESSAGES: Partial<Record<MessageKey, string>> = {
  'status.draft': 'Borrador',
  'status.sending': 'Enviando',
  'status.send_failed': 'No se pudo enviar',
  'status.waiting': 'Esperando a otros',
  'status.waiting_for': 'Esperando a {name}',
  'status.ready_to_finish': 'Listo para terminar',
  'status.your_turn': 'Te toca firmar',
  'status.completed': 'Completado',
  'status.finish_failed': 'No se pudo terminar',
  'status.declined': 'Rechazado',
  'status.voided': 'Anulado',
  'status.expired': 'Vencido',
  'status.participant_not_yet': 'Aún no es su turno',
  'status.participant_waiting': 'Pendiente de firma',
  'status.participant_signed': 'Firmado',
  'status.participant_declined': 'Rechazó',
  'status.participant_not_signed': 'Sin firmar',
  'status.participant_cc': 'Recibe una copia',
  'insights.in_period': 'En el periodo seleccionado',
  'editor.format.none': 'Ninguno',
  'editor.format.plain_text': 'Texto sin formato',
  'editor.format.number': 'Número',
  'editor.format.currency': 'Moneda',
  'editor.format.percent': 'Porcentaje',
  'editor.format.date': 'Fecha',
  'editor.sign_here': 'Firmar aquí',
  'editor.change_signature': 'Cambiar firma',
  'editor.change_mark': 'Cambiar',
  'editor.adopt_failed': 'No se pudo guardar tu firma en este dispositivo. Inténtalo de nuevo.',
  'editor.adopt_saved_note':
    'Se guarda en este dispositivo para tu próximo documento. Puedes cambiarla en Ajustes.',
  'editor.date_signed_note':
    'Se rellena con la fecha en que terminas, según el calendario de este dispositivo.',
  'editor.discard_draft': 'Eliminar',
  'editor.field_signs_in_person': '{name} firma esto cuando firméis en persona.',
  'editor.first_use_sign':
    'Elige Firma y haz clic donde firmas: se completa tu firma guardada. Haz clic en un cuadro de nombre o texto para escribir.',
  'editor.first_use_sign_touch':
    'Toca Firma y luego donde firmas: se completa tu firma guardada. Toca un cuadro de nombre o texto para escribir.',
  'editor.in_person': 'Firmar en persona',
  'editor.in_person_add': 'Añadir una persona',
  'editor.in_person_remove': 'Quitar',
  'editor.in_person_name_placeholder': 'Nombre de la persona que firma',
  'editor.in_person_default_name': 'Firmante {number}',
  'editor.in_person_done': 'Todos han firmado. Termina para crear la copia firmada.',
  'editor.in_person_empty': 'Añade a cada persona que firmará contigo en este dispositivo.',
  'editor.in_person_exit': 'Salir de la firma en persona',
  'editor.in_person_needs_field':
    '{name} aún no tiene ninguna casilla. En "Colocar para", elige a {name} y toca la página.',
  'editor.in_person_needs_name': 'Indica el nombre de cada persona que firma en persona.',
  'editor.in_person_needs_signer':
    'Primero añade a las personas que firman contigo. Cada una tendrá sus propias casillas en la página.',
  'editor.in_person_start': 'Empezar a firmar en persona',
  'editor.issue.signs_in_person': '{name} firma en persona',
  'editor.issue.signer_no_fields': '{name} aún no tiene nada que firmar',
  'editor.issue.signer_no_name': 'Escribe el nombre de quien firma en persona',
  'editor.owner_sign': 'Quién lo rellena',
  'editor.save_failed': 'Tu último cambio no se pudo guardar en este dispositivo.',
  'editor.suggest_add_all': 'Añadir todo',
  'editor.suggest_add_one': 'Añadir {field}',
  'editor.suggest_dismiss_all': 'Descartar',
  'editor.suggest_dismiss_one': 'Descartar esta sugerencia',
  'editor.suggest_failed':
    'No se pudieron sugerir campos para este documento. Colócalos desde la lista.',
  'editor.suggest_fields': 'Sugerir campos',
  'editor.suggest_found': 'Se encontraron {count} lugares para rellenar, en este dispositivo.',
  'editor.suggest_found_one': 'Se encontró 1 lugar para rellenar, en este dispositivo.',
  'editor.suggest_none':
    'No se encontraron lugares para rellenar. Coloca los campos desde la lista.',
  'editor.sign': 'Firmar',
  'editor.in_person_progress': 'Firmante {index} de {total}',
  'editor.in_person_pass': 'Pasa el dispositivo a {name}',
  'editor.in_person_pass_hint':
    '{name}: lee el documento y marca la declaración de abajo para firmar.',
  'editor.in_person_turn': '{name} está firmando',
  'editor.in_person_resumed':
    'Seguimos donde se quedó la firma. No se perdió nada de lo ya completado.',
  'editor.in_person_consent_needed': 'Marca la declaración de arriba para continuar.',
  'editor.in_person_owner_first':
    'Primero completa tus propias casillas ({field}) y luego empieza a firmar en persona.',
  'editor.in_person_next': 'Listo: pasa a la siguiente persona',
  'editor.in_person_last': 'Listo: terminar la firma',
  'editor.in_person_not_adopted': 'Aún sin adoptar',
  'editor.in_person_adopted': 'Firma adoptada',
  'nav.home': 'Inicio',
  'nav.agreements': 'Acuerdos',
  'count.days': '{count} días',
  'count.years': '{count} años',
  'count.years_one': '1 año',
  'count.days_one': '1 día',
  'count.files': '{count} archivos',
  'count.files_one': '1 archivo',
  'count.pages': '{count} páginas',
  'count.pages_one': '1 página',
  'count.participants': '{count} participantes',
  'count.participants_one': '1 participante',
  'account_action.title': 'Confirma una solicitud de tu asistente de IA',
  'account_action.description': 'Un asistente puede prepararla, pero solo tú puedes completarla.',
  'account_action.your_assistant': 'Tu asistente de IA',
  'account_action.ask_disconnect': '{host} pidió desconectarse. Perderá el acceso a tus acuerdos.',
  'account_action.ask_manage':
    '{host} pidió cambiar lo que puede ver a {count} acuerdos seleccionados.',
  'account_action.ask_manage_one':
    '{host} pidió cambiar lo que puede ver a 1 acuerdo seleccionado.',
  'account_action.ask_export':
    '{host} pidió preparar una exportación de los datos de tu cuenta. No incluye los archivos locales ni tus copias de seguridad.',
  'account_action.ask_delete':
    'La eliminación de la cuenta se hace en su propia página, donde la confirmas con un código.',
  'account_action.expires': 'Esta solicitud caduca {when}.',
  'account_action.reauth_note': 'Te pediremos que vuelvas a iniciar sesión antes de continuar.',
  'account_action.confirm': 'Confirmar',
  'account_action.not_now': 'Ahora no',
  'account_action.to_deletion': 'Ir a eliminar la cuenta',
  'account_action.to_settings': 'Volver a Ajustes',
  'account_action.reauth_needed': 'Vuelve a iniciar sesión para confirmar esta solicitud.',
  'account_action.sign_in_needed': 'Inicia sesión en la cuenta a la que pertenece esta solicitud.',
  'account_action.not_found': 'No se encontró esta solicitud en esta cuenta.',
  'account_action.expired':
    'Esta solicitud ha caducado. Pide a tu asistente que la prepare de nuevo.',
  'account_action.already_done': 'Esta solicitud ya se completó.',
  'account_action.failed': 'No se pudo completar esta solicitud. Inténtalo de nuevo en un momento.',
  'account_action.done_disconnect': '{host} está desconectado.',
  'account_action.done_manage': 'Se actualizó lo que {host} puede ver.',
  'account_action.done_export': 'La exportación de tu cuenta está lista.',
  'ops.health.title': 'Estado del servicio',
  'ops.health.description':
    'Vista de operación de colas, correo, finalización y alertas. No se muestra contenido de documentos.',
  'ops.health.costs': 'Revisión de costes',
  'ops.health.unconfigured':
    'Este despliegue no declara cuentas de operación, así que el estado permanece cerrado.',
  'ops.health.forbidden': 'Esta cuenta no es una cuenta de operación.',
  'ops.health.signed_out':
    'Inicia sesión con una cuenta de operación para ver el estado del servicio.',
  'ops.health.failed': 'No se pudo cargar el estado del servicio.',
  'ops.health.meta': '{environment} · API {version} · correo {provider}',
  'ops.health.kill_switch_on':
    'El interruptor de envío gestionado está activado: se omiten las invitaciones gestionadas.',
  'ops.health.queue_lag': 'Retraso de la cola',
  'ops.health.dead_letters': 'Mensajes fallidos',
  'ops.health.oldest_pending': 'Correo pendiente más antiguo',
  'ops.health.mail': 'Entregados / rebotados / quejas',
  'ops.health.leases': 'Bloqueos de finalización (caducados)',
  'ops.health.overdue': 'Purgas atrasadas',
  'ops.health.errors': 'Errores / solicitudes',
  'ops.health.cost': 'Coste estimado del periodo',
  'ops.health.alerts_title': 'Alertas',
  'ops.health.alerts_firing': 'Activas ahora: {count}',
  'ops.health.alerts_unset':
    'No hay política de alertas declarada, así que todas las alertas indican UNSET.',
  'ops.health.owner': 'Responsable: {owner}',
  'ops.health.top_errors': 'Códigos de error principales',
  'ops.health.isolate_scope': 'Los recuentos cubren una instancia de la API, no toda la flota.',
  'agreements.title': 'Acuerdos',
  'agreements.empty': 'Todavía no hay acuerdos',
  'agreements.count': '{count} acuerdos',
  'agreements.count_one': '1 acuerdo',
  'agreements.search_label': 'Buscar acuerdos',
  'agreements.search_placeholder': 'Buscar por título o nombre',
  'agreements.searching': 'Buscando…',
  'agreements.search_count': '{count} de {total} acuerdos',
  'agreements.search_count_one': '{count} de 1 acuerdo',
  'agreements.search_count_partial': '{count}+ de {total} acuerdos hasta ahora',
  'agreements.search_count_partial_one': '{count}+ de 1 acuerdo hasta ahora',
  'agreements.search_empty': 'Ningún acuerdo coincide con esta búsqueda.',
  'agreements.search_empty_description':
    'Tu biblioteca no ha cambiado. Borra la búsqueda para volver a ver todos los acuerdos.',
  'nav.templates': 'Plantillas',
  'nav.insights': 'Estadísticas',
  'nav.settings': 'Ajustes',
  'nav.more': 'Más',
  'nav.main_label': 'Principal',
  'nav.skip_to_content': 'Saltar al contenido',
  'nav.signed_in_as': 'Sesión iniciada como',
  'nav.more_settings_label': 'De Ajustes',
  'nav.more_my_info': 'Mis datos y firmas',
  'nav.more_plan': 'Plan y créditos',
  'nav.more_backup': 'Copia de seguridad',
  'nav.more_help': 'Ayuda',
  'shell.offline':
    'No tienes conexión. Tus documentos, la firma y las plantillas funcionan como siempre; los envíos y los cambios de cuenta esperan a que vuelvas a tener conexión.',
  'shell.not_found_title': 'Esta página no existe',
  'shell.not_found_body':
    'Puede que el enlace sea antiguo o tenga un error. No ha cambiado nada en este dispositivo.',
  'shell.go_home': 'Ir a Inicio',
  'shell.go_agreements': 'Abrir Acuerdos',
  'shell.page_failed': 'No se pudo cargar esta página.',
  'shell.page_failed_body':
    'Recarga para intentarlo de nuevo. Tus documentos y borradores están guardados en este dispositivo.',
  'shell.page_update': 'Hay una versión nueva.',
  'shell.page_update_body':
    'Recarga para continuar. Tus borradores están guardados en este dispositivo.',
  'shell.page_offline': 'Esta página todavía no está disponible sin conexión.',
  'shell.page_offline_body':
    'Conéctate a internet y recarga. Tus documentos están guardados en este dispositivo.',
  'shell.page_failed_body_recipient': 'Recarga para intentarlo de nuevo.',
  'shell.page_update_body_recipient': 'Recarga para continuar.',
  'shell.page_offline_body_recipient': 'Conéctate a internet y recarga.',
  'shell.storage_title':
    'Este navegador no deja que {product} guarde documentos en este dispositivo.',
  'shell.storage_body':
    'Pasa en una ventana privada, cuando los datos del sitio están bloqueados para {product} o cuando el dispositivo no tiene espacio. Abre {product} en una ventana normal, permite los datos del sitio o libera espacio y recarga.',
  'shell.contact_support': 'Escribir a soporte',
  'shell.new_device_title': 'Tus documentos se quedan en el dispositivo donde los creaste',
  'shell.new_device_body':
    'Este dispositivo empieza vacío. Para traer aquí tus documentos y plantillas, haz una copia de seguridad en tu otro dispositivo y restáurala aquí.',
  'shell.new_device_action': 'Traerlos',
  'shell.dismiss': 'Descartar',
  'common.view_all': 'Ver todo',
  'common.back': 'Atrás',
  'common.close': 'Cerrar',
  'common.reload': 'Recargar',
  'home.sign_document': 'Firmar un documento',
  'home.welcome': 'Bienvenido a {product}',
  'home.welcome_back': 'Hola de nuevo',
  'home.handoff_document_waiting':
    '{host} te ha enviado un documento. Revísalo antes de que se use.',
  'home.handoff_waiting': '{host} está esperando a que elijas un documento.',
  'home.handoff_host_unknown': 'Tu asistente de IA',
  'home.handoff_open': 'Abrir',
  'home.first_run':
    '¿Eres nuevo? Envía un PDF para que otra persona lo firme, firma uno tú mismo o empieza con un acuerdo listo para usar.',
  'home.send_for_signature': 'Enviar para firma',
  'home.use_template': 'Usar una plantilla',
  'recipient.powered_by': 'Con tecnología de {product}',
  'recipient.trust_plain': 'Enlace de firma seguro',
  'recipient.cta_title': '¿Necesitas que te firmen algo?',
  'recipient.cta_lead':
    'Prueba {product} gratis. Firma en tu propio teléfono u ordenador y envía a cualquiera un enlace para firmar.',
  'recipient.cta_point_local': 'Tus documentos se quedan en tu dispositivo',
  'recipient.cta_point_share': 'Envía el enlace por WhatsApp, correo o SMS',
  'recipient.cta_point_once': '¿Necesitas más? Pagas una vez. Sin suscripción.',
  'recipient.cta_try': 'Prueba {product} gratis',
  'recipient.cta_no_account': 'No necesitas una cuenta para empezar.',
  'recipient.cta_quick_sign_in': 'Un inicio de sesión rápido con tu correo. Sin contraseña.',
  'recipient.upgrade_title': 'Consigue {product} Lifetime',
  'recipient.upgrade_lead':
    'Usas {product} Free en este navegador. Con Lifetime, envía a más personas y deja que {product} les escriba y les recuerde.',
  'recipient.upgrade_action': 'Pasar a Lifetime',
  'recipient.open_product': 'Abrir {product}',
  'recipient.lifetime_note': 'Usas {product} Lifetime en este navegador.',
  'common.loading': 'Cargando…',
  'error.notice.what': 'Algo ha salido mal.',
  'error.code_line': 'Código: {code}',
  'error.recovery.retry_later': 'Inténtalo de nuevo en un momento.',
  'error.recovery.fix_input': 'Revisa los valores marcados e inténtalo de nuevo.',
  'error.recovery.reload': 'Recarga la página para continuar.',
  'error.recovery.update_app': 'Actualiza la aplicación e inténtalo de nuevo.',
  'error.recovery.free_a_slot':
    'Anula o espera una solicitud activa, o comparte el enlace tú mismo.',
  'error.recovery.remove_signer':
    'Quita el firmante adicional o mejora el plan para más firmantes.',
  'error.recovery.share_manually': 'Comparte el enlace de firma tú mismo.',
  'error.recovery.remove_signer_free': 'Quita el firmante adicional.',
  'error.recovery.free_limits': 'Continúa dentro de los límites gratuitos.',
  'error.recovery.upgrade': 'Revisa los planes o continúa con los límites gratuitos.',
  'error.recovery.sign_in_again': 'Inicia sesión de nuevo para continuar.',
  'error.recovery.link_account': 'Vincula una cuenta para continuar.',
  'error.recovery.check_connection': 'Comprueba tu conexión e inténtalo de nuevo.',
  'error.recovery.contact_support': 'Contacta con soporte si sigue ocurriendo.',
  'error.recovery.not_authorized':
    'No tienes autorización para eso. Pide al remitente que conceda acceso.',
  'error.recovery.retry_same_request': 'Inténtalo de nuevo con la misma solicitud.',
  'error.notice.help': 'Escribir a soporte sobre este error',
  'purchases.title': 'Plan y créditos',
  'purchases.guest':
    'Estás usando la versión gratuita sin cuenta. Inicia sesión para ver tu plan o restaurar compras.',
  'purchases.plan_free_revoked': 'Tu compra Lifetime se reembolsó, así que estás en Free',
  'purchases.plan_signers_one': 'Hasta {count} firmante por solicitud',
  'purchases.plan_signers': 'Hasta {count} firmantes por solicitud',
  'purchases.plan_requests': '{used} de {limit} solicitudes activas en uso',
  'purchases.plan_window_one': 'Los enlaces de firma duran {count} día',
  'purchases.plan_window': 'Los enlaces de firma duran {count} días',
  'purchases.plan_window_lifetime':
    'Los enlaces de firma duran {days} días, o hasta {max} si lo eliges',
  'purchases.plan_credits_one': 'Te queda {count} crédito de envío',
  'purchases.plan_credits': 'Te quedan {count} créditos de envío',
  'purchases.plan_reserved_one': '({count} reservado para una solicitud en curso)',
  'purchases.plan_reserved': '({count} reservados para solicitudes en curso)',
  'purchases.restored_lifetime': 'Lifetime se restauró en esta cuenta.',
  'purchases.sign_in_again': 'Vuelve a iniciar sesión para ver tu plan y restaurar compras.',
  'purchases.sign_in_to_restore': 'Inicia sesión para restaurar compras',
  'purchases.offline_cached': 'Estás sin conexión. Mostrando tu plan a fecha de {at}.',
  'purchases.plan_name_free': 'Free',
  'purchases.plan_name_lifetime': 'Lifetime',
  'purchases.return_unlocked':
    'Ahora puedes enviar a hasta {signers} firmantes por solicitud, mantener {requests} solicitudes activas, y tienes {credits} créditos de envío.',
  'purchases.return_check_again': 'Comprobar de nuevo',
  'purchases.return_done': 'Listo',
  'purchases.lifetime_active': 'Lifetime está activo en esta cuenta.',
  'purchases.lifetime_revoked': 'La compra Lifetime de esta cuenta se reembolsó o se revocó.',
  'purchases.free_with_credits': 'Tienes créditos de envío disponibles.',
  'purchases.free_without_credits':
    'No quedan créditos de envío. Aún puedes compartir los enlaces de firma tú mismo.',
  'purchases.unknown': 'No pudimos leer tu estado de compra ahora mismo.',
  'purchases.plan_free': 'Estás en Free',
  'purchases.plan_lifetime': 'Tienes Lifetime',
  'purchases.trial_available':
    'Envío por correo gratis: tu primera solicitud se puede enviar por correo a los firmantes.',
  'purchases.trial_reserved': 'Envío por correo gratis: lo está usando una solicitud en curso.',
  'purchases.trial_consumed':
    'Envío por correo gratis: usado. Puedes seguir compartiendo tú los enlaces de firma.',
  'purchases.restore_scope':
    '¿Compraste Lifetime o créditos en otro dispositivo? Restaurar los trae a esta cuenta. No recupera documentos; usa una copia de seguridad para eso.',
  'purchases.restore': 'Restaurar compras',
  'purchases.restored': 'Tus compras se restauraron.',
  'purchases.nothing_to_restore': 'No hay nada nuevo que restaurar. Tu plan está al día.',
  'purchases.restore_failed': 'No se pudieron restaurar las compras.',
  'purchases.offline': 'No se pudo cargar tu plan ahora mismo. Tus documentos no se ven afectados.',
  'purchases.lifetime_offer':
    'Lifetime: ${price} una sola vez, sin suscripción. Hasta {signers} firmantes por solicitud, {requests} solicitudes activas, enlaces de firma más largos y {credits} envíos por correo incluidos.',
  'purchases.lifetime_offer_store':
    'Lifetime: {price} una sola vez, sin suscripción. Hasta {signers} firmantes por solicitud, {requests} solicitudes activas, enlaces de firma más largos y {credits} envíos por correo incluidos.',
  'purchases.upgrade': 'Obtener Lifetime por ${price}',
  'purchases.upgrade_store': 'Obtener Lifetime por {price}',
  'purchases.checkout_not_on_sale': 'Esa compra aún no está disponible. No se cobró nada.',
  'purchases.checkout_failed': 'No se pudo abrir el pago. No se cobró nada.',
  'purchases.store_pending':
    'Tu compra está esperando aprobación. No se desbloquea nada hasta que la tienda la confirme, y aparecerá aquí cuando lo haga.',
  'purchases.store_cancelled': 'Compra cancelada. No se cobró nada.',
  'purchases.store_failed':
    'No se pudo completar la compra. Si la tienda te cobró, Restaurar compras la añade a esta cuenta.',
  'purchases.store_refused':
    'No se pudo añadir esta compra a esta cuenta. Si se compró con otra cuenta, inicia sesión en esa y restaura.',
  'purchases.store_other_account':
    'Una compra en este dispositivo pertenece a otra cuenta, así que no se añadió aquí.',
  'purchases.unavailable':
    'Las compras aún no están disponibles aquí. Las funciones gratuitas siguen funcionando, como firmar en este dispositivo y compartir tú mismo los enlaces de firma.',
  'purchases.return_title': 'Estado de la compra',
  'purchases.verify_pending': 'Comprobando esta compra con el proveedor de pagos…',
  'purchases.return_purchased': 'Compra confirmada. Lifetime está activo en esta cuenta.',
  'purchases.return_pack_purchased':
    'Compra confirmada. Se añadieron créditos de envío a esta cuenta.',
  'purchases.return_pending':
    'El proveedor de pagos todavía no ha confirmado esta compra. No se desbloquea nada hasta que lo haga. Puedes volver a comprobarlo en Compras y créditos.',
  'purchases.return_revoked': 'Esta compra se reembolsó o se revocó. Lifetime ya no está activo.',
  'purchases.return_nothing_to_verify':
    'No hay ninguna compra que confirmar en esta cuenta. No se cobró nada.',
  'purchases.return_canceled':
    'El pago se canceló. No se cobró nada y tu plan gratuito sigue exactamente igual.',
  'purchases.return_plan': 'Tu plan: {plan}',
  'purchases.return_draft_kept': 'Tu trabajo preparado sigue aquí. No se perdió nada.',
  'purchases.return_review':
    'Revisa tu borrador otra vez antes de enviarlo: lo que preparaste superaba los límites del plan gratuito.',
  'purchases.return_continue': 'Continuar donde lo dejé',
  'purchases.return_settings': 'Volver a plan y créditos',
  'purchases.return_unlocked_title': 'Lifetime desbloqueado',
  'purchases.return_continue_draft': 'Continuar con tu borrador',
  'lifetime_offer.title_signers': '¿Necesitas {count} firmantes?',
  'lifetime_offer.title_delivery': '¿Quieres que {product} lo envíe por ti?',
  'lifetime_offer.title_active': '¿Necesitas más solicitudes a la vez?',
  'lifetime_offer.title_copy': '¿Necesitas enviar una copia a alguien?',
  'lifetime_offer.title_order': '¿Necesitas que firmen en orden?',
  'lifetime_offer.title_window': '¿Necesitas un enlace de firma más largo?',
  'lifetime_offer.title_saved': '¿Quieres guardar cómo envías?',
  'lifetime_offer.title_request': '¿Necesitas corregir o ampliar esta solicitud?',
  'lifetime_offer.title_plan': '¿Necesitas más firmantes?',
  'lifetime_offer.title_attachments': '¿Necesitas archivos de tus firmantes?',
  'lifetime_offer.reason_signers': 'Tu plan gratuito envía a {free} firmante a la vez.',
  'lifetime_offer.reason_plan':
    'Tu plan gratuito envía a {free} firmante a la vez, y tú compartes el enlace.',
  'lifetime_offer.reason_attachments':
    'Pedir a los firmantes que suban archivos, como una identificación o un recibo, es parte de Lifetime.',
  'lifetime_offer.reason_copy':
    'Enviar a alguien una copia del documento firmado es parte de Lifetime.',
  'lifetime_offer.reason_delivery':
    'Ya usaste tu envío gratuito por correo. Con Lifetime, {product} envía el enlace de firma por correo y les recuerda por ti.',
  'lifetime_offer.reason_active':
    'El plan gratuito mantiene hasta {free} solicitudes esperando firmas a la vez. Lifetime permite {requests}.',
  'lifetime_offer.reason_order': 'Elegir quién firma primero es parte de Lifetime.',
  'lifetime_offer.reason_window':
    'Los enlaces de firma gratuitos duran {free} días. Con Lifetime duran {days} días por defecto, hasta {max}.',
  'lifetime_offer.reason_saved':
    'Lifetime recuerda quién firma, el orden, el mensaje, los recordatorios y la duración del enlace de cada plantilla.',
  'lifetime_offer.reason_correct':
    'Con Lifetime puedes corregir el nombre o el correo de un firmante después de enviar, sin empezar de nuevo.',
  'lifetime_offer.reason_extend':
    'Con Lifetime puedes dar más tiempo a los firmantes, hasta {max} días desde que lo enviaste.',
  'lifetime_offer.compare_label': 'Gratis y Lifetime comparados',
  'lifetime_offer.compare_free': 'Gratis',
  'lifetime_offer.compare_free_signers': '{free} firmante a la vez',
  'lifetime_offer.compare_free_sharing': 'Tú compartes el enlace',
  'lifetime_offer.compare_free_window': 'Los enlaces duran {days} días',
  'lifetime_offer.compare_lifetime_signers': 'Hasta {signers} firmantes, en orden y con copias',
  'lifetime_offer.compare_lifetime_delivery': '{product} les envía el correo y los recordatorios',
  'lifetime_offer.compare_lifetime_window': 'Enlaces de hasta {max} días',
  'lifetime_offer.compare_lifetime_active': 'Hasta {requests} solicitudes a la vez',
  'lifetime_offer.upgrade': 'Pasar a Lifetime',
  'lifetime_offer.benefits_title': 'Pasa a {product} Lifetime',
  'lifetime_offer.benefits_intro':
    'Consigue la flexibilidad que necesitas. Un solo pago, sin suscripción.',
  'lifetime_offer.benefit_signers_title': 'Añade más de {free} firmante',
  'lifetime_offer.benefit_signers':
    'Envía una solicitud a hasta {signers} personas, a la vez o por turnos, con copias.',
  'lifetime_offer.benefit_delivery_title': 'Deja que {product} envíe y recuerde',
  'lifetime_offer.benefit_delivery':
    'Enviamos a cada firmante su enlace por correo y se lo recordamos. Incluye {credits} envíos por correo.',
  'lifetime_offer.benefit_more_title': 'Enlaces más largos, más a la vez',
  'lifetime_offer.benefit_more':
    'Los enlaces duran hasta {max} días, con hasta {requests} solicitudes en espera a la vez.',
  'lifetime_offer.benefit_once_title': 'Compra única',
  'lifetime_offer.benefit_once':
    '{price} una vez. Sin suscripción, nunca se cobra nada automáticamente.',
  'lifetime_offer.continue': 'Continuar',
  'lifetime_offer.maybe_later': 'Quizás más tarde',
  'lifetime_offer.free_stays':
    'Firmar, compartir enlaces tú mismo y ver quién ha abierto y firmado siguen siendo gratis.',
  'lifetime_offer.price': '{price} una vez. Sin suscripción.',
  'lifetime_offer.keep_one_signer': 'Mantener 1 firmante',
  'lifetime_offer.share_myself': 'Lo comparto yo',
  'lifetime_offer.not_now': 'Ahora no',
  'lifetime_offer.save_failed':
    'No se pudo guardar tu borrador, así que la compra no empezó. No se cobró nada.',
  'lifetime_offer.resumed': 'Ya iniciaste sesión. Todavía no se cobró nada.',
  'lifetime_offer.tag': 'Lifetime',
  'lifetime_offer.quiet_signers': 'Más de un firmante es parte de Lifetime.',
  'lifetime_offer.quiet_copy': 'Las copias son parte de Lifetime.',
  'lifetime_offer.quiet_delivery': 'El envío por correo es parte de Lifetime.',
  'lifetime_offer.quiet_active': 'Más solicitudes a la vez es parte de Lifetime.',
  'lifetime_offer.quiet_order': 'El orden de firma es parte de Lifetime.',
  'lifetime_offer.quiet_window': 'Los enlaces más largos son parte de Lifetime.',
  'lifetime_offer.quiet_saved': 'Los ajustes de envío guardados son parte de Lifetime.',
  'lifetime_offer.quiet_correct': 'Corregir a un firmante es parte de Lifetime.',
  'lifetime_offer.quiet_extend': 'Dar más tiempo es parte de Lifetime.',
  'lifetime_offer.quiet_plan': 'Más firmantes y el envío por correo son parte de Lifetime.',
  'lifetime_offer.quiet_attachments': 'Pedir archivos es parte de Lifetime.',
  'lifetime_offer.locked_order': 'Orden de firma',
  'lifetime_offer.locked_window': 'Enlace de firma más largo',
  'lifetime_offer.locked_saved': 'Ajustes de envío guardados',
  'lifetime_offer.locked_attachments': 'Pedir archivos a los firmantes',
  'signin.error_verification_failed':
    'No pudimos verificar esta solicitud de inicio de sesión. Espera a que termine la comprobación de seguridad y vuelve a intentarlo. Si sigue fallando, recarga la página.',
  'settings.backup_restore_done_items': 'Se restauró: {items}.',
  'settings.backup_restore_done_empty': 'Restaurado. La copia no tenía documentos ni plantillas.',
  'settings.backup_restore_done_nothing_new': 'No había nada nuevo que restaurar.',
  'settings.backup_restore_already_here_one': '1 elemento ya estaba en este dispositivo.',
  'settings.backup_restore_already_here': '{count} elementos ya estaban en este dispositivo.',
  'settings.backup_restore_open_agreements': 'Abrir Acuerdos',
  'settings.date_format_device': '{example} (este dispositivo)',
  'settings.date_format_option': '{example} ({format})',
  'settings.my_info_suggested':
    'Se rellenó con tu firma adoptada y el correo con el que iniciaste sesión. Guarda para conservarlo.',
  'settings.my_info_suggested_email':
    'Se rellenó tu correo de inicio de sesión. Guarda para conservarlo.',
  'settings.account_deletion_scope':
    'Se eliminan tu cuenta, sus compras y créditos, y todo lo que publicaste en tu espacio de trabajo en línea.',
  'settings.account_retention_financial':
    'Los registros de pagos y créditos se conservan el tiempo necesario por motivos fiscales, de reembolso y como prueba en caso de disputa.',
  'settings.account_retention_audit':
    'Se conserva un registro mínimo de seguridad y auditoría el tiempo necesario por motivos fiscales, de reembolso y como prueba en caso de disputa.',
  'settings.account_retention_sealed':
    'Los acuerdos que otras personas ya firmaron siguen a su disposición durante el plazo habitual.',
  'settings.account_retention_days': 'Se conserva {days}.',
  'settings.account_retention_lifecycle': 'Se conserva mientras dure el registro firmado.',
  'settings.account_delete_requests_checking': 'Comprobando las solicitudes de tu cuenta…',
  'settings.account_delete_requests_unknown':
    'No pudimos comprobar tus solicitudes ahora mismo. Las que aún esperan firmas se anularán.',
  'settings.account_delete_requests_active_one':
    '1 solicitud que aún espera firmas se anulará, y sus firmantes lo verán.',
  'settings.account_delete_requests_active':
    '{count} solicitudes que aún esperan firmas se anularán, y sus firmantes lo verán.',
  'settings.account_delete_requests_sealed_one':
    '1 solicitud terminada sigue disponible para sus firmantes durante el plazo habitual.',
  'settings.account_delete_requests_sealed':
    '{count} solicitudes terminadas siguen disponibles para sus firmantes durante el plazo habitual.',
  'settings.account_delete_requests_none': 'Tu cuenta no tiene solicitudes que anular.',
  'settings.account_reconcile': '¿Problemas con una compra?',
  'settings.account_reconcile_description':
    '¿Pagaste y no lo ves? Escribe el recibo de la tienda o la referencia de pago de tu correo de recibo y comprobaremos a qué cuenta pertenece.',
  'settings.account_reconcile_kind': 'Tipo de referencia',
  'settings.account_reconcile_kind_receipt': 'Recibo de tienda',
  'settings.account_reconcile_kind_session': 'Referencia de pago',
  'settings.account_reconcile_reference': 'Recibo o referencia de pago',
  'settings.account_reconcile_submit': 'Comprobar compra',
  'settings.account_reconcile_need_reference':
    'Escribe primero el recibo de la tienda o la referencia de pago.',
  'settings.account_reconcile_on_this_account':
    'Esta compra ya está en esta cuenta. No hace falta nada más.',
  'settings.account_reconcile_other_account':
    'Esta compra pertenece a otra cuenta. Para usarla, inicia sesión con el correo de esa cuenta.',
  'settings.account_reconcile_not_recorded':
    'No encontramos esta compra. Revisa la referencia en tu correo de recibo.',
  'settings.account_reconcile_not_confirmed':
    'El proveedor de pagos aún no ha confirmado esta compra. Inténtalo de nuevo en unos minutos.',
  'settings.account_reconcile_failed': 'No se pudo comprobar la compra.',
  'settings.account_switch_clear_local':
    'Este dispositivo borra el estado local de la cuenta que el servicio indica.',
  'settings.account_switch_keep_vault': 'Este dispositivo conserva su bóveda local.',
  'settings.account_switch_attach_explicit':
    'Vincular una bóveda existente a otra cuenta es una acción aparte y explícita.',
  'settings.account_export_done': 'Los datos de tu cuenta están listos ({at}).',
  'settings.account_export_summary':
    'Incluye {requests} solicitudes, {purchases} compras y {items} elementos del espacio de trabajo.',
  'settings.account_export_download': 'Descargar los datos de la cuenta',
  'settings.account_export_backup': 'Hacer copia de los documentos',
  'settings.account_export_truncated_requests':
    'El archivo incluye tus {count} solicitudes más recientes. Escribe a soporte para las anteriores.',
  'settings.account_export_truncated_workspace':
    'El archivo incluye tus {count} elementos del espacio de trabajo más recientes. Escribe a soporte para los anteriores.',
  'settings.account_export_failed': 'No se pudieron exportar los datos de tu cuenta.',
  'settings.account_delete_reauth':
    'Para proteger tu cuenta, puede que enviemos un código a {email} para confirmar que eres tú.',
  'settings.account_delete_confirm_label': 'Escribe DELETE para confirmar',
  'settings.account_delete_confirm': 'Eliminar mi cuenta',
  'settings.account_delete_receipt':
    'Tu cuenta se eliminó. Recibo {receipt}: indícalo si escribes a soporte.',
  'settings.account_delete_receipt_at': 'Eliminada el {at}.',
  'settings.account_delete_replay':
    'Esta cuenta ya estaba eliminada. Es la misma eliminación, no una nueva.',
  'settings.account_delete_recheck': 'Comprobar de nuevo',
  'settings.account_delete_recheck_signed_out':
    'Este dispositivo no tiene la sesión iniciada, así que la eliminación no se puede volver a comprobar aquí. El recibo de arriba es tu constancia.',
  'settings.account_delete_disposition':
    'Solicitudes anuladas: {requests}. Se cerraron las sesiones y las conexiones de IA.',
  'settings.account_delete_outside_erasure':
    'Los archivos de tus dispositivos y las copias que guardaste no se ven afectados; elimínalos tú si quieres.',
  'settings.account_delete_failed': 'No se pudo eliminar tu cuenta.',
  'settings.account_delete_web_hint':
    '¿No tienes la aplicación a mano? También puedes eliminar tu cuenta en un navegador.',
  'settings.account_why_sign_in':
    'Inicia sesión para enviar documentos a firmar por correo y llevar contigo tu plan y tus compras. Todo lo demás funciona sin cuenta.',
  'settings.account_signed_in': 'Sesión iniciada.',
  'settings.account_signed_in_as': 'Sesión iniciada como {email}',
  'settings.account_card_title': 'Inicio de sesión',
  'settings.account_session_expired':
    'Tu sesión caducó. Vuelve a iniciar sesión para enviar a firmar o gestionar compras.',
  'settings.account_sign_in_again': 'Volver a iniciar sesión',
  'settings.account_new_device_one':
    'Este dispositivo aún no tiene documentos. Tu cuenta tiene {count} solicitud en el servicio; los documentos solo vuelven desde una copia de seguridad.',
  'settings.account_new_device':
    'Este dispositivo aún no tiene documentos. Tu cuenta tiene {count} solicitudes en el servicio; los documentos solo vuelven desde una copia de seguridad.',
  'settings.account_new_device_empty':
    'Este dispositivo aún no tiene documentos. Los documentos vuelven solo desde una copia de seguridad, nunca al iniciar sesión.',
  'settings.confirm_identity_title': 'Confirma que eres tú',
  'settings.confirm_identity_body':
    'Por tu seguridad, enviaremos un código de 6 dígitos a {email}.',
  'settings.confirm_identity_send': 'Enviarme un código',
  'settings.confirm_identity_sent': 'Enviamos un código a {email}. Escríbelo para continuar.',
  'settings.confirm_identity_confirm': 'Confirmar y continuar',
  'settings.confirm_identity_resend': 'Enviar otro código',
  'settings.confirm_identity_sign_in':
    'Por tu seguridad, vuelve a iniciar sesión para continuar. Volverás aquí.',
  'settings.account_sign_out_of': 'Cerrar sesión de {email}',
  'settings.sign_out_note':
    'Tus documentos, plantillas y copias de seguridad se quedan en este dispositivo.',
  'settings.sign_out_confirm_title': '¿Cerrar sesión?',
  'settings.sign_out_confirm_body':
    'Tus documentos se quedan en este dispositivo. Para volver a entrar, necesitarás un código que enviaremos a {email}.',
  'settings.sign_out_confirm_body_no_email':
    'Tus documentos se quedan en este dispositivo. Para volver a entrar, necesitarás un código que te enviaremos por correo.',
  'settings.sign_out_confirm_cancel': 'Seguir con la sesión iniciada',
  'settings.sign_out_note_gated':
    'Tus documentos se quedan en este dispositivo y vuelven cuando inicies sesión de nuevo. Otra cuenta que inicie sesión aquí tiene su propio espacio aparte.',
  'settings.account_delete_local_title': 'Documentos en este dispositivo',
  'settings.account_delete_local':
    'Este dispositivo tiene {items}. Eliminar tu cuenta no los quita de este dispositivo.',
  'settings.account_delete_local_gated':
    'Este dispositivo tiene {items}. Cuando se elimine tu cuenta ya no podrás abrirlos en {product} en este dispositivo, salvo que los restaures desde una copia de seguridad.',
  'settings.account_delete_backup_first': 'Guardar una copia primero',
  'settings.account_delete_backed_up':
    'Tengo una copia de seguridad, o no necesito estos documentos',
  'settings.account_delete_reauth_no_email':
    'Para proteger tu cuenta, puede que tengas que volver a iniciar sesión para confirmar que eres tú.',
  'settings.account_delete_signed_out':
    'Inicia sesión para eliminar tu cuenta aquí, o usa la página web de abajo sin iniciar sesión.',
  'settings.account_delete_receipt_download': 'Descargar el recibo',
  'settings.account_delete_restore_hint':
    '¿Guardaste una copia? Al volver a iniciar sesión empiezas una cuenta nueva y vacía. Después abre Ajustes, Tus datos, y restaura el archivo de copia para recuperar tus documentos.',
  'settings.account_cache_age_today': 'Derecho en caché de hoy.',
  'settings.account_cache_age': 'Derecho en caché de hace {days} días.',
  'settings.account_cache_age_one': 'Derecho en caché de hace 1 día.',
  'settings.account_vault_documents': 'Este dispositivo contiene {count} documentos.',
  'settings.account_vault_documents_one': 'Este dispositivo contiene 1 documento.',
  'settings.account_vault_empty':
    'La bóveda de este dispositivo está vacía: todavía no se ha guardado nada aquí.',
  'settings.account_vault_empty_connected':
    'La bóveda de este dispositivo está vacía. Los datos remotos no se descargan aquí, así que esto no dice nada sobre lo que contiene tu cuenta.',
  'settings.account_vault_empty_remote':
    'La bóveda de este dispositivo está vacía, pero tu cuenta tiene registros remotos ({records}): una bóveda local vacía no es lo mismo que no tener documentos.',
  'packs.option_title': '${price} por {credits} créditos de envío',
  'packs.option_title_store': '{price} por {credits} créditos de envío',
  'packs.option_detail': 'Compra única. Los créditos no caducan y cubren el envío gestionado.',
  'packs.credits_healthy': 'Tus créditos disponibles pueden cubrir una solicitud gestionada.',
  'packs.credits_low':
    'Esta solicitud necesita más créditos de los que te quedan. Puedes compartir tú mismo los enlaces de firma, o añadir un pack de pago único.',
  'packs.credits_zero':
    'No te quedan créditos de envío. Aun así puedes compartir tú mismo los enlaces de firma. Lifetime no se ve afectado.',
  'packs.none_on_sale': 'Los packs de créditos aún no están a la venta.',
  'packs.credits_low_free':
    'Esta solicitud necesita más créditos de los que te quedan. Puedes compartir tú mismo los enlaces de firma.',
  'packs.title': 'Créditos de envío',
  'packs.buy_credits': 'Comprar créditos de envío',
  'packs.lifetime_disclosure':
    'Los créditos de envío no caducan. Los créditos adicionales son compras únicas; nunca se cobra nada automáticamente.',
  'packs.sheet_title': 'Comprar créditos de envío',
  'packs.sheet_intro':
    'Un crédito envía una solicitud por correo, con recordatorios, sin importar el número de firmantes.',
  'packs.sheet_balance':
    'Te quedan {count} créditos de envío. Un crédito envía una solicitud por correo.',
  'packs.sheet_balance_one':
    'Te queda 1 crédito de envío. Un crédito envía una solicitud por correo.',
  'packs.bought': 'Créditos añadidos. Ahora tienes {count} créditos de envío.',
  'packs.bought_one': 'Créditos añadidos. Ahora tienes 1 crédito de envío.',
  'ops.cost.title': 'Costos operativos',
  'ops.cost.unavailable':
    'Los costos operativos no están disponibles. Inicia sesión con una cuenta de operador autorizada.',
  'ops.cost.scenario_notice':
    'Los cambios en esta página son solo un escenario local. No cambian la facturación, los precios ni la configuración de producción.',
  'ops.cost.rate_card': 'Tarifas y fuentes',
  'ops.cost.r2_rate': 'Almacenamiento R2 Standard, USD por GB-mes',
  'ops.cost.workers_minimum': 'Mínimo mensual de Workers Paid, USD',
  'ops.cost.email_allowance': 'Correos salientes incluidos por cuenta y mes',
  'ops.cost.email_overage': 'Exceso de correos salientes, USD por 1.000',
  'ops.cost.official_source': 'Página oficial de precios',
  'ops.cost.reset_rates': 'Restablecer tarifas del repositorio',
  'ops.cost.measured': 'Datos medidos y estimación parcial',
  'ops.cost.observed':
    'Se observaron {envelopes} solicitudes, {emails} correos y {bytes} bytes almacenados en este periodo.',
  'ops.cost.baseline': 'Estimación de la API para componentes medidos',
  'ops.cost.repriced': 'Estimación del escenario para esos componentes',
  'ops.cost.workers_separate':
    'El mínimo de la cuenta Workers ({amount}) se muestra aparte; esta estimación parcial no lo asigna.',
  'ops.cost.unmeasured': 'Componentes aún sin medir',
  'ops.cost.pack_review': 'Revisión del margen de packs de créditos',
  'ops.cost.pack_pending':
    'Introduce el costo de envío medido y los datos comerciales para calcular un escenario. La venta está activada por decisión de producto; la aprobación del margen sigue pendiente.',
  'ops.cost.delivery_unit': 'Costo por solicitud con envío gestionado, USD',
  'ops.cost.channel_fee_percent': 'Comisión del canal de pago, porcentaje del precio',
  'ops.cost.channel_fee_fixed': 'Comisión fija del canal, USD por compra',
  'ops.cost.refund_allowance': 'Reserva para reembolsos y contracargos, porcentaje del precio',
  'ops.cost.target_margin': 'Margen objetivo, porcentaje',
  'ops.cost.margin_pending': 'Faltan datos para el margen',
  'ops.cost.meets_target': 'Cumple el objetivo del escenario',
  'ops.cost.below_target': 'Por debajo del objetivo del escenario',
  'workflows.title': 'Ajustes de envío guardados',
  'workflows.review_always':
    'Envía una plantilla de nuevo con el mismo orden, entrega, mensaje y recordatorios. Siempre la revisas antes de que se envíe nada.',
  'workflows.save_from_draft': 'Guardar los ajustes de envío de un borrador',
  'workflows.no_drafts':
    'Usa una plantilla y prepara cómo se envía. Después podrás guardar esos ajustes aquí.',
  'workflows.save': 'Guardar ajustes',
  'workflows.saved_notice': 'Se guardaron los ajustes de «{title}».',
  'workflows.empty': 'Todavía no hay ajustes de envío guardados.',
  'workflows.item_template': 'Plantilla: {title}',
  'workflows.item_settings': '{routing} · {delivery} · {days} para responder',
  'workflows.routing.parallel': 'Todos a la vez',
  'workflows.routing.sequential': 'Uno tras otro',
  'workflows.delivery.manual': 'Tú compartes el enlace',
  'workflows.delivery.managed': '{product} lo envía por correo',
  'workflows.send_again': 'Enviar de nuevo',
  'workflows.send_again_title': 'Enviar de nuevo: {title}',
  'workflows.remove': 'Eliminar',
  'workflows.removed': 'Se eliminó «{title}».',
  'workflows.save_failed': 'No se pudieron guardar estos ajustes de envío.',
  'workflows.remove_failed': 'No se pudieron eliminar estos ajustes de envío.',
  'workflows.send_again_failed': 'No se pudieron abrir estos ajustes de envío.',
  'workflows.repair.template_missing':
    'La plantilla de estos ajustes ya no existe. Elimínalos y guarda otros desde otra plantilla.',
  'workflows.repair.template_deleted':
    'La plantilla de estos ajustes está en Eliminados recientemente. Restáurala para enviar de nuevo.',
  'workflows.repair.role_unassigned':
    'Los roles de la plantilla cambiaron desde que guardaste estos ajustes. Elimínalos y guarda otros.',
  'workflows.repair_no_side_effects': 'No se creó, reservó ni cobró nada.',
  'workflows.note.template_changed': 'La plantilla cambió desde que guardaste estos ajustes.',
  'workflows.note.manual_no_credits':
    'No te quedan créditos de entrega, así que esta vez compartirás tú el enlace.',
  'workflows.note.limit':
    'Algunos ajustes superan lo que permite tu plan. La revisión muestra qué cambiar.',
  'connections.title': 'Asistentes de IA',
  'connections.intro':
    'Asistentes de IA conectados a esta cuenta. Cada conexión es independiente: revocar una nunca cambia otra.',
  'connections.refresh': 'Actualizar',
  'connections.unavailable':
    'No pudimos leer tus conexiones. Nada ha cambiado: inténtalo de nuevo.',
  'connections.signed_out':
    'Inicia sesión para ver y gestionar los asistentes de IA conectados a esta cuenta.',
  'connections.empty_title': 'No hay ningún asistente de IA conectado',
  'connections.empty_description':
    'Nada puede leer esta cuenta a través de un asistente hasta que apruebes una conexión. Los pasos de abajo conectan uno.',
  'connections.connected_only_note':
    'Solo se listan los asistentes conectados. Un asistente revocado tiene que volver a pedir tu aprobación para conectarse.',
  'connections.host_granted': 'Conectado el {date}',
  'connections.host_last_used': 'Último uso: {date}',
  'connections.host_never_used': 'Aún no se ha usado',
  'connections.state.active': 'Conectado',
  'connections.state.pending_consent': 'Esperando tu aprobación',
  'connections.state.revoked': 'Revocado',
  'connections.state.expired': 'Caducado',
  'connections.distribution.openai': 'ChatGPT',
  'connections.distribution.claude': 'Claude',
  'connections.distribution.other': 'Otro asistente de IA',
  'connections.permissions_title': 'Qué puede hacer este asistente',
  'connections.permissions_note':
    'Añadir un permiso más adelante vuelve a necesitar tu aprobación: esta página nunca amplía una conexión por sí sola.',
  'connections.item_scope_all': 'Puede ver todo lo que publiques para este asistente.',
  'connections.item_scope_selected': 'Puede ver {count} elementos que seleccionaste.',
  'connections.item_scope_selected_one': 'Puede ver 1 elemento que seleccionaste.',
  'connections.item_scope_selected_count_unknown':
    'Puede ver solo los elementos que seleccionaste.',
  'connections.item_scope_not_reported':
    'El servicio no informó de qué elementos puede ver este asistente.',
  'connections.activity_title': 'Actividad reciente de herramientas',
  'connections.activity_none': 'No hay llamadas de herramientas registradas para este asistente.',
  'connections.activity_calls': '{count} llamadas de herramientas registradas.',
  'connections.activity_calls_one': '1 llamada de herramienta registrada.',
  'connections.activity_tool': '{name} · {count} llamadas',
  'connections.activity_tool_one': '{name} · 1 llamada',
  'connections.revoke': 'Revocar acceso',
  'connections.revoke_title': '¿Revocar {host}?',
  'connections.revoke_stops':
    'Su acceso se detiene de inmediato: todos los tokens que se le dieron dejan de funcionar, así que ya no puede leer nada aquí y una llamada que ya había empezado falla.',
  'connections.revoke_keeps':
    'Nada más cambia: tus otras conexiones siguen funcionando y tus documentos, plantillas y acuerdos quedan intactos.',
  'connections.revoke_only_this': 'Esto revoca un asistente. No elimina nada.',
  'connections.revoke_confirm': 'Revocar este asistente',
  'connections.revoke_cancel': 'Mantenerlo conectado',
  'connections.revoke_done': '{host} se revocó. Sus tokens ya no funcionan.',
  'connections.revoke_gone': 'Esa conexión ya no existe. No se cambió nada.',
  'connections.revoke_failed': 'No pudimos revocar esta conexión. Nada ha cambiado.',
  'connections.revoke_signed_out': 'Inicia sesión de nuevo para revocar esta conexión.',
  'connections.scope.profile_read': 'Leer tu nombre y los datos de tu cuenta',
  'connections.scope.agreements_read': 'Leer tus acuerdos y su estado',
  'connections.scope.agreements_write': 'Crear y cambiar acuerdos',
  'connections.scope.workspace_read': 'Leer los elementos que publicaste para este asistente',
  'connections.scope.workspace_write': 'Añadir o cambiar elementos en el espacio de trabajo',
  'connections.scope.templates_read': 'Leer tus plantillas',
  'connections.scope.templates_write': 'Crear y cambiar plantillas',
  'connections.scope.content_read': 'Leer el contenido de los elementos que publicaste',
  'connections.scope.contacts_read': 'Leer tus contactos',
  'connections.scope.contacts_write': 'Crear y cambiar contactos',
  'connections.scope.analytics_read': 'Leer tus resúmenes de uso',
  'connections.scope.delivery_write': 'Enviar y entregar solicitudes',
  'connections.scope.evidence_read': 'Leer los registros de evidencia',
  'connections.scope.billing_read': 'Leer datos de facturación y créditos',
  'connections.scope.purchase_start': 'Iniciar una compra',
  'connections.scope.account_manage': 'Cambiar la configuración de la cuenta',
  'reviews.title': 'Revisiones',
  'reviews.intro':
    'Lo que un asistente de IA preparó para que lo apruebes. Nada se envía, se firma ni se cobra hasta que registres aquí tu decisión.',
  'reviews.refresh': 'Actualizar',
  'reviews.unavailable':
    'No pudimos leer tus revisiones ({code}). Nada ha cambiado: inténtalo de nuevo.',
  'reviews.signed_out':
    'Inicia sesión para ver las acciones que un asistente de IA preparó para esta cuenta.',
  'reviews.empty_title': 'No hay nada esperando tu aprobación',
  'reviews.empty_description':
    'Cuando un asistente de IA prepara algo para ti, espera aquí hasta que lo apruebes o lo rechaces. Ningún asistente puede aprobar en tu nombre.',
  'reviews.open': 'Revisar y aprobar',
  'reviews.row_deadline': 'Responde en {days} días',
  'reviews.row_deadline_one': 'Responde en 1 día',
  'reviews.row_document': '{title} · revisión {revision}',
  'reviews.row_operation': '{kind} · {target}',
  'reviews.back': 'Volver a las revisiones',
  'reviews.not_found': 'Esa revisión no está disponible en esta cuenta.',
  'reviews.not_found_note':
    'Puede pertenecer a otra cuenta o no haber existido nunca. No se cambió nada.',
  'reviews.state.awaiting_approval': 'Esperando tu aprobación',
  'reviews.state.approved': 'Aprobada',
  'reviews.state.rejected': 'Rechazada',
  'reviews.state.committed': 'Confirmada',
  'reviews.state.expired': 'Caducada',
  'reviews.state.invalidated': 'Ya no es válida',
  'reviews.invalidated.source_revision_changed':
    'El documento de origen cambió después de prepararse esto.',
  'reviews.invalidated.participants_changed':
    'La lista de participantes cambió después de prepararse esto.',
  'reviews.invalidated.fields_changed':
    'La asignación de campos cambió después de prepararse esto.',
  'reviews.invalidated.action_changed': 'La acción preparada cambió después de prepararse esto.',
  'reviews.invalidated.grant_revoked': 'Se revocó el acceso concedido al host de IA.',
  'reviews.invalidated.grant_revision_changed': 'Los permisos del host de IA cambiaron.',
  'reviews.invalidated.account_inactive': 'Esta cuenta no está activa.',
  'reviews.invalidated.review_superseded': 'Una preparación más reciente sustituyó a esta.',
  'reviews.document_title': 'Documento',
  'reviews.document_identity': '{title} · revisión {revision} · {pages} · {size}',
  'reviews.document_digest': 'Huella del contenido {digest}',
  'reviews.document_no_content':
    'El documento permanece en tu espacio de trabajo. La vista previa de abajo se lee desde allí solo para ti; esta pantalla nunca se la entrega al asistente.',
  'reviews.preview_title': 'Vista previa desde tu espacio de trabajo',
  'reviews.preview_current': 'Esta es la versión actual en tu espacio de trabajo.',
  'reviews.preview_moved':
    'Tu espacio de trabajo tiene ahora la revisión {current}; esta revisión fijó la revisión {frozen}. Hay que prepararla de nuevo.',
  'reviews.preview_unavailable':
    'No se pudo leer la copia del espacio de trabajo, así que esto no se puede aprobar ahora mismo.',
  'reviews.agreement_title': 'Acuerdo',
  'reviews.agreement_identity': '{title} · revisión {revision}',
  'reviews.agreement_no_content':
    'El acuerdo permanece en tu espacio de trabajo. Esta pantalla muestra su identidad y la edición, nunca su contenido.',
  'reviews.operation_title': 'Lo que haría esta revisión',
  'reviews.operation.kind.correction': 'Corregir los datos de un participante',
  'reviews.operation.kind.resend': 'Enviar otra invitación',
  'reviews.operation.kind.void': 'Anular el acuerdo',
  'reviews.operation.kind.expiry_extension': 'Ampliar el plazo',
  'reviews.operation_target': '{agreement} · {recipient}',
  'reviews.operation_target_envelope': '{agreement} · el acuerdo en su conjunto',
  'reviews.operation_revision':
    'Preparada sobre la revisión {revision}; se vuelve a leer al confirmar la edición.',
  'reviews.operation.effect.replace_capability_and_edit_identity':
    'Sustituye el enlace de firma de ese participante y edita sus datos de identidad.',
  'reviews.operation.effect.queue_another_invitation':
    'Pone en cola otra invitación para ese participante.',
  'reviews.operation.effect.revoke_every_capability_and_close':
    'Revoca todos los enlaces de firma y cierra el acuerdo.',
  'reviews.operation.effect.lengthen_deadline': 'Amplía el plazo del acuerdo.',
  'reviews.operation_changes_title': 'Qué cambia',
  'reviews.operation_change': '{field}: {to}',
  'reviews.operation.field.name': 'Nombre',
  'reviews.operation.field.email': 'Correo',
  'reviews.operation.field.role_id': 'Rol',
  'reviews.operation.field.locale_code': 'Idioma',
  'reviews.operation_resend': 'Esto también entrega una nueva invitación.',
  'reviews.operation_requested_days': 'Pide {days} días más de plazo.',
  'reviews.operation_requested_days_one': 'Pide 1 día más de plazo.',
  'reviews.operation_reason': 'Motivo: {reason}',
  'reviews.operation_credit': 'Efecto en los créditos: {credit}',
  'reviews.operation_missing':
    'Esta revisión actúa sobre un acuerdo, pero la edición que realizaría no se describió.',
  'reviews.operation_missing_body':
    'Aquí no hay nada que aprobar. Pide al modelo que prepare la edición de nuevo.',
  'reviews.participants_title': 'Participantes, en orden',
  'reviews.participant_signer': 'Firmante',
  'reviews.participant_cc': 'Copia',
  'reviews.participant_sender': 'Remitente',
  'reviews.participant_position': '{index}. {name}',
  'reviews.participant_no_email': 'Sin correo registrado',
  'reviews.copy_loading': 'Abriendo el documento…',
  'reviews.copy_unreadable':
    'El documento no se pudo abrir aquí, así que no se pueden mostrar sus campos. No ha cambiado nada.',
  'reviews.placed_field': '{type} · {name}',
  'reviews.placement_page': 'Página {page}',
  'reviews.placement_default':
    'Final de la página {page}, añadido porque el asistente no colocó ninguno',
  'reviews.placement_anchor_found': 'Junto a «{text}»',
  'reviews.placement_anchor_skipped':
    '«{text}» no está en este documento, así que este campo se omite.',
  'reviews.placement_anchor_missing': '«{text}» no está en este documento.',
  'reviews.placement_anchor_matching': 'Coincidencia: {case}, {words}.',
  'reviews.placement_anchor_match_case_insensitive': 'cualquier mayúscula',
  'reviews.placement_anchor_match_case_sensitive': 'mayúsculas exactas',
  'reviews.placement_anchor_match_whole_word': 'solo palabras completas',
  'reviews.placement_anchor_match_anywhere': 'en cualquier parte del texto',
  'reviews.placement_blocking':
    'Un campo no se puede colocar porque su texto no está en este documento. Pide al asistente que corrija la colocación y lo prepare de nuevo.',
  'reviews.placement_label': 'Etiqueta: {label}',
  'reviews.placement_tooltip': 'Ayuda: {tooltip}',
  'reviews.placement_value': 'Rellenado por ti: «{value}» (el firmante puede cambiarlo)',
  'reviews.placement_value_locked': 'Rellenado por ti y bloqueado: «{value}»',
  'reviews.placement_max_length': 'Hasta {count} caracteres',
  'reviews.placement_validation': 'Debe ser: {preset}',
  'reviews.placement_formatting': 'Formato: {format}',
  'reviews.placement_formula': 'Calculado: {expression}',
  'reviews.fields_title': 'Campos',
  'reviews.fields_none': 'No hay campos asignados.',
  'reviews.field_required': 'Obligatorio',
  'reviews.field_optional': 'Opcional',
  'reviews.field_locked': 'Bloqueado',
  'reviews.field_owner': 'para {role}',
  'reviews.deadline_title': 'Plazo',
  'reviews.deadline_value': '{days} para responder (hasta {max})',
  'reviews.delivery_title': 'Entrega',
  'reviews.delivery_mode.manual': 'Compartes tú mismo los enlaces de firma',
  'reviews.delivery_mode.managed': 'Entrega gestionada',
  'reviews.routing_mode.parallel': 'Todos los firmantes pueden actuar a la vez',
  'reviews.routing_mode.sequential': 'Los firmantes actúan en orden',
  'reviews.sender_order.before': 'Firmas antes que los destinatarios',
  'reviews.sender_order.after': 'Firmas después de los destinatarios',
  'reviews.credit_title': 'Efecto en los créditos',
  'reviews.credit_none': 'No se usa ningún crédito de entrega.',
  'reviews.credit_trial': 'Se usa un crédito de prueba.',
  'reviews.credit_amount': 'Créditos de entrega usados: {debited}; quedarán {available}.',
  'reviews.credit_at_activation': 'Se cobra cuando se realiza la acción, nunca antes.',
  'reviews.approve': 'Aprobar',
  'reviews.deny': 'Rechazar',
  'reviews.approve_title': '¿Aprobar esta acción preparada?',
  'reviews.approve_binding':
    'Aprobar registra tu decisión sobre esta revisión exacta del documento, este conjunto de participantes y estos campos. Caduca cinco minutos después de registrarla.',
  'reviews.approve_confirm': 'Registrar mi aprobación',
  'reviews.approve_cancel': 'Ahora no',
  'reviews.approved_recorded': 'Aprobación registrada.',
  'reviews.approved_expires':
    'Esta aprobación caduca el {expires} y está vinculada a esta revisión exacta.',
  'reviews.approved_token_note':
    'El token de aprobación va al host de IA por su propia conexión autenticada, nunca a esta página.',
  'reviews.rejected_recorded': 'Has rechazado esto. No se envió ni se cobró nada.',
  'reviews.committed_note':
    'Esta acción se confirmó: la aprobación se usó y aquí no hace falta nada más.',
  'reviews.committed_send_note':
    'Tu asistente terminó de prepararlo. No se envía nada hasta que lo abras, revises los campos y pulses Enviar.',
  'reviews.send_now': 'Abrir para enviar',
  'reviews.send_unavailable':
    'Este acuerdo todavía no se puede abrir aquí. Recarga la página e inténtalo de nuevo.',
  'reviews.send_failed': 'No pudimos abrirlo en este dispositivo. No se envió nada.',
  'reviews.decision_expired':
    'Esta revisión caducó antes de tu decisión. Pide al modelo que la prepare de nuevo.',
  'reviews.decision_terminal':
    'Esta revisión ya ha terminado, así que no se puede volver a decidir.',
  'reviews.decision_forbidden':
    'El acceso concedido al host de IA ya no está activo, así que esto no se puede aprobar.',
  'reviews.decision_failed': 'No pudimos registrar tu decisión. Nada ha cambiado.',
  'reviews.decision_signed_out': 'Inicia sesión de nuevo para registrar tu decisión.',
  'reviews.decision_gone': 'Esa revisión ya no está disponible.',
  'reviews.reask_title': 'Hay que prepararlo de nuevo',
  'reviews.reask_body':
    'Algo importante cambió después de prepararse esto, así que una aprobación ahora ya no coincidiría con la acción. Pide al modelo que lo prepare de nuevo.',
  'reviews.reask_invalidated': 'Esta revisión ya no es válida: {reason}',
  'reviews.reask_source_moved':
    'El documento de origen cambió, así que la revisión fijada ya no es la actual.',
  'reviews.continuation_title': 'Terminar en este navegador',
  'reviews.continuation_intro':
    'Tu propio navegador completa la acción preparada. El host de IA nunca tiene autoridad para enviar ni firmar.',
  'reviews.continuation_open': 'Abrir la continuación del navegador',
  'reviews.continuation_authority_seal':
    'Esta continuación puede sellar, subir y activar el borrador preparado.',
  'reviews.continuation_authority_edit':
    'Esta continuación solo puede editar campos y metadatos; no puede enviar ni firmar.',
  'reviews.continuation_no_host_send':
    'El host de IA no puede enviar ni firmar esto. Solo tú puedes, en tu navegador.',
  'reviews.continuation_draft': '{title} · {participants} · {days} para responder',
  'reviews.continuation_expired':
    'Esta continuación ha caducado. Pide al modelo que prepare la acción de nuevo.',
  'reviews.continuation_gone': 'Todavía no hay ninguna continuación para esta revisión.',
  'reviews.continuation_failed': 'No pudimos abrir la continuación.',
  'reviews.continuation_signed_out': 'Inicia sesión de nuevo para abrir la continuación.',
  'reviews.handoff.title': 'Traspaso',
  'reviews.handoff.intro':
    'Un modelo te pidió que actuaras sobre un documento en tu propio dispositivo. Nada se ejecuta en segundo plano mientras la aplicación está cerrada.',
  'reviews.handoff.state.waiting_for_user': 'Esperando a ti',
  'reviews.handoff.state.waiting_for_device': 'Esperando al dispositivo que tiene el origen',
  'reviews.handoff.state.running': 'En curso',
  'reviews.handoff.state.succeeded': 'Completado',
  'reviews.handoff.state.failed': 'Fallido',
  'reviews.handoff.state.cancelled': 'Cancelado',
  'reviews.handoff.state.expired': 'Caducado',
  'reviews.handoff.operation': 'Operación {id}',
  'reviews.handoff.action_ref': 'Acción preparada: {action}',
  'reviews.handoff.expires': 'Caduca el {expires}',
  'reviews.handoff.actions_title': 'Lo que puedes hacer',
  'reviews.handoff.resume': 'Reanudar aquí',
  'reviews.handoff.resume_done': 'Reanudado en este dispositivo.',
  'reviews.handoff.wrong_device_title': 'Este es el dispositivo equivocado',
  'reviews.handoff.wrong_device':
    'Este traspaso se abrió en otro dispositivo. Sigue abierto allí y aquí no cambió nada.',
  'reviews.handoff.no_actions': 'Ya no queda nada que hacer en este traspaso.',
  'reviews.handoff.no_background':
    'Nada continúa en segundo plano. Esta operación solo avanza cuando actúas sobre ella.',
  'reviews.handoff.restart': 'Pide al modelo que abra un traspaso nuevo',
  'reviews.handoff.restart_note':
    'Este no se puede reabrir. Una operación nueva inicia un traspaso desde cero.',
  'reviews.handoff.complete': 'Informar de la finalización',
  'reviews.handoff.complete_outcome': 'Resultado',
  'reviews.handoff.complete_succeeded': 'Completado',
  'reviews.handoff.complete_failed': 'Fallido',
  'reviews.handoff.commit_ref': 'Referencia de confirmación',
  'reviews.handoff.commit_ref_note':
    'La finalización solo se informa después de una confirmación. Sin una referencia no se puede informar como completada.',
  'reviews.handoff.failure_reason': 'Motivo',
  'reviews.handoff.submit': 'Enviar',
  'reviews.handoff.document_title': 'Tu asistente de IA te ha enviado un documento',
  'reviews.handoff.document_intro':
    'Comprueba que es el correcto. Al usarlo se guarda una copia en tu espacio de {product} que este asistente puede leer para preparar el acuerdo. No se envía nada hasta que lo apruebes.',
  'reviews.handoff.document_name': 'Nombre del documento',
  'reviews.handoff.document_default_name': 'Documento para firmar',
  'reviews.handoff.document_pages': '{count} páginas · {size}',
  'reviews.handoff.document_pages_one': '1 página · {size}',
  'reviews.handoff.document_loading': 'Abriendo el documento…',
  'reviews.handoff.document_unreadable':
    'Este archivo no se pudo abrir como PDF, así que no se puede usar. Pide al asistente que lo envíe de nuevo.',
  'reviews.handoff.use_document': 'Usar este documento',
  'reviews.handoff.not_this_document': 'No es el documento correcto',
  'reviews.handoff.document_used':
    'Listo. El asistente ya puede preparar el acuerdo, y lo revisarás antes de que se envíe nada.',
  'reviews.handoff.signed_out': 'Inicia sesión de nuevo para abrir este traspaso.',
  'reviews.handoff.gone': 'Ese traspaso no está disponible en esta cuenta.',
  'reviews.handoff.unavailable': 'No pudimos leer este traspaso. Nada ha cambiado.',
  'reviews.handoff.failed': 'No pudimos actualizar este traspaso. Nada ha cambiado.',
  'reviews.handoff.terminal':
    'Este traspaso ya ha terminado, así que no se puede volver a actualizar.',
  'reviews.handoff.returned_title': 'Contenido compartido de vuelta',
  'reviews.handoff.returned_body':
    'Se compartieron con el host una referencia y una huella, nunca el documento.',
  'reviews.handoff.not_returned': 'No se ha compartido nada de vuelta con el host.',
  'reviews.handoff.failure.wrong_device': 'Abierto en el dispositivo equivocado',
  'reviews.handoff.failure.source_unavailable': 'El origen ya no está disponible',
  'reviews.handoff.failure.no_commit': 'No se registró ninguna confirmación',
  'reviews.handoff.failure.user_cancelled': 'Lo cancelaste',
  'reviews.handoff.failure.upload_expired': 'La ventana de subida caducó',
  'reviews.handoff.failure.upload_rejected': 'La subida fue rechazada',
  'reviews.handoff.failure.grant_revoked': 'Se revocó el acceso concedido al host de IA',
  'reviews.handoff.explanation.wrong_device':
    'El origen está en otro dispositivo. Abre allí el traspaso; sigue abierto.',
  'reviews.handoff.explanation.source_unavailable':
    'El origen ya no está disponible en el dispositivo, así que esto no se puede completar.',
  'reviews.handoff.explanation.no_commit':
    'Se informó de la finalización sin una confirmación, así que no se registra como éxito.',
  'reviews.handoff.explanation.user_cancelled': 'Has cancelado este traspaso.',
  'reviews.handoff.explanation.upload_expired':
    'La ventana de subida caducó antes de que llegara el archivo.',
  'reviews.handoff.explanation.upload_rejected': 'El archivo subido fue rechazado.',
  'reviews.handoff.explanation.grant_revoked':
    'Se revocó el acceso concedido al host de IA, así que este traspaso se canceló.',
  'reviews.handoff.explanation.workspace_full_free':
    'Tu espacio de {product} está lleno. Las cuentas gratuitas guardan hasta {free_items} elementos, {free_versions} versiones y {free_size}. Elimina elementos en {product} para liberar espacio y vuelve a tocar "Usar este documento". Lifetime guarda hasta {lifetime_items} elementos y {lifetime_size}.',
  'reviews.handoff.explanation.workspace_full_lifetime':
    'Tu espacio de {product} está lleno. Lifetime guarda hasta {lifetime_items} elementos, {lifetime_versions} versiones y {lifetime_size}. Elimina elementos en {product} para liberar espacio y vuelve a tocar "Usar este documento".',
  'reviews.settings_title': 'Revisiones',
  'reviews.settings_description':
    'Cuando un host de IA prepara algo para ti, espera aquí tu aprobación. Sin ella no se envía, firma ni cobra nada.',
  'reviews.settings_open': 'Abrir revisiones',
  'request.correction_requires_lifetime':
    'Las correcciones forman parte de Lifetime. En el plan gratuito, anula esta solicitud y prepara otra: tu trabajo se conserva.',
  'request.correction_after_submission':
    'Ese firmante ya ha enviado su firma. Las firmas aceptadas nunca se editan; prepara una solicitud nueva.',
  'request.correction_declined':
    'Ese firmante rechazó la solicitud, así que no hay nada que corregir.',
  'request.correction_terminal':
    'Esta solicitud ya ha terminado, así que sus destinatarios ya no se pueden corregir.',
  'request.correction_expired':
    'El plazo de firma ha terminado. Una solicitud caducada nunca se reabre: prepara una nueva.',
  'request.resend_limit':
    'Este firmante ya ha recibido todas las invitaciones que permite el plan. Comparte el enlace que ya tienes.',
  'request.resend_too_soon':
    'Demasiado pronto desde la última invitación. Inténtalo en un momento.',
  'request.extension_requires_lifetime': 'Cambiar el plazo forma parte de Lifetime.',
  'request.extension_terminal': 'Solo una solicitud activa tiene un plazo que se pueda ampliar.',
  'request.extension_not_active':
    'Esta solicitud aún no ha empezado, así que no hay plazo que ampliar.',
  'request.extension_expired':
    'El plazo de firma ya ha terminado. Una solicitud caducada nunca se reabre: prepara una nueva.',
  'request.extension_after_acceptance':
    'Alguien ya ha aceptado, así que el plazo ya no puede cambiar.',
  'request.extension_must_lengthen': 'Una ampliación solo puede mover el plazo hacia delante.',
  'request.cc_read_after_commit':
    'La lectura para un participante en copia se abre cuando se completa la solicitud.',
  'error.recovery.upgrade_or_void_and_recreate':
    'Mejora el plan, o anula esta solicitud y prepara otra.',
  'error.recovery.share_the_existing_link': 'Comparte el enlace que ya tienes.',
  'error.recovery.wait_then_resend': 'Espera unos minutos e inténtalo de nuevo.',
  'error.recovery.upgrade_or_new_request': 'Mejora el plan, o prepara una solicitud nueva.',
  'error.recovery.choose_later_deadline': 'Elige una fecha posterior al plazo actual.',
  'error.recovery.wait_for_completion': 'Espera a que se complete la solicitud.',
  // Pasada de catálogo de errores S6.5: una línea concreta y accionable por cada acción alcanzable.
  'error.recovery.wait_void_upgrade':
    'Anula o espera una solicitud activa, o mejora el plan para empezar más.',
  'error.recovery.reduce_signers_or_upgrade':
    'Quita un firmante o mejora el plan para más firmantes.',
  'error.recovery.share_it_myself_or_upgrade':
    'Comparte el enlace de firma tú mismo o mejora el plan para que lo enviemos nosotros.',
  'error.recovery.share_it_myself_or_buy_pack':
    'Comparte el enlace de firma tú mismo o compra un pack de envío.',
  'error.recovery.create_new_request': 'Prepara una solicitud nueva para enviar un enlace nuevo.',
  'error.recovery.view_current_state': 'Abre la solicitud para ver su estado actual.',
  'error.recovery.refresh_state': 'Actualiza para ver el estado más reciente de esta solicitud.',
  'error.recovery.retry_support_code':
    'Inténtalo de nuevo; si se repite, facilita este código de error a soporte.',
  'error.recovery.refresh_balance': 'Actualiza para ver la compra que ya hiciste.',
  'error.recovery.refresh_or_reconcile':
    'Actualiza en un momento; si la compra sigue sin aparecer, contacta con soporte.',
  'error.recovery.reconnect_destination':
    'Vuelve a conectar tu ubicación de copia de seguridad e inténtalo de nuevo.',
  'error.recovery.retry_or_change_location':
    'Inténtalo de nuevo o elige otra ubicación de copia de seguridad.',
  'error.recovery.remove_password_or_export_plain':
    'Quita la contraseña del PDF o exporta una copia sin protección y añade esa.',
  'error.recovery.choose_another_file': 'Elige otro archivo que se abra con normalidad.',
  'error.recovery.flattened_export_guidance': 'Exporta una copia aplanada del PDF y añade esa.',
  'error.recovery.invalidate_or_reject':
    'Quita la firma existente del PDF, o recházalo y sube una copia limpia.',
  'error.recovery.split_or_reduce_file': 'Divide el PDF o reduce su tamaño y añádelo de nuevo.',
  'error.recovery.split_document':
    'Divide el documento en partes más pequeñas y añádelas de nuevo.',
  'error.recovery.downscale_image': 'Reduce la resolución de la imagen y añádela de nuevo.',
  'error.recovery.export_to_pdf_locally':
    'Convierte la imagen a PDF en este dispositivo y añade el PDF.',
  'error.recovery.retry_compose':
    'Inténtalo de nuevo; si sigue fallando, quita el archivo y añádelo otra vez.',
  'error.recovery.retry_or_manual_fields': 'Inténtalo de nuevo o coloca los campos a mano.',
  'error.recovery.retry_backoff':
    'Inténtalo de nuevo en unos minutos; el envío no está disponible temporalmente.',
  'error.recovery.manual_share':
    'Comparte el enlace de firma tú mismo; el correo a esa dirección está bloqueado.',
  'error.recovery.wait_or_manual': 'Espera e inténtalo más tarde, o comparte el enlace tú mismo.',
  'error.recovery.none_required': 'No hay nada que hacer: se reintenta automáticamente.',
  'error.recovery.retry_or_contact_support':
    'Inténtalo de nuevo con la compra; si se repite, contacta con soporte.',
  'error.recovery.restore_or_contact_support':
    'Restaura tus compras; si Lifetime sigue sin aparecer, contacta con soporte.',
  'error.recovery.request_new_code': 'Pide un código nuevo e introdúcelo.',
  'error.recovery.free_space_or_change_location':
    'Libera espacio o elige otra ubicación de copia de seguridad.',
  'error.recovery.retry_backup': 'Inténtalo de nuevo con la copia de seguridad.',
  'error.recovery.choose_another_backup':
    'Elige otro archivo de copia de seguridad para restaurar.',
  'error.recovery.check_backup_passphrase':
    'Comprueba la contraseña de la copia de seguridad e inténtalo de nuevo.',
  'error.recovery.update_app_for_backup':
    'Actualiza la aplicación y luego restaura la copia de seguridad.',
  'error.recovery.free_local_space': 'Libera espacio e inténtalo de nuevo.',
  'error.recovery.restore_from_backup':
    'Restaura este archivo desde una copia de seguridad e inténtalo de nuevo.',
  'error.recovery.recover_previous_revision': 'Recupera la revisión anterior e inténtalo de nuevo.',
  'error.recovery.reload_and_merge':
    'Recarga para obtener la revisión más reciente y vuelve a aplicar tus cambios.',
  'error.recovery.reprepare_finalize': 'Prepara el documento final de nuevo.',
  'error.recovery.refresh_and_retry':
    'Actualiza para obtener el estado más reciente e inténtalo de nuevo.',
  'error.recovery.incomplete_evidence_export':
    'El plazo ha terminado. Exporta el registro de evidencia incompleta.',
  'error.recovery.wait_for_signers': 'Espera a que todos los firmantes hayan respondido.',
  'error.recovery.fix_field_errors': 'Corrige los campos marcados e inténtalo de nuevo.',
  'error.recovery.sign_in': 'Inicia sesión e inténtalo de nuevo.',
  'error.recovery.view_available_items': 'Abre tu lista para ver lo que sigue disponible.',
  'error.recovery.reduce_payload': 'Reduce el tamaño de lo que envías e inténtalo de nuevo.',
  'error.recovery.backoff_retry':
    'Demasiadas solicitudes en poco tiempo. Espera un momento e inténtalo de nuevo.',
  'send.resend': 'Reenviar',
  'send.resend_queued': 'Invitación en cola. Quedan {remaining} para este firmante.',
  'send.resend_revealed': 'Enlace listo. Quedan {remaining} para este firmante.',
  'send.resend_link_ready': 'El enlace para {name}',
  'send.correct': 'Corregir datos',
  'send.correct_unavailable': 'No hay corrección disponible para este paso',
  'send.correct_scope':
    'Corregir rota el enlace: el que enviaste deja de funcionar. Las firmas aceptadas nunca se editan, y un archivo ya descargado no se puede recuperar.',
  'send.correct_name': 'Nombre',
  'send.correct_email': 'Correo',
  'send.correct_submit': 'Aplicar corrección',
  'send.corrected':
    'Corregido. El enlace nuevo es la versión {version}; el anterior ya no funciona.',
  'send.extend_label': 'Días para responder (desde el inicio original)',
  'send.extend_submit': 'Ampliar plazo',
  'send.extend_limit':
    'Una ampliación puede llegar como máximo a {allowed} días desde el inicio original; has pedido {days}.',
  'send.extended': 'El plazo ahora es {date}, y el documento no se modificó.',
  'send.cc_reveal': 'Mostrar enlace de copia final',
  'send.cc_reveal_loading': 'Preparando el enlace',
  'send.cc_revealed': 'El enlace de copia final para {name} está abajo. Caduca por sí solo.',
  'send.cc_waiting': 'La lectura se abre cuando se completa la solicitud',
  'send.step_conflict': 'Otra persona cambió este paso. Recarga el estado e inténtalo de nuevo.',
  'send.step_failed': 'Eso no funcionó.',
  'send.request_status_title': 'Estado de la solicitud',
  'send.request_status_unavailable':
    'El estado no está disponible ahora mismo. Nada de la solicitud ha cambiado.',
  'send.request_state': '{state} · {submitted} de {total} han respondido',
  'send.request_step': '{order}. {name} — {state}',
  'send.request_step_sender': 'tu paso',
  'send.next_link_ready':
    '{name} tiene ahora el enlace activo. No se envió ningún correo: comparte su enlace cuando quieras.',
  'send.adopt_sender_step': 'Firmar mi parte',
  'send.adopt_sender_note':
    'Tu propio paso se registra como una adopción de lo que aprobaste; nunca usa un enlace, y todos los firmantes anteriores han terminado.',
  'send.sender_display_name': 'El remitente',
  'send.sender_name_label': 'Tu nombre',
  'send.sender_name_hint': 'Los destinatarios lo ven como remitente de la solicitud.',
  'send.adopted': 'Tu paso está registrado. La solicitud ya puede completarse.',
  'send.adoption_not_due': 'Todavía no es tu turno: un firmante anterior aún tiene que terminar.',
  'send.adoption_failed': 'No pudimos registrar tu paso.',
  'send.refresh_status': 'Actualizar estado',
  'send.title': 'Enviar una solicitud',
  'send.ready_title': 'Listo para compartir',
  'send.link_not_ready': 'El enlace aún no está listo. Abre la solicitud para compartirlo.',
  'send.prepare_document': 'Preparar un documento',
  'send.list_label': 'Elige qué enviar',
  'send.load_more': 'Cargar más borradores',
  'send.review_title': 'Revisa los detalles',
  'send.review_fields_one': '1 campo',
  'send.review_fields': '{count} campos',
  'send.sent_draft_title': 'Solicitud ya creada',
  'send.pending_recovery_title': 'Comprueba esta solicitud antes de volver a enviar',
  'send.pending_recovery_description':
    'Se preparó una solicitud para este borrador. Puede que ya se haya activado. Comprueba el estado del servicio para continuar esa misma solicitud.',
  'send.create_recovery_description':
    'Se interrumpió la creación de esta solicitud. Comprueba el mismo borrador en el servicio antes de continuar; {product} reutilizará su ID de solicitud original.',
  'send.pending_recovery_code': 'Última comprobación: {code}',
  'send.pending_recovery_checked':
    'Comprobado a las {time}: esta solicitud aún no se ha enviado. Vuelve a intentarlo en un momento.',
  'send.spent_title': 'Esta solicitud no se puede enviar',
  'send.spent_description':
    'El servicio rechazó este intento de forma definitiva, así que no puede terminarse.',
  'send.spent_next':
    'Enviar de nuevo primero se asegura de que este intento nunca pueda salir y luego crea una solicitud nueva con el mismo documento, las mismas personas y los mismos campos.',
  'send.spent_send_again': 'Enviar de nuevo',
  'send.spent_restarted_elsewhere':
    'Este borrador ya se envió de nuevo, o se eliminó, en otra pestaña.',
  'send.spent_open_agreements': 'Abrir Acuerdos',
  'send.replaced_title': 'Ya se envió de nuevo',
  'send.replaced_description':
    'Este borrador ya se envió de nuevo, así que esta copia no se enviará. Busca su solicitud en Acuerdos.',
  'send.spent_fresh_failed':
    'Aún no se pudo crear una solicitud nueva a partir de este borrador. Comprueba su estado en Solicitudes y vuelve a intentarlo.',
  'send.pending_recovery_corrupt':
    'Este dispositivo no puede leer el intento de envío guardado. El borrador está en pausa para evitar una solicitud duplicada. Consulta tus solicitudes o usa un borrador nuevo.',
  'send.check_request': 'Comprobar y continuar',
  'send.use_again_editor': 'Abrir el editor para usar de nuevo',
  'send.already_sent':
    'Este borrador ya creó una solicitud. Usa «Usar de nuevo» en el editor para iniciar otra.',
  'send.view_request': 'Ver solicitud',
  'send.local_link_not_saved':
    'La solicitud existe, pero este dispositivo no pudo guardar su vínculo con el borrador. Mantén abierta esta página o busca la solicitud en tu cuenta.',
  'send.share_unavailable_copy': 'No se abrió el menú de compartir. Copia el enlace.',
  'send.edit_document': 'Editar documento',
  'send.review_document': 'Documento',
  'send.review_signers': 'Firmantes externos',
  'send.review_signature_fields': 'Campos de firma obligatorios',
  'send.review_delivery': 'Entrega',
  'send.review_credit_effect': 'Efecto en los créditos',
  'send.review_zero_credits': 'Sin crédito de entrega',
  'send.review_one_credit': '1 crédito al activar',
  'send.review_trial_effect': 'Una prueba Free si sigue disponible',
  'send.review_trial_unavailable': 'Prueba no disponible; la creación gestionada puede rechazarse',
  'send.review_unknown_effect': 'La prueba o el crédito se comprueba antes de crear',
  'send.review_manual': 'Comparte el enlace tú mismo',
  'send.review_managed': 'Invitación gestionada',
  'send.review_expiry': 'Plazo de firma',
  'send.review_days': '{days}',
  'send.review_window_free':
    'Este borrador pedía un plazo más largo. Las solicitudes gratuitas se pueden firmar durante {days}.',
  'send.delivery_manual_choice': 'Comparte el enlace tú mismo — sin crédito de entrega',
  'send.delivery_managed_choice': 'Invitación gestionada — usa una prueba o un crédito disponible',
  'send.delivery_managed_note':
    'El servicio comprueba si puedes usar la prueba o un crédito antes de crear la solicitud. Puedes volver aquí al envío manual.',
  'send.reminders': 'Recordatorios automáticos',
  'editor.reminders': 'Recordatorios',
  'editor.reminders_off': 'Desactivados',
  'reminders.first_after_option': 'El primero tras {days}',
  'reminders.after_window':
    'Esta solicitud se cierra tras {days}, antes del primer recordatorio, así que no se enviará ninguno. Elige uno anterior.',
  'reminders.repeat_every': 'Después, cada',
  'editor.reminders_managed_only':
    'Los recordatorios automáticos solo se envían con una invitación gestionada. Elígela en la pantalla de envío; los enlaces que compartes tú mismo no reciben recordatorios automáticos.',
  'editor.reminders_hint':
    'Los recordatorios se envían por correo a quienes aún no han firmado y se detienen cuando firman o la solicitud termina.',
  'send.managed_no_credits':
    'La última información de la cuenta no muestra créditos de entrega disponibles. El servicio volverá a comprobarlo; el envío manual sigue disponible.',
  'send.managed_trial_unavailable':
    'La última información de la cuenta no muestra una prueba gestionada disponible. El servicio volverá a comprobarlo; el envío manual sigue disponible.',
  'send.no_required_signature_recommendation':
    'Ningún campo de firma obligatorio está asignado a un firmante externo. Revisa los campos antes de crear esta solicitud.',
  'send.free_signer_limit':
    'Este borrador tiene {count} firmantes externos. Free permite {limit}; conserva el borrador y quita un firmante, o verifica una cuenta Lifetime antes de crear la solicitud.',
  'send.free_signer_limit_confirmed':
    'Este borrador tiene {count} firmantes externos. Tu cuenta Free permite {limit}; el borrador está guardado. Quita un firmante o mejora tu plan antes de crear la solicitud.',
  'send.review_upgrade': 'Ver Lifetime',
  'send.managed_switch_manual': 'Envíalo tú mismo (manual)',
  'send.remove_extra_signer': 'Quitar el firmante adicional',
  'send.active_limit_one':
    'Tienes {count} solicitud activa, el máximo que tu plan permite a la vez. Espera a que termine o anúlala si ya no la necesitas y luego envía esta. Tu borrador está guardado.',
  'send.active_limit':
    'Tienes {count} solicitudes activas, el máximo que tu plan permite a la vez. Espera a que una termine o anula una que ya no necesites y luego envía esta. Tu borrador está guardado.',
  'send.active_limit_upgrade_one': 'Obtener Lifetime: hasta {count} solicitud activa',
  'send.active_limit_upgrade': 'Obtener Lifetime: hasta {count} solicitudes activas',
  'send.active_limit_waiting': 'Ver solicitudes en espera',
  'send.free_signer_limit_free':
    'Este borrador tiene {count} firmantes externos. Tu cuenta Free permite {limit}; el borrador está guardado. Quita un firmante antes de crear la solicitud.',
  'send.fix_before_sending_title': 'Corrige antes de enviar',
  'send.issue.no_external_signer': 'Añade a alguien que firme este documento.',
  'send.issue.unassigned_required_one': '1 campo obligatorio no tiene firmante asignado.',
  'send.issue.unassigned_required_other':
    '{count} campos obligatorios no tienen firmante asignado.',
  'send.issue.invalid_owner_one': '1 campo pertenece a un destinatario que se quitó.',
  'send.issue.invalid_owner_other': '{count} campos pertenecen a un destinatario que se quitó.',
  'send.issue.unknown': 'Algo en este borrador necesita atención antes de enviarlo.',
  'send.issue.action_add_recipient': 'Añadir destinatario',
  'send.issue.action_assign_fields': 'Asignar campos',
  'send.issue.action_open_draft': 'Abrir borrador',
  'send.failed': 'No pudimos enviar esta solicitud.',
  'send.start': 'Enviar para firma',
  'send.checking_plan': 'Comprobando tu plan…',
  'send.review_problems': 'Revisar lo que falta corregir',
  'send.start_manual': 'Crear enlace',
  'send.start_managed': 'Enviar con {product}',
  'send.start_manual_sign': 'Firmar y crear enlace',
  'send.start_managed_sign': 'Firmar y enviar',
  'send.start_title': 'Enviar para firma',
  'send.start_description':
    'Elige el documento que quieres que firmen. Después añades quién firma y dónde.',
  'send.replace_title': 'Cambiar documento',
  'send.replace_description':
    'Elige el archivo nuevo. Quién firma, tu mensaje y los campos se quedan en tu borrador.',
  'send.replace_back': 'Volver a tu borrador',
  'send.continue_title': 'Continuar un borrador',
  'send.continue': 'Continuar',
  'send.continue_to': 'Para {names}',
  'send.active_limit_banner_one':
    'Tienes {count} solicitud en espera, el máximo que permite tu plan a la vez. Puedes prepararla ahora y enviarla cuando esa termine.',
  'send.active_limit_banner':
    'Tienes {count} solicitudes en espera, el máximo que permite tu plan a la vez. Puedes prepararla ahora y enviarla cuando una termine.',
  'send.step_review': 'Revisar y enviar',
  'send.step_review_short': 'Revisar',
  'send.steps_progress': 'Paso {current} de {total}',
  'send.review_page_title': 'Revisar y enviar',
  'send.review_people': 'Personas',
  'send.edit': 'Editar',
  'send.edit_people': 'Editar quién firma',
  'send.edit_message': 'Editar el mensaje',
  'send.person_signs': 'Firma',
  'send.person_you_first': 'Tú · firmas primero',
  'send.person_you_last': 'Tú · firmas al final',
  'send.person_signs_first': 'Firmas primero',
  'send.person_signs_last': 'Firmas al final',
  'send.person_no_email': 'Sin correo',
  'send.person_no_name': 'Sin nombre todavía',
  'send.draft_missing':
    'Este borrador no está en este dispositivo. Los borradores se quedan en el dispositivo donde se crearon, así que ábrelo allí.',
  'send.back_to_start': 'Elige algo para enviar',
  'send.order_in_turn': 'Uno tras otro: {names}.',
  'send.order_together': 'Cada persona recibe su propio enlace al mismo tiempo.',
  'send.review_link_until': 'Funciona hasta el {date} ({days})',
  'send.review_message': 'Tu mensaje',
  'send.review_note_manual': 'Se añade al mensaje que compartes con el enlace.',
  'send.review_note_managed':
    'Los correos que enviamos usan una invitación estándar, así que tus palabras no van en ellos. Se añaden cuando compartes un enlace tú mismo.',
  'send.your_part': 'Tu parte',
  'send.your_part_page': 'Página {page}',
  'send.your_part_none':
    'No tienes campos aquí. Firmar primero aprueba el documento tal como está.',
  'send.your_part_check': 'Revisar tus campos',
  'send.sender_name_needed': 'Añade tu nombre arriba. Se registra con tu firma.',
  'send.sender_name_needed_from':
    'Añade tu nombre arriba para que los destinatarios sepan de quién es la solicitud.',
  'send.sender_first_note':
    'Firmas primero. «{action}» te pide adoptar tu firma y después los demás reciben sus enlaces.',
  'send.sender_last_note':
    'Firmas al final. Cuando todos los demás hayan firmado, abre la solicitud para añadir tu firma.',
  'send.sending_managed':
    'Enviando… {product} enviará el correo a tus firmantes en cuanto termine.',
  'send.sending_manual': 'Creando tu enlace de firma… Puede tardar unos segundos.',
  'send.progress_preparing': 'Preparando la solicitud',
  'send.progress_securing': 'Cifrando el documento en este dispositivo',
  'send.progress_uploading': 'Subiendo la copia cifrada',
  'send.progress_activating': 'Iniciando la solicitud',
  'send.progress_activating_manual': 'Iniciando la solicitud y creando los enlaces',
  'send.delivery_heading': 'Elige cómo enviarlo',
  'send.delivery_question': '¿Cómo quieres enviarlo para firmar?',
  'send.delivery_managed_only':
    'Con Lifetime, {product} envía cada solicitud por correo. Si un correo no se puede entregar, la página de la solicitud te deja compartir el enlace tú mismo.',
  'send.delivery_managed_title': 'Deja que {product} se encargue',
  'send.delivery_managed_trial':
    'Enviamos el correo a cada firmante, le recordamos y seguimos el progreso.',
  'send.delivery_managed_unknown':
    '{product} envía a cada firmante su enlace por correo y manda recordatorios.',
  'send.delivery_managed_credit_one':
    'Enviamos el correo a cada firmante, le recordamos y seguimos el progreso. Usa 1 de tu {count} envío.',
  'send.delivery_managed_credit':
    'Enviamos el correo a cada firmante, le recordamos y seguimos el progreso. Usa 1 de tus {count} envíos.',
  'send.delivery_managed_locked':
    'Enviamos el correo a cada firmante, le recordamos y seguimos el progreso. Parte de Lifetime.',
  'send.delivery_manual_title': 'Lo comparto yo',
  'send.delivery_manual_hint':
    'Recibe un enlace para cada firmante y envíalo como quieras: correo, WhatsApp, mensaje y más.',
  'send.delivery_badge_free': 'Gratis',
  'send.delivery_badge_trial': 'Primer envío gratis',
  'send.delivery_trial_title': 'Tu primer envío corre por nuestra cuenta',
  'send.delivery_trial_body':
    '{product} envía los correos y los recordatorios, y puedes añadir hasta {signers} firmantes, copias y un orden de firma. Después, los envíos gratuitos los compartes tú.',
  'send.delivery_trial_kept':
    'Si lo compartes tú, conservas tu primer envío gratuito para otra ocasión.',
  'send.compare_title': 'Comparación rápida',
  'send.compare_manual': 'Lo comparto yo',
  'send.compare_managed': '{product} se encarga',
  'send.compare_who': 'Quién lo envía',
  'send.compare_who_manual': 'Tú, como quieras',
  'send.compare_who_managed': '{product}, por correo',
  'send.compare_reminders': 'Recordatorios',
  'send.compare_reminders_manual': 'Tú haces el seguimiento',
  'send.compare_reminders_managed': 'Automáticos',
  'send.compare_signers': 'Firmantes',
  'send.compare_signers_manual': '{count} a la vez',
  'send.compare_signers_managed': 'Hasta {count}',
  'send.compare_link': 'El enlace dura',
  'send.compare_link_manual': '{days} días',
  'send.compare_link_managed': 'Hasta {days} días',
  'send.compare_cost': 'Precio',
  'send.compare_cost_manual': 'Gratis',
  'send.compare_cost_trial': 'Gratis esta vez',
  'send.compare_cost_lifetime': 'Lifetime',
  'send.compare_cost_unknown': 'Prueba o crédito',
  'send.delivery_no_credits':
    'Ya usaste todos tus envíos, así que esta vez comparte el enlace tú mismo.',
  'send.delivery_buy_pack': 'Comprar más envíos',
  'send.limits_title': 'Antes de poder enviar',
  'send.limit_signers':
    'Free envía a {limit} firmante a la vez y este borrador tiene {count}. Tu borrador está guardado.',
  'send.limit_cc_one':
    'Enviar una copia a {names} es parte de Lifetime. Puedes compartir el PDF firmado tú mismo cuando esté listo.',
  'send.limit_cc':
    'Enviar copias a {names} es parte de Lifetime. Puedes compartir el PDF firmado tú mismo cuando esté listo.',
  'send.remove_cc': 'Quitar a {names}',
  'send.limits_upgrade': 'Ver qué añade Lifetime',
  'send.limits_use_trial': 'Que {product} lo envíe, gratis esta vez',
  'send.plan_banner_free': 'Plan gratuito: {free} firmante externo a la vez.',
  'send.plan_banner_upgrade_hint':
    'Mejora tu plan para añadir más firmantes y que {product} se encargue del envío.',
  'send.plan_banner_share_hint': 'Tú compartes cada enlace de firma.',
  'send.plan_banner_upgrade': 'Mejorar',
  'send.plan_banner_lifetime': 'Lifetime: te quedan {count} créditos de envío.',
  'send.plan_banner_lifetime_one': 'Lifetime: te queda 1 crédito de envío.',
  'send.plan_banner_lifetime_hint':
    '{product} envía la solicitud por correo y recuerda a cada firmante; cada solicitud usa un crédito.',
  'send.issue.signer_without_signature_one': '{names} no tiene dónde firmar.',
  'send.issue.signer_without_signature_other': '{names} no tienen dónde firmar.',
  'send.issue.sender_without_signature':
    'Tú también firmas, pero no tienes dónde firmar. Añade un campo de firma para ti.',
  'send.issue.action_add_signature': 'Añadir un campo de firma',
  'send.issue.recipient_name_missing_one': 'Una persona aún no tiene nombre.',
  'send.issue.recipient_name_missing_other': '{count} personas aún no tienen nombre.',
  'send.issue.action_add_name': 'Añadir nombres',
  'send.issue.managed_email_missing_one': 'Añade un correo para {names} para que podamos enviarlo.',
  'send.issue.managed_email_missing_other': 'Añade correos para {names} para que podamos enviarlo.',
  'send.issue.action_add_email': 'Añadir correo',
  'send.issue.share_myself_instead': 'Compartirlo yo mismo',
  'send.issue.cc_needs_lifetime_one': 'Una copia para {names} es parte de Lifetime.',
  'send.issue.cc_needs_lifetime_other': 'Las copias para {names} son parte de Lifetime.',
  'send.sent_managed_title': 'Enviado',
  'send.sent_managed_description':
    'Estamos enviando la invitación por correo. La página de la solicitud muestra cuándo cada persona la abre y la firma.',
  'send.sent_managed_signed_first':
    'Ya firmaste. Estamos enviando a {names} su enlace por correo. La página de la solicitud muestra cuándo lo abren y lo firman.',
  'send.sent_managed_to': '{name} · {email}',
  'send.sent_reminders': 'Recordatorios: tras {first} y después cada {repeat}.',
  'send.sent_reminders_off': 'Sin recordatorios automáticos.',
  'send.sent_trial_used':
    'Ese fue tu primer envío gratuito. A partir de ahora, las solicitudes gratuitas las compartes tú.',
  'send.sent_trial_used_lifetime':
    'Ese fue tu primer envío gratuito. A partir de ahora, las solicitudes gratuitas las compartes tú, o Lifetime las envía (incluye {credits} envíos por correo).',
  'send.sent_credits_left_one': 'Queda {count} envío.',
  'send.sent_credits_left': 'Quedan {count} envíos.',
  'send.sent_link_until': 'El enlace funciona hasta el {date}.',
  'send.share_title_one': 'Comparte el enlace con {name}',
  'send.share_title_many': 'Comparte el enlace de cada persona',
  'send.created_title': 'Solicitud creada',
  'send.share_description':
    'Envíalo como prefieras. No se envía ningún correo por ti. La página de la solicitud muestra cuándo se abre y se firma.',
  'send.share_signed_first': 'Ya firmaste. Ahora comparte el enlace con {names}.',
  'send.share': 'Compartir…',
  'send.copy_message': 'Copiar mensaje',
  'send.copied': 'Copiado',
  'send.link_label': 'Enlace de firma para {name}',
  'send.share_message_label': 'Mensaje para {name}',
  'send.each_own_link':
    'Cada persona tiene su propio enlace. No reenvíes el enlace de una persona a otra.',
  'send.qr_caption': 'Escanéalo para abrir la página de firma. Funciona hasta el {date}.',
  'send.next_after':
    '{next} recibe su enlace cuando {current} firme. Compártelo entonces desde la página de la solicitud.',
  'send.done': 'Listo',
  'share.message': 'Hola {name}: revisa y firma «{title}». El enlace funciona hasta el {date}.',
  'share.message_no_expiry': 'Hola {name}: revisa y firma «{title}».',
  'share.message_from':
    'Hola {name}: {sender} te pide que revises y firmes «{title}». El enlace funciona hasta el {date}.',
  'share.message_from_no_expiry': 'Hola {name}: {sender} te pide que revises y firmes «{title}».',
  'share.message_ask_from': '{sender} te pide que revises y firmes «{title}».',
  'share.message_ask': 'Revisa y firma «{title}».',
  'share.message_until': 'El enlace funciona hasta el {date}.',
  'send.copy_link': 'Copiar enlace',
  'send.show_qr': 'Mostrar código QR',
  'share.via': 'Compartir por',
  'share.more': 'Más',
  'share.qr_short': 'Código QR',
  'share.sheet_title': 'Comparte el enlace',
  'share.sheet_title_named': 'Comparte el enlace de {name}',
  'share.channel_email': 'Correo',
  'share.channel_sms': 'SMS',
  'share.email_title': 'Enviar por correo',
  'share.email_office365': 'Outlook (trabajo o centro educativo)',
  'share.email_app': 'App de correo de este dispositivo',
  'share.all_apps': 'Todas las apps',
  'share.other_apps': 'Otras apps de este dispositivo',
  'share.paste_note': 'Mensaje y enlace copiados. Pégalos en {app}.',
  'share.paste_manual': 'No se pudo copiar el mensaje. Cópialo desde aquí y pégalo en {app}.',
  'send.qr_label': 'Código QR del enlace de firma de {name}',
  'send.observed': 'Lo que observamos',
  'send.link_copied': 'Enlace copiado en este dispositivo',
  'send.qr_shown': 'Código QR mostrado en este dispositivo',
  'send.no_delivery_claim':
    'Compartir es cosa tuya: registramos que compartiste, nunca que alguien lo recibió o lo abrió. La entrega aparece solo cuando se abre la página de firma.',
  'recipient.signing_title': 'Revisar y firmar',
  'recipient.loading': 'Cargando…',
  'recipient.offline':
    'No pudimos contactar con el servicio de firma. Tu trabajo está a salvo; inténtalo de nuevo.',
  'recipient.submitted': 'Ya firmaste este documento.',
  'recipient.receipt_title': 'Firma recibida',
  'recipient.receipt_body': 'Tu firma se ha recibido.',
  'recipient.final_title': 'Tu copia firmada',
  'recipient.final_hint': 'Se compartió contigo la copia firmada de un acuerdo.',
  'recipient.final_failed': 'No se pudo descargar tu copia firmada.',
  'recipient.receipt_signed_on': 'Firmaste esto el {date}.',
  'recipient.receipt_complete': 'Este acuerdo está completo.',
  'recipient.receipt_reference': 'Referencia del recibo: {code}',
  'recipient.next_title': 'Qué pasa ahora',
  'recipient.next_waiting':
    'Otras personas aún tienen que firmar. Cuando todas lo hayan hecho, el dispositivo de {sender} termina el acuerdo y crea la copia firmada.',
  'recipient.next_waiting_one':
    '{name} aún tiene que firmar. Después, el dispositivo de {sender} termina el acuerdo y crea la copia firmada.',
  'recipient.next_waiting_sender':
    '{sender} aún tiene que firmar. Después, su dispositivo termina el acuerdo y crea la copia firmada.',
  'recipient.next_finishing':
    'Todas las personas han firmado. El acuerdo se termina en el dispositivo de {sender} la próxima vez que abra {product}.',
  'recipient.next_email':
    'Te enviaremos por correo un enlace a tu copia firmada en cuanto esté lista.',
  'recipient.next_come_back':
    'Guarda este enlace. Vuelve a él para descargar tu copia firmada: estará aquí durante {days} después de que se termine el acuerdo.',
  'recipient.next_come_back_plain':
    'Guarda este enlace y vuelve a él más tarde para descargar tu copia firmada.',
  'recipient.next_ready':
    'Descarga tu copia firmada y guárdala en un lugar seguro. Está disponible aquí hasta el {date}.',
  'recipient.next_closed':
    'La copia firmada estuvo disponible aquí hasta el {date}. Pide una copia a {sender}.',
  'recipient.next_ended':
    'Esta solicitud terminó antes de que todas las personas firmaran, así que no hay copia firmada. Contacta con {sender} si tienes preguntas.',
  'recipient.sender_fallback': 'quien te lo envió',
  'recipient.final_download': 'Descargar PDF firmado',
  'recipient.final_downloading': 'Obteniendo tu copia…',
  'recipient.final_not_ready':
    'La copia firmada aún no está lista. Vuelve a este enlace más tarde.',
  'recipient.final_closed':
    'Esta copia ya no está disponible aquí. Pide el archivo a quien te lo envió.',
  'recipient.final_no_key':
    'Este enlace está incompleto. Abre el enlace completo de tu correo o mensaje.',
  'recipient.cc_intro': '{sender} compartió contigo la copia firmada de “{title}”.',
  'recipient.cc_available_until':
    'Disponible hasta el {date}. Descárgala para conservar una copia.',
  'recipient.cc_title': 'Copia firmada',
  'recipient.cc_invalid': 'Este enlace no funciona.',
  'recipient.cc_invalid_next':
    'Comprueba que abriste el enlace completo de tu correo o mensaje. Si sigue sin funcionar, pide uno nuevo a quien te lo compartió.',
  'recipient.from_sender': 'De {sender}',
  'recipient.no_key':
    'Este enlace está incompleto, así que el documento no se puede abrir. Abre el enlace completo de tu correo o mensaje, o pide al remitente que lo envíe de nuevo.',
  'recipient.open_failed': 'No pudimos abrir este documento. Pide al remitente que lo reenvíe.',
  'recipient.page_of': 'Página {page} de {total}',
  'recipient.previous_page': 'Página anterior',
  'recipient.next_page': 'Página siguiente',
  'recipient.field_required': '{label} es obligatorio.',
  'recipient.signature_apply': 'Usar esta firma',
  'recipient.signature_applied': 'Firma añadida',
  'recipient.signature_label': 'Dibuja tu firma',
  'recipient.signature_clear': 'Borrar',
  'recipient.submit_failed': 'No se pudo enviar tu firma. Revisa tu conexión e inténtalo de nuevo.',
  'recipient.consent_intro': 'Al continuar, aceptas firmar este documento electrónicamente.',
  'recipient.language_label': 'Idioma',
  'recipient.attachments_title': 'Adjuntos',
  'recipient.attachments_progress': '{collected} de {required} adjuntos obligatorios recogidos',
  'recipient.attachment_required': 'obligatorio',
  'recipient.attachment_limits': '{types} · hasta {files} · hasta {megabytes} MB cada uno',
  'recipient.attachment_choose': 'Elegir un archivo',
  'recipient.attachment_remove': 'Quitar',
  'recipient.attachment_stored': 'añadido',
  'recipient.attachment_pending': 'aún no guardado',
  'recipient.attachment_collected': 'Adjunto guardado.',
  'recipient.attachment_kept':
    'Los archivos que añadiste se guardan con tu firma y no se pueden quitar aquí. Si añadiste un archivo equivocado, avisa a {sender}.',
  'recipient.attachment_rejected': 'Ese archivo se rechazó. Elige otro archivo.',
  'recipient.attachment_retry':
    'La subida no terminó. Inténtalo de nuevo: el mismo archivo no se duplicará.',
  'recipient.attachment_blocks_finish': 'Este adjunto es obligatorio antes de poder finalizar.',
  'recipient.not_yet': 'Esta solicitud sigue con un firmante anterior.',
  'recipient.not_yet_hint':
    'Tu enlace es válido y nada ha cambiado: este documento se firma en orden, así que pasará a ser tuyo cuando se complete el paso anterior. Recarga para comprobarlo de nuevo.',
  'recipient.consent_locale_note':
    'Esta declaración se muestra en {locale}, el idioma más cercano en el que está disponible.',
  'recipient.submit': 'Enviar firma',
  'recipient.submitting': 'Enviando tu firma de forma segura…',
  'recipient.submit_busy': 'Enviando…',
  'recipient.submit_retry': 'Reintentar',
  'recipient.submit_link_changed':
    'Este enlace se reemplazó o la solicitud cambió, así que esta firma no se puede enviar. Usa el enlace más reciente que te envió {sender}.',
  'recipient.submit_expired':
    'Este enlace caducó antes de que terminaras. Pide un enlace nuevo a {sender}.',
  'recipient.declined': 'Esta solicitud fue rechazada.',
  'recipient.from_sender_expires': 'De {sender} · El enlace caduca el {date}',
  'recipient.consent_title': 'Acepta firmar electrónicamente',
  'recipient.consent_lead':
    '{sender} te envió “{title}” para revisarlo y firmarlo. Antes de empezar, lee y acepta esta declaración.',
  'recipient.consent_continue': 'Continuar',
  'recipient.disclosure_summary': 'Sobre la firma electrónica',
  'recipient.disclosure_record':
    'Tu aceptación se guarda con tu firma, junto con la fecha, la hora y la versión de esta declaración.',
  'recipient.disclosure_decline':
    'No tienes que firmar electrónicamente. Para decir que no, abre Otras opciones y elige Rechazar la firma. {sender} verá que la rechazaste.',
  'recipient.disclosure_copy':
    'Cuando todas las personas hayan firmado, podrás obtener la copia firmada. Para una copia en papel o preguntas sobre el documento, contacta con {sender}.',
  'recipient.disclosure_privacy': 'Política de privacidad',
  'recipient.disclosure_terms': 'Condiciones de uso',
  'recipient.consent_unavailable':
    'Esta página no puede mostrar la declaración de consentimiento correcta. Vuelve a cargarla; si sigue pasando, pide al remitente un enlace nuevo.',
  'recipient.next': 'Siguiente',
  'recipient.finish_ready': 'Listo para terminar.',
  'recipient.remaining_one': 'Queda 1 campo obligatorio',
  'recipient.remaining_other': 'Quedan {count} campos obligatorios',
  'recipient.field_error_required': 'Este campo es obligatorio.',
  'recipient.field_error_sign': 'Toca aquí para firmar.',
  'recipient.field_error_check': 'Marca esta casilla para continuar.',
  'recipient.field_error_too_long': 'Es demasiado largo.',
  'recipient.field_error_email': 'Escribe un correo electrónico, como nombre@ejemplo.com.',
  'recipient.field_error_number': 'Escribe un número.',
  'recipient.field_error_letters': 'Usa solo letras.',
  'recipient.field_error_date': 'Escribe una fecha.',
  'recipient.field_error_zip': 'Escribe un código postal de 5 dígitos.',
  'recipient.field_error_zip4': 'Escribe un código ZIP+4, como 12345-6789.',
  'recipient.field_error_ssn': 'Escríbelo como 123-45-6789.',
  'recipient.field_error_format': 'No coincide con el formato que pidió el remitente.',
  'recipient.field_error_formula': 'Completa los campos que usa este total.',
  'recipient.date_signed_hint': 'Se completa con la fecha de hoy al terminar',
  'recipient.locked_hint': 'Lo fijó el remitente',
  'recipient.formula_hint': 'Calculado a partir de tus datos',
  'recipient.previous_field': 'Campo anterior',
  'recipient.optional_left_one': 'Queda 1 campo opcional vacío',
  'recipient.optional_left_other': 'Quedan {count} campos opcionales vacíos',
  'recipient.optional_review': 'Revisar',
  'recipient.todo_title': 'Pendiente',
  'recipient.todo_item': 'Página {page} · {label}',
  'recipient.draft_restored': 'Guardamos en este dispositivo lo que completaste.',
  'recipient.render_failed':
    'No se pudo mostrar esta página. Actualiza Android System WebView o tu navegador. En iPhone, actualiza iOS y vuelve a abrir el documento.',
  'recipient.document_label': 'Documento',
  'recipient.page_text': 'Texto de la página {page}',
  'recipient.sign_here': 'Firma aquí',
  'recipient.initial_here': 'Iniciales aquí',
  'recipient.sign_short': 'Firmar',
  'recipient.initial_short': 'Iniciales',
  'recipient.adopt_title': 'Adopta tu firma',
  'recipient.adopt_title_both': 'Adopta tu firma y tus iniciales',
  'recipient.adopt_title_initials': 'Adopta tus iniciales',
  'recipient.adopt_draw': 'Dibujar',
  'recipient.adopt_type': 'Escribir',
  'recipient.adopt_upload': 'Subir',
  'recipient.adopt_upload_choose': 'Elegir una imagen',
  'recipient.adopt_upload_hint': 'Un PNG o JPEG de tu marca sobre un fondo liso.',
  'recipient.adopt_upload_for_signature': 'Esta imagen será tu firma.',
  'recipient.adopt_upload_for_initials': 'Esta imagen se usará como tus iniciales.',
  'recipient.adopt_upload_for_both': 'Esta imagen será tu firma. Tus iniciales se escriben abajo.',
  'recipient.adopt_upload_failed':
    'No se pudo usar esa imagen. Elige un PNG o JPEG con una marca visible.',
  'recipient.adopt_name_label': 'Tu nombre',
  'recipient.adopt_initials_label': 'Iniciales',
  'recipient.adopt_preview_signature': 'Aquí aparece tu firma',
  'recipient.adopt_preview_initials': 'Tus iniciales',
  'recipient.initials_draw_label': 'Dibuja tus iniciales',
  'recipient.adopt_statement':
    'Al seleccionar Adoptar y firmar, acepto que esta marca es mi firma electrónica y mis iniciales en este documento.',
  'recipient.adopt_statement_signature':
    'Al seleccionar Adoptar y firmar, acepto que esta marca es mi firma electrónica en este documento.',
  'recipient.adopt_statement_initials':
    'Al seleccionar Adoptar y firmar, acepto que estas son mis iniciales electrónicas en este documento.',
  'recipient.adopt_and_sign': 'Adoptar y firmar',
  'recipient.adopt_cancel': 'Cancelar',
  'recipient.zoom_in': 'Acercar',
  'recipient.zoom_out': 'Alejar',
  'recipient.zoom_fit': 'Ajustar al ancho',
  'recipient.options': 'Otras opciones',
  'recipient.decline_action': 'Rechazar la firma',
  'recipient.session_info': 'Información de la sesión',
  'recipient.finish_later': 'Terminar más tarde',
  'recipient.finish_later_title': '¿Terminar más tarde?',
  'recipient.finish_later_body':
    'Lo que escribiste se queda en este dispositivo hasta el {date}. Abre el mismo enlace en este dispositivo para continuar. Al volver, aceptarás la declaración y añadirás tu firma de nuevo.',
  'recipient.finish_later_blocked':
    'Este navegador no guarda datos de esta página, así que lo que escribiste no se puede conservar. Si sales ahora, tendrás que completarlo de nuevo al volver.',
  'recipient.finish_later_confirm': 'Guardar y salir',
  'recipient.finish_later_saved_title': 'Guardado para más tarde',
  'recipient.finish_later_saved':
    'Lo que escribiste se guarda en este dispositivo hasta el {date}. Puedes cerrar esta página. Para continuar, abre el mismo enlace otra vez en este dispositivo.',
  'recipient.finish_later_resume': 'Seguir firmando',
  'recipient.decline_title': '¿Rechazar la firma?',
  'recipient.decline_consequence':
    'Al rechazar, esta solicitud termina para todos y {sender} verá que la rechazaste. No se puede deshacer.',
  'recipient.decline_continue': 'Continuar con el rechazo',
  'recipient.decline_keep_signing': 'Seguir firmando',
  'recipient.decline_reason_label': 'Motivo (opcional, {sender} lo verá)',
  'recipient.decline_reason_count': '{count} de {max} caracteres',
  'recipient.decline_reason_too_long': 'El motivo es demasiado largo. Acórtalo para continuar.',
  'recipient.decline_submit': 'Rechazar',
  'recipient.decline_failed':
    'No pudimos registrar tu rechazo. Revisa tu conexión e inténtalo de nuevo.',
  'recipient.declined_title': 'Rechazaste la firma',
  'recipient.declined_on': 'Rechazaste “{title}” el {date}.',
  'recipient.declined_told':
    '{sender} puede ver que la rechazaste, y la solicitud terminó para todas las personas.',
  'recipient.declined_reason': 'Tu motivo',
  'recipient.declined_next_steps':
    'No tienes que hacer nada más. Si cambias de opinión, pide a {sender} que envíe una nueva solicitud.',
  'recipient.back': 'Atrás',
  'recipient.session_signer': 'Firmas como',
  'recipient.session_sender': 'Enviado por',
  'recipient.session_expires': 'El enlace caduca',
  'recipient.session_reference': 'Referencia de la solicitud',
  'recipient.session_copy': 'Copiar referencia',
  'recipient.session_copied': 'Referencia copiada',
  'recipient.not_me': 'No soy yo',
  'recipient.not_me_title': '¿No eres {name}?',
  'recipient.not_me_body':
    'Esta solicitud es para {name}. Por favor, no la firmes. Avisar a {sender} le permite enviarla a la persona correcta; esto termina la solicitud.',
  'recipient.not_me_continue': 'Avisar al remitente',
  'recipient.not_me_reason': 'Se envió a la persona equivocada.',
  'recipient.help_title': '¿Necesitas ayuda?',
  'recipient.help_render_q': 'El documento no se ve o se ve mal',
  'recipient.help_render_a':
    'Vuelve a cargar la página. Si sigue sin verse, abre el enlace en otro navegador o pide al remitente que lo envíe de nuevo.',
  'recipient.help_sign_q': 'No puedo añadir mi firma',
  'recipient.help_sign_a':
    'Toca un recuadro “Firma aquí”, escribe tu nombre o dibuja con el dedo o el ratón y elige Adoptar y firmar.',
  'recipient.help_link_q': 'Mi enlace caducó o no funciona',
  'recipient.help_link_a':
    'Los enlaces dejan de funcionar cuando caducan o se reemplazan por uno más reciente. Pide un enlace nuevo a quien te lo envió.',
  'recipient.help_wrong_q': 'Esto le llegó a la persona equivocada',
  'recipient.help_wrong_a':
    'No lo firmes. Abre Otras opciones y elige “No soy yo” para avisar al remitente.',
  'recipient.help_contact':
    'Para preguntas sobre el documento, contacta con quien te lo envió. Nosotros no podemos ver tu documento.',
  'recipient.help_email': 'Escribir a soporte',
  'recipient.expired_next': 'Pide un enlace nuevo a quien te lo envió.',
  'recipient.unavailable_next':
    'Puede que se haya reemplazado por un enlace más reciente, o que la solicitud se haya cancelado o rechazado. Si aún tienes que firmar, usa el mensaje más reciente del remitente o pídele un enlace nuevo.',
  'recipient.declined_next':
    'Ya nadie puede firmarla. Si no lo esperabas, contacta con quien te la envió.',
  'recipient.invalid': 'Este enlace de firma no funciona.',
  'recipient.invalid_next':
    'Comprueba que abriste el enlace completo de tu correo o mensaje. Si sigue sin funcionar, pide uno nuevo al remitente.',
  'recipient.submitted_next': 'Vuelve a este enlace más tarde para descargar tu copia firmada.',
  'recipient.expired': 'Este enlace de firma ha caducado.',
  'recipient.unavailable': 'Este enlace de firma ya no está disponible.',
  'editor.attachments_title': 'Adjuntos',
  'editor.attachments_hint':
    'Pide a un firmante que adjunte un archivo con su respuesta. Los adjuntos son evidencia propia: nunca se añaden al documento firmado, y cada solicitud se ofrece a quien le corresponde.',
  'editor.attachments_lifetime_only':
    'Los adjuntos forman parte de Lifetime. Tu borrador no se modifica.',
  'editor.attachment_add': 'Añadir una solicitud de adjunto',
  'editor.pages_open': 'Abrir lista de páginas',
  'editor.pages_close': 'Cerrar lista de páginas',
  'editor.attachment_owner': '¿Quién proporciona el archivo?',
  'editor.attachment_me': 'Yo (opcional)',
  'editor.attachment_sender_optional':
    'Los archivos que proporcionas son opcionales y nunca bloquean a un firmante.',
  'editor.attachment_label': '¿Qué estás pidiendo?',
  'editor.attachment_instructions': 'Instrucciones (opcional)',
  'editor.attachment_required_label': 'Obligatorio antes de que el firmante pueda finalizar',
  'editor.attachment_required': 'obligatorio',
  'editor.attachment_optional': 'opcional',
  'editor.attachment_limits': '{types} · hasta {files} · hasta {megabytes} MB cada uno',
  'editor.attachment_scope':
    'Visible para quien sube el archivo y para ti; el acceso del finalizador sigue el contrato de acceso revisado.',
  'editor.attachment_save': 'Añadir solicitud',
  'editor.attachment_remove': 'Quitar',
  'editor.attachment_cancel': 'Cancelar',
  'editor.placement_overlay':
    'Área de colocación de campos del documento. Elige un tipo de campo y usa las flechas y la tecla Intro para colocarlo.',

  /* Design pass — the surface hierarchy (page headers, badges, toolbars, the appearance choice).
   * Every new key ships translated: the localization gate fails on an untranslated addition. */
  'home.description': 'Firma localmente o prepara una solicitud cuando quieras.',
  'home.attention_title': 'Requiere atención',
  'home.waiting_title': 'Esperando a otros',
  'home.waiting_empty': 'Las solicitudes que envías para firmar aparecen aquí.',
  'home.waiting_unavailable': 'No se pudieron actualizar tus solicitudes.',
  'home.requests_offline': 'No se pudieron actualizar tus solicitudes. No tienes conexión.',
  'home.requests_as_of': 'Se muestran tal como estaban a las {time}.',
  'home.requests_as_of_day': 'Se muestran tal como estaban el {time}.',
  'home.signed_out': 'Inicia sesión para ver las solicitudes que enviaste.',
  'home.completed_title': 'Completados',
  'home.completed_empty': 'Los acuerdos terminados aparecen aquí, listos para volver a usar.',
  'home.attention_description': 'Solicitudes y borradores que te necesitan.',
  'home.attention.pending_finalization': 'Listo para terminar',
  'home.attention.delivery_failure': 'El correo no llegó',
  'home.attention.signing_expiring': 'Vence pronto',
  'home.attention.sender_signature': 'Te toca firmar',
  'home.attention.next_link_ready': 'Comparte el siguiente enlace',
  'home.attention.request_declined': 'Rechazado: ver motivo',
  'home.attention.finishing': 'Terminando…',
  'home.recent_document': 'Documento',
  'home.recent_draft': 'Borrador',
  'home.recent_completed': 'Completado',
  'home.attention_signed_out':
    'Inicia sesión para ver las solicitudes que te necesitan. Tus borradores siguen aquí.',
  'home.attention_unavailable': 'No se pudieron actualizar las solicitudes que te necesitan.',
  'requests.attention_title': 'Solicitudes que requieren atención',
  'requests.attention_description': 'Solicitudes y borradores que te necesitan.',
  'requests.attention_count': '{count} requieren tu atención',
  'requests.attention_count_one': '1 requiere tu atención',
  'requests.attention_empty': 'Nada te necesita ahora',
  'requests.attention_empty_hint':
    'Aquí aparecen las solicitudes que esperan algo de ti y los borradores sin terminar.',
  'requests.detail_title': 'Detalle de la solicitud',
  'requests.detail_description':
    'El estado actual que el servicio ha observado para esta solicitud.',
  'requests.detail_unavailable':
    'No se pudo cargar esta solicitud. Revisa tu conexión e inténtalo de nuevo.',
  'requests.detail_not_found':
    'Esta solicitud no está disponible. Puede que se haya eliminado o que pertenezca a otra cuenta.',
  'requests.detail_retry': 'Reintentar',
  'requests.back_to_agreements': 'Volver a acuerdos',
  'requests.detail_responses': 'Respuestas de firmantes obligatorios',
  'requests.detail_progress': '{submitted} de {total} aceptadas',
  'requests.detail_delivery': 'Modo de entrega',
  'requests.detail_signing_deadline': 'Plazo de firma',
  'requests.detail_recovery_deadline': 'Plazo de recuperación de finalización',
  'requests.detail_signers': 'Participantes',
  'requests.detail_step': 'Respuesta: {state} · Entrega: {delivery}',
  'requests.detail_viewed': 'Vista: {time}',
  'requests.person_signed_at': 'Firmó: {time}',
  'requests.person_declined_at': 'Rechazó: {time}',
  'requests.detail_not_viewed': 'Aún no vista',
  'requests.timeline_title': 'Cronología',
  'requests.timeline_description':
    'Lo que el servicio registró para esta solicitud, de lo más antiguo a lo más reciente. Las horas son las del registro de cada evento en el servicio.',
  'requests.timeline_participant': 'un participante',
  'requests.timeline_previous_recipient': 'el destinatario anterior',
  'requests.timeline.created': 'Preparada',
  'requests.timeline.activated': 'Enviada',
  'requests.timeline.activated_manual_one': 'Enlace de firma creado',
  'requests.timeline.activated_manual': 'Enlaces de firma creados',
  'requests.timeline.delivered': 'Correo entregado a {name}',
  'requests.timeline.reminder_sent': 'Recordatorio enviado a {name}',
  'requests.timeline.viewed': 'Vista por {name}',
  'requests.timeline.corrected': 'Datos corregidos de {name}',
  'requests.timeline.corrected_earlier': 'Datos corregidos',
  'requests.timeline.signed': 'Firmada por {name}',
  'requests.timeline.declined': 'Rechazada por {name}',
  'requests.timeline.completed': 'Completada',
  'requests.timeline.voided': 'Anulada',
  'requests.timeline.expired': 'Vencida',
  'requests.timeline.upcoming_label': 'Lo que falta',
  'requests.timeline.upcoming_sign': 'Firma {name}',
  'requests.timeline.upcoming_you': 'Firmas tú',
  'requests.timeline.upcoming_you_later': 'Cuando los demás hayan firmado',
  'requests.timeline.upcoming_after': 'Cuando todos hayan firmado',
  'requests.timeline.upcoming_finish': 'Cuando se cree la copia firmada',
  'requests.detail_finalization_pending':
    'Todas las respuestas obligatorias están selladas. La finalización está pendiente; actualiza para ver el estado de recuperación.',
  'requests.finalization_title': 'Completar esta solicitud',
  'requests.finalization_action': 'Finalizar en este dispositivo',
  'requests.finalization_committed':
    'Se guardaron el PDF completado y la evidencia. Verifica el PDF antes de guardar una copia.',
  'requests.finalization_conflict':
    'Otro finalizador o un cambio en las respuestas modificó esta solicitud. Actualiza antes de volver a intentarlo.',
  'requests.finalization_failed':
    'No se pudo completar esta solicitud. Las respuestas aceptadas siguen registradas.',
  'requests.download_title': 'PDF completado',
  'requests.download_description':
    'Este dispositivo verifica la descarga cifrada y el hash exacto del PDF antes de guardar una copia.',
  'requests.download_verify': 'Verificar PDF',
  'requests.download_verified':
    'PDF verificado en este dispositivo. Elige Guardar PDF para conservar una copia.',
  'requests.download_save': 'Guardar PDF',
  'requests.download_saved': 'PDF guardado.',
  'requests.download_started':
    'Se inició la descarga del PDF. Busca el archivo en las descargas del navegador.',
  'requests.activity_save_cancelled': 'Guardado cancelado. La solicitud no ha cambiado.',
  'requests.download_cancelled': 'Guardado cancelado. La solicitud completada sigue disponible.',
  'requests.download_unavailable':
    'No se puede guardar aquí. Abre la solicitud en otro navegador compatible.',
  'requests.download_failed': 'No se pudo guardar el PDF firmado.',
  'requests.download_check_failed':
    'Todavía no se pudo comprobar el PDF firmado. Descargar PDF firmado lo vuelve a intentar.',
  'requests.download_needs_local_key':
    'Este dispositivo necesita su clave local para abrir el PDF cifrado.',
  'requests.detail_delivery_failed':
    'La entrega requiere atención para {names}. El estado de entrega registrado no significa que el destinatario haya visto el documento.',
  'requests.detail_recovery_actions': 'Acciones de recuperación',
  'requests.share_again': 'Compartir de nuevo',
  'requests.detail_free_correction':
    'Los datos del destinatario en Free no se pueden corregir después de crear la solicitud. Conserva la evidencia actual, anula esta solicitud y prepara una nueva si debes cambiar los datos.',
  'requests.detail_correction_resend':
    'Enviar ahora la invitación corregida con esta solicitud; sin otro crédito de entrega.',
  'requests.detail_void_title': '¿Anular esta solicitud?',
  'requests.detail_void_action': 'Anular solicitud',
  'requests.detail_void_warning':
    'Esto cierra definitivamente la solicitud y sus enlaces actuales. La evidencia aceptada se conserva; los archivos ya descargados no se pueden retirar.',
  'requests.detail_prepare_new': 'Preparar una solicitud nueva',
  'requests.detail_cancel': 'Cancelar',
  'requests.detail_extend_title': 'Ampliar el plazo de firma',
  'requests.detail_extend_note':
    'Elige el plazo total desde la activación original. El servicio comprueba el límite antes de cambiar la fecha.',
  'requests.detail_extend_after_response':
    'Ya se aceptó una respuesta, así que este plazo no se puede ampliar.',
  'requests.state.unknown': 'Estado no disponible',
  'requests.state.local_draft': 'Borrador local',
  'requests.state.preparing': 'Preparando',
  'requests.state.preparation_failed': 'La preparación falló',
  'requests.state.active': 'Activa',
  'requests.state.awaiting_finalization': 'Esperando finalización',
  'requests.state.completed': 'Completada',
  'requests.state.finalization_failed': 'La finalización requiere recuperación',
  'requests.state.declined': 'Rechazada',
  'requests.state.voided': 'Anulada',
  'requests.state.expired': 'Vencida',
  'requests.recipient.blocked': 'Esperando turno',
  'requests.recipient.active': 'Esperando respuesta',
  'requests.recipient.submitted': 'Aceptada',
  'requests.recipient.declined': 'Rechazada',
  'requests.delivery_mode.manual': 'Comparte el enlace tú mismo',
  'requests.delivery_mode.managed': 'Invitación gestionada',
  'requests.delivery.not_managed': 'El remitente comparte el enlace',
  'requests.delivery.queued': 'En cola',
  'requests.delivery.dispatching': 'Enviando',
  'requests.delivery.accepted': 'Aceptada por el proveedor',
  'requests.delivery.delivered': 'Entregada',
  'requests.delivery.bounced': 'Rebotada',
  'requests.delivery.complained': 'Queja',
  'requests.delivery.suppressed': 'Suprimida',
  'requests.delivery.unknown': 'Resultado desconocido',
  'requests.sent_on': 'Enviado el {date}',
  'requests.completed_on': 'Completado el {date}',
  'requests.expires_on': 'Vence el {date}',
  'requests.expires_left': 'Vence el {date} · quedan {count} días',
  'requests.expires_left_one': 'Vence el {date} · queda 1 día',
  'requests.expired_on': 'Venció el {date}',
  'requests.progress_signed': '{signed} de {total} firmaron',
  'requests.expiring_banner': 'El enlace de firma deja de funcionar el {date}.',
  'requests.next.share_title': 'Comparte el enlace de firma',
  'requests.next.share_body':
    'No enviamos correos para esta solicitud. Envía a cada persona su enlace tú mismo. Puedes enviarlo de nuevo como recordatorio cuando quieras; es el mismo enlace.',
  'requests.next.next_link': 'Ahora le toca a {name}. Comparte su enlace.',
  'requests.next.waiting_title': 'Esperando a {names}',
  'requests.next.managed_body':
    'Enviamos el enlace de firma por correo. Si aún no han firmado, envía un recordatorio.',
  'requests.next.bounced': 'No se pudo entregar el correo a {name}.',
  'requests.next.your_turn_title': 'Te toca firmar',
  'requests.next.your_turn_body': '{names} firmó. Añade tu firma para terminar «{title}».',
  'requests.next.your_turn_first': 'Firma primero tu parte de «{title}». Los demás firman después.',
  'requests.adopted_next': 'Firmaste. Ahora comparte el enlace con {name}.',
  'requests.adopted_next_managed': 'Firmaste. Le estamos enviando a {name} su enlace por correo.',
  'requests.adopted_done': 'Firmaste. Todos han firmado, así que estamos terminando el acuerdo.',
  'requests.next.review_sign': 'Revisar y firmar',
  'requests.sign.title': 'Revisar y firmar',
  'requests.sign.lead':
    'Lee «{title}», acepta firmar electrónicamente y luego completa y firma tus campos.',
  'requests.sign.others_note':
    'Aquí solo se muestran tus campos. Lo que completó {names} aparece en el PDF firmado.',
  'requests.sign.finish': 'Firmar y terminar',
  'requests.sign.todo': 'Faltan {count} campos por completar.',
  'requests.sign.todo_one': 'Falta 1 campo por completar.',
  'requests.sign.loading': 'Abriendo el documento…',
  'requests.sign.no_source':
    'Abre {product} en el dispositivo desde el que lo enviaste para firmar. Ese dispositivo tiene el documento.',
  'requests.sign.save_failed': 'No se pudo guardar tu firma en este dispositivo.',
  'requests.sign.no_fields':
    'Este documento no tiene campos para ti. Firmar y terminar registra que lo aceptas tal como se envió.',
  'requests.share_link': 'Compartir…',
  'requests.link_until': 'El enlace funciona hasta el {date}',
  'requests.link_copied': 'Enlace copiado.',
  'requests.link_shared': 'Enlace compartido.',
  'requests.copy_by_hand': 'No se pudo copiar el enlace automáticamente. Selecciónalo y cópialo:',
  'requests.link_failed': 'No se pudo preparar el enlace.',
  'requests.link_too_soon':
    'El enlace de {name} se preparó hace poco, así que podrás obtenerlo aquí de nuevo después de las {time}. Un enlace que ya compartiste sigue funcionando.',
  'requests.link_limit':
    'El enlace de {name} ya no se puede obtener de nuevo aquí. Usa el que ya compartiste, o anula esta solicitud y envía una nueva.',
  'requests.remind': 'Enviar recordatorio',
  'requests.reminded': 'Recordatorio en camino a {name}. Puedes enviar {count} más.',
  'requests.reminded_one': 'Recordatorio en camino a {name}. Puedes enviar 1 más.',
  'requests.remind_too_soon':
    'Enviamos un correo a {name} hace poco. Podrás enviar un recordatorio después de las {time}.',
  'requests.remind_after': 'Podrás enviar un recordatorio después de las {time}.',
  'requests.remind_failed': 'No se pudo enviar el recordatorio.',
  'requests.remind_limit':
    'Ya enviaste todos los recordatorios que permite esta solicitud para {name}.',
  'requests.last_reminded': 'Último recordatorio: {date}',
  'requests.correct_failed': 'No se pudieron corregir los datos.',
  'requests.correct_scope':
    'Al corregir, {name} recibe un enlace nuevo y el que enviaste deja de funcionar. Las firmas ya hechas no cambian.',
  'requests.corrected': 'Datos corregidos. El enlace anterior de {name} ya no funciona.',
  'requests.finish.title': 'Todos firmaron',
  'requests.finish.working': 'Terminando la copia firmada en este dispositivo…',
  'requests.finish.body':
    'Este dispositivo crea el PDF firmado final. Mantén {product} abierto un momento.',
  'requests.finish.retry': 'Terminar ahora',
  'requests.finish.failed': 'No se pudo terminar el acuerdo.',
  'requests.finish.sender_marks_missing':
    'Tu propia firma para este acuerdo ya no está en este dispositivo, así que no se puede crear la copia firmada. Anula la solicitud y envíala de nuevo.',
  'requests.finish.conflict':
    'Otro dispositivo está terminando este acuerdo. Vuelve a comprobarlo en un momento.',
  'requests.finish.elsewhere':
    'Abre {product} en el dispositivo desde el que lo enviaste para terminarlo antes del {date}. Solo ese dispositivo puede crear la copia firmada.',
  'requests.finish.done': 'Terminado. La copia firmada está lista.',
  'requests.finish.elsewhere_nodate':
    'Abre {product} en el dispositivo desde el que lo enviaste para terminarlo. Solo ese dispositivo puede crear la copia firmada.',
  'requests.done.title': 'Firmado por todos',
  'requests.done.next_hint':
    'Enviar de nuevo crea una solicitud nueva a partir del original sin firmar, con las mismas personas. Usar de nuevo crea un acuerdo nuevo con el mismo documento.',
  'requests.download_pdf': 'Descargar PDF firmado',
  'requests.download_checking': 'Comprobando la copia firmada…',
  'requests.download_ready': 'Copia firmada verificada',
  'requests.file_signed': 'PDF firmado',
  'requests.file_signed_hint': 'El acuerdo con las firmas de todos.',
  'requests.document_view': 'Ver documento',
  'requests.document_hide': 'Ocultar documento',
  'requests.document_as_sent': 'Tal como lo enviaste',
  'requests.document_signed_copy': 'Copia firmada',
  'requests.document_pages_one': '{count} página',
  'requests.document_pages': '{count} páginas',
  'requests.document_unavailable':
    'Este documento no está en este dispositivo, así que no se puede mostrar aquí.',
  'requests.kept_on_device': 'Se guarda una copia en este dispositivo.',
  'requests.server_copy_until':
    'El servidor guarda una copia hasta el {date} (quedan {count} días). Guárdala ahora; después solo quedará la copia que guardaste.',
  'requests.server_copy_until_one':
    'El servidor guarda una copia hasta el {date} (queda 1 día). Guárdala ahora; después solo quedará la copia que guardaste.',
  'requests.server_copy_gone':
    'La copia del servidor se eliminó el {date}. Usa la copia que guardaste.',
  'requests.download_elsewhere':
    'El PDF firmado solo se abre en el dispositivo desde el que lo enviaste. Abre {product} allí o mueve tu biblioteca a este dispositivo.',
  'requests.move_library': 'Mover mi biblioteca',
  'requests.cc.title': 'Personas que reciben una copia',
  'requests.cc.waiting': 'Recibe la copia final cuando todos firmen',
  'requests.cc.emailed': 'Le enviamos la copia final por correo',
  'requests.cc.share': 'Compartir copia final',
  'requests.cc.failed': 'No se pudo preparar el enlace de la copia final.',
  'requests.closed.declined': '{name} rechazó el {date}.',
  'requests.closed.declined_someone': 'Rechazado el {date}.',
  'requests.closed.declined_nodate': '{name} rechazó.',
  'requests.closed.declined_plain': 'Rechazado.',
  'requests.closed.reason': 'Su motivo: «{reason}»',
  'requests.closed.no_reason': 'No indicó un motivo.',
  'requests.closed.void_reason': 'Tu motivo: «{reason}»',
  'requests.closed.expired': 'Venció el {date}. {names} no firmó a tiempo.',
  'requests.closed.expired_plain': 'Venció el {date}.',
  'requests.closed.voided': 'Anulaste esta solicitud el {date}.',
  'requests.closed.voided_plain': 'Anulaste esta solicitud.',
  'requests.closed.finish_failed':
    'Este acuerdo no se pudo terminar a tiempo, así que no está firmado. Envía una solicitud nueva para volver a recoger las firmas.',
  'requests.closed.prepare_failed':
    'Esta solicitud nunca salió: no se pudo preparar a tiempo, así que no se envió nada a nadie.',
  'requests.preparing.body':
    'Esta solicitud aún no ha salido. Compruébala desde Revisar y enviar en el dispositivo que la inició.',
  'requests.closed.next':
    'Envíalo de nuevo a las mismas personas. Tendrá un enlace y un plazo nuevos.',
  'requests.send_new': 'Enviar una solicitud nueva',
  'requests.send_again': 'Enviar de nuevo',
  'requests.save_template': 'Guardar como plantilla',
  'requests.template_saved': '«{title}» se guardó en tus plantillas.',
  'requests.template_failed': 'No se pudo guardar la plantilla.',
  'requests.template_open': 'Abrir plantillas',
  'requests.no_source':
    'Abre {product} en el dispositivo desde el que lo enviaste para enviarlo de nuevo.',
  'requests.restart_failed': 'No se pudo preparar la solicitud nueva.',
  'requests.activity.save': 'Guardar registro de actividad',
  'requests.activity.hint':
    'Un PDF con a quién se pidió la firma, qué pasó y cuándo. No es un acuerdo firmado. Disponible hasta el {date}.',
  'requests.activity.failed': 'No se pudo guardar el registro de actividad.',
  'requests.activity.heading': 'Registro de actividad',
  'requests.activity.notice':
    'Esto no es un acuerdo firmado. La solicitud terminó antes de que todos firmaran, así que no existe un documento firmado.',
  'requests.activity.source':
    'Creado en el dispositivo del remitente a partir de lo que el servicio de {product} registró sobre esta solicitud.',
  'requests.activity.footer': 'Registro de actividad de {product} · no es un acuerdo firmado',
  'requests.activity.section_request': 'Solicitud',
  'requests.activity.section_privacy': 'Lo que este registro no incluye',
  'requests.activity.title': 'Título',
  'requests.activity.outcome': 'Resultado',
  'requests.activity.sent': 'Enviada',
  'requests.activity.deadline': 'Plazo de firma',
  'requests.activity.ended': 'Terminó',
  'requests.activity.decline_reason': 'Motivo del rechazo',
  'requests.activity.void_reason': 'Motivo de la anulación',
  'requests.activity.request_id': 'ID de la solicitud',
  'requests.activity.generated': 'Registro creado',
  'requests.activity.viewed': 'vio el documento {time}',
  'requests.activity.signed': 'firmó {time}',
  'requests.more_title': 'Más acciones',
  'requests.void.consequence_manual':
    'El enlace de {names} deja de funcionar al instante. No avisamos a nadie, así que díselo tú.',
  'requests.void.consequence_managed':
    'El enlace de {names} deja de funcionar al instante y se detienen los recordatorios. No le enviamos ningún correo sobre esto.',
  'requests.void.slot':
    'Deja de contar entre tus solicitudes activas. Tu documento sigue en este dispositivo.',
  'requests.void.reason_label': '¿Por qué la anulas? (opcional)',
  'requests.void.reason_mistake': 'Enviada por error',
  'requests.void.reason_changes': 'El documento necesita cambios',
  'requests.void.reason_wrong_person': 'Persona o correo equivocados',
  'requests.void.reason_other': 'Otro',
  'requests.void.reason_other_label': 'Motivo',
  'requests.void.correct_instead': '¿Correo o nombre equivocados? Corrígelos en su lugar',
  'requests.void_failed': 'No se pudo anular la solicitud.',
  'requests.extend.current': 'Plazo actual: {date}',
  'requests.extend.week': '1 semana más',
  'requests.extend.two_weeks': '2 semanas más',
  'requests.extend.pick': 'Elegir una fecha',
  'requests.extend.date_label': 'Nuevo plazo',
  'requests.extend.preview': 'Nuevo plazo: {date}',
  'requests.extend.max': 'Puedes ampliarlo hasta el {date}.',
  'requests.extend_failed': 'No se pudo cambiar el plazo.',
  'requests.people_title': 'Personas',
  'requests.person_you': '{name} (tú)',
  'requests.you': 'Tú',
  'requests.email.sending': 'Correo en camino',
  'requests.email.delivered': 'Correo entregado',
  'requests.email.bounced': 'Correo rebotado',
  'requests.email.complained': 'Marcado como spam',
  'requests.email.suppressed': 'Correo bloqueado',
  'requests.attachments': 'Adjuntos: {collected} de {requested} recibidos',
  'requests.attachments_note': 'Los archivos recibidos aún no se pueden abrir aquí.',
  'requests.message.invite':
    'Hola, {name}: revisa y firma «{title}», por favor. El enlace funciona hasta el {date}.',
  'requests.message.invite_unnamed':
    'Revisa y firma «{title}», por favor. El enlace funciona hasta el {date}.',
  'requests.message.reminder':
    'Hola, {name}: te recuerdo que revises y firmes «{title}». El enlace funciona hasta el {date}.',
  'requests.message.reminder_unnamed':
    'Te recuerdo que revises y firmes «{title}». El enlace funciona hasta el {date}.',
  'requests.message.final_copy':
    'Hola, {name}: aquí tienes la copia firmada de «{title}». El enlace funciona hasta el {date}.',
  'requests.message.final_copy_unnamed':
    'Aquí tienes la copia firmada de «{title}». El enlace funciona hasta el {date}.',
  'import.prepare_send_title': 'Preparar una solicitud',
  'import.prepare_send_description':
    'Elige un PDF o una imagen y añade un firmante antes de enviarla.',
  'home.actions_label': 'Empezar',
  'home.recent_description': 'Los tres documentos más recientes de este dispositivo.',
  'home.templates_label': 'Plantillas recientes',
  'agreements.description': 'Aquí aparecen los documentos que firmas o envías.',
  'agreements.new': 'Nuevo acuerdo',
  'agreements.search_hint': 'Coincide con un título, un rol o una etiqueta.',
  'agreements.views_label': 'Vistas',
  'agreements.view_all': 'Todos',
  'agreements.view_drafts': 'Borradores',
  'agreements.view_waiting': 'Esperando a otros',
  'agreements.view_completed': 'Completados',
  'agreements.view_closed': 'Rechazados, anulados o vencidos',
  'agreements.view_attention': 'Requieren atención',
  'agreements.view_empty_drafts': 'No hay borradores en este dispositivo.',
  'agreements.empty_drafts_hint': 'Aquí aparecen los documentos que empezaste y no has terminado.',
  'agreements.empty_waiting': 'Nada está esperando firmas',
  'agreements.empty_waiting_hint':
    'Las solicitudes que envíes aparecen aquí hasta que todos firmen.',
  'agreements.empty_completed': 'Todavía no hay nada completado',
  'agreements.empty_completed_hint': 'Aquí aparecen los acuerdos que todos firmaron.',
  'agreements.empty_closed': 'Nada rechazado, anulado ni vencido',
  'agreements.empty_closed_hint':
    'Aquí aparecen las solicitudes que terminan sin todas las firmas.',
  'agreements.section_device': 'En este dispositivo',
  'agreements.section_requests': 'Solicitudes',
  'agreements.status_filter': 'Estado',
  'agreements.requests_signed_out': 'Inicia sesión para ver las solicitudes que has enviado.',
  'agreements.requests_failed': 'No se pudieron cargar tus solicitudes.',
  'agreements.requests_more_failed': 'No se pudieron cargar más solicitudes.',
  'agreements.requests_no_match': 'Ninguna solicitud coincide con esta búsqueda.',
  'agreements.local_failed': 'No se pudieron cargar tus acuerdos desde este dispositivo.',
  'agreements.retry': 'Reintentar',
  'agreements.load_more': 'Cargar más',
  'agreements.section_elsewhere': 'Solicitudes que no están en este dispositivo',
  'agreements.requests_empty': 'No hay solicitudes aquí',
  'agreements.requests_empty_hint': 'Las solicitudes que envíes aparecen aquí con su estado.',
  'agreements.request_progress': '{submitted} de {total} firmadas',
  'agreements.waiting_for_more': 'Esperando a {name} y {count} más',
  'agreements.at_sent': 'Enviado {date}',
  'agreements.at_completed': 'Completado {date}',
  'agreements.at_created': 'Creado {date}',
  'agreements.at_edited': 'Editado {date}',
  'agreements.expires': 'Vence {date}',
  'agreements.expires_today': 'Vence hoy a las {time}',
  'agreements.expired_on': 'Venció {date}',
  'agreements.delivery_manual': 'Tú compartes el enlace',
  'agreements.delivery_managed': 'Enviado por correo',
  'agreements.action_remind': 'Recordar',
  'agreements.action_share_again': 'Compartir de nuevo',
  'agreements.action_sign': 'Firmar',
  'agreements.action_finish': 'Terminar',
  'agreements.action_review': 'Revisar',
  'agreements.action_send_again': 'Enviar de nuevo',
  'agreements.view_request': 'Ver solicitud',
  'document.pages': '{count} páginas',
  'document.page_one': '1 página',
  'document.status.ready': 'Listo',
  'document.status.processing': 'Procesando',
  'document.status.error': 'Necesita atención',
  'templates.sort_and_filter': 'Ordenar y filtrar',
  'templates.description': 'Diseños guardados que puedes reutilizar en un documento nuevo.',
  'templates.reload_failed': 'No se pudieron recargar las plantillas.',
  'templates.favorite_failed': 'No se pudo actualizar esta plantilla.',
  'templates.duplicate_failed': 'No se pudo duplicar esta plantilla.',
  'templates.delete_failed': 'No se pudo eliminar esta plantilla.',
  'templates.toolbar_label': 'Controles de la biblioteca',
  'templates.search_empty': 'Ninguna plantilla coincide con esta búsqueda.',
  'templates.search_empty_description':
    'Tu biblioteca no ha cambiado. Borra la búsqueda o cambia el filtro para ver todas las plantillas.',
  'templates.empty_description':
    'Empieza con un acuerdo listo de abajo o importa tu propio PDF. Las plantillas se quedan en este dispositivo.',
  'templates.empty_import': 'Añadir tu propio documento',
  'templates.restore_menu': 'Restaurar o importar',
  'templates.restore_backup': 'Restaurar desde un archivo de copia',
  'templates.views': 'Mostrar',
  'templates.filter.all': 'Todas',
  'templates.action.purge': 'Eliminar para siempre',
  'templates.source_unavailable':
    'Falta el PDF de esta plantilla. Reemplaza el PDF para volver a usarla.',
  'templates.replace_pdf': 'Reemplazar PDF',
  'templates.deleted_toast_many': 'Se eliminaron {count} plantillas.',
  'templates.undo_all': 'Deshacer todo',
  'templates.open_recently_deleted_count': 'Eliminadas recientemente ({count})',
  'templates.recently_deleted_local_note':
    'Las plantillas eliminadas se quedan aquí 30 días en este dispositivo. Una copia de seguridad que exportaste antes conserva la suya.',
  'templates.agreements_bin_link':
    'Mira los acuerdos y las plantillas eliminados juntos en Eliminados recientemente.',
  'templates.purge_at': 'Se elimina para siempre el {date}',
  'templates.deleted_empty': 'No se eliminó nada en los últimos 30 días',
  'templates.deleted_empty_description': 'Las plantillas que elimines se quedan aquí 30 días.',
  'templates.purge_all': 'Vaciar Eliminadas recientemente',
  'templates.purge_title': '¿Eliminar «{title}» para siempre?',
  'templates.purge_all_title': '¿Vaciar Eliminadas recientemente?',
  'templates.purge_body':
    'No se puede deshacer. Las copias de seguridad que exportaste antes no cambian, y los acuerdos hechos con estas plantillas se quedan.',
  'templates.purge_confirm': 'Eliminar para siempre',
  'templates.purged': '«{title}» se eliminó para siempre.',
  'templates.purged_all': 'Eliminadas recientemente está vacío.',
  'templates.purge_failed': 'No se pudo eliminar esta plantilla para siempre.',
  'templates.new_source': 'Empezar desde',
  'templates.new_file_change': 'Elegir otro PDF',
  'templates.new_title_label': 'Nombre de la plantilla',
  'templates.new_title_missing': 'Ponle un nombre a la plantilla.',
  'templates.new_roles': 'Nombra los roles',
  'templates.new_roles_hint':
    'Los roles representan a las personas a quienes envías, como Cliente o Inquilino. Pones a las personas reales cada vez que la usas.',
  'templates.new_role_label': 'Rol {number}',
  'templates.default_role.signer': 'Firmante {number}',
  'templates.default_role.cc': 'Copia {number}',
  'templates.created': '«{title}» se guardó en tus plantillas.',
  'templates.saved': 'Se guardaron los cambios de «{title}».',
  'templates.use_now': 'Usarla ahora',
  'templates.saved_view': 'Ver en Plantillas',
  'templates.save_sender_note':
    'Tu propio paso de firma se guarda con tus campos. Si también firmas, coloca primero tu campo de firma.',
  'templates.new_place_fields': 'Colocar campos',
  'templates.new_file_next':
    'Después, añade a las personas que firman y coloca sus campos. No se envía nada.',
  'templates.make_title': 'Crear una plantilla',
  'templates.make_body':
    'Añade a las personas que firman como roles, por ejemplo Cliente, y coloca sus campos. Después guárdala como plantilla. Desde aquí no se envía nada.',
  'templates.editing_title': 'Editar la plantilla',
  'templates.editing_body':
    'Coloca, mueve o quita los campos de cada rol y después guarda. La plantilla solo cambia al guardar. Desde aquí no se envía nada.',
  'templates.leave_title': '¿Salir sin guardar?',
  'templates.leave_body': 'Los cambios en esta plantilla aún no se han guardado.',
  'templates.leave_discard': 'Descartar cambios',
  'templates.edit_place_fields': 'Colocar campos en la página',
  'templates.edit_place_save_first':
    'Guarda o descarta los cambios de arriba antes de colocar campos.',
  'templates.edit_open_failed': 'No se pudo abrir esta plantilla para editarla.',
  'templates.import_note':
    'Recupera una plantilla que eliminaste en los últimos 30 días, o restaura todo desde un archivo de copia en Ajustes.',
  'templates.preview_missing': 'Esta plantilla ya no está en este dispositivo.',
  'templates.preview_page': 'Página {page}',
  'templates.preview_more_pages': 'Aquí no se muestran {count} páginas más.',
  'templates.role_me': 'Tú',
  'templates.use_sheet_title': 'Usar esta plantilla',
  'templates.use_no_fields': 'Aún no tiene campos: los colocas después de este paso.',
  'templates.save_no_fields':
    'Este diseño aún no tiene campos. Colócalos primero, o guárdalo ya y colócalos cada vez que se use.',
  'templates.use_intro':
    '¿Para quién es? Los nombres van en el acuerdo; con un correo se le puede enviar.',
  'templates.use_sender_signs': 'Tú también firmas: tus campos vienen con la plantilla.',
  'templates.use_role_cc': 'recibe una copia',
  'templates.use_this_is_me': 'Soy yo',
  'templates.use_name': 'Nombre',
  'templates.use_name_missing': 'Añade un nombre para {role}.',
  'templates.use_email': 'Correo (opcional)',
  'templates.use_email_invalid':
    'Escribe un correo completo, como nombre@ejemplo.com, o déjalo vacío.',
  'templates.use_preview_all': 'Ver todas las páginas',
  'templates.use_extra_signer': 'Otro firmante',
  'templates.use_extra_add': 'Añadir otro firmante',
  'templates.use_extra_remove': 'Quitar firmante',
  'templates.use_extra_note': 'Colocas sus campos en el siguiente paso.',
  'templates.use_extra_name_missing': 'Añade un nombre o quita este firmante.',
  'templates.use_extra_limit': 'Hasta {count} personas pueden firmar un acuerdo.',
  'templates.use_agreement_title': 'Nombre del acuerdo',
  'templates.use_title_for': '{title} – {name}',
  'templates.use_continue_send': 'Continuar',
  'templates.use_continue_sign': 'Continuar para firmar',
  'templates.use_sign_instead': 'Firmarlo yo mismo',
  'templates.use_failed': 'No se pudo usar esta plantilla.',
  'templates.edit_role_label': 'Nombre del rol',
  'templates.edit_role_missing': 'Ponle un nombre a este rol.',
  'templates.edit_role_kind': 'Hace',
  'templates.role_kind.signer': 'Firma',
  'templates.role_kind.cc': 'Recibe una copia',
  'templates.edit_remove_role_fields':
    'Sus {count} campos quedarán sin responsable hasta que los asignes.',
  'templates.edit_remove_role_fields_one': 'Su campo quedará sin responsable hasta que lo asignes.',
  'templates.edit_keep_role': 'Conservar el rol',
  'templates.edit_failed': 'No se pudo guardar esta plantilla.',
  'templates.edit_discard': '¿Descartar los cambios en esta plantilla?',
  'templates.edit_discard_confirm': 'Descartar',
  'templates.edit_keep_editing': 'Seguir editando',
  'templates.edit_source': 'PDF',
  'templates.thumbnail_refreshed': 'Miniatura actualizada.',
  'templates.thumbnail_failed': 'No se pudo actualizar la miniatura.',
  'templates.replace_save_first': 'Guarda o descarta tus cambios antes de reemplazar el PDF.',
  'templates.replace_fits': 'Todos los campos caben en el PDF nuevo.',
  'templates.replace_decide':
    'El PDF nuevo tiene menos páginas. Elige qué pasa con {count} campos de las páginas que faltan.',
  'templates.replace_decide_one':
    'El PDF nuevo tiene menos páginas. Elige qué pasa con el campo de una página que falta.',
  'templates.replace_keep_field': 'Mover a la página 1',
  'templates.replace_remove_field': 'Quitar',
  'templates.replace_apply': 'Usar este PDF',
  'templates.replaced': 'PDF reemplazado.',
  'templates.replace_failed': 'No se pudo reemplazar el PDF.',
  'starter.title': 'Acuerdos de inicio',
  'starter.description':
    'Acuerdos listos para completar y enviar. Son un punto de partida, no asesoría legal.',
  'starter.categories': 'Categorías',
  'starter.category.all': 'Todos',
  'starter.category.confidentiality': 'Confidencialidad',
  'starter.category.housing': 'Vivienda',
  'starter.category.work': 'Trabajo',
  'starter.category.sales': 'Ventas',
  'starter.category.personal': 'Personal',
  'starter.category.finance': 'Finanzas',
  'starter.browse': 'Acuerdos de inicio',
  'starter.use': 'Usar este acuerdo',
  'starter.read_text': 'Leer el texto completo',
  'starter.choices': 'Elige los términos',
  'starter.choice_label': 'Opción {number}',
  'starter.free_one_signer':
    'Con el plan gratuito, un acuerdo se envía a {limit} persona más cada vez. Marca Soy yo para tu parte, o fírmalo tú en su lugar.',
  'starter.optional_hint': 'Frase opcional: desmárcala para dejarla fuera.',
  'starter.details': 'Completa los datos',
  'starter.details_hint':
    'Esto va en el acuerdo antes de enviarlo. Quienes firman completan sus propias partes.',
  'starter.detail_missing': 'Completa esto para continuar.',
  'starter.detail_unprintable': 'Estos caracteres todavía no se pueden imprimir: {characters}',
  'starter.who_signs': 'Quién firma',
  'starter.use_failed': 'No se pudo crear este acuerdo.',
  'insights.description': 'Lo que firmaste y enviaste, contado en este dispositivo y en tu cuenta.',
  'insights.empty_sign': 'Firmar un documento',
  'insights.empty_send': 'Enviar para firmar',
  'insights.backup_now': 'Hacer una copia ahora',
  'insights.connected.title': 'Solicitudes que enviaste',
  'insights.connected.refresh': 'Actualizar',
  'insights.connected.retry': 'Reintentar',
  'insights.connected.as_of': 'Actualizado {time}',
  'insights.connected.signed_out': 'Inicia sesión para ver las solicitudes que enviaste.',
  'insights.connected.sign_in': 'Iniciar sesión',
  'insights.connected.empty': 'No hay solicitudes abiertas ni enviadas en este periodo.',
  'insights.connected.open': 'Verlas en Acuerdos',
  'insights.connected.more': 'Más detalles',
  'insights.connected.too_few': 'Aún muy pocas',
  'insights.connected.hours': '{value} h',
  'insights.connected.days': '{value} días',
  'insights.connected.def.R01':
    'Solicitudes que enviaste que siguen abiertas, esperando a que alguien firme.',
  'insights.connected.def.R02':
    'Personas que todavía deben firmar una de tus solicitudes abiertas.',
  'insights.connected.def.R03': 'Personas a las que ahora les toca firmar.',
  'insights.connected.def.R04':
    'Invitaciones por correo que {product} entregó para solicitudes enviadas en este periodo.',
  'insights.connected.def.R05':
    'Firmantes que abrieron el acuerdo, en solicitudes enviadas en este periodo. La apertura se ve en la página de firma; no prueba quién lo abrió.',
  'insights.connected.def.R06':
    'Firmantes que terminaron su parte, en solicitudes enviadas en este periodo.',
  'insights.connected.def.R07': 'Solicitudes enviadas en este periodo que alguien rechazó.',
  'insights.connected.def.R08':
    'Solicitudes enviadas en este periodo que vencieron antes de que todos firmaran.',
  'insights.connected.def.R09': 'Solicitudes enviadas en este periodo que firmaron todos.',
  'insights.connected.def.R10':
    'La parte de las solicitudes enviadas en este periodo que firmaron todos.',
  'insights.connected.def.R11': 'El tiempo habitual desde el envío hasta que un firmante lo abre.',
  'insights.connected.def.R12': 'El tiempo habitual desde el envío hasta que un firmante termina.',
  'insights.connected.def.R13':
    'Correos de recordatorio que el servicio de correo aceptó para entregar.',
  'insights.scope_account': 'Tu cuenta',
  'insights.kpi.sent': 'Enviadas',
  'insights.kpi.sent_hint': 'Solicitudes enviadas en este periodo',
  'insights.kpi.rate': '{rate} de las enviadas',
  'insights.kpi.so_far': 'por ahora',
  'insights.kpi.pending': '{count} personas aún deben firmar',
  'insights.kpi.pending_one': '1 persona aún debe firmar',
  'insights.timing.needs': 'Se muestra desde {min} firmas ({count} por ahora)',
  'insights.timing.too_few': 'Aún muy pocas (hacen falta {count})',
  'insights.timing.based_on': 'Valor central de {count} firmas',
  'insights.timing.based_on_one': 'De 1 firma',
  'insights.chart.requests_title': 'Enviadas y firmadas por todos',
  'insights.chart.requests_description':
    'Solicitudes según el día en que se enviaron. Firmadas por todos cuenta las que todos han firmado desde entonces.',
  'insights.chart.sent': 'Enviadas',
  'insights.chart.day': 'Día',
  'insights.chart.week': 'Semana',
  'insights.chart.table': 'Ver como tabla',
  'insights.chart.device_title': 'Creados y terminados',
  'insights.chart.device_description':
    'Documentos en este dispositivo, según el día en que ocurrió.',
  'insights.chart.created': 'Creados',
  'insights.chart.finished': 'Terminados',
  'insights.outcome.title': 'En qué punto están',
  'insights.outcome.description': 'Las {count} solicitudes enviadas en este periodo.',
  'insights.outcome.description_one': 'La solicitud enviada en este periodo.',
  'insights.outcome.completed': 'Firmadas por todos',
  'insights.outcome.in_progress': 'Esperando firmas',
  'insights.outcome.declined': 'Rechazadas',
  'insights.outcome.expired': 'Vencidas',
  'insights.outcome.voided': 'Canceladas',
  'insights.activity.title': 'Actividad reciente',
  'insights.activity.all': 'Ver solicitudes',
  'insights.activity.empty': 'Todavía no ha pasado nada en tus solicitudes.',
  'insights.activity.sent': 'Enviada para firmar',
  'insights.activity.opened': 'Abierta por un firmante',
  'insights.activity.signed': 'Firmada por un firmante',
  'insights.activity.completed': 'Firmada por todos',
  'insights.activity.declined': 'Rechazada',
  'insights.activity.expired': 'Vencida',
  'insights.activity.voided': 'Cancelada',
  'insights.signers.title': 'Firmantes',
  'insights.signers.description':
    'Personas a las que se pidió firmar las solicitudes enviadas en este periodo. Una persona puede contar en más de una fila.',
  'insights.signers.delivery_note':
    'La entrega por correo y los recordatorios se siguen en las solicitudes que {product} envía por ti.',
  'insights.refresh': 'Volver a contar',
  'insights.trends_empty': 'No se creó ni se completó nada en este periodo.',
  'insights.window_label': '{period} · {dates}',
  'insights.value_about': 'unos {value}',
  'insights.drilldown_summary': 'Ver cuáles ({count})',
  'insights.drilldown_summary_one': 'Ver cuál (1)',
  'insights.drilldown_all': 'Ver las {count} en Acuerdos',
  'insights.template_gone': 'Una plantilla eliminada',
  'insights.row.untitled': 'Documento sin título',
  'insights.pending': 'Leyendo recuentos locales…',
  'editor.description': 'Coloca los campos, añade firmantes y envía.',
  'editor.reused_review_source':
    'Una copia nueva con los mismos campos y sin los datos de nadie. Revisa el documento por si queda algo de la vez anterior y añade a las personas a quienes enviarlo.',
  'editor.reused_sign': 'Una copia nueva con los mismos campos. Complétala y fírmala.',
  'editor.continue_to_send': 'Revisar y enviar',
  'editor.add_signer_first': 'Añade un firmante externo para continuar al envío.',
  'editor.note_more': 'Más',
  'editor.note_less': 'Menos',
  'editor.note_dismiss': 'Ocultar esta nota',
  'editor.go_to_recipients': 'Ir a los destinatarios',
  'editor.send_save_failed':
    'No se pudo guardar el borrador. Inténtalo de nuevo antes de enviarlo.',
  'editor.document_title': 'Título del documento',
  'editor.tools_label': 'Herramientas del documento',
  'editor.pending': 'Abriendo el documento…',
  'send.description': 'Elige un documento preparado y envíalo.',
  'send.pending': 'Cargando tus borradores…',
  'send.list_failed':
    'No se pudieron leer los borradores preparados en este dispositivo. Recarga para intentarlo de nuevo.',
  'recipient.progress': '{filled} de {required} campos obligatorios completados',
  'recipient.required_fields': 'Campos obligatorios',
  'settings.description': 'Tu cuenta, tus datos y lo que guarda este dispositivo.',
  'settings.sections_label': 'Secciones de ajustes',
  'settings.help_title': 'Ayuda y aviso legal',
  'settings.help_description': 'Preguntas, problemas y las condiciones de uso de la aplicación.',
  'settings.help_email': 'Escribir a soporte',
  'settings.recently_deleted_title': 'Eliminados recientemente',
  'settings.recently_deleted_description':
    'Los acuerdos firmados y las plantillas que eliminas en este dispositivo se guardan aquí {days} días y después se borran definitivamente.',
  'settings.recently_deleted_open': 'Abrir Eliminados recientemente',
  'workspace.title': 'Espacio de trabajo',
  'workspace.description':
    'Copias que eliges publicar para que un asistente de IA conectado pueda buscarlas. Tus documentos se quedan en este dispositivo.',
  'workspace.show_archived': 'Mostrar archivados',
  'workspace.signed_out': 'Inicia sesión para ver y publicar copias del espacio de trabajo.',
  'workspace.unavailable':
    'El espacio de trabajo aún no está abierto en este servicio. No se ha publicado nada.',
  'workspace.failed':
    'No se pudo actualizar el espacio de trabajo. No ha cambiado nada; inténtalo de nuevo.',
  'workspace.read_failed':
    'No se pudo leer tu espacio de trabajo. Inténtalo de nuevo en un momento.',
  'workspace.empty_title': 'No hay copias publicadas',
  'workspace.empty_description':
    'Publica abajo una copia de un documento cuando quieras que un asistente de IA pueda encontrarlo.',
  'workspace.state_private': 'Privado',
  'workspace.state_published': 'Publicado',
  'workspace.state_unpublished': 'Despublicado',
  'workspace.state_archived': 'Archivado',
  'workspace.state_deleted': 'Eliminado',
  'workspace.action_publish': 'Publicar',
  'workspace.action_unpublish': 'Despublicar',
  'workspace.action_archive': 'Archivar',
  'workspace.action_delete': 'Eliminar',
  'workspace.cancel': 'Cancelar',
  'workspace.item_meta': 'Versión {version} · actualizado {when}',
  'workspace.delete_confirm':
    '¿Eliminar esta copia y su texto buscable del servicio? Tu documento local no se ve afectado.',
  'workspace.publish_title': 'Publicar una copia',
  'workspace.publish_document': 'Documento',
  'workspace.publish_choose': 'Elige un documento',
  'workspace.disclosure':
    'Entiendo que {product} puede leer esta copia para poder buscarla. La copia permanece hasta que la despublique o la elimine.',
  'workspace.publish_button': 'Publicar copia',
  'workspace.nothing_to_publish':
    'Todos los documentos de este dispositivo ya tienen una copia publicada.',
  'workspace.no_documents': 'Todavía no hay documentos en este dispositivo.',
  'workspace.published': 'Se publicó una copia de «{title}».',
  'workspace.done_publish': '«{title}» vuelve a estar publicado.',
  'workspace.done_unpublish':
    '«{title}» está despublicado. Los asistentes de IA ya no pueden verlo.',
  'workspace.done_archive': '«{title}» está archivado.',
  'workspace.deleted':
    'Se eliminó «{title}». Sus bytes y su texto almacenados se borran en un plazo de {hours} horas.',
  'settings.account_delete_before': 'Antes de eliminar',
  'settings.group_general': 'Mis datos',
  'settings.group_prefs': 'Preferencias',
  'settings.group_account': 'Cuenta',
  'settings.group_ai': 'Conexiones de IA',
  'settings.group_data': 'Tus archivos',
  'settings.hub_account': 'Inicio de sesión, plan y créditos',
  'settings.hub_general': 'Tu nombre, tu correo y tus firmas guardadas',
  'settings.hub_prefs': 'Apariencia e idioma de las invitaciones',
  'settings.hub_data': 'Copia de seguridad, restauración y eliminados recientemente',
  'settings.hub_ai': 'Asistentes de IA y las copias que pueden leer',
  'settings.hub_help': 'Soporte, política de privacidad y términos',
  'settings.hub_danger': 'Descarga los datos de tu cuenta o elimínala',
  'settings.group_help': 'Ayuda y aviso legal',
  'settings.group_danger': 'Exportar o eliminar',
  'settings.back_to_draft': 'Volver a tu borrador',
  'settings.help_email_subject': 'Soporte de {product}',
  'settings.help_email_body': '\n\n---\nVersión de la aplicación: {version}',
  'settings.privacy_policy': 'Política de privacidad',
  'settings.terms': 'Condiciones de uso',
  'settings.version': '{product} versión {version}',
  'settings.diagnostics_export_failed': 'No se pudo crear el archivo de diagnóstico.',
  'settings.telemetry_label': 'Compartir datos de uso anónimos',
  'settings.my_info_use_account_email': 'Usar mi correo de inicio de sesión',
  'settings.signature_undo': 'Deshacer',
  'settings.signature_delete_failed': 'No se pudo eliminar la firma.',
  'transfer.step_backup_title': '1. Guarda una copia de seguridad aquí',
  'transfer.step_backup_body': 'Elige una frase de contraseña y guarda el archivo cifrado.',
  'transfer.step_backup_action': 'Ir a la copia de seguridad',
  'transfer.step_carry_title': '2. Lleva el archivo al dispositivo nuevo',
  'transfer.step_carry_body':
    'Usa iCloud Drive, Google Drive, AirDrop, un cable USB o envíatelo por correo. El archivo está cifrado.',
  'transfer.step_restore_title': '3. Restaura en el dispositivo nuevo',
  'transfer.step_restore_body':
    'Abre {product} allí, ve a Ajustes → Copia de seguridad, elige el archivo y escribe la frase de contraseña.',
  'transfer.step_restore_action': 'Restaurar en este dispositivo',
  'transfer.account_note':
    'Al iniciar sesión en el dispositivo nuevo recuperas tu plan y tus créditos. Tus documentos solo vuelven desde la copia de seguridad.',
  'settings.backup_status_never': 'Todavía no hay copia de seguridad.',
  'settings.backup_status_last': 'Última copia: {at}',
  'settings.backup_status_changes':
    'Hiciste cambios desde entonces. Haz otra copia para incluirlos.',
  'settings.backup_status_current': 'No ha cambiado nada en este dispositivo desde entonces.',
  'settings.backup_status_failed':
    'La copia del {at} no terminó. Tus documentos aquí no cambiaron.',
  'settings.backup_passphrase_repeat': 'Repite la frase de contraseña',
  'settings.backup_passphrase_mismatch': 'Las frases de contraseña no coinciden.',
  'settings.backup_saved': 'Copia guardada: {file}. Guárdala junto con su frase de contraseña.',
  'settings.backup_downloaded':
    'Archivo descargado: {file}. Muévelo a un lugar seguro, como una nube, y guarda su frase de contraseña.',
  'settings.backup_shared':
    'Archivo listo: {file}. Asegúrate de guardarlo en un lugar seguro, como Archivos o una nube.',
  'settings.backup_count_document_one': '{count} documento',
  'settings.backup_count_document': '{count} documentos',
  'settings.backup_count_completion_one': '{count} copia firmada',
  'settings.backup_count_completion': '{count} copias firmadas',
  'settings.backup_count_draft_one': '{count} borrador',
  'settings.backup_count_draft': '{count} borradores',
  'settings.backup_count_template_one': '{count} plantilla',
  'settings.backup_count_template': '{count} plantillas',
  'settings.backup_count_signature_one': '{count} firma guardada',
  'settings.backup_count_signature': '{count} firmas guardadas',
  'settings.backup_restore_description':
    'Elige un archivo de copia que guardaste antes. Verás lo que contiene antes de que cambie nada.',
  'settings.backup_restore_pick_another': 'Elegir otro archivo',
  'settings.backup_restore_not_backup': 'Ese archivo no es una copia de {product}, o está dañado.',
  'settings.backup_restore_account_export':
    'Ese es el archivo con los datos de tu cuenta. Es un registro para guardar, no una copia: no tiene documentos. Elige el archivo .agbackup que guardaste en Copia de seguridad.',
  'settings.backup_restore_made': 'Copia hecha el {at}.',
  'settings.backup_restore_passphrase': 'Frase de contraseña de esta copia',
  'settings.backup_restore_unlock': 'Abrir copia',
  'settings.backup_restore_checking': 'Abriendo…',
  'settings.backup_restore_wrong_passphrase': 'Esa frase de contraseña no abre esta copia.',
  'settings.backup_restore_newer_version':
    'Esta copia se hizo con una versión más nueva de la aplicación. Actualízala e inténtalo de nuevo.',
  'settings.backup_restore_damaged': 'No se pudo leer esta copia. Puede estar dañada.',
  'settings.backup_restore_failed':
    'No se pudo restaurar la copia. No cambió nada en este dispositivo.',
  'settings.backup_restore_contents': 'Contiene {items}.',
  'settings.backup_restore_contents_empty': 'No contiene documentos ni plantillas.',
  'settings.backup_restore_merge_hint':
    'Añade lo que falta. Todo lo que ya está en este dispositivo se queda como está.',
  'settings.backup_restore_replace_hint':
    'Se elimina todo lo de este dispositivo que no esté en la copia.',
  'settings.backup_restore_replace_removes':
    'Esto elimina {items} de este dispositivo, porque no están en la copia.',
  'settings.backup_restore_replace_removes_nothing': 'No se eliminaría nada de este dispositivo.',
  'settings.backup_restore_replace_overwrites_one':
    'También reemplaza {count} elemento que cambiaste en este dispositivo por la copia de seguridad.',
  'settings.backup_restore_replace_overwrites':
    'También reemplaza {count} elementos que cambiaste en este dispositivo por las copias de seguridad.',
  'settings.backup_restore_replace_confirm':
    'Entiendo que esto cambia lo que hay en este dispositivo',
  'settings.backup_restore_run_replace': 'Reemplazar y restaurar',
  'settings.backup_restore_conflicts_one':
    '{count} elemento es distinto de la copia de este dispositivo. Se conservó la de aquí.',
  'settings.appearance_title': 'Apariencia',
  'settings.appearance_label': 'Tema',
  'settings.appearance_description': 'Cómo se ve esta aplicación en este dispositivo.',
  'settings.appearance_system': 'Sistema',
  'settings.appearance_light': 'Claro',
  'settings.appearance_dark': 'Oscuro',
  'settings.appearance_system_hint': 'Sigue este dispositivo, y sigue siguiéndolo cuando cambia.',
  'settings.appearance_saved': 'Guardado en este dispositivo.',
  'settings.invitations_title': 'Invitaciones',
  'settings.invitations_description':
    'El idioma del correo de invitación y de la página de firma para las personas que añadas. El documento en sí no se traduce.',
  'settings.invitation_locale': 'Idioma predeterminado de las invitaciones',
  'settings.invitation_locale_saved':
    'Guardado. Las personas que añadas a partir de ahora empiezan en este idioma, y aún puedes cambiarlo para cada persona.',
  'common.new_version_available':
    'Hay una nueva versión disponible. Recarga cuando termines con el borrador actual.',
  'home.title': 'Inicio',
  'home.sign_document_description': 'Importa un PDF o una imagen y fírmalo en este dispositivo.',
  'home.send_for_signature_description':
    'Prepara una solicitud y compártela tú mismo o deja que la entreguemos.',
  'home.use_template_description': 'Reutiliza un diseño guardado.',
  'home.use_template_starter': 'Empieza con un acuerdo listo para usar.',
  'home.templates_title': 'Usar una plantilla',
  'home.templates_empty': 'Todavía no hay plantillas. Guarda una al firmar un documento.',
  'home.templates_manage': 'Gestionar plantillas',
  'home.use_again': 'Usar de nuevo',
  'home.continue': 'Continuar',
  'home.new_agreement_title': 'Nuevo acuerdo',
  'home.use_again_layout_repair': 'Este diseño necesita reparación antes de reutilizarse.',
  'home.use_again_failed': 'No se pudo crear un nuevo borrador desde esta fuente.',
  'home.drafts_check_failed': 'Algunos borradores no se pudieron comprobar ahora mismo.',
  'home.document_open_failed': 'Este documento no se pudo abrir en este dispositivo.',
  'import.choose_files': 'Elegir archivos',
  'import.drop_title': 'Suelta los archivos aquí o elígelos',
  'import.drop_hint': 'Los archivos se leen en este dispositivo. Importar no sube nada.',
  'import.send_drop_hint':
    'Los archivos se leen en este dispositivo. Solo al crear la solicitud, tras tu revisión, se sube una copia cifrada.',
  'import.accept_hint':
    'PDF, Word (DOCX), JPEG, PNG o HEIC. Guarda Excel y PowerPoint como PDF primero.',
  'import.mode_title': '¿Cómo se deben combinar estos archivos?',
  'import.mode_combined': 'Un documento (archivos combinados en orden)',
  'import.mode_separate': 'Documentos separados',
  'import.mode_combined_note':
    'Un paquete combinado usa una solicitud activa y un crédito de entrega.',
  'import.mode_combined_note_sign':
    'Los archivos forman un solo documento para firmar, en este orden.',
  'import.mode_separate_note_sign':
    'Cada archivo se guarda como un documento propio en Acuerdos, listo para abrir y firmar.',
  'import.import_button': 'Continuar',
  'import.combine_button': 'Combinar y continuar',
  'import.importing': 'Importando…',
  'import.state.importing': 'Leyendo…',
  'import.state.validating': 'Comprobando…',
  'import.state.converting': 'Convirtiendo…',
  'import.state.choice': 'Archivo de Word',
  'import.state.ready': 'Listo',
  'import.state.failed': 'Necesita atención',
  'import.state.cancelled': 'Cancelado',
  'import.pages': '{count} páginas',
  'import.page_one': '1 página',
  'import.retry': 'Reintentar',
  'import.remove': 'Quitar',
  'import.replace': 'Reemplazar',
  'import.replace_drops_title': 'Algunos campos se quedarían sin página',
  'import.replace_drops_one':
    'El documento nuevo tiene {pages}, así que se quitará 1 campo de una página posterior. Quién firma y tu mensaje se mantienen.',
  'import.replace_drops':
    'El documento nuevo tiene {pages}, así que se quitarán {count} campos de páginas posteriores. Quién firma y tu mensaje se mantienen.',
  'import.replace_drops_accept': 'Reemplazar y quitar esos campos',
  'import.replace_other': 'Elegir otro archivo',
  'import.move_up': 'Subir',
  'import.move_down': 'Bajar',
  'import.issue.encrypted': 'Este PDF está protegido con contraseña.',
  'import.issue.malformed': 'No se pudo abrir este archivo.',
  'import.issue.zero_pages': 'Este PDF no tiene páginas.',
  'import.issue.active_content':
    'Este PDF tiene scripts o partes de formulario activas que no se pueden firmar con seguridad aquí.',
  'import.issue.existing_signature':
    'Este PDF ya tiene una firma digital. Firmarlo aquí rompería esa firma.',
  'import.issue.too_large': 'Este archivo es demasiado grande para firmarlo aquí.',
  'import.issue.too_many_pages': 'Este PDF tiene demasiadas páginas para firmarlo de una vez.',
  'import.issue.pixel_limit': 'Esta imagen es demasiado grande para abrirla.',
  'import.issue.image_unsupported': 'Este tipo de imagen no se puede abrir aquí.',
  'import.issue.heic_unsupported': 'Este navegador no puede abrir fotos HEIC.',
  'import.issue.unknown_type': '{product} abre archivos PDF, Word (DOCX), JPEG, PNG y HEIC.',
  'import.issue.docx_failed': 'No pudimos convertir este archivo de Word.',
  'import.issue.docx_too_large': 'Este archivo de Word es demasiado grande para convertirlo aquí.',
  'import.issue.docx_too_complex':
    'Este archivo de Word es demasiado complejo para convertirlo aquí.',
  'import.issue.docx_too_many_pages': 'Este archivo de Word tiene más de {count} páginas.',
  'import.issue.docx_timeout': 'Este archivo de Word tardó demasiado en convertirse aquí.',
  'import.issue.doc_old_format':
    'Este es un archivo de Word antiguo (.doc), que no se puede convertir aquí.',
  'import.issue.docx_protected':
    'Este archivo de Word está protegido con contraseña o tiene el formato .doc antiguo.',
  'import.next.print_to_pdf': 'Imprímelo primero como PDF y añade esa copia.',
  'import.next.existing_signature':
    'Pide al remitente una copia sin firmar, o imprímela como PDF para firmar una copia aplanada.',
  'import.next.choose_supported': 'Elige un PDF o una foto.',
  'import.next.docx_export':
    'Guárdalo como PDF en Word (Archivo → Guardar como → PDF) y luego elige el PDF.',
  'import.next.docx_split':
    'Guárdalo en partes de hasta {count} páginas como PDF en Word (Archivo → Guardar como → PDF) y luego elígelas.',
  'import.next.heic': 'Elige un JPEG, o exporta la foto como JPEG y añádela de nuevo.',
  'import.next.pixel_limit':
    'Añade una copia más pequeña, como una captura de pantalla de la foto.',
  'import.issue.storage_write': 'Este dispositivo no pudo guardar el archivo.',
  'import.next.storage_write':
    'Si usas una ventana privada, abre {product} en una ventana normal. Si no, libera espacio en este dispositivo e inténtalo de nuevo.',
  'import.rotate': 'Girar',
  'import.drop_title_touch': 'Elige un documento o una foto',
  'import.camera': 'Hacer una foto',
  'import.camera_more': 'Añadir otra página',
  'import.partial':
    '{imported} importados · {failed} necesitan atención. Corrígelos o quítalos abajo.',
  'import.open_agreements': 'Abrir Acuerdos',
  'import.duplicate_warning':
    'El mismo archivo se añadió más de una vez. Conserva ambos solo si fue intencional.',
  'import.guidance.export_to_pdf_locally':
    'Convierte primero este archivo a PDF en este dispositivo (Imprimir → Guardar como PDF) y luego añade el PDF aquí.',
  'import.batch_failed':
    'No se importó nada. Corrige o quita el archivo que falló e inténtalo de nuevo.',
  'import.error_prefix': 'No se pudo importar este archivo.',
  'import.ready_count': '{count} listos',
  'import.empty': 'Añade al menos un archivo para continuar.',
  'import.thumbnail_alt': 'Vista previa de {name}',
  'import.template_title': 'Añade un documento para tu plantilla',
  'import.template_description':
    'Elige el documento y luego añade quién firma y coloca sus campos. Se convierte en plantilla al guardarla.',
  'import.sources_title': 'O añade desde',
  'import.sources_system_note':
    'Google Drive, OneDrive y otras apps de almacenamiento de este dispositivo se abren desde Elegir archivos.',
  'import.source.scan': 'Escanear',
  'import.source.scan_hint': 'Fotografía cada página',
  'import.source.library': 'Biblioteca de {product}',
  'import.source.library_hint': 'Plantillas y documentos guardados',
  'import.source.library_hint_documents': 'Tus documentos guardados',
  'import.source.google_drive': 'Google Drive',
  'import.source.dropbox': 'Dropbox',
  'import.source.cloud_hint': 'Elige un archivo para traerlo',
  'import.source.cloud_hint_system': 'Abre los archivos de tu dispositivo',
  'import.library_title': 'Desde tu biblioteca de {product}',
  'import.library_search': 'Buscar por nombre',
  'import.library_failed': 'No se pudo abrir tu biblioteca. Inténtalo de nuevo.',
  'import.library_templates': 'Plantillas',
  'import.library_gallery': 'Acuerdos listos para usar',
  'import.library_documents': 'Documentos guardados',
  'import.library_kind_template': 'Plantilla',
  'import.library_kind_document': 'Documento guardado',
  'import.library_documents_note':
    'Crea una copia nueva con las mismas páginas y campos. El original no cambia.',
  'import.library_no_documents': 'Aún no hay documentos guardados en este dispositivo.',
  'import.library_no_match': 'Nada coincide con ese nombre.',
  'import.cloud_help_title': 'Añadir desde {provider}',
  'import.cloud_help_body':
    '{provider} aún no está conectado a {product} en este navegador, pero puedes añadir un archivo desde allí.',
  'import.cloud_help_touch':
    'Toca Elegir archivos y luego elige {provider} en la lista de ubicaciones. Si no aparece, instala primero la app de {provider}.',
  'import.cloud_help_desktop':
    'Elige archivos y abre tu carpeta de {provider}, o descarga el archivo desde {provider} y elígelo en Descargas.',
  'import.cloud_failed': 'No se pudo abrir {provider}.',
  'import.cloud_popup_blocked':
    'Tu navegador bloqueó la ventana de {provider}. Permite las ventanas emergentes para este sitio e inténtalo de nuevo.',
  'import.cloud_too_large': 'Ese archivo es demasiado grande para firmarlo aquí.',
  'import.crop': 'Recortar',
  'import.crop_title': 'Recortar la página',
  'import.crop_hint': 'Arrastra las esquinas hasta los bordes del papel.',
  'import.crop_failed': 'No se pudo mostrar esta foto para recortarla.',
  'import.crop_handle_nw': 'Esquina superior izquierda',
  'import.crop_handle_ne': 'Esquina superior derecha',
  'import.crop_handle_sw': 'Esquina inferior izquierda',
  'import.crop_handle_se': 'Esquina inferior derecha',
  'import.crop_apply': 'Listo',
  'import.crop_reset': 'Usar la foto completa',
  'import.converted_badge': 'Convertido desde Word',
  'import.converted_title': 'Convertido desde Word: revisa el diseño',
  'import.converted_note':
    '{name} se convirtió en este dispositivo; no se subió nada. Las fuentes y el espaciado pueden variar un poco respecto a Word. Si algo no se ve bien, guárdalo como PDF en Word y añade ese archivo.',
  'import.converted_page': 'Página {number}',
  'import.converted_previous': 'Anterior',
  'import.converted_next': 'Siguiente',
  'import.converted_continue': 'Se ve bien, continuar',
  'import.docx_choice_title': 'Es un archivo de Word',
  'import.docx_choice_body':
    '{product} puede convertirlo en PDF en este dispositivo. Si ya tienes un PDF del documento, puedes usarlo en su lugar.',
  'import.docx_choice_convert': 'Convertirlo por mí',
  'import.docx_choice_pdf': 'Elegir un PDF',
  'import.choose_pdf': 'Elegir un PDF',
  'import.preview': 'Vista previa',
  'editor.finish_no_fields': 'Añade una firma u otro campo al documento antes de terminar.',
  'editor.finish_no_signature_title': 'Este documento no tiene firma',
  'editor.finish_no_signature_body':
    'Puedes terminarlo como formulario completado o añadir primero un campo de firma.',
  'editor.finish_without_signature': 'Terminar sin firmar',
  'editor.finish_add_signature': 'Añadir una firma',
  'editor.finish_issues_title': 'Antes de terminar',
  'editor.issue_go_to': 'Ir al campo',
  'editor.issue_page': 'Página {page}',
  'editor.unavailable':
    'Este documento no está disponible. Puede que se haya eliminado o que no se haya podido leer su copia guardada.',
  'editor.sent_title': 'Enviado para firmar',
  'editor.sent_body': 'Este acuerdo está pendiente de firma.',
  'editor.sent_locked':
    'Un acuerdo enviado no se puede cambiar. Su estado, sus enlaces y la copia firmada están en la solicitud. Para enviarlo de nuevo con cambios, úsalo otra vez como un borrador nuevo.',
  'editor.sent_view_request': 'Ver la solicitud',
  'editor.not_found_title': 'No se encontró el documento',
  'editor.owner_me': 'Yo',
  'editor.switch_to_sign': 'Firmarlo yo en su lugar',
  'editor.switch_to_send': 'Enviar para firma en su lugar',
  'editor.edit_recipients_message': 'Editar destinatarios y mensaje',
  'editor.to_finish_title': 'Antes de finalizar',
  'editor.ready_to_finish': 'Listo para finalizar.',
  'editor.ready_in_person': 'Listo para firmar en persona.',
  'editor.page_of': 'Página {page} de {count}',
  'editor.close_settings': 'Cerrar',
  'editor.settings_done': 'Listo',
  'editor.back': 'Volver a acuerdos',
  'editor.save_all': 'Todos los cambios guardados',
  'editor.save_retry': 'Reintentar',
  'editor.more': 'Más',
  'editor.finish_next': 'Siguiente',
  'editor.finish_blocked_one': '{count} pendiente',
  'editor.finish_blocked': '{count} pendientes',
  'editor.steps_label': 'Pasos',
  'editor.step_setup': 'Destinatarios y mensaje',
  'editor.step_fields': 'Campos',
  'editor.step_setup_short': 'Destinatarios',
  'editor.step_next_fields': 'Siguiente: añadir campos',
  'editor.back_to_fields': 'Volver a los campos',
  'editor.invalid_owners_one': '{count} campo necesita un responsable antes de poder enviarse.',
  'editor.invalid_owners': '{count} campos necesitan un responsable antes de poder enviarse.',
  'editor.signers_title': 'Personas que firman en este dispositivo',
  'editor.signers_hint':
    'Añade a cada persona que firmará aquí en persona. Los campos que coloques podrán ser suyos.',
  'editor.setup_signers': 'Firmantes',
  'editor.local_note':
    'Guardado en este dispositivo, cifrado. No se envía nada hasta que lo revises y compartas.',
  'editor.local_note_sign': 'Se guarda en este dispositivo, cifrado.',
  'editor.armed_hint': 'Haz clic o toca la página para colocar {field}.',
  'editor.armed_hint_touch': 'Toca la página para colocar {field}.',
  'editor.armed_cancel': 'Cancelar',
  'editor.view_label': 'Vista de página',
  'editor.previous_page': 'Página anterior',
  'editor.next_page': 'Página siguiente',
  'editor.fit_page': 'Ajustar página',
  'editor.field_settings': 'Ajustes',
  'editor.finish_blocked_none': 'Añade un campo primero',
  'editor.placing_for': 'Campos para',
  'editor.palette_search': 'Buscar campos',
  'editor.palette_no_match': 'Ningún campo coincide.',
  'editor.palette_more': 'Más campos',
  'editor.palette_group_signing': 'Firma',
  'editor.palette_group_info': 'Datos de contacto',
  'editor.palette_group_input': 'Entrada',
  'editor.recipient_remove_title': '¿Quitar a {name}?',
  'editor.recipient_remove_fields_one':
    '{name} tiene 1 campo. Al quitarle, también se quita su campo.',
  'editor.recipient_remove_fields':
    '{name} tiene {count} campos. Al quitarle, también se quitan sus campos.',
  'editor.recipient_remove_confirm': 'Quitar con sus campos',
  'editor.recipient_remove_move_to': 'O pasar sus campos a',
  'editor.recipient_remove_move_none': 'Nadie: quitar los campos',
  'editor.recipient_remove_move': 'Pasar campos y quitar',
  'editor.recipient_remove_cancel': 'Cancelar',
  'editor.recipient_removed': 'Se quitó a {name}.',
  'editor.recipient_removed_fields_one': 'Se quitó a {name} y su campo.',
  'editor.recipient_removed_fields': 'Se quitó a {name} y sus {count} campos.',
  'editor.recipient_removed_moved': 'Se quitó a {name}. Sus campos pasan a {to}.',
  'editor.recipient_more': 'Idioma',
  'editor.recipient_language': 'Idioma de la invitación',
  'editor.recipient_language_default': 'Inglés (predeterminado)',
  'editor.recipient_kind_cc_lifetime': 'Recibe una copia (Lifetime)',
  'editor.recipient_kind_cc_unavailable': 'Recibe una copia (no incluido en Free)',
  'editor.sender_row': 'Tú',
  'editor.recipient_email_hint': 'Necesario si {product} lo envía por correo por ti.',
  'editor.recipient_email_invalid': 'Revisa esta dirección de correo.',
  'editor.recipient_field_count_none': 'aún sin campos',
  'editor.placing_for_count': '{name} · {fields}',
  'editor.setup_issue_no_signer': 'Añade el nombre de la persona que tiene que firmar.',
  'editor.setup_issue_name': 'Añade su nombre.',
  'editor.only_you_question': '¿Solo tienes que firmar tú?',
  'editor.only_you_action': 'Fírmalo tú',
  'editor.change_document': 'Cambiar documento',
  'editor.field_assign': 'Asignar',
  'editor.recipient_name_placeholder': 'Nombre completo',
  'editor.routing': 'Orden de firma',
  'editor.routing_parallel': 'Todos a la vez',
  'editor.routing_sequential': 'Uno tras otro',
  'editor.move_up': 'Mover a {name} antes',
  'editor.move_down': 'Mover a {name} después',
  'editor.details': 'Detalles',
  'editor.details_more': 'Archivo (opcional)',
  'editor.issue.signature_missing': 'Añade tu firma',
  'editor.issue.initials_missing': 'Añade tus iniciales',
  'editor.issue.required_missing': 'Completa {field}',
  'editor.issue.tick_missing': 'Marca {field}',
  'editor.issue.text_too_long': '{field}: el texto es demasiado largo.',
  'editor.issue.preset_email': '{field}: escribe un correo electrónico, como nombre@ejemplo.com.',
  'editor.issue.preset_number': '{field}: escribe un número.',
  'editor.issue.preset_letters': '{field}: usa solo letras.',
  'editor.issue.preset_date': '{field}: escribe una fecha válida.',
  'editor.issue.preset_zip': '{field}: escribe un código postal de 5 dígitos.',
  'editor.issue.preset_zip4': '{field}: escribe un código ZIP+4, como 12345-6789.',
  'editor.issue.preset_ssn': '{field}: escribe el número como 123-45-6789.',
  'editor.issue.preset_custom': '{field}: el valor no tiene el formato requerido.',
  'editor.issue.formula': '{field}: no se puede calcular la fórmula. Revisa su expresión.',
  'editor.issue.formula_divide_by_zero': '{field}: la fórmula divide entre cero.',
  'editor.issue.formula_unresolved': '{field}: completa primero los campos que usa esta fórmula.',
  'editor.issue.unknown': '{field}: este campo necesita atención.',
  'editor.issue.compose_failed':
    'No se pudo crear la copia firmada. Tu borrador no ha cambiado; inténtalo de nuevo.',
  'lifecycle.importing': 'Importando',
  'lifecycle.import_failed': 'Error al importar',
  'lifecycle.draft': 'Borrador',
  'lifecycle.completed': 'Completado',
  'lifecycle.sent': 'Enviado',
  'completed.title': 'Firmado en este dispositivo',
  'completed.title_by': 'Firmado en este dispositivo por {names}',
  'completed.signer_you': 'ti',
  'completed.subtitle': 'Completado el {date}',
  'completed.pages_one': '1 página',
  'completed.pages': '{count} páginas',
  'completed.save_copy': 'Descargar PDF firmado',
  'completed.save_share_print': 'Guardar, compartir o imprimir',
  'completed.share': 'Compartir',
  'completed.verify': 'Comprobar la copia de este dispositivo',
  'completed.view': 'Ver el documento firmado',
  'completed.view_close': 'Ocultar el documento firmado',
  'completed.print': 'Imprimir',
  'completed.done': 'Listo',
  'completed.check_copy': 'Comprobar una copia guardada',
  'completed.check_hint':
    'Elige un PDF para ver si es exactamente la copia firmada aquí. No se sube nada.',
  'completed.device_time': '{time} (reloj de este dispositivo)',
  'completed.check_match': 'Este archivo es exactamente la copia firmada en este dispositivo.',
  'completed.check_mismatch':
    'Este archivo no es la copia firmada aquí. Puede ser otro documento o se cambió después de firmarlo.',
  'completed.verify_match': 'Esta copia coincide con la huella registrada al completarla.',
  'completed.verify_mismatch':
    'Esta copia no coincide con su huella registrada. No confíes en ella; usa Usar de nuevo para firmar una copia nueva.',
  'completed.saved': 'Guardado. La copia firmada también se queda en este dispositivo.',
  'completed.shared': 'Listo. La copia firmada también se queda en este dispositivo.',
  'completed.export_failed':
    'No funcionó. La copia firmada sigue en este dispositivo; inténtalo de nuevo.',
  'completed.details': 'Detalles',
  'completed.fingerprint': 'Huella (SHA-256)',
  'completed.time_label': 'Hora registrada por este dispositivo',
  'trash.back': 'Acuerdos',
  'trash.cancel': 'Cancelar',
  'trash.delete': 'Eliminar',
  'trash.deleted_toast': '«{title}» se movió a Eliminados recientemente. Se queda ahí 30 días.',
  'trash.delete_failed': 'No se pudo eliminar este acuerdo.',
  'trash.delete_named': 'Eliminar {title}',
  'trash.description':
    'Los acuerdos firmados y las plantillas que eliminas en este dispositivo se quedan aquí 30 días y luego se eliminan para siempre.',
  'trash.discarded_toast': 'Se eliminó «{title}».',
  'trash.dismiss': 'Cerrar',
  'trash.empty': 'No se eliminó nada en los últimos 30 días',
  'trash.empty_description':
    'Los acuerdos firmados y las plantillas que eliminas esperan aquí 30 días.',
  'trash.gone_on': 'Se elimina para siempre el {date}',
  'trash.deleted_on': 'Eliminado {date}',
  'trash.link': 'Eliminados recientemente ({count})',
  'trash.load_failed': 'No se pudo abrir Eliminados recientemente.',
  'trash.open': 'Abrir Eliminados recientemente',
  'trash.purge': 'Eliminar para siempre',
  'trash.purge_confirm': 'No se puede deshacer. Las copias que guardaste en otro sitio no cambian.',
  'trash.purged': '«{title}» se eliminó para siempre.',
  'trash.section_agreements': 'Acuerdos',
  'trash.section_templates': 'Plantillas',
  'trash.none_agreements': 'No hay acuerdos eliminados.',
  'trash.none_templates': 'No hay plantillas eliminadas.',
  'trash.template_restored': '«{title}» vuelve a estar en Plantillas.',
  'trash.template_restore_failed': 'No se pudo restaurar esta plantilla.',
  'trash.purge_failed': 'No se pudo eliminar este acuerdo para siempre.',
  'trash.restore': 'Restaurar',
  'trash.restored': '«{title}» vuelve a estar en Acuerdos.',
  'trash.restore_failed': 'No se pudo restaurar este acuerdo.',
  'trash.sent_blocked':
    'Este acuerdo se envió para firmar, así que no se puede eliminar aquí. Abre su solicitud.',
  'trash.title': 'Eliminados recientemente',
  'trash.undo': 'Deshacer',
  'certificate.download': 'Descargar resumen',
  'certificate.download_all': 'Descargar todo (.zip)',
  'certificate.hint_bundle': 'Descargar todo guarda el PDF firmado y el resumen juntos en un .zip.',
  'certificate.hint_remote':
    'El resumen es un PDF aparte, un certificado de finalización: quién firmó, cuándo y las huellas del PDF firmado.',
  'certificate.hint_local':
    'El resumen es un PDF aparte, un certificado de finalización: cuándo se firmó el acuerdo en este dispositivo y las huellas del PDF firmado.',
  'certificate.failed': 'No se pudo preparar el resumen. Inténtalo de nuevo.',
  'certificate.bundle_failed': 'No se pudieron preparar los archivos. Inténtalo de nuevo.',
  'certificate.bundle_saved': 'Archivos guardados en un .zip.',
  'certificate.bundle_started':
    'Descarga iniciada. Busca en las descargas del navegador el .zip con ambos archivos.',
  'certificate.heading': 'Certificado de finalización',
  'certificate.footer': 'Certificado de finalización de {product} · {reference}',
  'certificate.footer_local':
    'Certificado de finalización de {product} · registrado en este dispositivo',
  'certificate.page': 'Página {page} de {total}',
  'certificate.overview_reference': 'Referencia: {reference}',
  'certificate.overview_subject': 'Asunto: {title}',
  'certificate.overview_status': 'Estado: Completado',
  'certificate.overview_pages': 'Páginas del documento: {count}',
  'certificate.overview_signatures': 'Firmas: {count}',
  'certificate.overview_initials': 'Iniciales: {count}',
  'certificate.overview_routing': 'Orden de firma: {order}',
  'certificate.overview_sender': 'Enviado por',
  'certificate.overview_recorded': 'Registrado por',
  'certificate.overview_recorded_device': 'Solo este dispositivo',
  'certificate.time_zone':
    'Las horas se muestran en {zone} ({offset}). El registro de auditoría de las páginas siguientes usa UTC.',
  'certificate.time_zone_local':
    'Las horas se muestran en {zone} ({offset}), según el reloj de este dispositivo.',
  'certificate.made_on_request':
    'Generado a petición el {time} a partir de los registros que guarda {product}.',
  'certificate.made_on_device': 'Generado en este dispositivo el {time}.',
  'certificate.signer_copy_note':
    'Copia del firmante: las direcciones de correo solo aparecen en la copia del remitente.',
  'certificate.section_signers': 'Eventos de los firmantes',
  'certificate.section_in_person': 'Eventos de firma en persona',
  'certificate.section_copies': 'Copias',
  'certificate.section_summary': 'Eventos del resumen',
  'certificate.section_disclosure': 'Aviso sobre registros y firmas electrónicos',
  'certificate.column_signature': 'Firma',
  'certificate.column_timestamps': 'Fechas y horas',
  'certificate.column_status': 'Estado',
  'certificate.role_sender': 'Remitente',
  'certificate.mark_in_pdf': 'Aparece en el PDF firmado',
  'certificate.mark_none': 'Sin firmar',
  'certificate.mark_adoption': 'Firma adoptada: {method}',
  'certificate.mark_from_sender_device': 'La firma guardada del remitente, de este dispositivo',
  'certificate.mark_from_device': 'La firma guardada, de este dispositivo',
  'certificate.time_sent': 'Enviado: {time}',
  'certificate.time_queued': 'Correo en cola: {time}',
  'certificate.time_link_shared': 'Enlace compartido: {time}',
  'certificate.time_viewed': 'Visto: {time}',
  'certificate.time_signed': 'Firmado: {time}',
  'certificate.time_copy_shared': 'Copia firmada compartida: {time}',
  'certificate.copy_status': 'Recibe una copia',
  'certificate.disclosure': 'Aviso sobre registros y firmas electrónicos:',
  'certificate.disclosure_accepted': 'Aceptado: {time}, versión {version}, {language}',
  'certificate.disclosure_accepted_untimed': 'Aceptado, versión {version}, {language}',
  'certificate.disclosure_accepted_time': 'Aceptado: {time}',
  'certificate.disclosure_none': 'No registrado',
  'certificate.disclosure_intro':
    'La declaración que cada firmante aceptó antes de firmar, exactamente como se le mostró.',
  'certificate.disclosure_group': 'Versión {version}, {language}. Aceptado por: {names}',
  'certificate.disclosure_text_missing':
    'El texto de esta versión no forma parte de esta aplicación, así que no se repite aquí. Su SHA-256 es {hash}.',
  'certificate.summary_sent': 'Solicitud enviada',
  'certificate.summary_sent_status': 'Cifrada en el dispositivo del remitente',
  'certificate.summary_viewed': 'Primera vista',
  'certificate.summary_viewed_status': 'Documento mostrado',
  'certificate.summary_signed': 'Firma completa',
  'certificate.summary_signed_status': 'Todos firmaron',
  'certificate.summary_completed': 'Completado',
  'certificate.summary_completed_status': 'Huella del PDF firmado registrada',
  'certificate.summary_completed_local': 'Firmado en este dispositivo',
  'certificate.notice_retention':
    'Puedes descargar el PDF firmado y este certificado de {product} hasta el {date}. Después, {product} elimina el PDF y conserva solo los registros de finalización y auditoría que exigen sus reglas de retención. Guarda ambos ahora si los vas a necesitar.',
  'certificate.notice_unsigned':
    'Sin firma de {product}. No había una clave de firma aprobada cuando se completó este acuerdo, así que el registro de finalización no lleva firma del servicio. Las huellas de abajo son los registros que guarda {product}.',
  'certificate.notice_signed':
    '{product} firmó el registro de finalización con la clave {key} ({algorithm}). La firma cubre solo ese registro, identificado por el SHA-256 del registro de finalización de abajo. No cubre cuándo se generó este certificado ni los eventos de auditoría posteriores a la finalización, y no muestra cómo se ve el PDF.',
  'certificate.notice_local':
    'Registrado en el dispositivo. Este acuerdo se completó en este dispositivo y nunca se envió a {product}. Las horas vienen del reloj de este dispositivo y los nombres son los que se escribieron en él. {product} no vio, no firmó ni verificó este registro.',
  'certificate.section_agreement': 'Acuerdo',
  'certificate.section_participants': 'Participantes',
  'certificate.section_integrity': 'Huellas',
  'certificate.section_trail': 'Registro de auditoría',
  'certificate.section_privacy': 'Lo que este certificado no incluye',
  'certificate.title': 'Título',
  'certificate.reference': 'Referencia de la solicitud',
  'certificate.sent_by': 'Enviado por',
  'certificate.delivery': 'Entrega',
  'certificate.delivery_managed': 'Invitaciones enviadas por correo por {product}',
  'certificate.delivery_manual': 'Enlaces de firma compartidos por el remitente',
  'certificate.routing': 'Orden de firma',
  'certificate.routing_parallel': 'En cualquier orden',
  'certificate.routing_sequential': 'Uno tras otro',
  'certificate.created': 'Creado',
  'certificate.activated': 'Enviado',
  'certificate.completed': 'Completado',
  'certificate.generated': 'Certificado generado',
  'certificate.pages': 'Páginas',
  'certificate.completed_device': 'Completado (reloj de este dispositivo)',
  'certificate.device_time_label': 'Hora mostrada en este dispositivo',
  'certificate.identity_note':
    'Los nombres y correos son los que escribió el remitente. Abrir un enlace de firma demuestra que se tiene el enlace, no quién es la persona.',
  'certificate.local_identity_note':
    'Los nombres son los que se escribieron en este dispositivo; no se verificaron.',
  'certificate.local_signer_note':
    'Firmado en este dispositivo. No se registró el nombre de quien firmó con esta finalización.',
  'certificate.participant_sender': 'Remitente (paso de firma)',
  'certificate.participant_signer': 'Firmante {count}',
  'certificate.participant_owner': 'Firmado por',
  'certificate.owner_signed_at': 'Firmado (reloj de este dispositivo)',
  'certificate.owner_unnamed': 'El titular de este dispositivo (sin nombre registrado)',
  'certificate.participant_cc': 'Destinatario en copia {count}',
  'certificate.cc_role': 'Recibe una copia; no firma',
  'certificate.presented': 'Documento mostrado',
  'certificate.accepted': 'Firma aceptada',
  'certificate.consent': 'Consentimiento aceptado',
  'certificate.consent_value': 'Versión {version}, idioma {locale}',
  'certificate.consent_local': 'Aceptó firmar electrónicamente ({language})',
  'certificate.consent_hash': 'SHA-256 del texto de consentimiento',
  'certificate.response_hash': 'SHA-256 de la respuesta',
  'certificate.adoption_method': 'Método de firma',
  'certificate.method_draw': 'Dibujada',
  'certificate.method_type': 'Escrita',
  'certificate.method_upload': 'Imagen subida',
  'certificate.adopted_at': 'Adoptada (reloj de este dispositivo)',
  'certificate.integrity_note':
    'Una huella SHA-256 identifica un archivo exacto: cualquier cambio da otra huella. Muestra si un archivo cambió, no quién lo firmó ni cuándo.',
  'certificate.integrity_attested':
    'El dispositivo que completó el acuerdo calculó la huella del PDF final; {product} comprobó la copia cifrada que guarda.',
  'certificate.final_pdf': 'SHA-256 del PDF final',
  'certificate.final_pdf_size': 'Tamaño del PDF final',
  'certificate.bytes': '{count} bytes',
  'certificate.source': 'SHA-256 del documento original',
  'certificate.field_layout': 'SHA-256 de la disposición de campos',
  'certificate.responses': 'SHA-256 de las respuestas ({count})',
  'certificate.manifest': 'SHA-256 del registro de finalización',
  'certificate.audit_tip': 'SHA-256 del registro de auditoría al completarse',
  'certificate.field_values': 'SHA-256 de los valores completados',
  'certificate.profile': 'Perfil de evidencia',
  'certificate.composer': 'Compositor del PDF',
  'certificate.signature': 'Firma del servicio',
  'certificate.signature_signed': 'Firmado con la clave {key} ({algorithm})',
  'certificate.signature_unsigned': 'Sin firmar',
  'certificate.signature_local': 'Ninguna: registrado en este dispositivo',
  'certificate.trail_note':
    'Cada evento tal como lo registraron los servidores de {product}, en UTC.',
  'certificate.actor_sender': 'Remitente',
  'certificate.actor_sender_for': 'Remitente, para {participant}',
  'certificate.actor_system': '{product}',
  'certificate.actor_system_for': '{product}, para {participant}',
  'certificate.not_collected':
    'No se recopilan direcciones IP ni datos del dispositivo o del navegador, así que no aparecen aquí.',
  'certificate.limits':
    'Este certificado registra lo que observó {product}. No es una notarización, ni una verificación de identidad, ni asesoría legal.',
  'certificate.local_limits':
    'Este certificado registra lo que observó este dispositivo. No es una notarización, ni una verificación de identidad, ni asesoría legal.',
  'certificate.event.created': 'Solicitud creada',
  'certificate.event.activated': 'Solicitud enviada',
  'certificate.event.manual_share_intent': 'El remitente compartió un enlace de firma',
  'certificate.event.invitation_queued': 'Invitación en cola',
  'certificate.event.invitation_dispatched': 'Invitación enviada',
  'certificate.event.invitation_provider_result': 'Resultado de entrega del correo registrado',
  'certificate.event.bootstrap_observed': 'Enlace de firma solicitado',
  'certificate.event.document_presented': 'Documento mostrado',
  'certificate.event.consent_accepted': 'Consentimiento aceptado',
  'certificate.event.response_accepted': 'Firma aceptada',
  'certificate.event.recipient_corrected': 'Datos del destinatario corregidos',
  'certificate.event.deadline_extended': 'Plazo de firma ampliado',
  'certificate.event.attachment_accepted': 'Adjunto recibido',
  'certificate.event.attachment_replaced': 'Adjunto reemplazado',
  'certificate.event.attachment_access_authorized': 'Acceso al adjunto concedido',
  'certificate.event.attachment_notice_queued': 'Aviso de adjunto en cola',
  'certificate.event.reminder_queued': 'Recordatorio en cola',
  'certificate.event.reminder_dispatched': 'Recordatorio enviado',
  'certificate.event.reminder_skipped': 'Recordatorio omitido',
  'certificate.event.declined': 'Rechazado',
  'certificate.event.voided': 'Solicitud anulada',
  'certificate.event.preparation_discarded': 'Solicitud no enviada descartada',
  'certificate.event.expired': 'Solicitud vencida',
  'certificate.event.finalization_started': 'Finalización iniciada',
  'certificate.event.finalization_failed': 'Finalización fallida',
  'certificate.event.completed': 'Acuerdo completado',
  'certificate.event.final_download_authorized': 'Acceso a la copia final concedido',
  'certificate.event.artifact_deleted': 'Copia del servidor eliminada',
  'signin.gate_intro':
    'Inicia sesión una vez en este dispositivo. Tus documentos se quedan en este dispositivo. Solo se sube, cifrado, un documento que envías para firmar, para que la otra persona pueda firmarlo.',
  'signin.guest_title': '¿Añadir tu trabajo anterior a esta cuenta?',
  'signin.guest_body': 'Esto se creó en este dispositivo antes de que iniciaras sesión:',
  'signin.guest_documents_one': '1 documento',
  'signin.guest_documents_other': '{count} documentos',
  'signin.guest_templates_one': '1 plantilla',
  'signin.guest_templates_other': '{count} plantillas',
  'signin.guest_other_one': '1 elemento más (una firma, un borrador o Mis datos)',
  'signin.guest_other_other': '{count} elementos más (firmas, borradores y Mis datos)',
  'signin.guest_attach': 'Añadir a mi cuenta',
  'signin.guest_decline': 'No son míos',
  'signin.guest_decline_confirm':
    'Se quedarán en este dispositivo, pero no aparecerán en ninguna cuenta y no se te volverá a preguntar. No se puede deshacer.',
  'signin.guest_decline_yes': 'Dejarlos fuera de mi cuenta',
  'signin.guest_decline_back': 'Atrás',
  'signin.resumes_send':
    'Cuando inicies sesión, volverás a enviar tu solicitud. Tu borrador se conserva.',
  'signin.resumes_purchase': 'Cuando inicies sesión, volverás a tu compra.',
  'signin.resumes_account': 'Cuando inicies sesión, volverás a los ajustes de tu cuenta.',
  'signin.code_sent':
    'Enviamos un código de {digits} dígitos a {email}. Caduca en {minutes} minutos.',
  'signin.change_email': 'Cambiar correo',
  'signin.resend_wait': 'Enviar un código nuevo en {time}',
  'signin.check_waiting': 'Haciendo una comprobación de seguridad rápida…',
  'signin.check_failed':
    'No se pudo cargar la comprobación de seguridad. Revisa tu conexión o desactiva los bloqueadores de contenido para este sitio y vuelve a intentarlo.',
  'signin.check_retry': 'Reintentar',
  'signin.error_rate_limited_until':
    'No se pudo enviar un código porque se pidieron demasiados. Vuelve a intentarlo después de las {time} o usa un código que ya tengas.',
  'signin.error_rate_limited_in': 'Demasiados intentos. Vuelve a intentarlo en {time}.',
  'signin.use_different_email': 'Usar otro correo',
  'signin.legal': 'Al continuar, aceptas las {terms} y la {privacy}.',
  'signin.error_offline':
    'No tienes conexión. Iniciar sesión necesita conexión una vez; después {product} funciona sin conexión.',
  'signin.error_storage':
    'Este navegador no deja que {product} guarde documentos en este dispositivo, así que no se puede completar el inicio de sesión. Abre {product} en una ventana normal o permite los datos del sitio y vuelve a intentarlo.',
  'signin.gate_switched':
    'Saliste de la cuenta anterior en este dispositivo. Sus documentos siguen aquí, ocultos, hasta que vuelva a iniciar sesión. Inicia sesión con la cuenta que quieres usar.',
  'signin.gate_signed_out':
    'Cerraste sesión. Tus documentos se quedan en este dispositivo y vuelven cuando inicies sesión de nuevo.',
  'settings.marketing_failed': 'No se pudo guardar tu elección. Inténtalo de nuevo.',
  'settings.marketing_load_failed': 'No se pudo cargar tu elección.',
}

/**
 * Plural choice without a message syntax (UX-23): the catalog keeps one key per CLDR category the
 * locales need (`one`, `other`), and `Intl.PluralRules` picks between them. `{count}` is passed
 * through so the chosen message can print it.
 */
export function tPlural(
  i18n: I18n,
  keys: { readonly one: MessageKey; readonly other: MessageKey },
  count: number,
  params: Readonly<Record<string, string | number>> = {},
): string {
  const category = new Intl.PluralRules(i18n.locale).select(count)
  return i18n.t(category === 'one' ? keys.one : keys.other, { ...params, count })
}

/** A count with its unit, pluralised ("1 day", "3 days"), for sentences that hold several counts. */
export type CountUnit = 'days' | 'years' | 'files' | 'pages' | 'participants'

export function tCount(i18n: I18n, unit: CountUnit, count: number): string {
  return tPlural(i18n, { one: `count.${unit}_one`, other: `count.${unit}` }, count)
}

export interface I18n {
  readonly locale: LocaleCode
  t(key: MessageKey, params?: Readonly<Record<string, string | number>>): string
  /** Keys that fell back to English for the active locale (QA signal, LNG-02). */
  fallbackHits(): readonly MessageKey[]
  hasTranslation(key: MessageKey, locale: LocaleCode): boolean
}

/** A production bundle sets `import.meta.env.MODE`; a plain library build has no `env`. */
function isProductionBuild(): boolean {
  const meta = import.meta as ImportMeta & { readonly env?: { readonly MODE?: string } }
  return meta.env?.MODE === 'production'
}

/**
 * Substitutes `{name}` placeholders. A placeholder left unsubstituted is a bug in the caller, so
 * it fails loudly outside production builds instead of showing `{product}` to a signer.
 */
function interpolate(
  template: string,
  params?: Readonly<Record<string, string | number>>,
  key?: string,
): string {
  const missing: string[] = []
  const resolved = template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params?.[name]
    if (value === undefined) {
      missing.push(name)
      return match
    }
    return String(value)
  })
  if (missing.length > 0 && !isProductionBuild()) {
    throw new Error(`Missing message parameters for ${key ?? 'message'}: ${missing.join(', ')}`)
  }
  return resolved
}

export function createI18n(options: { locale: LocaleCode }): I18n {
  const { locale } = options
  const fallbackHits = new Set<MessageKey>()

  function hasTranslation(key: MessageKey, candidate: LocaleCode): boolean {
    if (candidate === 'en') return true
    return ES_MESSAGES[key] !== undefined
  }

  function t(key: MessageKey, params?: Readonly<Record<string, string | number>>): string {
    if (!(key in EN_MESSAGES)) {
      throw new Error(`Unknown message key: ${key}`)
    }
    if (locale === 'en') return interpolate(EN_MESSAGES[key], params, key)
    const localized = ES_MESSAGES[key]
    if (localized === undefined) {
      fallbackHits.add(key)
      return interpolate(EN_MESSAGES[key], params, key)
    }
    return interpolate(localized, params, key)
  }

  return { locale, t, fallbackHits: () => [...fallbackHits], hasTranslation }
}
